/* eslint-disable max-len */

// ===== Referenced Resources =====

// https://css-tricks.com/bounce-element-around-viewport-in-css/
// https://scotch.io/tutorials/drawing-and-animating-jelly-shapes-with-canvas
// https://codepen.io/raubaca/pen/rxLwPq

// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
}                                               from 'react';
import {
    useNavigate,
    useLocation,
}                                               from 'react-router-dom';

// ===== Components =====

import MenuButton                               from '../MenuButton';
import Jelly                                    from '../../Jelly';
import Modal                                    from '../Modal';
import {
    GenericModal,
}                                               from '../Editor/helpers';

// ===== Hooks =====

import {
    useInterval,
    useTimeout,
}                                               from '../../hooks';

// ===== Services =====

import {
    detectTouchDevice,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isSafariBrowser,
    isCustomEvent,
    playAudio,
    isMobile,
    updatePageTitle,
    validateEmail,
    isMacOS,
    detectBrowser,
    getUserIPInfo,
    recordUserAction,
    updateUser,
    sendMailingListSubscribeEmail,
    createMailingListRequest,
    isInMailingList,
    isEmailUnusedByOtherUsers,
    isTestMode,
}                                               from '../../services';
import useLazyAudioRef                                   from '../../services/useLazyAudioRef';

// ===== Constants =====

import KEYCODE                                  from '../../constants/keycodes';
import CURSOR_SIGN                              from '../../constants/cursorSigns';
import {
    CURSOR_Z_INDEX,
    DEFAULT_CSS_TRANSITION_DURATION,
    DEFAULT_ANIMATION_FPS,
    MILLISECONDS_IN_A_SECOND,
    BODY_FONT_SIZE,
    DEFAULT_AUDIO_VOLUME,
    DEFAULT_CSS_TRANSITION_DELAY,
    UNASSIGNED_ERROR_MESSAGE,
    GRAB_OBJECT_DELAY_DURATION,
    HOVER_TARGET_CLASSNAME,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    MISC_EMAIL_ADDRESS,
    SUPPORT_EMAIL_ADDRESS,
}                                               from '../../constants/generalConstants';
import {
    ADD_EXISTING_EMAIL_TO_MAILING_LIST_MESSAGE,
    OVERWRITE_EMAIL_MESSAGE,
}                                               from '../../constants/notificationMessages';

// ===== Enums =====

import {
    PAGE_ROUTE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    USER_ACTION_TYPE,
    AUTHENTICATION_TYPE,
}                                               from '../../enums';

// ===== Interfaces =====

import {
    ICoord,
    IContentBoundingBox,
    IInteractableObjectMouseDownEvent,
    IJellyMouseMoveEvent,
    IObjectDragEvent,
    IInteractableObjectState,
    IDimension,
    IPartialUserItem,
    IInteractableObjectPosition,
    ISnackbarItem,
    IMailingListSubscriptionValue,
    IModalMessage,
}                                               from '../../interfaces';

// ===== Images =====

import MoveSign                                 from '../../images/move.svg';
import GrabSign                                 from '../../images/grab.svg';
import ClickSign                                from '../../images/click.svg';
import EnvelopeIcon                             from '../../images/envelope.svg';
import CautionIcon                              from '../../images/caution.svg';
import Spinner                                  from '../../images/editor/spinner.svg';

// ===== Sounds =====

import ButtonClick                              from '../../sounds/button_click.mp3';
import ButtonHover                              from '../../sounds/button_hover.mp3';
import LogoCircleSwoosh                         from '../../sounds/logo_circle_swoosh.mp3';
import LogoLetterSwoosh                         from '../../sounds/logo_letter_swoosh.mp3';
import LogomarkExpand                           from '../../sounds/logomark_expand.mp3';
import LogomarkThump                            from '../../sounds/logomark_thump.mp3';
import MessageSuccess                           from '../../sounds/message_success.mp3';
import ObjectGrasp                              from '../../sounds/object_grasp.mp3';
import ObjectThump                              from '../../sounds/object_thump.mp3';
import CuneiformTabletThud                      from '../../sounds/cuneiform_thud.mp3';
import CuneiformTabletGlow                      from '../../sounds/cuneiform_glow.mp3';
import CuneiformTabletDolly                     from '../../sounds/cuneiform_dolly.mp3';
// import Typing                                   from '../../sounds/typing.mp3';
import LogomarkWobble11                         from '../../sounds/logomark_wobble_1_1.mp3';
import LogomarkWobble21                         from '../../sounds/logomark_wobble_2_1.mp3';
import LogomarkWobble22                         from '../../sounds/logomark_wobble_2_2.mp3';
import LogomarkWobble23                         from '../../sounds/logomark_wobble_2_3.mp3';
import LogomarkWobble24                         from '../../sounds/logomark_wobble_2_4.mp3';
import LogomarkWobble31                         from '../../sounds/logomark_wobble_3_1.mp3';
import LogomarkWobble32                         from '../../sounds/logomark_wobble_3_2.mp3';
import LogomarkWobble33                         from '../../sounds/logomark_wobble_3_3.mp3';
import LogomarkWobble41                         from '../../sounds/logomark_wobble_4_1.mp3';
import LogomarkWobble42                         from '../../sounds/logomark_wobble_4_2.mp3';
import LogomarkWobble43                         from '../../sounds/logomark_wobble_4_3.mp3';
import LogomarkWobble51                         from '../../sounds/logomark_wobble_5_1.mp3';
import LogomarkWobble52                         from '../../sounds/logomark_wobble_5_2.mp3';
import LogomarkWobble53                         from '../../sounds/logomark_wobble_5_3.mp3';
import LogomarkWobble54                         from '../../sounds/logomark_wobble_5_4.mp3';

// ===== Styles =====

import { theme }                                from '../../themes/theme-context';
import {
    Group,
    Path,
    Circle,
    Rect,
    Polygon,
}                                               from '../../styles';
import {
    VerascopeQuadrilateral,
    Quadrilateral,
    JellyContainer,
    JellyCanvas,
    CentroidContainer,
    LogomarkGroup,
    VerascopeShapeGroup,
    VerascopeCircle,
    VerascopeLetterE1,
    VerascopeLetterR,
    VerascopeLetterA,
    VerascopeLetterS,
    VerascopeLetterC,
    VerascopeLetterP,
    VerascopeLetterE2,
    VerascopeSlogan,
    MenuContainer,
    InputWrapper,
    Input,
    VerascopeLetterGroup,
    VerascopeTriangle,
    CentroidBackground,
    InteractableObjectContainer,
    InteractableObject,
    InteractableObjectShadow,
    InteractableObjectShadowGroup,
    RightArrow,
    LeftArrow,
    Footer,
    CopyrightClause,
    FooterButtonContainer,
    FooterButton,
    Envelope,
    XTwitter,
    TooltipContainer,
    TooltipIcon,
    TooltipText,
    Anchor,
    CuneiformTabletFlicker,
    CuneiformTabletContainer,
    CuneiformTablet,
    CuneiformTabletText,
    SpinnerContainer,
    Instagram,
}                                               from './styles';

interface Props {
    customCursorActive: boolean,
    browserTabIsFocused: number | null,
    hasSound: boolean,
    user: IPartialUserItem | null,
    userEmail: string | null,
    currentSessionId: string | null,
    viewportDimensions: IDimension,
    landingPageContentRevealed: boolean,
    cuneiformTabletRevealed: boolean,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    setLandingPageContentRevealed: React.Dispatch<React.SetStateAction<boolean>>,
    setCuneiformTabletRevealed: React.Dispatch<React.SetStateAction<boolean>>,
    setCursorSigns: React.Dispatch<React.SetStateAction<string[]>>,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    onCursorMouseDown: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    onCursorMouseUp: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function LandingView({
    customCursorActive,
    browserTabIsFocused,
    hasSound,
    user,
    userEmail,
    currentSessionId,
    viewportDimensions,
    landingPageContentRevealed,
    cuneiformTabletRevealed,
    setSnackbarData,
    setLandingPageContentRevealed,
    setCuneiformTabletRevealed,
    setCursorSigns,
    setInputFocused,
    onCursorEnter,
    onCursorLeave,
    onCursorMouseDown,
    onCursorMouseUp,
}: Props): JSX.Element {
    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();

    // ===== General Constants =====

    const CANVAS_MULTIPLIER = 1.7;
    // Initial width of Verascope Quadrilateral
    const INIT_QUAD_WIDTH = 400;
    // Initial height of Verascope Quadrilateral
    const INIT_QUAD_HEIGHT = 272.26;
    // Initial width of Verascope Logomark Triangle + Circle
    const INIT_SHAPES_WIDTH = 171.54;
    // Initial height of Verascope Logomark Triangle + Circle
    const INIT_SHAPES_HEIGHT = 78.18;
    // Initial width of Verascope Logomark ERASC
    const INIT_LETTER_GROUP_ONE_WIDTH = 335.38;
    // Initial height of Verascope Logomark ERASC
    const INIT_LETTER_GROUP_ONE_HEIGHT = 93.68;
    // Initial width of Verascope Logomark PE
    const INIT_LETTER_GROUP_TWO_WIDTH = 161.74;
    // Initial height of Verascope Logomark PE
    const INIT_LETTER_GROUP_TWO_HEIGHT = 103.24;
    // Y Offset of Verascope Logomark ERASC to align with Triangle + Circle
    const INIT_LETTER_GROUP_ONE_TOP_OFFSET = -11.55;
    // X Offset of Verascope Logomark ERASC to align with Triangle + Circle
    const INIT_LETTER_GROUP_ONE_LEFT_OFFSET = 64.83;
    // Y Offset of Verascope Logomark PE to align with Triangle + Circle
    const INIT_LETTER_GROUP_TWO_TOP_OFFSET = 5.33;
    // X Offset of Verascope Logomark PE to align with Triangle + Circle
    const INIT_LETTER_GROUP_TWO_LEFT_OFFSET = 488.45;
    // Desired Width for Verascope Quadrilateral
    const ADJUSTED_QUAD_WIDTH = detectTouchDevice(document) ? 0.5 * viewportDimensions.width : 0.3 * viewportDimensions.width;
    // Desired Height for Verascope Quadrilateral
    const ADJUSTED_QUAD_HEIGHT = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_QUAD_HEIGHT;
    // Desired Width for Canvas within which Jelly Object that holds Verascope Quadrilateral is Drawn
    const CANVAS_WIDTH = (): number => Math.min(viewportDimensions.width, CANVAS_MULTIPLIER * ADJUSTED_QUAD_WIDTH);
    // Desired Height for Canvas within which Jelly Object that holds Verascope Quadrilateral is Drawn
    const CANVAS_HEIGHT = (): number => Math.min(viewportDimensions.height, CANVAS_MULTIPLIER * ADJUSTED_QUAD_HEIGHT);
    // Desired Height for Verascope Logomark Triangle + Circle
    const SHAPES_WIDTH = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_SHAPES_WIDTH;
    // Desired Width for Verascope Logomark Triangle + Circle
    const SHAPES_HEIGHT = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_SHAPES_HEIGHT;
    // X Offset of Verascope Logomark Triangle + Circle
    const SHAPES_LEFT_OFFSET = -20 * (SHAPES_WIDTH / INIT_SHAPES_WIDTH);
    // Y Offset of Verascope Logomark Triangle + Circle
    const SHAPES_TOP_OFFSET = 5 * (SHAPES_HEIGHT / INIT_SHAPES_HEIGHT);
    // Desired Width for Verascope Logomark ERASC to align with Triangle + Circle
    const LETTER_GROUP_ONE_WIDTH = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_ONE_WIDTH;
    // Desired Height for Verascope Logomark ERASC to align with Triangle + Circle
    const LETTER_GROUP_ONE_HEIGHT = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_ONE_HEIGHT;
    // Desired Width for Verascope Logomark PE to align with Triangle + Circle
    const LETTER_GROUP_TWO_WIDTH = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_TWO_WIDTH;
    // Desired Height for Verascope Logomark PE to align with Triangle + Circle
    const LETTER_GROUP_TWO_HEIGHT = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_TWO_HEIGHT;
    // Y Offset of Verascope Logomark ERASC to align with Triangle + Circle
    const LETTER_GROUP_ONE_TOP_OFFSET = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_ONE_TOP_OFFSET;
    // X Offset of Verascope Logomark ERASC to align with Triangle + Circle
    const LETTER_GROUP_ONE_LEFT_OFFSET = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_ONE_LEFT_OFFSET;
    // Y Offset of Verascope Logomark PE to align with Triangle + Circle
    const LETTER_GROUP_TWO_TOP_OFFSET = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_TWO_TOP_OFFSET;
    // X Offset of Verascope Logomark PE to align with Triangle + Circle
    const LETTER_GROUP_TWO_LEFT_OFFSET = (ADJUSTED_QUAD_WIDTH / INIT_QUAD_WIDTH) * INIT_LETTER_GROUP_TWO_LEFT_OFFSET;
    // Desired Height for Verascope Logomark
    const LOGOMARK_HEIGHT = Math.max(SHAPES_HEIGHT, LETTER_GROUP_ONE_HEIGHT, LETTER_GROUP_TWO_HEIGHT);
    // Duration (in milliseconds) it takes for Verascope Logomark to drift horizontally across viewport
    const XDRIFT_DURATION = 13000;
    // Duration  (in milliseconds) it takes for Verascope Logomark to drift vertically across viewport
    const YDRIFT_DURATION = 7000;
    // Constant used to define which direction we take to be 'forward' drift
    const DRIFT_DIRECTION_FORWARD = 1;
    // Constant used to define which direction we take to be 'backward' drift
    const DRIFT_DIRECTION_BACKWARD = -1;
    // Right Padding Error Correction for Jelly Object
    const DRIFT_X_RIGHT_PADDING_ERROR = 35;
    // Left Padding Error Correction for Jelly Object
    const DRIFT_X_LEFT_PADDING_ERROR = 15;
    // Top Padding Error Correction for Jelly Object
    const DRIFT_Y_TOP_PADDING_ERROR = 15;
    // Bottom Padding Error Correction for Jelly Object
    const DRIFT_Y_BOTTOM_PADDING_ERROR = 20;
    // Initial Left Position of Jelly Object
    const JELLY_LEFT_EDGE_POSITION = -1 * ((CANVAS_WIDTH() - ADJUSTED_QUAD_WIDTH) / 2)
        - DRIFT_X_LEFT_PADDING_ERROR;
    // Initial Right Position of Jelly Object
    const JELLY_RIGHT_EDGE_POSITION = viewportDimensions.width
        - CANVAS_WIDTH()
        + (CANVAS_WIDTH() - ADJUSTED_QUAD_WIDTH) / 2
        + DRIFT_X_RIGHT_PADDING_ERROR;
    // Initial Top Position of Jelly Object
    const JELLY_TOP_EDGE_POSITION = -1 * ((CANVAS_HEIGHT() - ADJUSTED_QUAD_HEIGHT) / 2)
        - DRIFT_Y_TOP_PADDING_ERROR;
    // Initial Bottom Position of Jelly Object
    const JELLY_BOTTOM_EDGE_POSITION = viewportDimensions.height
        - CANVAS_HEIGHT()
        + (CANVAS_HEIGHT() - ADJUSTED_QUAD_HEIGHT) / 2
        + DRIFT_Y_BOTTOM_PADDING_ERROR;
    // Coefficient determining strength of Jelly Shake when bumping viewport extents
    const BUMP_IMPULSE_COEFF = 4;
    // Coefficient determining strength of Jelly Shake when expanding
    const EXPAND_IMPULSE_COEFF = 20;
    // Coefficient determining strength of Jelly Shake when contracting
    const CONTRACT_IMPULSE_COEFF = 40;
    // Extra Margin added when computing menu offsets for Menu Content
    const LOGOMARK_MARGIN = 100;
    // Coefficient determining size of slogan font
    const SLOGAN_FONT_MULTIPLIER = 1.5;
    // Margin size around slogan text
    const SLOGAN_MARGIN = 1.25 * BODY_FONT_SIZE;
    // Margin around menu arrows
    const ARROW_MARGIN = 0.625 * BODY_FONT_SIZE;
    // Height of menu arrow
    const ARROW_HEIGHT = 0.9375 * BODY_FONT_SIZE;
    // Coefficient determining size of copyright font
    const COPYRIGHT_FONT_MULTIPLIER = 0.75;
    // Margin around 'stay informed' input
    const INPUT_CONTAINER_MARGIN = 0.9375 * BODY_FONT_SIZE;
    // Width of Menu Button
    const MENU_BUTTON_WIDTH_MULTIPLIER = 18.75; // em
    // Height of Menu Button
    const MENU_BUTTON_HEIGHT_MULTIPLIER = 2.5; // em
    // Margin around Menu Button
    const MENU_BUTTON_MARGIN = 0.5 * BODY_FONT_SIZE;
    // Margin around all menu buttons
    const MENU_BUTTON_GROUP_MARGIN = 1.875 * BODY_FONT_SIZE;
    // Animation offset of each verascope logomark letter from resting position
    const LETTER_OFFSET = -20;
    // 'Stay Informed' Submission Response
    const SUBMITTED_EMAIL_MESSAGE = "we'll be in touch!";
    // Slogan
    const VERASCOPE_SLOGAN = 'learn playfully';
    // Touch Device Select Logo Tooltip
    const PULSE_TAP_TEXT = 'Tap Logo';
    // Non-touch Device Select Logo Tooltip
    const PULSE_CLICK_TEXT = 'Click Logo';
    // Grab Logo Tooltip
    const PULSE_GRAB_TEXT = 'Grab Logo';
    // Move Logo Tooltip
    const PULSE_MOVE_TEXT = 'Move Logo';
    // Select Logo Tooltip to use on current device type
    const PULSE_POINT_TEXT = isMobile() ? PULSE_TAP_TEXT : PULSE_CLICK_TEXT;
    // List of menu buttons
    const MENU_BUTTONS = [
        CURSOR_TARGET.about,
        CURSOR_TARGET.book,
        CURSOR_TARGET.stayInformed,
        CURSOR_TARGET.input,
        CURSOR_TARGET.continue,
        CURSOR_TARGET.treasureChest,
        // Keep the team page and navigation handler available while hiding its
        // landing-menu entry until the page is ready to be shown again.
        // CURSOR_TARGET.characters,
        CURSOR_TARGET.exit,
    ];
    // Total count of menu buttons
    // We subtract one because 'stay informed' and 'input' cannot be visible simultaneously
    const NUM_MENU_BUTTONS = MENU_BUTTONS.length - 1;
    // Number of menu buttons before the mailing list button
    const NUM_MENU_BUTTONS_BEFORE_MAILING_LIST_BUTTON = MENU_BUTTONS.indexOf(CURSOR_TARGET.stayInformed);
    // Index representing select tooltip
    const TOOLTIP_POINT_INDEX = 0;
    // Index representing grab tooltip
    const TOOLTIP_GRAB_INDEX = 1;
    // Duration (in milliseconds) for Cuneiform Tablet to Glow
    const CUNEIFORM_FLICKER_DURATION = 3750;
    // Initial Value of Countdown before Cuneiform Tablet is Dollied
    const INITIAL_CUNEIFORM_COUNTDOWN_DIGIT = Math.floor(CUNEIFORM_FLICKER_DURATION / MILLISECONDS_IN_A_SECOND);
    // Message once Cuneiform Tablet Countdown is complete
    const CUNEIFORM_COUNTDOWN_COMPLETE_TEXT = 'Let\'s go!';
    // Resting Message on Cuneiform when return to home screen
    const CUNEIFORM_IDLE_TEXT = 'Read Book!';
    // Snackbar Message when mailing list email is someone else's email
    const SNACKBAR_MESSAGE_EMAIL_USED_BY_OTHER_USER = (email: string): string => `'${email}' is already used by someone else.`;
    // Snackbar Message when user already on mailing list tries to subscribe again
    const SNACKBAR_MESSAGE_ALREADY_SUBSCRIBED_TO_MAILING_LIST = (email: string): string => `'${email}' is already subscribed to mailing list.`;
    // Snackbar Message when error encountered while subscribing user to mailing list
    const SNACKBAR_MESSAGE_UNCAUGHT_ERROR = 'Error encountered while attempting to subscribe email to mailing list.';
    // Snackbar Message when has an existing email associated with their account and presents a new one that they want to use to overwrite the old
    const SNACKBAR_MESSAGE_OVERWRITE_EMAIL = (email: string): string => `${email} is now subscribed to our mailing list and associated with this user account.`;
    // Snackbar Message when a user with an email auth account tries to add a different email to the mailing list and we prompt them if they want to add their current one
    const SNACKBAR_MESSAGE_ADD_EXISTING_EMAIL_TO_MAILING_LIST = (email: string): string => `Successfully subscribed '${email}' to mailing list.`;
    // Snackbar Message when a user with an email auth account tries to add a different email to the mailing list that's already on the mailing list
    const SNACKBAR_MESSAGE_EXISTING_EMAIL_ALREADY_ON_MAILING_LIST = (email: string): string => `Your email-authenticated account (${email}) is already subscribed to the mailing list and a second email cannot be added.`;

    // ===== Refs =====

    const JELLY_CANVAS_REF_NAME = 'jellyCanvasRef';
    const JELLY_CONTAINER_REF_NAME = 'jellyContainerRef';
    const CENTROID_REF_NAME = 'centroidRef';
    const SHAPE_REF_NAME = 'shapeRef';
    const TRIANGLE_REF_NAME = 'triangleRef';
    const CIRCLE_REF_NAME = 'circleRef';
    const LETTER_GROUP_TWO_REF_NAME = 'letterGroupTwoRef';
    const LEFT_ARROW_REF_NAME = 'leftArrowRef';
    const RIGHT_ARROW_REF_NAME = 'rightArrowRef';
    const INPUT_REF_NAME = 'inputRef';
    const MENU_CONTAINER_REF_NAME = 'menuContainerRef';
    const GAME_CONTROLLER_REF_NAME = 'gameControllerRef';
    const MAGNET_REF_NAME = 'magnetRef';
    const ABACUS_REF_NAME = 'abacusRef';
    const MOLECULE_REF_NAME = 'moleculeRef';
    const LIGHTBULB_REF_NAME = 'lightbulbRef';
    const PUZZLE_BLUE_REF_NAME = 'puzzleBlueRef';
    const PUZZLE_GREEN_REF_NAME = 'puzzleGreenRef';
    const PUZZLE_RED_REF_NAME = 'puzzleRedRef';
    const PUZZLE_ORANGE_REF_NAME = 'puzzleOrangeRef';
    const CUNEIFORM_TABLET_REF_NAME = 'cuneiformTabletRef';

    // ----- Logo
    const jellyCanvasRef = useRef<HTMLCanvasElement>(null);
    const jellyContainerRef = useRef<HTMLDivElement>(null);
    const centroidRef = useRef<HTMLDivElement>(null);
    const shapeRef = useRef<SVGSVGElement>(null);
    const triangleRef = useRef<SVGPolygonElement>(null);
    const circleRef = useRef<SVGCircleElement>(null);

    // ----- Logomark
    const letterGroupTwoRef = useRef<SVGSVGElement>(null);

    // ----- Interactable Objects
    const gameControllerRef = useRef<SVGSVGElement>(null);
    const magnetRef = useRef<SVGSVGElement>(null);
    const abacusRef = useRef<SVGSVGElement>(null);
    const moleculeRef = useRef<SVGSVGElement>(null);
    const lightbulbRef = useRef<SVGSVGElement>(null);
    const puzzleBlueRef = useRef<SVGSVGElement>(null);
    const puzzleGreenRef = useRef<SVGSVGElement>(null);
    const puzzleRedRef = useRef<SVGSVGElement>(null);
    const puzzleOrangeRef = useRef<SVGSVGElement>(null);
    const cuneiformTabletRef = useRef<HTMLDivElement>(null);

    // ----- Buttons
    const leftArrowRef = useRef<SVGSVGElement>(null);
    const rightArrowRef = useRef<SVGSVGElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuContainerRef = useRef<HTMLDivElement>(null);

    // ----- Sound Clips
    const buttonClickClip = useLazyAudioRef({
        src: ButtonClick,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const buttonHoverClip = useLazyAudioRef({
        src: ButtonHover,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const buttonSwooshClip = useLazyAudioRef({
        src: LogoLetterSwoosh,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logoCircleSwooshClip = useLazyAudioRef({
        src: LogoCircleSwoosh,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logoLetterSwooshClip = useLazyAudioRef({
        src: LogoLetterSwoosh,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkExpandClip = useLazyAudioRef({
        src: LogomarkExpand,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkThumpClip = useLazyAudioRef({
        src: LogomarkThump,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkSwooshClip = useLazyAudioRef({
        src: LogoLetterSwoosh,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const messageSuccessClip = useLazyAudioRef({
        src: MessageSuccess,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const objectGraspClip = useLazyAudioRef({
        src: ObjectGrasp,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const objectThumpClip = useLazyAudioRef({
        src: ObjectThump,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const cuneiformTabletThudClip = useLazyAudioRef({
        src: CuneiformTabletThud,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const cuneiformTabletGlowClip = useLazyAudioRef({
        src: CuneiformTabletGlow,
        volume: DEFAULT_AUDIO_VOLUME,
        loop: true,
    });
    const cuneiformTabletDollyClip = useLazyAudioRef({
        src: CuneiformTabletDolly,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    // const typingClip = useLazyAudioRef({ src: Typing, volume: DEFAULT_AUDIO_VOLUME });
    const logomarkWobble1Clip = useLazyAudioRef({
        src: LogomarkWobble11,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble21Clip = useLazyAudioRef({
        src: LogomarkWobble21,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble22Clip = useLazyAudioRef({
        src: LogomarkWobble22,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble23Clip = useLazyAudioRef({
        src: LogomarkWobble23,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble24Clip = useLazyAudioRef({
        src: LogomarkWobble24,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble31Clip = useLazyAudioRef({
        src: LogomarkWobble31,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble32Clip = useLazyAudioRef({
        src: LogomarkWobble32,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble33Clip = useLazyAudioRef({
        src: LogomarkWobble33,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble41Clip = useLazyAudioRef({
        src: LogomarkWobble41,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble42Clip = useLazyAudioRef({
        src: LogomarkWobble42,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble43Clip = useLazyAudioRef({
        src: LogomarkWobble43,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble51Clip = useLazyAudioRef({
        src: LogomarkWobble51,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble52Clip = useLazyAudioRef({
        src: LogomarkWobble52,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble53Clip = useLazyAudioRef({
        src: LogomarkWobble53,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const logomarkWobble54Clip = useLazyAudioRef({
        src: LogomarkWobble54,
        volume: DEFAULT_AUDIO_VOLUME,
    });

    // ===== State =====

    // index of tooltip action to present
    const [tooltipActionIndex, setTooltipActionIndex] = useState<number>(TOOLTIP_POINT_INDEX);
    // position state of interacatble objects
    const [
        initialInteractableObjectPositions,
        setInitialInteractableObjectPositions,
    ] = useState<IInteractableObjectState>({
        [INTERACTABLE_OBJECT.gameController]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.magnet]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.abacus]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.molecule]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.lightbulb]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.puzzleBlue]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.puzzleGreen]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.puzzleRed]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.puzzleOrange]: { left: 0, top: 0, rotate: 0 },
        [INTERACTABLE_OBJECT.cuneiformTablet]: { left: 0, top: 0, rotate: 0 }, // doesn't ever get read
    });
    // position state of cuneiform tablet
    const [
        initialCuneiformTabletPosition,
        setInitialCuneiformTabletPosition,
    ] = useState<IInteractableObjectPosition>({
        left: 0,
        top: 0,
        rotate: 0,
    });
    // reference to Jelly class the manages the behavior of verascope's logomark background
    const [jelly, setJelly] = useState<Jelly | null>(null);
    // initial position of verascope's logomark background
    const [initJellyPos] = useState<ICoord>({
        x: JELLY_LEFT_EDGE_POSITION,
        y: JELLY_TOP_EDGE_POSITION,
    });
    // current horizontal drift direction of verascope logomark
    const [xDriftDirection, setXDriftDirection] = useState<number>(DRIFT_DIRECTION_FORWARD);
    // current vertical drift direction of verascope logomark
    const [yDriftDirection, setYDriftDirection] = useState<number>(DRIFT_DIRECTION_FORWARD);
    // indicates whether verascope logo should drift or remain stationary
    const [animateLogo, setAnimateLogo] = useState<boolean>(true);
    // indicates whether jelly is being held
    const [jellyHeld, setJellyHeld] = useState<boolean>(false);
    // indicates whether jelly sounds and shaking should be triggered
    const [wobbleJelly, setWobbleJelly] = useState<IJellyMouseMoveEvent | null>(null);
    // indicates whether interactable objects are visible
    const [objectsRevealed, setObjectsRevealed] = useState<boolean>(false);
    // indicates whether Menu button listeners should be activated
    const [enableMenuButtonHoverEvents, setEnableMenuButtonHoverEvents] = useState<boolean>(false);
    // offset of menu to shift it back into view within the viewport
    const [menuOffset, setMenuOffset] = useState<ICoord>({ x: 0, y: 0 });
    // indicates whether user has expressed interest to stay informed
    const [stayInformed, setStayInformed] = useState<boolean>(false);
    // indicates whether continue menu button should have an enter and exit transition delay
    const [delayContinueButton, setDelayContinueButton] = useState<boolean>(false);
    // indicates whether hover arrows besides menu buttons should be presented
    const [arrowsRevealed, setArrowsRevealed] = useState<boolean>(false);
    // stores email address of user who's expressed interest in staying informed
    const [email, setEmail] = useState<string>('');
    // indicates whether email address of user who's expressed interest in staying informed is valid or not
    const [emailValid, setEmailValid] = useState<boolean>(true);
    // indicates whether user is currently submitting their email address
    const [isSubmittingEmail, setIsSubmittingEmail] = useState<boolean>(false);
    // indicates whether user has submitted their email address
    const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false);
    // holds the initial position where an interactable object being dragged was clicked
    const [objectInitialClickPoint, setObjectInitialClickPoint] = useState<IObjectDragEvent>({
        x: 0,
        y: 0,
        name: null,
    });
    // holds the initial origin position of an interactable object when it was initially clicked
    const [objectInitialOriginPoint, setObjectInitialOriginPoint] = useState<IObjectDragEvent>({
        x: 0,
        y: 0,
        name: null,
    });
    // indicates whether an interactable object is being held
    const [objectHeld, setObjectHeld] = useState<boolean>(false);
    // indicates whether we've already recorded jelly logo grab user action
    const [recordedJellyMoveUserAction, setRecordedJellyMoveUserAction] = useState<boolean>(false);
    // indicates whether we've already recorded interactable object grab user action
    const [recordedObjectMoveUserAction, setRecordedObjectMoveUserAction] = useState<boolean>(false);
    // indicates whether viewport (camera) is dollying toward cuneiform tablet
    const [dollyingTowardCuneiformTablet, setDollyingTowardCuneiformTablet] = useState<boolean>(false);
    // indicates whether cuneiform countdown should initiate
    const [initiateCuneiformCountdown, setInitiateCuneiformCountdown] = useState<boolean>(false);
    // stores current cuneiform countdown digit
    const [cuneiformCountdownDigit, setCuneiformCountdownDigit] = useState<number>(INITIAL_CUNEIFORM_COUNTDOWN_DIGIT);
    // indicates whether cuneiform tablet should be temporarily hidden
    const [temporarilyHideCuneiformTablet, setTemporarilyHideCuneiformTablet] = useState<boolean>(false);
    // Stores the location key for which a view action has been recorded.
    const recordedViewPageUserActionKey = useRef<string | null>(null);
    // Tracks whether the one-time automatic landing page reveal has already been used or dismissed.
    const autoRevealLandingPageContentConsumed = useRef<boolean>(false);
    // Stores timestamp of when user entered page
    const [startOrResumeBrowsingTimestamp, setStartOrResumeBrowsingTimestamp] = useState<number>(Date.now());
    // Stores duration reader has been reading post before tab is unfocused
    const [cachedPostFocusedDuration, setCachedPostFocusedDuration] = useState<number>(0);
    // Stores the state of the editor modal
    const [modal, setModal] = useState<{
        visible: boolean,
        message: IModalMessage,
        proceedCallback?:() => void,
        rejectCallback?: () => void,
            }>({
                visible: false,
                message: {},
            });

    // ===== Animation Constants =====

    // Duration (in milliseconds) for Verascope Triangle-Circle to change fill color
    const SHAPE_FILL_DURATION = 200;
    // Duration (in milliseconds) for Verascope Circle to Translate (to make space for ERASC Letters)
    const CIRCLE_TRANSLATE_DURATION = DEFAULT_CSS_TRANSITION_DURATION;
    // Duration (in milliseconds) that Verascope Circle must delay before translating out (to make space for ERASC Letters)
    const CIRCLE_EXPAND_DELAY_DURATION = 300;
    // Duration (in milliseconds) for Verascope Circle must delay before translating in (to fill space of ERASC Letters)
    const CIRCLE_CONTRACT_DELAY_DURATION = CIRCLE_EXPAND_DELAY_DURATION + 100;
    // Duration (in milliseconds) for Centroid that holds Menu Contnet to Expand out
    const CENTROID_BACKGROUND_EXPAND_DURATION = 300;
    // Duration (in milliseconds) that every Verascope Letter must delay before translating out
    const LETTER_DELAY_DURATION = CIRCLE_EXPAND_DELAY_DURATION + CIRCLE_TRANSLATE_DURATION;
    // Duration (in milliseconds) that each Verascope Letter must delay from previous Letter
    const LETTER_DELAY_OFFSET_DURATION = 50;
    // Duration (in milliseconds) for Verascope Letter translation
    const LETTER_TRANSLATE_DURATION = 75;
    // Duration (in milliseconds) for Verascope Letter Opacity Reveal/Hide
    const LETTER_OPACITY_DURATION = LETTER_TRANSLATE_DURATION + 75;
    // Duration (in milliseconds) that Entire Verascope Logomark Takes to Execute
    const LOGOMARK_EXPANSION_DURATION = Math.max(
        LETTER_DELAY_DURATION + 6 * LETTER_DELAY_OFFSET_DURATION
        + LETTER_TRANSLATE_DURATION + DEFAULT_CSS_TRANSITION_DELAY, // Duration for last e to show up
        CIRCLE_TRANSLATE_DURATION + DEFAULT_CSS_TRANSITION_DELAY, // Duration for circle to expand out
        CENTROID_BACKGROUND_EXPAND_DURATION + DEFAULT_CSS_TRANSITION_DELAY, // Duration for background to expand out
    );
    // Duration (in milliseconds) for Veracope Slogan to Translate
    const SLOGAN_TRANSLATE_DURATION = DEFAULT_CSS_TRANSITION_DURATION / 2;
    // Duration (in milliseconds) for Menu Content to Translate when correcting for Menu Content Position
    const LOGOMARK_OFFSET_EXPAND_DURATION = 200;
    // Duration (in milliseconds) for Menu Content to Translate when correcting for Menu Content Position
    const LOGOMARK_OFFSET_CONTRACT_DURATION = 300;
    // Duration (in milliseconds) from Verascope Logomark Expansion until Slogan Visible
    const SLOGAN_EXPANSION_DURATION = LOGOMARK_EXPANSION_DURATION
        + LOGOMARK_OFFSET_EXPAND_DURATION
        + SLOGAN_TRANSLATE_DURATION;
    // Duration (in milliseconds) for Menu Button to Translate
    const MENU_BUTTON_TRANSLATE_DURATION = DEFAULT_CSS_TRANSITION_DURATION / 2;
    // Duration (in milliseconds) that Menu Button must delay from previous button
    const MENU_BUTTON_DELAY_DURATION = MENU_BUTTON_TRANSLATE_DURATION;
    // Total count of hidden menu buttons at present
    const NUM_HIDDEN_MENU_BUTTONS = email.length > 0 && delayContinueButton ? 1 : 2;
    // Duration (in milliseconds) for
    const EXPANSION_DURATION = SLOGAN_EXPANSION_DURATION
        + MENU_BUTTON_TRANSLATE_DURATION
        + (MENU_BUTTONS.length - NUM_HIDDEN_MENU_BUTTONS) * MENU_BUTTON_DELAY_DURATION
        + DEFAULT_CSS_TRANSITION_DELAY;
    // Duration (in milliseconds) from Verascope Logomark Contraction until Verascope Letters Removed
    const LETTER_REVERSE_DELAY_DURATION = LETTER_DELAY_DURATION + 6 * LETTER_DELAY_OFFSET_DURATION;
    // Duration (in milliseconds) for Centroid that holds Menu Content to Delay Contracting
    const CENTROID_CONTRACT_DELAY_DURATION = LETTER_REVERSE_DELAY_DURATION / 2;
    // Duration (in milliseconds) for 'Stay Informed' Typing Response to Complete
    const TYPING_DURATION = 1 * MILLISECONDS_IN_A_SECOND;
    // Duration (in milliseconds) for 'Stay Informed' Typing Caret to Blink
    const CARET_BLINKING_DURATION = 1 * MILLISECONDS_IN_A_SECOND;
    // Duration (in milliseconds) that Interactable Object must delay from other objects
    const OBJECT_DELAY_OFFSET_DURATION = 200;
    // Duration (in milliseconds) for tooltip action to change
    const TOOLTIP_ACTION_ALTERNATE_DURATION = 4 * MILLISECONDS_IN_A_SECOND;
    // Duration (in milliseconds) for Tooltip to Delay Being Shown
    const TOOLTIP_DELAY_DURATION = EXPANSION_DURATION;
    // Duration (in milliseconds) for Tooltip Icon to Reveal/Hide
    const TOOLTIP_ICON_TRANSITION_DURATION = DEFAULT_CSS_TRANSITION_DURATION / 2;
    // Duration (in milliseconds) for Tooltip Icon to Pulse
    const PULSE_DURATION = 2 * MILLISECONDS_IN_A_SECOND;
    // Duration (in milliseconds) for Cuneiform Tablet to be Dollied (Zoomed In)
    const CUNEIFORM_DOLLY_DURATION = 200;
    // Duration (in milliseconds) for Cuneiform Tablet to Dolly
    const BOOK_TRANSITION_DURATION = CUNEIFORM_FLICKER_DURATION;
    // Duration (in milliseconds) for Cuneiform Tablet Text to be Visible
    const CUNEIFORM_TABLET_TEXT_REVEAL_DURATION = 150;
    // Duration (in milliseconds) for Cuneifrom Tablet to be Visible
    const CUNEIFORM_TABLET_REVEAL_DURATION = 150;
    // Duration (in milliseconds) before we show landing page content if no user interaction
    const WAIT_FOR_USER_INTERACTION_DURATION = 15 * MILLISECONDS_IN_A_SECOND;

    // ===== Interactable Object Constants =====

    const CONTROLLER_WIDTH = 9.375 * BODY_FONT_SIZE;
    const ABACUS_WIDTH = 0.9 * CONTROLLER_WIDTH;
    const ABACUS_HEIGHT = 0.99 * ABACUS_WIDTH;
    const MAGNET_WIDTH = 0.53 * CONTROLLER_WIDTH;
    const MOLECULE_WIDTH = 0.49 * CONTROLLER_WIDTH;
    const LIGHTBULB_WIDTH = 0.26 * CONTROLLER_WIDTH;
    const PUZZLE_GREEN_WIDTH = 0.18 * CONTROLLER_WIDTH;
    const PUZZLE_BLUE_WIDTH = 0.18 * CONTROLLER_WIDTH;
    const PUZZLE_RED_WIDTH = 0.32 * CONTROLLER_WIDTH;
    const PUZZLE_ORANGE_WIDTH = 0.32 * CONTROLLER_WIDTH;
    const CUNEIFORM_TABLET_WIDTH = 2 * CONTROLLER_WIDTH;
    const OBJECT_REVEAL_DURATION = DEFAULT_CSS_TRANSITION_DURATION / 2;
    const OBJECT_GRAB_DURATION = DEFAULT_CSS_TRANSITION_DURATION / 2;
    const OBJECT_GRAB_SCALE = 1.5;
    const OBJECT_SHADOW_SCALE = 1.2;
    const DOLLY_SCALE = 10;
    const INTERACTABLE_OBJECT_ROTATE_LIMIT = 45;

    // ===== Methods =====

    /**
     * Triggers jelly wobble sound that varies in intensity based on mouse velocity
     * @param e jelly mouse event
     */
    const playJellyWobbleSound = useCallback((e: IJellyMouseMoveEvent): void => {
        const { velocity } = e.detail;
        let soundBank: React.MutableRefObject<HTMLAudioElement | null>[];
        if (velocity >= 0 && velocity < 25) {
            soundBank = [
                logomarkWobble1Clip,
            ];
        } else if (velocity >= 25 && velocity < 50) {
            soundBank = [
                logomarkWobble21Clip,
                logomarkWobble22Clip,
                logomarkWobble23Clip,
                logomarkWobble24Clip,
            ];
        } else if (velocity >= 50 && velocity < 100) {
            soundBank = [
                logomarkWobble31Clip,
                logomarkWobble32Clip,
                logomarkWobble33Clip,
            ];
        } else if (velocity >= 100 && velocity < 150) {
            soundBank = [
                logomarkWobble41Clip,
                logomarkWobble42Clip,
                logomarkWobble43Clip,
            ];
        } else {
            soundBank = [
                logomarkWobble51Clip,
                logomarkWobble52Clip,
                logomarkWobble53Clip,
                logomarkWobble54Clip,
            ];
        }

        // select clip randomly from soundback
        const sound = soundBank[Math.floor(Math.random() * soundBank.length)];
        if (sound.current) {
            playAudio(sound.current);
        }
    }, []);

    /**
     * Generates a random position for each interactable object that doesn't
     * cover any menu content
     */
    const computeInitialInteractableObjectPositions = (): void => {
        const getRandomInt = (min: number, max: number): number => {
            const minimum = Math.ceil(min);
            const maximum = Math.floor(max);
            return Math.floor(Math.random() * (maximum - minimum) + minimum); // The maximum is exclusive and the minimum is inclusive
        };

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!shapeRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(SHAPE_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(SHAPE_REF_NAME));
        }
        if (!letterGroupTwoRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(LETTER_GROUP_TWO_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(LETTER_GROUP_TWO_REF_NAME));
        }
        if (!menuContainerRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(MENU_CONTAINER_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(MENU_CONTAINER_REF_NAME));
        }

        // Determine content bounding box
        const shapeRect: DOMRect = shapeRef.current.getBoundingClientRect();
        const letterGroupTwoRect: DOMRect = letterGroupTwoRef.current.getBoundingClientRect();
        const menuContainerRect: DOMRect = menuContainerRef.current.getBoundingClientRect();

        const { top } = shapeRect;
        const { bottom } = menuContainerRect;
        const { left } = shapeRect;
        const { right } = letterGroupTwoRect;
        const contentRect: IContentBoundingBox = {
            bottom,
            height: bottom - top,
            left,
            right,
            top,
            width: right - left,
            x: left,
            y: top,
        };

        const positions: IInteractableObjectState = {
            [INTERACTABLE_OBJECT.gameController]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.magnet]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.abacus]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.molecule]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.lightbulb]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.puzzleBlue]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.puzzleGreen]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.puzzleRed]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.puzzleOrange]: { left: 0, top: 0, rotate: 0 },
            [INTERACTABLE_OBJECT.cuneiformTablet]: { left: 0, top: 0, rotate: 0 }, // doesn't ever get read
        };

        const initialPositions = Object.keys(initialInteractableObjectPositions);
        for (let i = 0; i < initialPositions.length; i += 1) {
            const key = initialPositions[i];
            // Select left or right of content rect
            const leftCoinToss: number = Math.floor(Math.random() * 2);
            if (leftCoinToss) {
                // Left of content
                const leftContent: number = getRandomInt(0, contentRect.left);
                const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
                positions[key as INTERACTABLE_OBJECT] = {
                    left: leftContent,
                    top: positions[key as INTERACTABLE_OBJECT].top,
                    rotate: Math.random() < 0.5 ? -rotate : rotate,
                };
            } else {
                // Right of content
                const leftContent: number = getRandomInt(contentRect.right, viewportDimensions.width);
                const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
                positions[key as INTERACTABLE_OBJECT] = {
                    left: leftContent - CONTROLLER_WIDTH, // object with longest width fully within viewport
                    top: positions[key as INTERACTABLE_OBJECT].top,
                    rotate: Math.random() < 0.5 ? -rotate : rotate,
                };
            }

            const topCoinToss: number = Math.floor(Math.random() * 2);
            if (topCoinToss) {
                // Above content
                const topContent: number = getRandomInt(0, contentRect.top);
                const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
                positions[key as INTERACTABLE_OBJECT] = {
                    left: positions[key as INTERACTABLE_OBJECT].left,
                    top: topContent,
                    rotate: Math.random() < 0.5 ? -rotate : rotate,
                };
            } else {
                // Below content
                const topContent: number = getRandomInt(contentRect.bottom, viewportDimensions.height);
                const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
                positions[key as INTERACTABLE_OBJECT] = {
                    left: positions[key as INTERACTABLE_OBJECT].left,
                    top: topContent - ABACUS_HEIGHT, // object with longest height fully within viewport
                    rotate: Math.random() < 0.5 ? -rotate : rotate,
                };
            }
        }

        setInitialInteractableObjectPositions(positions);
    };

    /**
     * Calculates a random position at which to place the cuneiform tablet
     * @param placeOnMenu indicates whether cuneiform tablet should
     * be placed on or around menu
     */
    const computeInitialCuneiformTabletPosition = (placeOnMenu: boolean): void => {
        const getRandomInt = (min: number, max: number): number => {
            const minimum = Math.ceil(min);
            const maximum = Math.floor(max);
            return Math.floor(Math.random() * (maximum - minimum) + minimum); // The maximum is exclusive and the minimum is inclusive
        };

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!shapeRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(SHAPE_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(SHAPE_REF_NAME));
        }
        if (!letterGroupTwoRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(LETTER_GROUP_TWO_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(LETTER_GROUP_TWO_REF_NAME));
        }
        if (!menuContainerRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(MENU_CONTAINER_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(MENU_CONTAINER_REF_NAME));
        }

        // Determine content bounding box
        const shapeRect: DOMRect = shapeRef.current.getBoundingClientRect();
        const letterGroupTwoRect: DOMRect = letterGroupTwoRef.current.getBoundingClientRect();
        const menuContainerRect: DOMRect = menuContainerRef.current.getBoundingClientRect();

        const { top } = shapeRect;
        const { bottom } = menuContainerRect;
        const { left } = shapeRect;
        const { right } = letterGroupTwoRect;
        const contentRect: IContentBoundingBox = {
            bottom,
            height: bottom - top,
            left,
            right,
            top,
            width: right - left,
            x: left,
            y: top,
        };

        let position: IInteractableObjectPosition = { left: 0, top: 0, rotate: 0 };

        // Select left or right of content rect
        const leftCoinToss: number = Math.floor(Math.random() * 2);
        if (leftCoinToss) {
            // Left of content
            const leftContent: number = getRandomInt(
                placeOnMenu ? contentRect.left : 0,
                placeOnMenu ? contentRect.left + contentRect.width / 2 : contentRect.left,
            );
            const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
            position = {
                left: leftContent,
                top: position.top,
                rotate: Math.random() < 0.5 ? -rotate : rotate,
            };
        } else {
            // Right of content
            const leftContent: number = getRandomInt(
                placeOnMenu ? contentRect.right - contentRect.width / 2 : contentRect.right,
                placeOnMenu ? contentRect.right : viewportDimensions.width,
            );
            const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
            position = {
                left: leftContent - CUNEIFORM_TABLET_WIDTH, // object with longest width fully within viewport
                top: position.top,
                rotate: Math.random() < 0.5 ? -rotate : rotate,
            };
        }

        const topCoinToss: number = Math.floor(Math.random() * 2);
        if (topCoinToss) {
            // Above content
            const topContent: number = getRandomInt(
                placeOnMenu ? contentRect.top : 0,
                placeOnMenu ? contentRect.top + contentRect.height / 2 : contentRect.top,
            );
            const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
            position = {
                left: position.left,
                top: topContent,
                rotate: Math.random() < 0.5 ? -rotate : rotate,
            };
        } else {
            // Below content
            const topContent: number = getRandomInt(
                placeOnMenu ? contentRect.bottom - contentRect.height / 2 : contentRect.bottom,
                placeOnMenu ? contentRect.bottom : viewportDimensions.height,
            );
            const rotate: number = Math.floor(Math.random() * INTERACTABLE_OBJECT_ROTATE_LIMIT);
            position = {
                left: position.left,
                top: topContent - CUNEIFORM_TABLET_WIDTH, // object with longest height fully within viewport
                rotate: Math.random() < 0.5 ? -rotate : rotate,
            };
        }

        setInitialCuneiformTabletPosition(position);
    };

    /**
     * Manages response to interactable object selection
     * @param object interactable object event
     * @param e mouse or touch event
     */
    const onObjectMouseDown = (
        object: IInteractableObjectMouseDownEvent,
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!object.ref.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE('Mousedown Object'),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE('Mousedown Object'));
        }

        if (!objectHeld) {
            if (e.type === 'touchstart' && e.cancelable) {
                e.preventDefault();
            }
            onCursorMouseDown(e);
            const clientX = e.type === 'touchstart'
                ? (e as React.TouchEvent).touches[0].clientX
                : (e as React.MouseEvent).clientX;
            const clientY = e.type === 'touchstart'
                ? (e as React.TouchEvent).touches[0].clientY
                : (e as React.MouseEvent).clientY;
            setObjectInitialClickPoint({
                x: clientX,
                y: clientY,
                name: object.name,
            });
            const rect: DOMRect = object.ref.current.getBoundingClientRect();
            setObjectInitialOriginPoint({
                x: rect.left + (rect.width - object.ref.current.clientWidth) / 2, // rotation contribution
                y: rect.top + (rect.height - object.ref.current.clientHeight) / 2, // rotation contribution
                name: object.name,
            });
            setObjectHeld(true);

            // Changes Cursor
            clearTimeoutRevealMoveAction();
            timeoutRevealMoveAction();

            // Play Sound
            if (hasSound && objectGraspClip.current) {
                objectGraspClip.current.pause();
                objectGraspClip.current.currentTime = 0;
                playAudio(objectGraspClip.current);
            }
        }
    };

    /**
     * Manages response to change in interactable object position
     * @param e mouse or touch event
     */
    const onObjectMouseMove = async (e: MouseEvent | TouchEvent): Promise<void> => {
        if (objectHeld && e.type === 'touchmove') {
            const touchEvent = e as TouchEvent;
            if (touchEvent.cancelable) {
                touchEvent.preventDefault();
            }
        }

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!gameControllerRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(GAME_CONTROLLER_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(GAME_CONTROLLER_REF_NAME));
        }
        if (!magnetRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(MAGNET_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(MAGNET_REF_NAME));
        }
        if (!abacusRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(ABACUS_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(ABACUS_REF_NAME));
        }
        if (!moleculeRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(MOLECULE_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(MOLECULE_REF_NAME));
        }
        if (!lightbulbRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(LIGHTBULB_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(LIGHTBULB_REF_NAME));
        }
        if (!puzzleBlueRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(PUZZLE_BLUE_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(PUZZLE_BLUE_REF_NAME));
        }
        if (!puzzleGreenRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(PUZZLE_GREEN_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(PUZZLE_GREEN_REF_NAME));
        }
        if (!puzzleRedRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(PUZZLE_RED_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(PUZZLE_RED_REF_NAME));
        }
        if (!puzzleOrangeRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(PUZZLE_ORANGE_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(PUZZLE_ORANGE_REF_NAME));
        }
        if (!cuneiformTabletRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(CUNEIFORM_TABLET_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(CUNEIFORM_TABLET_REF_NAME));
        }

        // Referenced: https://stackoverflow.com/questions/9971230/calculate-rotated-rectangle-size-from-known-bounding-box-coordinates
        // Referenced: https://math.stackexchange.com/questions/1628657/dimensions-of-a-rectangle-containing-a-rotated-rectangle
        if (objectHeld && objectInitialClickPoint.name) {
            const interactableObjectRefs: Record<INTERACTABLE_OBJECT | string, React.RefObject<SVGElement> | React.RefObject<HTMLElement>> = {
                [INTERACTABLE_OBJECT.gameController]: gameControllerRef,
                [INTERACTABLE_OBJECT.magnet]: magnetRef,
                [INTERACTABLE_OBJECT.abacus]: abacusRef,
                [INTERACTABLE_OBJECT.molecule]: moleculeRef,
                [INTERACTABLE_OBJECT.lightbulb]: lightbulbRef,
                [INTERACTABLE_OBJECT.puzzleBlue]: puzzleBlueRef,
                [INTERACTABLE_OBJECT.puzzleGreen]: puzzleGreenRef,
                [INTERACTABLE_OBJECT.puzzleRed]: puzzleRedRef,
                [INTERACTABLE_OBJECT.puzzleOrange]: puzzleOrangeRef,
                [INTERACTABLE_OBJECT.cuneiformTablet]: cuneiformTabletRef,
            };
            const object: React.RefObject<SVGElement> | React.RefObject<HTMLElement> = interactableObjectRefs[objectInitialClickPoint.name];
            if (object.current) {
                const clientX = e.type === 'touchmove'
                    ? (e as TouchEvent).touches[0].clientX
                    : (e as MouseEvent).clientX;
                const clientY = e.type === 'touchmove'
                    ? (e as TouchEvent).touches[0].clientY
                    : (e as MouseEvent).clientY;
                const x: number = clientX - objectInitialClickPoint.x;
                const y: number = clientY - objectInitialClickPoint.y;
                const rect: DOMRect = object.current.getBoundingClientRect();
                const scaleContributionX: number = (OBJECT_GRAB_SCALE * rect.width - rect.width) / 2;
                const scaleContributionY: number = (OBJECT_GRAB_SCALE * rect.height - rect.height) / 2;
                const left: number = objectInitialOriginPoint.x + x + rect.width - scaleContributionX > viewportDimensions.width
                    ? viewportDimensions.width - rect.width + scaleContributionX
                    : Math.max(scaleContributionX / 2, objectInitialOriginPoint.x + x); // This makes sure we don't let it moved past left edge of viewport

                const top: number = objectInitialOriginPoint.y + y + rect.height - scaleContributionY > viewportDimensions.height
                    ? viewportDimensions.height - rect.height + scaleContributionY
                    : Math.max(scaleContributionY / 2, objectInitialOriginPoint.y + y); // This makes sure we don't let it moved past top edge of viewport
                object.current.style.left = `${left}px`;
                object.current.style.top = `${top}px`;
            }

            if (
                currentSessionId
                && !recordedObjectMoveUserAction
            ) {
                setRecordedObjectMoveUserAction(true);
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.grabLandingPageInteractableObject,
                    sessionId: currentSessionId,
                    payload: {
                        objectType: objectInitialClickPoint.name,
                    },
                });
            }
        }
    };

    /**
     * Finalizes object drag state. Also used by touch-cancel to ensure interrupted drags reset correctly.
     */
    const releaseHeldObject = (cursorEvent?: React.MouseEvent | React.TouchEvent): void => {
        if (!objectHeld) return;
        const objectName = objectInitialClickPoint.name;
        setObjectHeld(false);

        // If we encountered the cuneiform tablet
        // We don't want to clear state object because
        // the onClick event that executes at the same
        // time as this method reads it and we want
        // to avoid race condition errors.
        // We will clear the flag in that method instead
        if (objectName !== INTERACTABLE_OBJECT.cuneiformTablet) {
            setObjectInitialClickPoint({
                x: 0,
                y: 0,
                name: null,
            });
        }
        setObjectInitialOriginPoint({
            x: 0,
            y: 0,
            name: null,
        });
        clearTimeoutRevealMoveAction();
        // We still want to show cursor action,
        // but because object no longer held
        // we switch to click action
        setCursorSigns([GrabSign]);
        onCursorMouseUp(cursorEvent);

        // If we encountered the cuneiform tablet
        // We don't want to turn off flag because
        // the onClick event that executes at the same
        // time as this method reads it and we want
        // to avoid race condition errors.
        // We will clear the flag in that method instead
        if (
            recordedObjectMoveUserAction
            && objectName !== INTERACTABLE_OBJECT.cuneiformTablet
        ) {
            setRecordedObjectMoveUserAction(false);
        }

        // Play Sound
        if (
            hasSound
            && objectThumpClip.current
            && objectName !== INTERACTABLE_OBJECT.cuneiformTablet
        ) {
            objectThumpClip.current.pause();
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        } else if (
            hasSound
            && objectThumpClip.current
            && objectName === INTERACTABLE_OBJECT.cuneiformTablet
        ) {
            cuneiformTabletThudClip.current.pause();
            cuneiformTabletThudClip.current.currentTime = 0;
            playAudio(cuneiformTabletThudClip.current);
        }
    };

    /**
     * Manages response to interactable object de-selection
     * @param e mouse or touch event
     */
    const onObjectMouseUp = (e: React.MouseEvent | React.TouchEvent): void => {
        if (e.type === 'touchend' && e.cancelable) {
            e.preventDefault();
        }
        releaseHeldObject(e);
    };

    /**
     * Manages response to selection of cuneiform tablet
     */
    const onCuneiformTabletClick = (): void => {
        // Prevent on click from executing
        // If the object was moved, we don't want to navigate to the book
        const objectName = objectInitialClickPoint.name;
        if (objectName === INTERACTABLE_OBJECT.cuneiformTablet && recordedObjectMoveUserAction) {
            setRecordedObjectMoveUserAction(false);
            setObjectInitialClickPoint({
                x: 0,
                y: 0,
                name: null,
            });
            return;
        }

        dollyTowardCuneiformTablet();
        // Wait for transition to complete before *routing* to book
        clearTimeoutNavigateToBook();
        timeoutNavigateToBook();

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickCuneiformTablet,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Dollys cuneiform tablet forward by enlarging it
     */
    const dollyTowardCuneiformTablet = (): void => {
        setDollyingTowardCuneiformTablet(true);

        // Play Sound
        if (
            hasSound
            && cuneiformTabletDollyClip.current
        ) {
            cuneiformTabletDollyClip.current.pause();
            cuneiformTabletDollyClip.current.currentTime = 0;
            playAudio(cuneiformTabletDollyClip.current);
        }
    };

    /**
     * Routes to Book View
     */
    const navigateToBook = (): void => {
        clearTimeoutRevealMoveAction();
        cuneiformTabletGlowClip.current.pause();
        cuneiformTabletGlowClip.current.currentTime = 0;
        onCursorLeave();
        setCursorSigns([]);
        if (initiateCuneiformCountdown) {
            setInitiateCuneiformCountdown(false);
            setCuneiformCountdownDigit(INITIAL_CUNEIFORM_COUNTDOWN_DIGIT);
        }
        navigate(
            `/${PAGE_ROUTE.book}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Manages response to jelly object focusing
     * @param e jelly event
     */
    const onJellyHovered = (e: Event): void => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!triangleRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(TRIANGLE_REF_NAME),
                    },
                });
            }
        }
        if (!circleRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(CIRCLE_REF_NAME),
                    },
                });
            }
        }

        if (!isCustomEvent(e)) throw new Error('[Error] Not a Custom Event');
        // e is now narrowed to CustomEvent ...

        // there should be no hover events on touch device
        if (detectTouchDevice(document)) return;

        const event = e as CustomEvent;

        if (event.detail.hover) {
            if (triangleRef.current) triangleRef.current.style.fill = theme.verascopeColor.white;
            if (circleRef.current) circleRef.current.style.fill = theme.verascopeColor.white;
            if (animateLogo) {
                // make sure we only run this once when we're going from
                // not hover to hover
                onCursorEnter(
                    CURSOR_TARGET.logomark,
                    [CURSOR_SIGN.click, CURSOR_SIGN.grab],
                );
            }
            setAnimateLogo(false);
        } else {
            if (triangleRef.current) triangleRef.current.style.fill = theme.verascopeColor.yellow200;
            if (circleRef.current) circleRef.current.style.fill = theme.verascopeColor.orange200;
            if (!animateLogo) {
                // make sure we only run this once when we're going from
                // hover to not hover
                onCursorLeave();
            }
            setAnimateLogo(true);
        }
    };

    /**
     * Manages response to jelly object selection
     * @param e jelly event
     */
    const onJellyMouseDown = (e: Event): void => {
        // avoid running expand function if already expanded
        if (landingPageContentRevealed) return;

        if (!isCustomEvent(e)) throw new Error('[Error] Not a Custom Event');
        // e is now narrowed to CustomEvent ...

        const event = e as CustomEvent;

        event.preventDefault();
        setJellyHeld(true);
        setAnimateLogo(false);
        // Changes Cursor
        clearTimeoutRevealMoveAction();
        timeoutRevealMoveAction();
    };

    /**
     * Manages response to change in jelly object position
     * @param e jelly event
     */
    const onJellyMouseMove = async (e: Event): Promise<void> => {
        // avoid running expand function if already expanded
        if (landingPageContentRevealed) return;

        if (!isCustomEvent(e)) throw new Error('[Error] Not a Custom Event');
        // e is now narrowed to CustomEvent ...

        const event = e as CustomEvent;

        setWobbleJelly(event);

        if (
            currentSessionId
            && !recordedJellyMoveUserAction
        ) {
            setRecordedJellyMoveUserAction(true);
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.grabJellyLogo,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Manages response to jelly object de-selection
     * @param e jelly event
     */
    const onJellyMouseUp = (e: Event): void => {
        // avoid running expand function if already expanded
        if (landingPageContentRevealed) return;

        if (!isCustomEvent(e)) throw new Error('[Error] Not a Custom Event');
        // e is now narrowed to CustomEvent ...

        const event = e as CustomEvent;

        event.preventDefault();
        setJellyHeld(false);
        setAnimateLogo(true);
        // Act as a triplet
        clearTimeoutRevealMoveAction();
        onCursorLeave();
        setCursorSigns([]);
    };

    /**
     * Manages response jelly object expanding
     * @param e jelly event
     */
    const onJellyExpand = async (e?: Event): Promise<void> => {
        e?.stopPropagation();

        // disable jelly mouse events
        if (jelly && !jelly.disableMouseEvents) jelly.setDisableMouseEvents(true);

        // Jelly Expand will execute when we click the EXIT BUTTON
        if (e && !isCustomEvent(e)) throw new Error('[Error] Not a Custom Event');
        // e is now narrowed to CustomEvent ...

        if (e) {
            const event = e as CustomEvent;

            if (event.detail.jelly) {
                const shakeImpulse = EXPAND_IMPULSE_COEFF * Math.log10(XDRIFT_DURATION);
                event.detail.jelly.shake({
                    x: xDriftDirection === DRIFT_DIRECTION_FORWARD ? -shakeImpulse : shakeImpulse,
                    y: yDriftDirection === DRIFT_DIRECTION_FORWARD ? -shakeImpulse : shakeImpulse,
                });
            }
        }

        if (!landingPageContentRevealed) setLandingPageContentRevealed(true);

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.revealLandingPageContent,
                sessionId: currentSessionId,
            });
        }

        if (!landingPageContentRevealed) {
            // Trigger timeout that checks whether to shift overflowed logo
            clearTimeoutCheckMoveLogomark();
            timeoutCheckMoveLogomark();
        }

        if (!landingPageContentRevealed) {
            // Trigger timeout that instantiates interactable objects
            clearLongTimeoutInitializeInteractableObjects();
            longTimeoutInitializeInteractableObjects();
        } else if (landingPageContentRevealed) {
            clearShortTimeoutInitializeInteractableObjects();
            shortTimeoutInitializeInteractableObjects();
        }

        // Play Sound
        if (hasSound && !landingPageContentRevealed) {
            clearTimeoutLogomarkExpandClip();
            timeoutLogomarkExpandClip();
            clearTimeoutLogoCircleSwooshClip();
            timeoutLogoCircleSwooshClip();
            if (!isMobile()) {
                clearTimeoutLogoLetterE1SwooshClip();
                timeoutLogoLetterE1SwooshClip();
                clearTimeoutLogoLetterRSwooshClip();
                timeoutLogoLetterRSwooshClip();
                clearTimeoutLogoLetterASwooshClip();
                timeoutLogoLetterASwooshClip();
                clearTimeoutLogoLetterSSwooshClip();
                timeoutLogoLetterSSwooshClip();
                clearTimeoutLogoLetterCSwooshClip();
                timeoutLogoLetterCSwooshClip();
                clearTimeoutLogoLetterPSwooshClip();
                timeoutLogoLetterPSwooshClip();
                clearTimeoutLogoLetterE2SwooshClip();
                timeoutLogoLetterE2SwooshClip();
            }
            // Menu Button Sounds
            clearTimeoutAboutSwooshClip();
            timeoutAboutSwooshClip();
            clearTimeoutBookSwooshClip();
            timeoutBookSwooshClip();
            clearTimeoutStayInformedSwooshClip();
            timeoutStayInformedSwooshClip();
            clearTimeoutEmailSwooshClip();
            timeoutEmailSwooshClip();
            clearTimeoutContinueSwooshClip();
            timeoutContinueSwooshClip();
            clearTimeoutTreasureChestSwooshClip();
            timeoutTreasureChestSwooshClip();
            if (MENU_BUTTONS.includes(CURSOR_TARGET.characters)) {
                clearTimeoutCharactersSwooshClip();
                timeoutCharactersSwooshClip();
            }
            clearTimeoutExitSwooshClip();
            timeoutExitSwooshClip();
        }
    };

    /**
     * Manages response jelly object contracting
     * @param e jelly event
     */
    const onJellyContract = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        // avoid running expand function if already expanded
        if (!landingPageContentRevealed) return;

        autoRevealLandingPageContentConsumed.current = true;
        clearTimeoutRevealLandingPageContent();

        // enable jelly mouse events
        if (jelly) jelly.setDisableMouseEvents(false);
        setLandingPageContentRevealed(false);
        setObjectsRevealed(false);
        onCursorLeave();
        hideMenuButtonArrows();
        const shakeImpulse = CONTRACT_IMPULSE_COEFF * Math.log10(XDRIFT_DURATION);
        if (jelly) {
            jelly.shake({
                x: xDriftDirection === DRIFT_DIRECTION_FORWARD ? -shakeImpulse : shakeImpulse,
                y: yDriftDirection === DRIFT_DIRECTION_FORWARD ? -shakeImpulse : shakeImpulse,
            });
        }

        if (menuOffset.x !== 0 || menuOffset.y !== 0) {
            setMenuOffset({ x: 0, y: 0 });
        }

        if (stayInformed && email.length === 0 && !emailSubmitted) {
            setStayInformed(false);
        }

        if (emailValid && email.length > 0) {
            setDelayContinueButton(true);
        }

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.hideLandingPageContent,
                sessionId: currentSessionId,
            });
        }

        clearTimeoutCheckMoveLogomark();

        // Play Sound
        if (hasSound) {
            clearTimeoutLogomarkExpandClip();
            timeoutLogomarkExpandClip();
            clearTimeoutLogoCircleSwooshClip();
            timeoutLogoCircleSwooshClip();
            if (!isMobile()) {
                clearTimeoutLogoLetterE1SwooshClip();
                timeoutLogoLetterE1SwooshClip();
                clearTimeoutLogoLetterRSwooshClip();
                timeoutLogoLetterRSwooshClip();
                clearTimeoutLogoLetterASwooshClip();
                timeoutLogoLetterASwooshClip();
                clearTimeoutLogoLetterSSwooshClip();
                timeoutLogoLetterSSwooshClip();
                clearTimeoutLogoLetterCSwooshClip();
                timeoutLogoLetterCSwooshClip();
                clearTimeoutLogoLetterPSwooshClip();
                timeoutLogoLetterPSwooshClip();
                clearTimeoutLogoLetterE2SwooshClip();
                timeoutLogoLetterE2SwooshClip();
            }
        }
    };

    /**
     * Manages response to interactable object focusing
     * @param targetType The kind of user interface element in focus
     * @param actions The list of (cursor) actions possible on user interface element
     * @param e mouse or touch event
     */
    const onObjectCursorEnter = (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        // Executing onCursorEnter when object is held
        // causes issues with cursor state
        if (!objectHeld) {
            onCursorEnter(
                targetType,
                actions,
                e.currentTarget as HTMLElement,
            );
        }

        // Occurs if we move object to edge of viewport and lose focus
        // But gain focus again when we re-enter viewport
        // We want to regain move action cursor
        if (objectHeld) {
            setCursorSigns([MoveSign]);
        }
    };

    /**
     * Manages response to interactable object blurring
     * @param e mouse or touch event
     */
    const onObjectCursorLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        // Executing onCursorEnter when object is held
        // causes issues with cursor state
        if (!objectHeld) onCursorLeave(e);
    };

    /**
     * Manages revealing interactable objects when jelly expanded
     */
    const revealInteractableObjects = (): void => {
        computeInitialInteractableObjectPositions();
        setObjectsRevealed(true);

        // Play Sound
        if (hasSound) {
            clearTimeoutControllerThumpClip();
            timeoutControllerThumpClip();
            clearTimeoutMagnetThumpClip();
            timeoutMagnetThumpClip();
            clearTimeoutAbacusThumpClip();
            timeoutAbacusThumpClip();
            clearTimeoutMoleculeThumpClip();
            timeoutMoleculeThumpClip();
            clearTimeoutLightbulbThumpClip();
            timeoutLightbulbThumpClip();
            clearTimeoutPuzzleBlueThumpClip();
            timeoutPuzzleBlueThumpClip();
            clearTimeoutPuzzleGreenThumpClip();
            timeoutPuzzleGreenThumpClip();
            clearTimeoutPuzzleRedThumpClip();
            timeoutPuzzleRedThumpClip();
            clearTimeoutPuzzleOrangeThumpClip();
            timeoutPuzzleOrangeThumpClip();
        }
    };

    /**
     * Manages revealing cuneiform tablet
     * Case 1: if Book Menu Button Selected
     * Case 2: if user returns to landing view after having revealed cuneiform tablet
     * @param placeOnMenu indicates whether cuneiform tablet should
     * be placed on or around menu
     */
    const revealCuneiformTablet = (placeOnMenu: boolean): void => {
        computeInitialCuneiformTabletPosition(placeOnMenu);
        // Happens in circumstances when user
        // might have already expanded landing page, gone to another view,
        // and then returned to landing page.
        // If so, toggle cuneiform tablet flag
        if (!cuneiformTabletRevealed) {
            setCuneiformTabletRevealed(true);
        }

        // Play Sound
        if (
            hasSound
            && !cuneiformTabletRevealed
            && cuneiformTabletThudClip.current
        ) {
            cuneiformTabletThudClip.current.pause();
            cuneiformTabletThudClip.current.currentTime = 0;
            playAudio(cuneiformTabletThudClip.current);
        }

        if (
            hasSound
            && !cuneiformTabletRevealed
            && cuneiformTabletGlowClip.current
        ) {
            cuneiformTabletGlowClip.current.pause();
            cuneiformTabletGlowClip.current.currentTime = 0;
            playAudio(cuneiformTabletGlowClip.current);
        }
    };

    /**
     * Manages revealing menu arrows around focused menu button on non-touch device
     */
    const revealMenuButtonArrows = (): void => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!rightArrowRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(RIGHT_ARROW_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(RIGHT_ARROW_REF_NAME));
        }
        if (!leftArrowRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(LEFT_ARROW_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(LEFT_ARROW_REF_NAME));
        }

        if (!arrowsRevealed) {
            rightArrowRef.current.style.display = 'block';
            leftArrowRef.current.style.display = 'block';
            setArrowsRevealed(true);
        }
    };

    /**
     * Manages hiding menu arrows around focused menu button on non-touch device
     */
    const hideMenuButtonArrows = (): void => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!rightArrowRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(RIGHT_ARROW_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(RIGHT_ARROW_REF_NAME));
        }
        if (!leftArrowRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(LEFT_ARROW_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(LEFT_ARROW_REF_NAME));
        }

        rightArrowRef.current.style.display = 'none';
        leftArrowRef.current.style.display = 'none';
        setArrowsRevealed(false);
    };

    /**
     * Manages response to custom cursor focusing on a menu button
     * @param targetType The kind of user interface element in focus
     * @param actions The list of (cursor) actions possible on menu button
     * @param e mouse or touch event
     */
    const onMenuButtonMouseEnter = (
        object: CURSOR_TARGET,
        actions: string[],
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        // Expand Cursor
        // Executing onCursorEnter when object is held
        // causes issues with cursor state
        if (objectHeld) return;

        onCursorEnter(
            object,
            actions,
            e.currentTarget as HTMLElement,
        );

        // Reveal Arrows
        revealMenuButtonArrows();

        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!rightArrowRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(RIGHT_ARROW_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(RIGHT_ARROW_REF_NAME));
        }
        if (!leftArrowRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(LEFT_ARROW_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(LEFT_ARROW_REF_NAME));
        }

        const buttonRect: DOMRect = (e.target as HTMLButtonElement).getBoundingClientRect();
        const rightArrowRect: DOMRect = rightArrowRef.current.getBoundingClientRect();
        const leftArrowRect: DOMRect = leftArrowRef.current.getBoundingClientRect();

        // Position Arrows
        rightArrowRef.current.style.left = `${buttonRect.x
            - rightArrowRect.width
            - ARROW_MARGIN}px`;
        rightArrowRef.current.style.top = `${buttonRect.y
            + buttonRect.height / 2
            - rightArrowRect.height / 2}px`;
        leftArrowRef.current.style.left = `${buttonRect.right + ARROW_MARGIN}px`;
        leftArrowRef.current.style.top = `${buttonRect.y + buttonRect.height / 2
            - leftArrowRect.height / 2}px`;

        // Play Sound
        if (hasSound && buttonHoverClip.current) {
            buttonHoverClip.current.pause();
            buttonHoverClip.current.currentTime = 0;
            playAudio(buttonHoverClip.current);
        }
    };

    /**
     * Manages response to custom cursor blurring a menu button
     * @param e mouse or touch event
     */
    const onMenuButtonMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        // Contract Cursor
        // Executing onCursorEnter when object is held
        // causes issues with cursor state
        if (objectHeld) return;

        onCursorLeave(e);

        // Hide Arrows
        hideMenuButtonArrows();
    };

    /**
     * Manages response to focusing of 'stay informed' input
     */
    const onInputFocus = (): void => {
        setInputFocused(true);

        // Play Sound
        if (hasSound && buttonClickClip.current) {
            buttonClickClip.current.pause();
            buttonClickClip.current.currentTime = 0;
            playAudio(buttonClickClip.current);
        }
    };

    /**
     * Manages response to blurring of 'stay informed' input
     */
    const onInputBlur = (): void => {
        setInputFocused(false);
    };

    /**
     * Manages response to change in text within 'stay informed' input
     * @param e mouse or touch event
     */
    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target.value.toLowerCase());
    };

    /**
     * Manages tracking enter key presses during changes to 'stay informed' input
     * @param e keyboard event
     */
    const checkForEnter = (e: React.KeyboardEvent): void => {
        if (e.key === KEYCODE.enter) {
            // Cancel the default action, if needed
            e.preventDefault();

            // Submit Email
            onSubmitEmail();
        } else if (e.key === KEYCODE.escape) {
            // Cancel the default action, if needed
            e.preventDefault();

            // Note that ref.current may be null. This is expected, because you may
            // conditionally render the ref-ed element, or you may forgot to assign it
            if (!inputRef.current) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME),
                        },
                    });
                }
                throw Error(UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME));
            }

            // Blur Input
            inputRef.current.blur();
        }
    };

    const overwriteUserEmailAndAddToMailingList = async (newEmail: string): Promise<void> => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!inputRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME));
        }
        if (!currentSessionId) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered a problem adding email to our mailing list. Unable to retrieve session details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            throw Error(`Encountered a problem adding email to our mailing list. Unable to retrieve session details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (user && user.authenticationType !== AUTHENTICATION_TYPE.email) {
            const userUpdates: Record<string, string | IMailingListSubscriptionValue[]> = {};
            userUpdates.email = newEmail;
            if (!user.mailingListSubscription
                || (
                    user.mailingListSubscription
                    && user.mailingListSubscription.history.length > 0
                    && user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1]
                    && !user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1].subscribed
                )
            ) {
                const mailingListSubscriptionToAdd: IMailingListSubscriptionValue[] = !user.mailingListSubscription
                    ? [{
                        subscribed: false,
                        timestamp: Date.now(),
                    },
                    {
                        subscribed: true,
                        timestamp: Date.now(),
                    },
                    ] : [
                        {
                            subscribed: true,
                            timestamp: Date.now(),
                        },
                    ];
                userUpdates.mailingListSubscriptionToAdd = mailingListSubscriptionToAdd;
            }
            updateUser({
                test: isTestMode(),
                ...userUpdates,
            });
            if (!isSubmittingEmail) setIsSubmittingEmail(true);
            sendMailingListSubscribeEmail({
                test: isTestMode(),
            }).then(async (success) => {
                if (!success) {
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                // eslint-disable-next-line max-len
                                error: `There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                            },
                        });
                    }
                    throw Error(`There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                }
                setIsSubmittingEmail(false);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_OVERWRITE_EMAIL(newEmail),
                    icon: EnvelopeIcon,
                    hasSuccess: true,
                });

                // Clear state
                setEmail('');
                inputRef.current!.value = '';
                setEmailSubmitted(true);

                // Play Sound
                if (hasSound && messageSuccessClip.current) {
                    messageSuccessClip.current.pause();
                    messageSuccessClip.current.currentTime = 0;
                    playAudio(messageSuccessClip.current);
                }

                getUserIPInfo().then((ip) => {
                    createMailingListRequest({
                        test: isTestMode(),
                        email: newEmail,
                        ip,
                        sessionId: currentSessionId,
                        browser: detectBrowser(),
                        isMobile: isMobile(),
                        isMacOS: isMacOS(),
                    }).then((mailingListRequest) => {
                        if (currentSessionId) {
                            // Record user action
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.submitNotificationRequest,
                                sessionId: currentSessionId,
                                payload: {
                                    request: mailingListRequest,
                                },
                            });
                        }
                    });
                });
            });
        } else {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered a problem updating user email. Unable to retrieve user details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            throw Error(`Encountered a problem updating user email. Unable to retrieve user details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
    };

    const addExistingEmailToMailingList = async (existingEmail: string): Promise<void> => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!inputRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME));
        }
        if (!currentSessionId) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered a problem adding email to our mailing list. Unable to retrieve session details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            throw Error(`Encountered a problem adding email to our mailing list. Unable to retrieve session details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (user && user.authenticationType !== AUTHENTICATION_TYPE.email) {
            const mailingListSubscriptionToAdd: IMailingListSubscriptionValue[] = !user.mailingListSubscription
                ? [{
                    subscribed: false,
                    timestamp: Date.now(),
                },
                {
                    subscribed: true,
                    timestamp: Date.now(),
                },
                ] : [
                    {
                        subscribed: true,
                        timestamp: Date.now(),
                    },
                ];
            updateUser({
                test: isTestMode(),
                mailingListSubscriptionToAdd,
            });
            if (!isSubmittingEmail) setIsSubmittingEmail(true);
            sendMailingListSubscribeEmail({
                test: isTestMode(),
            }).then(async (success) => {
                if (!success) {
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                // eslint-disable-next-line max-len
                                error: `There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                            },
                        });
                    }
                    throw Error(`There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                }
                setIsSubmittingEmail(false);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_ADD_EXISTING_EMAIL_TO_MAILING_LIST(existingEmail),
                    icon: EnvelopeIcon,
                    hasSuccess: true,
                });

                // Clear state
                setEmail('');
                inputRef.current!.value = '';
                setEmailSubmitted(true);

                // Play Sound
                if (hasSound && messageSuccessClip.current) {
                    messageSuccessClip.current.pause();
                    messageSuccessClip.current.currentTime = 0;
                    playAudio(messageSuccessClip.current);
                }

                getUserIPInfo().then((ip) => {
                    createMailingListRequest({
                        test: isTestMode(),
                        email: existingEmail,
                        ip,
                        sessionId: currentSessionId,
                        browser: detectBrowser(),
                        isMobile: isMobile(),
                        isMacOS: isMacOS(),
                    }).then((mailingListRequest) => {
                        if (currentSessionId) {
                            // Record user action
                            recordUserAction({
                                test: isTestMode(),
                                type: USER_ACTION_TYPE.submitNotificationRequest,
                                sessionId: currentSessionId,
                                payload: {
                                    request: mailingListRequest,
                                },
                            });
                        }
                    });
                });
            });
        } else {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered a problem adding email to our mailing list. Unable to retrieve user details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            throw Error(`Encountered a problem adding email to our mailing list. Unable to retrieve user details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
    };

    /**
     * Manages response to submitting email within 'stay informed' input
     */
    const onSubmitEmail = async (): Promise<void> => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!inputRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(INPUT_REF_NAME));
        }
        if (!currentSessionId) {
            throw Error(`Encountered a problem adding email to our mailing list. Unable to retrieve session details. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }

        setIsSubmittingEmail(true);
        const emailAvailable = await isEmailUnusedByOtherUsers({
            test: isTestMode(),
            email,
        });

        if (!emailAvailable) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_EMAIL_USED_BY_OTHER_USER(email),
                icon: EnvelopeIcon,
            });
            setEmail('');
            inputRef.current.value = '';
            setIsSubmittingEmail(false);
            return;
        }

        const emailInMailingList = await isInMailingList({
            test: isTestMode(),
            email,
        });

        if (emailInMailingList) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_ALREADY_SUBSCRIBED_TO_MAILING_LIST(email),
                icon: EnvelopeIcon,
            });
            setEmail('');
            inputRef.current.value = '';
            setIsSubmittingEmail(false);
            return;
        }

        // Subscribe User to Mailing List (if exists)
        // or Associate Email address with Anonymous Account
        if (
            user
            && userEmail
            && userEmail === email
            && (
                !user.mailingListSubscription
                || (
                    user.mailingListSubscription
                    && user.mailingListSubscription.history.length > 0
                    && !user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1].subscribed
                )
            )
        ) {
            // User is logged in and presented the same email as
            // listed in their user item
            // => Subscribe to mailing list
            const mailingListSubscriptionToAdd: IMailingListSubscriptionValue[] = !user.mailingListSubscription
                ? [{
                    subscribed: false,
                    timestamp: Date.now(),
                },
                {
                    subscribed: true,
                    timestamp: Date.now(),
                },
                ] : [
                    {
                        subscribed: true,
                        timestamp: Date.now(),
                    },
                ];
            updateUser({
                test: isTestMode(),
                mailingListSubscriptionToAdd,
            });

            // Send confirmation email
            sendMailingListSubscribeEmail({
                test: isTestMode(),
            }).then((success) => {
                if (!success) {
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                // eslint-disable-next-line max-len
                                error: `There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                            },
                        });
                    }
                    throw Error(`There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                }
                setIsSubmittingEmail(false);
            });
        } else if (
            user
            && userEmail
            && userEmail !== email
        ) {
            // Inform user that their account already has an email associatd with it
            // Ask them if they want to overwrite it
            if (user.authenticationType !== AUTHENTICATION_TYPE.email) {
                setModal({
                    visible: true,
                    message: OVERWRITE_EMAIL_MESSAGE(userEmail, email),
                    proceedCallback: async () => {
                        setModal({
                            visible: false,
                            // we keep content, so it doesn't flatten height all of the sudden
                            message: modal.message,
                        });
                        overwriteUserEmailAndAddToMailingList(email);
                    },
                    rejectCallback: () => {
                        setModal({
                            visible: false,
                            // we keep content, so it doesn't flatten height all of the sudden
                            message: modal.message,
                        });
                        setIsSubmittingEmail(false);
                    },
                });
                return;
            }

            if (
                user.authenticationType === AUTHENTICATION_TYPE.email
                && (
                    !user.mailingListSubscription
                    || (
                        user.mailingListSubscription
                        && user.mailingListSubscription.history.length > 0
                        && user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1]
                        && !user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1].subscribed
                    )
                )
            ) {
                setModal({
                    visible: true,
                    message: ADD_EXISTING_EMAIL_TO_MAILING_LIST_MESSAGE(email),
                    proceedCallback: async () => {
                        setModal({
                            visible: false,
                            // we keep content, so it doesn't flatten height all of the sudden
                            message: modal.message,
                        });
                        addExistingEmailToMailingList(userEmail);
                    },
                    rejectCallback: () => {
                        setModal({
                            visible: false,
                            // we keep content, so it doesn't flatten height all of the sudden
                            message: modal.message,
                        });
                        setIsSubmittingEmail(false);
                    },
                });
                return;
            }

            if (
                user.authenticationType === AUTHENTICATION_TYPE.email
                && user.mailingListSubscription
                && user.mailingListSubscription.history.length > 0
                && user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1]
                && user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1].subscribed
            ) {
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_EXISTING_EMAIL_ALREADY_ON_MAILING_LIST(userEmail),
                    icon: EnvelopeIcon,
                });
                setEmail('');
                inputRef.current.value = '';
                setIsSubmittingEmail(false);
                return;
            }
        } else if (
            user
            && !userEmail
        ) {
            const userUpdates: Record<string, string | IMailingListSubscriptionValue[]> = {};
            userUpdates.email = email;
            if (!user.mailingListSubscription
                || (
                    user.mailingListSubscription
                    && user.mailingListSubscription.history.length > 0
                    && user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1]
                    && !user.mailingListSubscription.history[user.mailingListSubscription.history.length - 1].subscribed
                )
            ) {
                const mailingListSubscriptionToAdd: IMailingListSubscriptionValue[] = !user.mailingListSubscription
                    ? [{
                        subscribed: false,
                        timestamp: Date.now(),
                    },
                    {
                        subscribed: true,
                        timestamp: Date.now(),
                    },
                    ] : [
                        {
                            subscribed: true,
                            timestamp: Date.now(),
                        },
                    ];
                userUpdates.mailingListSubscriptionToAdd = mailingListSubscriptionToAdd;
            }
            updateUser({
                test: isTestMode(),
                ...userUpdates,
            });

            // Send confirmation email
            sendMailingListSubscribeEmail({
                test: isTestMode(),
            }).then((success) => {
                if (!success) {
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                // eslint-disable-next-line max-len
                                error: `There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                            },
                        });
                    }
                    throw Error(`There was a problem sending mailing list subscription email. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                }
                setIsSubmittingEmail(false);
            });
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_UNCAUGHT_ERROR,
                icon: CautionIcon,
                hasFailure: true,
            });
            setEmail('');
            inputRef.current.value = '';
            setIsSubmittingEmail(false);
            return;
        }

        // In the event that we get here with the isSubmittingEmail flag
        // still set, turn it off
        if (isSubmittingEmail) setIsSubmittingEmail(false);

        // Clear state
        setEmail('');
        inputRef.current.value = '';
        setEmailSubmitted(true);

        // Play Sound
        if (hasSound && messageSuccessClip.current) {
            messageSuccessClip.current.pause();
            messageSuccessClip.current.currentTime = 0;
            playAudio(messageSuccessClip.current);
        }

        getUserIPInfo().then((ip) => {
            createMailingListRequest({
                test: isTestMode(),
                email,
                ip,
                sessionId: currentSessionId,
                browser: detectBrowser(),
                isMobile: isMobile(),
                isMacOS: isMacOS(),
            }).then((mailingListRequest) => {
                if (currentSessionId) {
                    // Record user action
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.submitNotificationRequest,
                        sessionId: currentSessionId,
                        payload: {
                            request: mailingListRequest,
                        },
                    });
                }
            });
        });
    };

    /**
     * Manages response to selecting 'stay informed' menu button
     */
    const onStayInformed = (): void => {
        setStayInformed(true);
        hideMenuButtonArrows();
    };

    /**
     * Manages response to selecting 'about' menu button
     */
    const onAbout = async (): Promise<void> => {
        clearTimeoutRevealMoveAction();
        onCursorLeave();
        setCursorSigns([]);
        // Stop cuneiform glowing sound if cuneiform revealed
        if (!cuneiformTabletGlowClip.current.paused) {
            clearTimeoutRevealMoveAction();
            cuneiformTabletGlowClip.current.pause();
            cuneiformTabletGlowClip.current.currentTime = 0;
        }
        navigate(
            `/${PAGE_ROUTE.about}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Manages response to selecting 'book' menu button
     * @param e mouse event
     */
    const onBook = async (e: React.MouseEvent): Promise<void> => {
        if (!cuneiformTabletRevealed) {
            revealCuneiformTablet(true);

            // Trigger Book Countdown
            setInitiateCuneiformCountdown(true);

            // Initiating Cuneiform Countdown disables menu
            // buttons.
            // Execute to hide arrow buttons
            onMenuButtonMouseLeave(e);

            // Wait for transition to complete before *navigating* to book
            clearTimeoutTransitionToBook();
            timeoutTransitionToBook();
        } else {
            dollyTowardCuneiformTablet();
            // Wait for transition to complete before *routing* to book
            clearTimeoutNavigateToBook();
            timeoutNavigateToBook();
        }
    };

    /**
     * Manages response to selecting 'characters' menu button
     */
    const onCharacters = async (): Promise<void> => {
        clearTimeoutRevealMoveAction();
        onCursorLeave();
        setCursorSigns([]);
        // Stop cuneiform glowing sound if cuneiform revealed
        if (!cuneiformTabletGlowClip.current.paused) {
            clearTimeoutRevealMoveAction();
            cuneiformTabletGlowClip.current.pause();
            cuneiformTabletGlowClip.current.currentTime = 0;
        }
        navigate(
            `/${PAGE_ROUTE.characters}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Manages response to selecting 'treasure chest' menu button
     */
    const onTreasureChest = async (): Promise<void> => {
        clearTimeoutRevealMoveAction();
        onCursorLeave();
        setCursorSigns([]);
        // Stop cuneiform glowing sound if cuneiform revealed
        if (!cuneiformTabletGlowClip.current.paused) {
            clearTimeoutRevealMoveAction();
            cuneiformTabletGlowClip.current.pause();
            cuneiformTabletGlowClip.current.currentTime = 0;
        }
        navigate(
            `/${PAGE_ROUTE.treasureChest}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Manages response to selecting email button
     */
    const onPrivacyPolicy = async (): Promise<void> => {
        clearTimeoutRevealMoveAction();
        onCursorLeave();
        setCursorSigns([]);
        // Stop cuneiform glowing sound if cuneiform revealed
        if (!cuneiformTabletGlowClip.current.paused) {
            clearTimeoutRevealMoveAction();
            cuneiformTabletGlowClip.current.pause();
            cuneiformTabletGlowClip.current.currentTime = 0;
        }
        navigate(
            `/${PAGE_ROUTE.privacyPolicy}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Manages response to selecting email button
     */
    const onTermsOfUse = async (): Promise<void> => {
        clearTimeoutRevealMoveAction();
        onCursorLeave();
        setCursorSigns([]);
        // Stop cuneiform glowing sound if cuneiform revealed
        if (!cuneiformTabletGlowClip.current.paused) {
            clearTimeoutRevealMoveAction();
            cuneiformTabletGlowClip.current.pause();
            cuneiformTabletGlowClip.current.currentTime = 0;
        }
        navigate(
            `/${PAGE_ROUTE.termsOfUse}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Manages response to selecting X/Twitter button
     */
    const onXTwitterButtonClick = async (): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewXTwitter,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Manages response to selecting Instagram button
     */
    const onInstagramButtonClick = async (): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewInstagram,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Manages response to selecting email button
     */
    const onEmailButtonClick = async (): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.composeEmail,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Records user action of viewing landing page
     */
    const recordLandingPageUserAction = async (): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewLandingPage,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Manages pausing and resuming timeouts based on whether tab or window
     * is focused and caching the amount of time that's elapsed before the
     * pause so resuming timeouts starts from there. It also pauses and resumes
     * logo animation.
     */
    const handleTabOrWindowChange = (): void => {
        if (browserTabIsFocused != null) {
            if (!animateLogo) setAnimateLogo(true);
            setStartOrResumeBrowsingTimestamp(Date.now());
        } else {
            if (animateLogo) setAnimateLogo(false);
            setCachedPostFocusedDuration(cachedPostFocusedDuration + (Date.now() - startOrResumeBrowsingTimestamp));
        }
    };

    // ===== Side Effects =====

    /**
     * Prevents browser scrolling/navigation gestures while an interactable object is held.
     */
    useEffect(() => {
        if (!objectHeld) return undefined;

        const bodyStyle = document.body.style;
        const htmlStyle = document.documentElement.style;
        const previousBodyOverflowX = bodyStyle.overflowX;
        const previousBodyOverflowY = bodyStyle.overflowY;
        const previousBodyTouchAction = bodyStyle.touchAction;
        const previousBodyOverscrollBehavior = bodyStyle.overscrollBehavior;
        const previousHtmlTouchAction = htmlStyle.touchAction;
        const previousHtmlOverscrollBehavior = htmlStyle.overscrollBehavior;

        bodyStyle.overflowX = 'hidden';
        bodyStyle.overflowY = 'hidden';
        bodyStyle.touchAction = 'none';
        bodyStyle.overscrollBehavior = 'none';
        htmlStyle.touchAction = 'none';
        htmlStyle.overscrollBehavior = 'none';

        return () => {
            bodyStyle.overflowX = previousBodyOverflowX;
            bodyStyle.overflowY = previousBodyOverflowY;
            bodyStyle.touchAction = previousBodyTouchAction;
            bodyStyle.overscrollBehavior = previousBodyOverscrollBehavior;
            htmlStyle.touchAction = previousHtmlTouchAction;
            htmlStyle.overscrollBehavior = previousHtmlOverscrollBehavior;
        };
    }, [objectHeld]);

    /**
     * Suppresses browser panning/scrolling while dragging interactable objects on touch devices.
     */
    useEffect(() => {
        const touchMoveListenerOptions: AddEventListenerOptions = { passive: false };
        const touchCancelListenerOptions: AddEventListenerOptions = { passive: false, capture: true };
        const onObjectTouchCancel = (e: TouchEvent): void => {
            if (!objectHeld) return;
            if (e.cancelable) {
                e.preventDefault();
            }
            // Browser/OS interrupted the gesture; force drag cleanup.
            releaseHeldObject();
        };

        // Add interactable mousemove event listener
        document.body.addEventListener('mousemove', onObjectMouseMove);
        document.body.addEventListener('touchmove', onObjectMouseMove, touchMoveListenerOptions);
        document.body.addEventListener('touchcancel', onObjectTouchCancel, touchCancelListenerOptions);

        return () => {
            document.body.removeEventListener('mousemove', onObjectMouseMove);
            document.body.removeEventListener('touchmove', onObjectMouseMove, touchMoveListenerOptions);
            document.body.removeEventListener('touchcancel', onObjectTouchCancel, touchCancelListenerOptions);
        };
    });

    /**
     * Manages page title changes
     */
    useEffect(() => {
        updatePageTitle();
    }, []);

    /**
     * Records View Landing Page User Action
     */
    useEffect(() => {
        if (
            !currentSessionId
            || recordedViewPageUserActionKey.current === location.key
        ) return;
        recordLandingPageUserAction();
        recordedViewPageUserActionKey.current = location.key;
    }, [
        currentSessionId,
        location.key,
    ]);

    /**
     * Triggers a method that hands tab or window changes
     */
    useEffect(() => {
        handleTabOrWindowChange();
    }, [browserTabIsFocused]);

    /**
     * Manages revealing landing page content if landing view visited already
     * User might have already expanded landing page, gone to another view,
     * and then returned to landing page.
     * No reason to expect them to open again
     */
    useEffect(() => {
        if (
            landingPageContentRevealed
            && jelly
            && !jelly.disableMouseEvents
        ) {
            onJellyExpand();
        }
    }, [jelly]);

    /**
     * Manages revealing cuneieform tablet if landing view visited already
     * User might have already expanded landing page, gone to another view,
     * and then returned to landing page.
     * If so, show tablet that had already been expanded
     */
    useEffect(() => {
        if (cuneiformTabletRevealed) {
            // Temporarily hide cuneiform tablet while we wait for
            // interactable objects to spawn
            setTemporarilyHideCuneiformTablet(true);
            clearTimeoutRevealCuneiformTablet();
            timeoutRevealCuneiformTablet();
        }
    }, []);

    /**
     * Manages initializing Jelly Object
     */
    useEffect(() => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!centroidRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(CENTROID_REF_NAME),
                    },
                });
            }
            throw Error(UNASSIGNED_ERROR_MESSAGE(CENTROID_REF_NAME));
        }

        // Setup options
        const options = {
            paths: '#quad-path', // Shape we want to draw
            pointsNumber: 13, // Number of points
            maxDistance: 250, // Max distance among points
            color: theme.verascopeColor.purple300, // Background color of jelly
            centroid: centroidRef.current, // Element to move accordingly with the centroid of the shape
            debug: false, // Uncomment this to see the points
            x: (CANVAS_WIDTH() - ADJUSTED_QUAD_WIDTH) / 2, // x-displacement from top-left corner
            y: (CANVAS_HEIGHT() - ADJUSTED_QUAD_HEIGHT) / 2, // y-displacement from top-left corner
        };

        // Initializing Jelly
        if (!jelly) {
            // Note that ref.current may be null. This is expected, because you may
            // conditionally render the ref-ed element, or you may forgot to assign it
            if (!jellyCanvasRef.current) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: UNASSIGNED_ERROR_MESSAGE(JELLY_CANVAS_REF_NAME),
                        },
                    });
                }
                return;
            }
            if (!jellyContainerRef.current) {
                if (currentSessionId) {
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: UNASSIGNED_ERROR_MESSAGE(JELLY_CONTAINER_REF_NAME),
                        },
                    });
                }
                return;
            }

            setJelly(new Jelly(jellyCanvasRef.current, jellyContainerRef.current, options));
        }

        // Add jelly hover event listener
        if (!detectTouchDevice(document)) window.addEventListener('jelly-hover', onJellyHovered);

        // Add jelly hover event listener
        window.addEventListener('jelly-mousedown', onJellyMouseDown);

        // Add jelly mousemove event listener
        window.addEventListener('jelly-mousemove', onJellyMouseMove);

        // Add jelly hover event listener
        window.addEventListener('jelly-mouseup', onJellyMouseUp);

        // Add jelly click event listener
        window.addEventListener('jelly-click', onJellyExpand);

        return () => {
            if (!detectTouchDevice(document)) window.removeEventListener('jelly-hover', onJellyHovered);
            window.removeEventListener('jelly-mousedown', onJellyMouseDown);
            window.removeEventListener('jelly-mousemove', onJellyMouseMove);
            window.removeEventListener('jelly-mouseup', onJellyMouseUp);
            window.removeEventListener('jelly-click', onJellyExpand);
        };
    }, [landingPageContentRevealed, jelly, animateLogo]);

    /**
     * Manages delay of showing move cursor action
     * when interactable object selected
     */
    const {
        start: timeoutRevealMoveAction,
        clear: clearTimeoutRevealMoveAction,
    } = useTimeout(() => {
        setCursorSigns([MoveSign]);
    }, GRAB_OBJECT_DELAY_DURATION);

    /**
     * Manages delay of determining whether menu content should be shifted to be fully
     * within viewport
     */
    const {
        start: timeoutCheckMoveLogomark,
        clear: clearTimeoutCheckMoveLogomark,
    } = useTimeout(() => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!shapeRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(SHAPE_REF_NAME),
                    },
                });
            }
            return;
        }
        if (!jellyContainerRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(JELLY_CONTAINER_REF_NAME),
                    },
                });
            }
            return;
        }
        if (!letterGroupTwoRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(LETTER_GROUP_TWO_REF_NAME),
                    },
                });
            }
            return;
        }

        const shapeRect = shapeRef.current.getBoundingClientRect();
        const letterGroupTwoRect = letterGroupTwoRef.current.getBoundingClientRect();
        const logomarkWidth = letterGroupTwoRect.right - shapeRect.left;
        const logoOffset: ICoord = { x: 0, y: 0 };
        if (shapeRect.left + logomarkWidth > viewportDimensions.width) {
            logoOffset.x = detectTouchDevice(document)
                ? 0 // We center the menu content using CSS rather than shift using an offset
                : viewportDimensions.width - (shapeRect.left + logomarkWidth) - LOGOMARK_MARGIN;
        } else if (shapeRect.left < 0) {
            logoOffset.x = detectTouchDevice(document)
                ? 0 // We center the menu content using CSS rather than shift using an offset
                : LOGOMARK_MARGIN;
        }

        const menuButtonHeight = (MENU_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE) + MENU_BUTTON_MARGIN;
        const menuHeight = shapeRect.bottom
            + (2 * SLOGAN_MARGIN + BODY_FONT_SIZE * SLOGAN_FONT_MULTIPLIER)
            + (2 * INPUT_CONTAINER_MARGIN + NUM_MENU_BUTTONS * menuButtonHeight)
            + MENU_BUTTON_GROUP_MARGIN;
        if (menuHeight > viewportDimensions.height) {
            logoOffset.y = viewportDimensions.height - menuHeight - LOGOMARK_MARGIN;
        } else if (shapeRect.top < 0) {
            logoOffset.y = LOGOMARK_MARGIN;
        }

        setMenuOffset(logoOffset);

        // Play Sound
        if (
            hasSound
            && (
                logoOffset.x > 0
                || logoOffset.y > 0
            )
            && logomarkSwooshClip.current) {
            logomarkSwooshClip.current.pause();
            playAudio(logomarkSwooshClip.current);
        }
    }, EXPANSION_DURATION);

    /**
     * Manages enabling menu buttons when landing page content revealed
     */
    const {
        start: timeoutEnableOptionButtonMouseEvents,
        clear: clearTimeoutEnableOptionButtonMouseEvents,
    } = useTimeout(() => {
        setEnableMenuButtonHoverEvents(true);
    }, EXPANSION_DURATION);

    /**
     * Manages playing jelly sound effects when disturbed
     */
    useEffect(() => {
        if (wobbleJelly && hasSound) {
            // Play Sound
            playJellyWobbleSound(wobbleJelly);
            setWobbleJelly(null);
        }
    }, [
        wobbleJelly,
        hasSound,
        playJellyWobbleSound,
    ]);

    /**
     * Manages determining whether 'stay informed' text represents
     * a valid email address
     */
    useEffect(() => {
        if (email.length > 0 && !validateEmail(email)) {
            setEmailValid(false);
            setDelayContinueButton(false);
        } else {
            setEmailValid(true);
        }
    }, [email]);

    /**
     * Manages alternating the cursor action tooltip
     */
    useInterval(() => {
        if (tooltipActionIndex === TOOLTIP_POINT_INDEX) {
            setTooltipActionIndex(TOOLTIP_GRAB_INDEX);
        } else {
            setTooltipActionIndex(TOOLTIP_POINT_INDEX);
        }
    }, browserTabIsFocused != null && !landingPageContentRevealed && !objectHeld ? TOOLTIP_ACTION_ALTERNATE_DURATION : null);

    /**
     * Manages revealing interactable objects when landing page content is revealed
     * Short version takes place when we return to landing page and logomark aborts animation
     */
    const {
        start: shortTimeoutInitializeInteractableObjects,
        clear: clearShortTimeoutInitializeInteractableObjects,
    } = useTimeout(() => {
        revealInteractableObjects();
    }, DEFAULT_CSS_TRANSITION_DELAY);

    /**
     * Manages revealing interactable objects when landing page content is revealed
     * Long version takes place when we first view landing page and logomark animates
     */
    const {
        start: longTimeoutInitializeInteractableObjects,
        clear: clearLongTimeoutInitializeInteractableObjects,
    } = useTimeout(() => {
        revealInteractableObjects();
    }, EXPANSION_DURATION + DEFAULT_CSS_TRANSITION_DELAY);

    /**
     * Manages response to landing page content being instructed to reveal
     */
    useEffect(() => {
        // Bind hover events to option buttons
        if (!detectTouchDevice(document)) {
            if (landingPageContentRevealed) {
                timeoutEnableOptionButtonMouseEvents();
            } else {
                // Clear existing timeout
                clearTimeoutEnableOptionButtonMouseEvents();
                // Disbale Option Button Hover Events
                setEnableMenuButtonHoverEvents(false);
            }
        }

        return () => {
            if (!detectTouchDevice(document)) {
                // Clear existing timeout
                clearTimeoutEnableOptionButtonMouseEvents();
                // Disbale Option Button Hover Events
                setEnableMenuButtonHoverEvents(false);
            }
        };
    }, [landingPageContentRevealed]);

    /**
     * Manages animating Verascope Logo horizontally when landing page content is not revealed
     */
    useInterval(() => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!jellyContainerRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(JELLY_CONTAINER_REF_NAME),
                    },
                });
            }
            return;
        }

        const x = parseFloat(
            window
                .getComputedStyle(jellyContainerRef.current, null)
                .getPropertyValue('left')
                .replace('px', ''),
        );
        const dX = (MILLISECONDS_IN_A_SECOND / DEFAULT_ANIMATION_FPS) * (
            (
                (viewportDimensions.width - CANVAS_WIDTH()) + (CANVAS_WIDTH() - ADJUSTED_QUAD_WIDTH) / 2
            ) / XDRIFT_DURATION
        );
        if (
            x < JELLY_RIGHT_EDGE_POSITION
            && xDriftDirection === DRIFT_DIRECTION_FORWARD
        ) {
            jellyContainerRef.current.style.left = `${x + dX}px`;
        } else if (
            x > initJellyPos.x
            && xDriftDirection === DRIFT_DIRECTION_BACKWARD
        ) {
            jellyContainerRef.current.style.left = `${x - dX}px`;
        } else if (jelly) {
            setXDriftDirection(xDriftDirection === DRIFT_DIRECTION_FORWARD
                ? DRIFT_DIRECTION_BACKWARD
                : DRIFT_DIRECTION_FORWARD);
            const shakeImpulse = BUMP_IMPULSE_COEFF * Math.log10(XDRIFT_DURATION);
            jelly.shake({
                x: xDriftDirection === DRIFT_DIRECTION_FORWARD ? -shakeImpulse : shakeImpulse,
                y: 0,
            });

            // Play Sound
            if (hasSound && logomarkThumpClip.current) {
                logomarkThumpClip.current.pause();
                logomarkThumpClip.current.currentTime = 0;
                playAudio(logomarkThumpClip.current);
            }
        }
    }, browserTabIsFocused != null && animateLogo && !landingPageContentRevealed ? MILLISECONDS_IN_A_SECOND / DEFAULT_ANIMATION_FPS : null);

    /**
     * Manages animating Verascope Logo vertically when landing page content is not revealed
     */
    useInterval(() => {
        // Note that ref.current may be null. This is expected, because you may
        // conditionally render the ref-ed element, or you may forgot to assign it
        if (!jellyContainerRef.current) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: UNASSIGNED_ERROR_MESSAGE(JELLY_CONTAINER_REF_NAME),
                    },
                });
            }
            return;
        }

        const y = parseFloat(
            window
                .getComputedStyle(jellyContainerRef.current, null)
                .getPropertyValue('top')
                .replace('px', ''),
        );
        const dY = (MILLISECONDS_IN_A_SECOND / DEFAULT_ANIMATION_FPS) * (
            (
                (viewportDimensions.height - CANVAS_HEIGHT()) + (CANVAS_HEIGHT() - ADJUSTED_QUAD_HEIGHT) / 2
            ) / YDRIFT_DURATION
        );
        if (
            y < JELLY_BOTTOM_EDGE_POSITION
            && yDriftDirection === DRIFT_DIRECTION_FORWARD
        ) {
            jellyContainerRef.current.style.top = `${y + dY}px`;
        } else if (
            y > initJellyPos.y
            && yDriftDirection === DRIFT_DIRECTION_BACKWARD
        ) {
            jellyContainerRef.current.style.top = `${y - dY}px`;
        } else if (jelly) {
            setYDriftDirection(yDriftDirection === DRIFT_DIRECTION_FORWARD
                ? DRIFT_DIRECTION_BACKWARD
                : DRIFT_DIRECTION_FORWARD);
            const shakeImpulse = BUMP_IMPULSE_COEFF * Math.log10(YDRIFT_DURATION);
            jelly.shake({
                x: 0,
                y: yDriftDirection === DRIFT_DIRECTION_FORWARD ? -shakeImpulse : shakeImpulse,
            });

            // Play Sound
            if (hasSound && logomarkThumpClip.current) {
                logomarkThumpClip.current.pause();
                logomarkThumpClip.current.currentTime = 0;
                playAudio(logomarkThumpClip.current);
            }
        }
    }, browserTabIsFocused != null && animateLogo && !landingPageContentRevealed ? MILLISECONDS_IN_A_SECOND / DEFAULT_ANIMATION_FPS : null);

    /**
     * Manages silencing cuneiform tablet glow sound if cuneiform tablet is visible and idle
     * If we turn off volume while cuneiform tablet is visible we don't stop
     * glow loop, we just silence it so that it maintains alignment with
     * glow visual.
     */
    useEffect(() => {
        if (
            cuneiformTabletRevealed
            && !hasSound
            && cuneiformTabletGlowClip.current.volume === DEFAULT_AUDIO_VOLUME
        ) {
            cuneiformTabletGlowClip.current.volume = 0;
        } else if (cuneiformTabletRevealed && hasSound) {
            cuneiformTabletGlowClip.current.volume = DEFAULT_AUDIO_VOLUME;
        }
    }, [hasSound]);

    /**
     * Manages counting down to navigate to book
     */
    useInterval(() => {
        const digit = Math.max(cuneiformCountdownDigit - 1, 0);
        if (digit !== cuneiformCountdownDigit) setCuneiformCountdownDigit(digit);
    }, browserTabIsFocused != null && initiateCuneiformCountdown ? MILLISECONDS_IN_A_SECOND : null);

    /**
     * Manages triggering timeout to reveal landing page content
     */
    useEffect(() => {
        clearTimeoutRevealLandingPageContent();

        if (
            autoRevealLandingPageContentConsumed.current
            || recordedJellyMoveUserAction
            || landingPageContentRevealed
        ) return;

        timeoutRevealLandingPageContent();
    }, [
        recordedJellyMoveUserAction,
        landingPageContentRevealed,
    ]);

    /**
     * Manages triggering logomark expansion sound when landing page content revealed
     */
    const logomarkExpandDuration = landingPageContentRevealed
        ? CENTROID_BACKGROUND_EXPAND_DURATION
        : CENTROID_CONTRACT_DELAY_DURATION;
    const {
        start: timeoutLogomarkExpandClip,
        clear: clearTimeoutLogomarkExpandClip,
    } = useTimeout(() => {
        if (logomarkExpandClip.current) {
            logomarkExpandClip.current.pause();
            logomarkExpandClip.current.currentTime = 0;
            playAudio(logomarkExpandClip.current);
        }
    }, hasSound ? logomarkExpandDuration : null);

    /**
     * Manages triggering verascope logomark circle sound when landing page content revealed
     */
    const logoCircleSwooshDuration = landingPageContentRevealed
        ? CIRCLE_EXPAND_DELAY_DURATION
        : CIRCLE_CONTRACT_DELAY_DURATION;
    const {
        start: timeoutLogoCircleSwooshClip,
        clear: clearTimeoutLogoCircleSwooshClip,
    } = useTimeout(() => {
        if (logoCircleSwooshClip.current) {
            logoCircleSwooshClip.current.pause();
            logoCircleSwooshClip.current.currentTime = 0;
            playAudio(logoCircleSwooshClip.current);
        }
    }, hasSound ? logoCircleSwooshDuration : null);

    /**
     * Manages triggering verascope logomark E sound when landing page content revealed
     */
    const logoLetterE1SwooshDuration = landingPageContentRevealed
        ? LETTER_DELAY_DURATION + 0 * LETTER_DELAY_OFFSET_DURATION
        : LETTER_REVERSE_DELAY_DURATION - LETTER_DELAY_DURATION + 0 * LETTER_DELAY_OFFSET_DURATION;
    const {
        start: timeoutLogoLetterE1SwooshClip,
        clear: clearTimeoutLogoLetterE1SwooshClip,
    } = useTimeout(() => {
        if (logoLetterSwooshClip.current) {
            logoLetterSwooshClip.current.pause();
            logoLetterSwooshClip.current.currentTime = 0;
            playAudio(logoLetterSwooshClip.current);
        }
    }, hasSound ? logoLetterE1SwooshDuration : null);

    /**
     * Manages triggering verascope logomark R sound when landing page content revealed
     */
    const logoLetterRSwooshDuration = landingPageContentRevealed
        ? LETTER_DELAY_DURATION + 1 * LETTER_DELAY_OFFSET_DURATION
        : LETTER_REVERSE_DELAY_DURATION - LETTER_DELAY_DURATION + 1 * LETTER_DELAY_OFFSET_DURATION;
    const {
        start: timeoutLogoLetterRSwooshClip,
        clear: clearTimeoutLogoLetterRSwooshClip,
    } = useTimeout(() => {
        if (logoLetterSwooshClip.current) {
            logoLetterSwooshClip.current.pause();
            logoLetterSwooshClip.current.currentTime = 0;
            playAudio(logoLetterSwooshClip.current);
        }
    }, hasSound ? logoLetterRSwooshDuration : null);

    /**
     * Manages triggering verascope logomark A sound when landing page content revealed
     */
    const logoLetterASwooshDuration = landingPageContentRevealed
        ? LETTER_DELAY_DURATION + 2 * LETTER_DELAY_OFFSET_DURATION
        : LETTER_REVERSE_DELAY_DURATION - LETTER_DELAY_DURATION + 2 * LETTER_DELAY_OFFSET_DURATION;
    const {
        start: timeoutLogoLetterASwooshClip,
        clear: clearTimeoutLogoLetterASwooshClip,
    } = useTimeout(() => {
        if (logoLetterSwooshClip.current) {
            logoLetterSwooshClip.current.pause();
            logoLetterSwooshClip.current.currentTime = 0;
            playAudio(logoLetterSwooshClip.current);
        }
    }, hasSound ? logoLetterASwooshDuration : null);

    /**
     * Manages triggering verascope logomark S sound when landing page content revealed
     */
    const logoLetterSSwooshDuration = landingPageContentRevealed
        ? LETTER_DELAY_DURATION + 3 * LETTER_DELAY_OFFSET_DURATION
        : LETTER_REVERSE_DELAY_DURATION - LETTER_DELAY_DURATION + 3 * LETTER_DELAY_OFFSET_DURATION;
    const {
        start: timeoutLogoLetterSSwooshClip,
        clear: clearTimeoutLogoLetterSSwooshClip,
    } = useTimeout(() => {
        if (logoLetterSwooshClip.current) {
            logoLetterSwooshClip.current.pause();
            logoLetterSwooshClip.current.currentTime = 0;
            playAudio(logoLetterSwooshClip.current);
        }
    }, hasSound ? logoLetterSSwooshDuration : null);

    /**
     * Manages triggering verascope logomark C sound when landing page content revealed
     */
    const logoLetterCSwooshDuration = landingPageContentRevealed
        ? LETTER_DELAY_DURATION + 4 * LETTER_DELAY_OFFSET_DURATION
        : LETTER_REVERSE_DELAY_DURATION - LETTER_DELAY_DURATION + 4 * LETTER_DELAY_OFFSET_DURATION;
    const {
        start: timeoutLogoLetterCSwooshClip,
        clear: clearTimeoutLogoLetterCSwooshClip,
    } = useTimeout(() => {
        if (logoLetterSwooshClip.current) {
            logoLetterSwooshClip.current.pause();
            logoLetterSwooshClip.current.currentTime = 0;
            playAudio(logoLetterSwooshClip.current);
        }
    }, hasSound ? logoLetterCSwooshDuration : null);

    /**
     * Manages triggering verascope logomark P sound when landing page content revealed
     */
    const logoLetterPSwooshDuration = landingPageContentRevealed
        ? LETTER_DELAY_DURATION + 5 * LETTER_DELAY_OFFSET_DURATION
        : LETTER_REVERSE_DELAY_DURATION - LETTER_DELAY_DURATION + 5 * LETTER_DELAY_OFFSET_DURATION;
    const {
        start: timeoutLogoLetterPSwooshClip,
        clear: clearTimeoutLogoLetterPSwooshClip,
    } = useTimeout(() => {
        if (logoLetterSwooshClip.current) {
            logoLetterSwooshClip.current.pause();
            logoLetterSwooshClip.current.currentTime = 0;
            playAudio(logoLetterSwooshClip.current);
        }
    }, hasSound ? logoLetterPSwooshDuration : null);

    /**
     * Manages triggering verascope logomark E sound when landing page content revealed
     */
    const logoLetterE2SwooshDuration = landingPageContentRevealed
        ? LETTER_DELAY_DURATION + 6 * LETTER_DELAY_OFFSET_DURATION
        : LETTER_REVERSE_DELAY_DURATION - LETTER_DELAY_DURATION + 6 * LETTER_DELAY_OFFSET_DURATION;
    const {
        start: timeoutLogoLetterE2SwooshClip,
        clear: clearTimeoutLogoLetterE2SwooshClip,
    } = useTimeout(() => {
        if (logoLetterSwooshClip.current) {
            logoLetterSwooshClip.current.pause();
            logoLetterSwooshClip.current.currentTime = 0;
            playAudio(logoLetterSwooshClip.current);
        }
    }, hasSound ? logoLetterE2SwooshDuration : null);

    /**
     * Manages triggering about menu button sound when landing page content revealed
     */
    const {
        start: timeoutAboutSwooshClip,
        clear: clearTimeoutAboutSwooshClip,
    } = useTimeout(() => {
        if (
            landingPageContentRevealed
            && !stayInformed
            && buttonSwooshClip.current
        ) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? SLOGAN_EXPANSION_DURATION : null);

    /**
     * Manages triggering book menu button sound when landing page content revealed
     */
    const bookSwooshDuration = SLOGAN_EXPANSION_DURATION + MENU_BUTTON_DELAY_DURATION;
    const {
        start: timeoutBookSwooshClip,
        clear: clearTimeoutBookSwooshClip,
    } = useTimeout(() => {
        if (
            landingPageContentRevealed
            && !stayInformed
            && buttonSwooshClip.current
        ) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? bookSwooshDuration : null);

    /**
     * Manages triggering stay informed menu button sound when landing page content revealed
     */
    const stayInformedSwooshDuration = SLOGAN_EXPANSION_DURATION + 2 * MENU_BUTTON_DELAY_DURATION;
    const {
        start: timeoutStayInformedSwooshClip,
        clear: clearTimeoutStayInformedSwooshClip,
    } = useTimeout(() => {
        if (
            landingPageContentRevealed
            && !stayInformed
            && buttonSwooshClip.current
        ) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? stayInformedSwooshDuration : null);

    /**
     * Manages triggering email input sound when landing page content revealed (if visible)
     */
    const emailSwooshDuration = SLOGAN_EXPANSION_DURATION + 2 * MENU_BUTTON_DELAY_DURATION;
    const {
        start: timeoutEmailSwooshClip,
        clear: clearTimeoutEmailSwooshClip,
    } = useTimeout(() => {
        if (
            landingPageContentRevealed
            && stayInformed
            && !emailSubmitted
            && buttonSwooshClip.current
        ) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? emailSwooshDuration : null);

    /**
     * Manages triggering continue menu button sound when landing page content revealed (if visible)
     */
    const continueSwooshDuration = emailValid && email.length > 0 && delayContinueButton
        ? SLOGAN_EXPANSION_DURATION + 3 * MENU_BUTTON_DELAY_DURATION
        : SLOGAN_EXPANSION_DURATION + MENU_BUTTON_DELAY_DURATION;
    const {
        start: timeoutContinueSwooshClip,
        clear: clearTimeoutContinueSwooshClip,
    } = useTimeout(() => {
        if (
            landingPageContentRevealed
            && emailValid
            && email.length > 0
            && buttonSwooshClip.current
        ) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? continueSwooshDuration : null);

    /**
     * Manages triggering treasure chest menu button sound when landing page content revealed
     */
    const treasureChestSwooshDuration = emailValid && email.length > 0 && delayContinueButton
        ? SLOGAN_EXPANSION_DURATION + 4 * MENU_BUTTON_DELAY_DURATION
        : SLOGAN_EXPANSION_DURATION + 3 * MENU_BUTTON_DELAY_DURATION;
    const {
        start: timeoutTreasureChestSwooshClip,
        clear: clearTimeoutTreasureChestSwooshClip,
    } = useTimeout(() => {
        if (buttonSwooshClip.current) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? treasureChestSwooshDuration : null);

    /**
     * Manages triggering characters menu button sound when landing page content revealed
     */
    const charactersMenuButtonIndex = MENU_BUTTONS.indexOf(CURSOR_TARGET.characters);
    const charactersSwooshDuration = charactersMenuButtonIndex >= 0
        ? SLOGAN_EXPANSION_DURATION + Math.max(
            0,
            (charactersMenuButtonIndex - NUM_HIDDEN_MENU_BUTTONS) * MENU_BUTTON_DELAY_DURATION,
        )
        : null;
    const {
        start: timeoutCharactersSwooshClip,
        clear: clearTimeoutCharactersSwooshClip,
    } = useTimeout(() => {
        if (buttonSwooshClip.current) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? charactersSwooshDuration : null);

    /**
     * Manages triggering exit menu button sound when landing page content revealed
     */
    const exitMenuButtonIndex = MENU_BUTTONS.indexOf(CURSOR_TARGET.exit);
    const exitSwooshDuration = SLOGAN_EXPANSION_DURATION + Math.max(
        0,
        (exitMenuButtonIndex - NUM_HIDDEN_MENU_BUTTONS) * MENU_BUTTON_DELAY_DURATION,
    );
    const {
        start: timeoutExitSwooshClip,
        clear: clearTimeoutExitSwooshClip,
    } = useTimeout(() => {
        if (buttonSwooshClip.current) {
            buttonSwooshClip.current.pause();
            buttonSwooshClip.current.currentTime = 0;
            playAudio(buttonSwooshClip.current);
        }
    }, hasSound ? exitSwooshDuration : null);

    /**
     * Manages triggering game controller interactable object sound when landing page content revealed
     */
    const {
        start: timeoutControllerThumpClip,
        clear: clearTimeoutControllerThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 0 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering magnet interactable object sound when landing page content revealed
     */
    const {
        start: timeoutMagnetThumpClip,
        clear: clearTimeoutMagnetThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 1 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering abacus interactable object sound when landing page content revealed
     */
    const {
        start: timeoutAbacusThumpClip,
        clear: clearTimeoutAbacusThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 2 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering molecule interactable object sound when landing page content revealed
     */
    const {
        start: timeoutMoleculeThumpClip,
        clear: clearTimeoutMoleculeThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 3 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering lightbulb interactable object sound when landing page content revealed
     */
    const {
        start: timeoutLightbulbThumpClip,
        clear: clearTimeoutLightbulbThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 4 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering blue puzzle interactable object sound when landing page content revealed
     */
    const {
        start: timeoutPuzzleBlueThumpClip,
        clear: clearTimeoutPuzzleBlueThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 5 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering green puzzle interactable object sound when landing page content revealed
     */
    const {
        start: timeoutPuzzleGreenThumpClip,
        clear: clearTimeoutPuzzleGreenThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 6 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering red puzzle interactable object sound when landing page content revealed
     */
    const {
        start: timeoutPuzzleRedThumpClip,
        clear: clearTimeoutPuzzleRedThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 7 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages triggering orange puzzle interactable object sound when landing page content revealed
     */
    const {
        start: timeoutPuzzleOrangeThumpClip,
        clear: clearTimeoutPuzzleOrangeThumpClip,
    } = useTimeout(() => {
        if (objectThumpClip.current) {
            objectThumpClip.current.currentTime = 0;
            playAudio(objectThumpClip.current);
        }
    }, hasSound ? 8 * OBJECT_DELAY_OFFSET_DURATION : null);

    /**
     * Manages revealing cuneiform tablet after a delay when returning to landing page
     * Reveal Cuneiform Tablet after delay (wait for interactable objects)
     * User might have already expanded landing page, gone to another view,
     * and then returned to landing page.
     * If so, show tablet that had already been expanded
     */
    const {
        start: timeoutRevealCuneiformTablet,
        clear: clearTimeoutRevealCuneiformTablet,
    } = useTimeout(() => {
        // Remove temporarily hiding of cuneiform tablet now that
        // interactable objects have spawned
        setTemporarilyHideCuneiformTablet(false);
        revealCuneiformTablet(false);

        if (hasSound) {
            if (cuneiformTabletThudClip.current) {
                cuneiformTabletThudClip.current.currentTime = 0;
                playAudio(cuneiformTabletThudClip.current);
            }
            if (cuneiformTabletGlowClip.current) {
                cuneiformTabletGlowClip.current.currentTime = 0;
                playAudio(cuneiformTabletGlowClip.current);
            }
        }
    }, 9 * OBJECT_DELAY_OFFSET_DURATION);

    /**
     * Manages dollying to cuneiform tablet
     */
    const {
        start: timeoutTransitionToBook,
        clear: clearTimeoutTransitionToBook,
    } = useTimeout(() => {
        dollyTowardCuneiformTablet();
        // Wait for transition to complete before *routing* to book
        clearTimeoutNavigateToBook();
        timeoutNavigateToBook();
    }, BOOK_TRANSITION_DURATION);

    /**
     * Manages routing to book page
     */
    const {
        start: timeoutNavigateToBook,
        clear: clearTimeoutNavigateToBook,
    } = useTimeout(() => {
        navigateToBook();
    }, CUNEIFORM_DOLLY_DURATION);

    /**
     * Manages revealing landing page content after some amount of time
     * without any user interaction
     */
    const {
        start: timeoutRevealLandingPageContent,
        clear: clearTimeoutRevealLandingPageContent,
    } = useTimeout(() => {
        if (
            autoRevealLandingPageContentConsumed.current
            || recordedJellyMoveUserAction
            || landingPageContentRevealed
        ) return;

        autoRevealLandingPageContentConsumed.current = true;
        onJellyExpand();
    }, browserTabIsFocused != null ? WAIT_FOR_USER_INTERACTION_DURATION - cachedPostFocusedDuration : null);

    let tooltipText: string;
    if (jellyHeld) {
        tooltipText = PULSE_MOVE_TEXT;
    } else {
        tooltipText = tooltipActionIndex === TOOLTIP_POINT_INDEX ? PULSE_POINT_TEXT : PULSE_GRAB_TEXT;
    }

    // ===== Rendering =====

    const renderModal = (): React.ReactElement => (
        <Modal
            hasContainer
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            isOpen={modal.visible}
            closeModal={modal.rejectCallback}
        >
            <GenericModal
                customCursorActive={customCursorActive}
                message={modal.message}
                callback={modal.proceedCallback}
                hideCancel={!modal.rejectCallback}
                onCursorEnter={onCursorEnter}
                onCursorLeave={onCursorLeave}
            />
        </Modal>
    );

    return (
        <>
            {renderModal()}
            <VerascopeQuadrilateral
                viewBox={`0 0 ${INIT_QUAD_WIDTH} ${INIT_QUAD_HEIGHT}`}
                width={CANVAS_WIDTH()}
                height={CANVAS_HEIGHT()}
            >
                <Quadrilateral
                    id="quad-path"
                    points={
                        `${ADJUSTED_QUAD_WIDTH * (0 / INIT_QUAD_WIDTH)},${ADJUSTED_QUAD_HEIGHT * (67.05 / INIT_QUAD_HEIGHT)} 
                        ${ADJUSTED_QUAD_WIDTH * (160.92 / INIT_QUAD_WIDTH)},${ADJUSTED_QUAD_HEIGHT * (0 / INIT_QUAD_HEIGHT)} 
                        ${ADJUSTED_QUAD_WIDTH * (400 / INIT_QUAD_WIDTH)},${ADJUSTED_QUAD_HEIGHT * (92.05 / INIT_QUAD_HEIGHT)} 
                        ${ADJUSTED_QUAD_WIDTH * (114.78 / INIT_QUAD_WIDTH)},${ADJUSTED_QUAD_HEIGHT * (272.26 / INIT_QUAD_HEIGHT)} 
                        ${ADJUSTED_QUAD_WIDTH * (0 / INIT_QUAD_WIDTH)},${ADJUSTED_QUAD_HEIGHT * (67.05 / INIT_QUAD_HEIGHT)}`
                    }
                />
            </VerascopeQuadrilateral>
            <JellyContainer
                ref={jellyContainerRef}
                customCursorActive={customCursorActive}
                showPointerCursor={tooltipActionIndex === TOOLTIP_POINT_INDEX && !jellyHeld}
                showGrabCursor={tooltipActionIndex === TOOLTIP_GRAB_INDEX && !jellyHeld}
                showMoveCursor={jellyHeld}
                isTouchDevice={detectTouchDevice(document)}
                initPosX={initJellyPos.x}
                initPosY={initJellyPos.y}
                width={CANVAS_WIDTH()}
                height={CANVAS_HEIGHT()}
                expanded={landingPageContentRevealed}
                offset={menuOffset}
                revealedExpandedTop={LOGOMARK_MARGIN}
                offsetExpandDuration={LOGOMARK_OFFSET_EXPAND_DURATION}
                offsetContractDuration={LOGOMARK_OFFSET_CONTRACT_DURATION}
            >
                {/* Canvas to draw the jelly pentagon */}
                <JellyCanvas
                    ref={jellyCanvasRef}
                    expanded={landingPageContentRevealed}
                    isTouchDevice={detectTouchDevice(document)}
                    width={CANVAS_WIDTH()}
                    height={CANVAS_HEIGHT()}
                />

                {/* Text in the centroid of the jelly pentagon */}
                <CentroidContainer
                    expanded={landingPageContentRevealed}
                    isTouchDevice={detectTouchDevice(document)}
                >
                    <CentroidBackground
                        expanded={landingPageContentRevealed}
                        isMobile={isMobile()}
                        canvasWidth={CANVAS_WIDTH()}
                        canvasHeight={CANVAS_HEIGHT()}
                        transitionDuration={CENTROID_BACKGROUND_EXPAND_DURATION}
                        contractionDelayDuration={CENTROID_CONTRACT_DELAY_DURATION}
                    />
                    <LogomarkGroup
                        ref={centroidRef}
                        expanded={landingPageContentRevealed}
                        isTouchDevice={detectTouchDevice(document)}
                    >
                        <VerascopeShapeGroup
                            viewBox={`0 0 ${INIT_SHAPES_WIDTH} ${INIT_SHAPES_HEIGHT}`}
                            ref={shapeRef}
                            topOffset={SHAPES_TOP_OFFSET}
                            leftOffset={SHAPES_LEFT_OFFSET}
                            adjWidth={SHAPES_WIDTH}
                            adjHeight={SHAPES_HEIGHT}
                            expanded={landingPageContentRevealed}
                            isTouchDevice={detectTouchDevice(document)}
                        >
                            <VerascopeTriangle
                                ref={triangleRef}
                                fillDuration={SHAPE_FILL_DURATION}
                                points={
                                    `${45.55},${78.18} 
                                    ${0},${1.21} 
                                    ${91.11},${1.21} 
                                    ${45.55},${78.18}`
                                }
                            />
                            <VerascopeCircle
                                ref={circleRef}
                                expanded={landingPageContentRevealed}
                                fillDuration={SHAPE_FILL_DURATION}
                                translateDuration={CIRCLE_TRANSLATE_DURATION}
                                translateExpandDelayDuration={CIRCLE_EXPAND_DELAY_DURATION}
                                translateContractDelayDuration={CIRCLE_CONTRACT_DELAY_DURATION}
                                cx={133.06}
                                cy={38.48}
                                r={38.48}
                                isSafariBrowser={isSafariBrowser()}
                                isMobile={isMobile()}
                            />
                        </VerascopeShapeGroup>
                        <VerascopeLetterGroup
                            viewBox={`0 0 ${INIT_LETTER_GROUP_ONE_WIDTH} ${INIT_LETTER_GROUP_ONE_HEIGHT}`}
                            top={LETTER_GROUP_ONE_TOP_OFFSET}
                            left={LETTER_GROUP_ONE_LEFT_OFFSET}
                            isTouchDevice={detectTouchDevice(document)}
                            expanded={landingPageContentRevealed}
                            shapeTopOffset={SHAPES_TOP_OFFSET}
                            shapeLeftOffset={SHAPES_LEFT_OFFSET}
                            width={LETTER_GROUP_ONE_WIDTH}
                            height={LETTER_GROUP_ONE_HEIGHT}
                        >
                            <VerascopeLetterE1
                                d="M73.82,40a38.42,38.42,0,1,0-8.41,42.1l-14.86-15a17.73,17.73,0,0,1-2,1.73,17.26,17.26,0,0,1-20.64-.28l45.31-19h0l3-1.27A38.39,38.39,0,0,0,73.82,40Zm-45.56.84a17.26,17.26,0,0,1,21.4.9L21.17,53.69A17.25,17.25,0,0,1,28.26,40.88Z"
                                expanded={landingPageContentRevealed}
                                delay={LETTER_DELAY_DURATION + 0 * LETTER_DELAY_OFFSET_DURATION}
                                opacityDuration={LETTER_OPACITY_DURATION}
                                translateDuration={LETTER_TRANSLATE_DURATION}
                                letterOffset={LETTER_OFFSET}
                                reverseDelayDuration={LETTER_REVERSE_DELAY_DURATION}
                            />
                            <VerascopeLetterR
                                d="M114.22,0A69,69,0,0,0,86.57,55.29V93.68h21.12V55.29a47.91,47.91,0,0,1,19.22-38.41Z"
                                expanded={landingPageContentRevealed}
                                delay={LETTER_DELAY_DURATION + 1 * LETTER_DELAY_OFFSET_DURATION}
                                opacityDuration={LETTER_OPACITY_DURATION}
                                translateDuration={LETTER_TRANSLATE_DURATION}
                                letterOffset={LETTER_OFFSET}
                                reverseDelayDuration={LETTER_REVERSE_DELAY_DURATION}
                            />
                            <VerascopeLetterA
                                d="M194.07,55.29a38.39,38.39,0,1,0-21.15,34.3v4.09H194.1V55.29ZM159.64,72.1a17.27,17.27,0,1,1,12.85-20.77,16.91,16.91,0,0,1,.46,4h0v1.18A17.28,17.28,0,0,1,159.64,72.1Z"
                                expanded={landingPageContentRevealed}
                                delay={LETTER_DELAY_DURATION + 2 * LETTER_DELAY_OFFSET_DURATION}
                                opacityDuration={LETTER_OPACITY_DURATION}
                                translateDuration={LETTER_TRANSLATE_DURATION}
                                letterOffset={LETTER_OFFSET}
                                reverseDelayDuration={LETTER_REVERSE_DELAY_DURATION}
                            />
                            <VerascopeLetterS
                                d="M272.16,16.91a38.41,38.41,0,0,0-38.43,38.36h0A17.29,17.29,0,0,1,210.3,71.42h0l-7.53,19.74a38.42,38.42,0,0,0,52.08-35.85h0a17.27,17.27,0,0,1,23.43-16.14h0l7.53-19.74A38.38,38.38,0,0,0,272.16,16.91Z"
                                expanded={landingPageContentRevealed}
                                delay={LETTER_DELAY_DURATION + 3 * LETTER_DELAY_OFFSET_DURATION}
                                opacityDuration={LETTER_OPACITY_DURATION}
                                translateDuration={LETTER_TRANSLATE_DURATION}
                                letterOffset={LETTER_OFFSET}
                                reverseDelayDuration={LETTER_REVERSE_DELAY_DURATION}
                            />
                            <VerascopeLetterC
                                d="M327.87,71.44a17.87,17.87,0,0,1-2.19.66,17.27,17.27,0,1,1,2.18-32.95h0l7.51-19.75a38.39,38.39,0,1,0,0,71.78l-7.51-19.74Z"
                                expanded={landingPageContentRevealed}
                                delay={LETTER_DELAY_DURATION + 4 * LETTER_DELAY_OFFSET_DURATION}
                                opacityDuration={LETTER_OPACITY_DURATION}
                                translateDuration={LETTER_TRANSLATE_DURATION}
                                letterOffset={LETTER_OFFSET}
                                reverseDelayDuration={LETTER_REVERSE_DELAY_DURATION}
                            />
                        </VerascopeLetterGroup>
                        <VerascopeLetterGroup
                            viewBox={`0 0 ${INIT_LETTER_GROUP_TWO_WIDTH} ${INIT_LETTER_GROUP_TWO_HEIGHT}`}
                            ref={letterGroupTwoRef}
                            top={LETTER_GROUP_TWO_TOP_OFFSET}
                            left={LETTER_GROUP_TWO_LEFT_OFFSET}
                            isTouchDevice={detectTouchDevice(document)}
                            expanded={landingPageContentRevealed}
                            shapeTopOffset={SHAPES_TOP_OFFSET}
                            shapeLeftOffset={SHAPES_LEFT_OFFSET}
                            width={LETTER_GROUP_TWO_WIDTH}
                            height={LETTER_GROUP_TWO_HEIGHT}
                        >
                            <VerascopeLetterP
                                d="M38,.16A38.38,38.38,0,0,0,0,38.55H0v64.69H21.12V72.83A38.39,38.39,0,1,0,38,.16Zm4.49,55.16A17.27,17.27,0,1,1,55.16,34.43,17.26,17.26,0,0,1,42.51,55.32Z"
                                expanded={landingPageContentRevealed}
                                delay={LETTER_DELAY_DURATION + 5 * LETTER_DELAY_OFFSET_DURATION}
                                opacityDuration={LETTER_OPACITY_DURATION}
                                translateDuration={LETTER_TRANSLATE_DURATION}
                                letterOffset={LETTER_OFFSET}
                                reverseDelayDuration={LETTER_REVERSE_DELAY_DURATION}
                            />
                            <VerascopeLetterE2
                                d="M160.05,23.59a38.39,38.39,0,1,0-8.4,42.09l-14.86-15a16.77,16.77,0,0,1-2,1.73,17.27,17.27,0,0,1-20.65-.27l45.32-19h0l3-1.26A37.55,37.55,0,0,0,160.05,23.59Zm-45.55.83a17.25,17.25,0,0,1,21.39.9L107.41,37.23A17.22,17.22,0,0,1,114.5,24.42Z"
                                expanded={landingPageContentRevealed}
                                delay={LETTER_DELAY_DURATION + 6 * LETTER_DELAY_OFFSET_DURATION}
                                opacityDuration={LETTER_OPACITY_DURATION}
                                translateDuration={LETTER_TRANSLATE_DURATION}
                                letterOffset={LETTER_OFFSET}
                                reverseDelayDuration={LETTER_REVERSE_DELAY_DURATION}
                            />
                        </VerascopeLetterGroup>
                        <VerascopeSlogan
                            isVisible={landingPageContentRevealed}
                            leftOffset={(SHAPES_WIDTH + LETTER_GROUP_ONE_WIDTH + LETTER_GROUP_TWO_WIDTH) / 2 + SHAPES_LEFT_OFFSET}
                            logomarkHeight={LOGOMARK_HEIGHT}
                            sloganFontMultiplier={SLOGAN_FONT_MULTIPLIER}
                            sloganMargin={SLOGAN_MARGIN}
                            translateDuration={SLOGAN_TRANSLATE_DURATION}
                            translateDelay={LOGOMARK_EXPANSION_DURATION + LOGOMARK_OFFSET_EXPAND_DURATION}
                            runTypingAnimation={emailSubmitted}
                            sloganLength={emailSubmitted ? SUBMITTED_EMAIL_MESSAGE.length : VERASCOPE_SLOGAN.length}
                            typingDuration={TYPING_DURATION}
                            blinkingDuration={CARET_BLINKING_DURATION}
                        >
                            {emailSubmitted ? SUBMITTED_EMAIL_MESSAGE : VERASCOPE_SLOGAN}
                        </VerascopeSlogan>
                        <MenuContainer
                            ref={menuContainerRef}
                            leftOffset={(SHAPES_WIDTH + LETTER_GROUP_ONE_WIDTH + LETTER_GROUP_TWO_WIDTH) / 2 + SHAPES_LEFT_OFFSET}
                            logomarkHeight={LOGOMARK_HEIGHT}
                            sloganFontMultiplier={SLOGAN_FONT_MULTIPLIER}
                            sloganMargin={SLOGAN_MARGIN}
                            margin={INPUT_CONTAINER_MARGIN}
                        >
                            {MENU_BUTTONS.map((target: CURSOR_TARGET, i: number) => {
                                // Used by both 'Stay Informed' and the Email Input
                                const INPUT_ROW_DELAY_DURATION = NUM_MENU_BUTTONS_BEFORE_MAILING_LIST_BUTTON * MENU_BUTTON_DELAY_DURATION;
                                if (target === CURSOR_TARGET.input) {
                                    return (
                                        <InputWrapper
                                            key={target}
                                            isVisible={landingPageContentRevealed && stayInformed && !emailSubmitted}
                                            width={MENU_BUTTON_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                            height={MENU_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                            withTransition={email.length > 0}
                                            withDelay={email.length > 0}
                                            offsetDelay={INPUT_ROW_DELAY_DURATION}
                                            translateDuration={MENU_BUTTON_TRANSLATE_DURATION}
                                            translateDelay={SLOGAN_EXPANSION_DURATION}
                                            margin={MENU_BUTTON_MARGIN}
                                        >
                                            <Input
                                                type="email"
                                                ref={inputRef}
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                defaultValue={email}
                                                placeholder={emailSubmitted ? '' : 'EMAIL'}
                                                onChange={onInputChange}
                                                onKeyUp={checkForEnter}
                                                isInvalid={!emailValid}
                                                disabled={emailSubmitted || isSubmittingEmail}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseLeave: (e) => onObjectCursorLeave(e),
                                                    onMouseEnter: (e) => onObjectCursorEnter(
                                                        CURSOR_TARGET.input,
                                                        [CURSOR_SIGN.click],
                                                        e,
                                                    ),
                                                })}
                                                onFocus={onInputFocus}
                                                onBlur={onInputBlur}
                                            />
                                        </InputWrapper>
                                    );
                                }

                                // Set variables
                                let isVisible: boolean;
                                let withTransition: boolean;
                                let withDelay: boolean;
                                let offsetDelay: number;
                                switch (target) {
                                case CURSOR_TARGET.book:
                                    isVisible = landingPageContentRevealed;
                                    withTransition = true;
                                    withDelay = true;
                                    offsetDelay = MENU_BUTTON_DELAY_DURATION;
                                    break;
                                case CURSOR_TARGET.stayInformed:
                                    isVisible = landingPageContentRevealed && !stayInformed;
                                    withTransition = !stayInformed;
                                    withDelay = true;
                                    offsetDelay = INPUT_ROW_DELAY_DURATION;
                                    break;
                                case CURSOR_TARGET.continue:
                                    isVisible = landingPageContentRevealed && emailValid && email.length > 0;
                                    withTransition = true;
                                    withDelay = emailValid && email.length > 0 && delayContinueButton;
                                    offsetDelay = Math.max(
                                        0,
                                        (i - NUM_HIDDEN_MENU_BUTTONS) * MENU_BUTTON_DELAY_DURATION,
                                    );
                                    break;
                                default:
                                    isVisible = landingPageContentRevealed;
                                    withTransition = true;
                                    withDelay = true;
                                    offsetDelay = Math.max(
                                        0,
                                        (i - NUM_HIDDEN_MENU_BUTTONS) * MENU_BUTTON_DELAY_DURATION,
                                    );
                                }

                                // Set handle click
                                let handleClick: ((e: React.MouseEvent) => void) | undefined;
                                switch (target) {
                                case CURSOR_TARGET.about:
                                    handleClick = onAbout;
                                    break;
                                case CURSOR_TARGET.book:
                                    handleClick = onBook;
                                    break;
                                case CURSOR_TARGET.stayInformed:
                                    handleClick = onStayInformed;
                                    break;
                                case CURSOR_TARGET.continue:
                                    handleClick = onSubmitEmail;
                                    break;
                                case CURSOR_TARGET.characters:
                                    handleClick = onCharacters;
                                    break;
                                case CURSOR_TARGET.treasureChest:
                                    handleClick = onTreasureChest;
                                    break;
                                default:
                                    // Accessed by CURSOR_TARGET.exit
                                    handleClick = onJellyContract;
                                }

                                const disabled = initiateCuneiformCountdown || isSubmittingEmail || (target === CURSOR_TARGET.continue && !currentSessionId);

                                return (
                                    <MenuButton
                                        key={target}
                                        customCursorActive={customCursorActive}
                                        width={MENU_BUTTON_WIDTH_MULTIPLIER * BODY_FONT_SIZE}
                                        height={MENU_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                                        isVisible={isVisible}
                                        disabled={disabled}
                                        withTransition={withTransition}
                                        withDelay={withDelay}
                                        offsetDelay={offsetDelay}
                                        translateDuration={MENU_BUTTON_TRANSLATE_DURATION}
                                        translateDelay={SLOGAN_EXPANSION_DURATION}
                                        margin={MENU_BUTTON_MARGIN}
                                        {...(enableMenuButtonHoverEvents
                                            ? {
                                                onHoverEnter: (e: React.MouseEvent | React.TouchEvent) => onMenuButtonMouseEnter(
                                                    target,
                                                    [CURSOR_SIGN.click],
                                                    e,
                                                ),
                                                onHoverLeave: (e: React.MouseEvent | React.TouchEvent) => onMenuButtonMouseLeave(e),
                                            } : {})}
                                        {...(!disabled ? { onClick: handleClick } : {})}
                                    >
                                        {target === CURSOR_TARGET.continue && isSubmittingEmail
                                            ? (
                                                <SpinnerContainer
                                                    src={Spinner}
                                                    alt="spinner"
                                                />
                                            )
                                            : target.toUpperCase()}
                                    </MenuButton>
                                );
                            })}
                        </MenuContainer>
                    </LogomarkGroup>
                </CentroidContainer>
            </JellyContainer>
            <RightArrow
                ref={rightArrowRef}
                height={ARROW_HEIGHT}
                viewBox="0 0 6.34 11.1"
            >
                <Polygon points="4.76 4.76 4.76 3.17 3.17 3.17 3.17 7.93 4.76 7.93 4.76 6.34 6.34 6.34 6.34 4.76 4.76 4.76" />
                <Polygon points="1.59 0 0 0 0 11.1 1.59 11.1 1.59 9.51 3.17 9.51 3.17 1.59 1.59 1.59 1.59 0" />
            </RightArrow>
            <LeftArrow
                ref={leftArrowRef}
                height={ARROW_HEIGHT}
                viewBox="0 0 6.34 11.1"
            >
                <Polygon points="4.76 4.76 4.76 3.17 3.17 3.17 3.17 7.93 4.76 7.93 4.76 6.34 6.34 6.34 6.34 4.76 4.76 4.76" />
                <Polygon points="1.59 0 0 0 0 11.1 1.59 11.1 1.59 9.51 3.17 9.51 3.17 1.59 1.59 1.59 1.59 0" />
            </LeftArrow>
            <InteractableObjectContainer>
                <InteractableObject
                    ref={gameControllerRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 257.83 116.14"
                    customCursorActive={customCursorActive}
                    width={CONTROLLER_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.gameController].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.gameController].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.gameController].rotate}
                    delay={0 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.gameController}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.gameController,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: gameControllerRef,
                            name: INTERACTABLE_OBJECT.gameController,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: gameControllerRef,
                            name: INTERACTABLE_OBJECT.gameController,
                        }, e),
                    })}
                >
                    <InteractableObjectShadow
                        id="Super_Nintendo_Entertainment_System_Game_Controller_Shadow"
                        dataName="Super Nintendo Entertainment System Game Controller Shadow"
                        fill="#0d1d38"
                        d="M52.52,12.21a52,52,0,1,0,35.07,90.31h83.2a52,52,0,1,0,35.07-90.31Z"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.gameController}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    />
                    <Group
                        id="Super_Nintendo_Entertainment_System_Game_Controller_Base"
                        dataName="Super Nintendo Entertainment System Game Controller Base"
                    >
                        <Path
                            id="Controller_Background"
                            dataName="Controller Background"
                            fill="#b6b4ba"
                            d="M52,3A52,52,0,1,0,87,93.33h83.2A52,52,0,1,0,205.31,3Z"
                        />
                        <Path
                            id="Controller_Sheen"
                            dataName="Controller Sheen"
                            fill="#e3e3e5"
                            d="M245.67,21.63h0c-.15-.19-.31-.37-.47-.55l-.35-.42-.48-.55c-.13-.14-.26-.29-.4-.43l-.49-.53c-.14-.15-.27-.3-.41-.44l-.5-.51-.43-.43-.51-.5-.45-.42c-.17-.16-.34-.33-.52-.48l-.46-.42-.52-.47-.48-.4c-.18-.15-.35-.31-.53-.45l-.51-.41-1-.79-.52-.4-.55-.4c-.18-.12-.35-.25-.52-.38l-.57-.38c-.17-.13-.35-.25-.53-.37l-.57-.37-.55-.36-.58-.36-.56-.34-.58-.34-.58-.33-.58-.33-.62-.32-.56-.3c-.26-.14-.52-.26-.79-.39l-.84-.41-.74-.34-.57-.25-.66-.29-.6-.24-.66-.26-.6-.23-.67-.25-.61-.21-.68-.23-.61-.2-.71-.22L220,4.77l-.77-.22-.55-.14c-.36-.1-.72-.19-1.09-.27L217,4l-1-.22-.55-.11-.82-.15-.59-.1-.78-.12-.61-.09-.78-.1-.62-.07L210.47,3l-.61,0L209,2.87l-.57,0-.93,0-.49,0-1.41,0H52.26c-.81,0-1.61,0-2.41.06h-.17c-.76,0-1.52.09-2.28.16l-.26,0c-.74.07-1.46.16-2.19.26l-.31,0c-.71.11-1.42.23-2.12.36l-.34.06c-.69.14-1.39.29-2.08.46l-.32.08c-.69.17-1.38.35-2.06.55l-.28.08c-.69.21-1.38.43-2.06.66l-.21.07c-.7.25-1.4.5-2.08.78l-.12,0q-2.17.88-4.25,1.93h0c-.68.35-1.35.71-2,1.09l-.11.06c-.64.37-1.28.75-1.91,1.14l-.15.1c-.61.39-1.22.79-1.82,1.21l-.16.11c-.6.42-1.18.84-1.76,1.28l-.16.12c-.57.45-1.14.9-1.7,1.37l-.14.12c-.56.47-1.11,1-1.65,1.45l0,0h0A52,52,0,0,0,18.2,94a52,52,0,0,1,40.07-85H211.62a51.92,51.92,0,0,1,11.64,1.32,52,52,0,0,1,14,85.7,52,52,0,0,0,8.42-74.3Z"
                        />
                        <Circle
                            id="Colored_Buttons_Background"
                            dataName="Colored Buttons Background"
                            fill="#817e89"
                            stroke="#575462"
                            strokeMiterLimit="10"
                            strokeWidth="2px"
                            cx="206.56"
                            cy="54.95"
                            r="46.78"
                        />
                        <Path
                            id="Controller_Button_Group_2"
                            dataName="Controller Button Group 2"
                            fill="#cbcccb"
                            d="M214.4,84.57a12.79,12.79,0,0,1-16-20l24.38-19.54a12.79,12.79,0,1,1,16,19.95Z"
                        />
                        <Path
                            id="Controller_Button_Group_1"
                            dataName="Controller Button Group 1"
                            fill="#cbcccb"
                            d="M189.66,65.22a12.79,12.79,0,0,1-16-20l24.39-19.54a12.79,12.79,0,1,1,16,20Z"
                        />
                        <Circle
                            id="Green_Button_Background"
                            dataName="Green Button Background"
                            fill="#817e89"
                            cx="181.76"
                            cy="55.13"
                            r="11.31"
                        />
                        <Circle
                            id="Blue_Button_Background"
                            dataName="Blue Button Background"
                            fill="#817e89"
                            cx="206.01"
                            cy="35.66"
                            r="11.31"
                        />
                        <Circle
                            id="Yellow_Button_Background"
                            dataName="Yellow Button Background"
                            fill="#817e89"
                            cx="206.19"
                            cy="74.88"
                            r="11.31"
                        />
                        <Circle
                            id="Red_Button_Background"
                            dataName="Red Button Background"
                            fill="#817e89"
                            cx="230.69"
                            cy="54.76"
                            r="11.31"
                        />
                        <Path
                            id="Left_Shoulder_Button"
                            dataName="Left Shoulder Button"
                            fill="#b6b4ba"
                            d="M26.53,9.53V6.39A56.43,56.43,0,0,1,51.64.11C65.64.11,90.75,0,90.75,0V2.71H51.64C38.17,2.71,26.53,9.53,26.53,9.53Z"
                        />
                        <Path
                            id="Right_Shoulder_Button"
                            dataName="Right Shoulder Button"
                            fill="#b6b4ba"
                            d="M231.5,9.53V6.39A56.41,56.41,0,0,0,206.4.11c-14,0-39.12-.11-39.12-.11V2.71H206.4C219.87,2.71,231.5,9.53,231.5,9.53Z"
                        />
                        <Group
                            id="Select_Button"
                            dataName="Select Button"
                        >
                            <Path
                                fill="#323341"
                                d="M109.5,70.32a5,5,0,0,1-6.24-7.73l9.5-7.67A5,5,0,1,1,119,62.64Z"
                            />
                            <Path
                                fill="#565461"
                                d="M108.39,69.27A3.48,3.48,0,1,1,104,63.85l9.65-7.77A3.49,3.49,0,1,1,118,61.51Z"
                            />
                            <Path
                                fill="#817e89"
                                d="M119.08,57.46a3.91,3.91,0,0,0-.51-.85,3.48,3.48,0,0,0-4.9-.53L104,63.85a3.51,3.51,0,0,0-.07,5.37h0a3.49,3.49,0,0,1,1-4l9.65-7.77a3.5,3.5,0,0,1,4.44.06Z"
                            />
                        </Group>
                        <Group
                            id="Green_Button"
                            dataName="Green Button"
                        >
                            <Circle
                                fill="#66bc46"
                                cx="181.76"
                                cy="55.13"
                                r="10.04"
                            />
                            <Path
                                fill="#24733a"
                                d="M182.24,63.92A10,10,0,0,1,172.64,51a9.92,9.92,0,0,0-.91,4.15A10,10,0,0,0,191.37,58,10,10,0,0,1,182.24,63.92Z"
                            />
                            <Path
                                fill="#c0dd99"
                                d="M181.76,45.1a10,10,0,0,0-7.65,3.55,10,10,0,0,1,14,14.27,10,10,0,0,0-6.33-17.82Z"
                            />
                        </Group>
                        <Group
                            id="Blue_Button"
                            dataName="Blue Button"
                        >
                            <Circle
                                fill="#3953a4"
                                cx="206.01"
                                cy="35.66"
                                r="10.04"
                            />
                            <Path
                                fill="#1d3363"
                                d="M206.49,44.45a10,10,0,0,1-10-10,9.93,9.93,0,0,1,.44-2.9,9.92,9.92,0,0,0-.91,4.15,10,10,0,0,0,19.64,2.91A10,10,0,0,1,206.49,44.45Z"
                            />
                            <Path
                                fill="#6a98d0"
                                d="M206,25.62a10,10,0,0,0-7.65,3.56,10,10,0,0,1,14,14.27A10,10,0,0,0,206,25.62Z"
                            />
                        </Group>
                        <Group
                            id="Yellow_Button"
                            dataName="Yellow Button"
                        >
                            <Circle
                                fill="#fcee26"
                                cx="206.19"
                                cy="74.88"
                                r="10.04"
                            />
                            <Path
                                fill="#c9862c"
                                d="M206.67,83.67a10,10,0,0,1-10-10,9.93,9.93,0,0,1,.43-2.9,9.92,9.92,0,0,0-.91,4.15,10,10,0,0,0,19.64,2.91A10,10,0,0,1,206.67,83.67Z"
                            />
                            <Path
                                fill="#f7f1ae"
                                d="M206.19,64.84a10,10,0,0,0-7.65,3.56,10,10,0,0,1,14,14.27,10,10,0,0,0-6.33-17.83Z"
                            />
                        </Group>
                        <Group
                            id="Red_Button"
                            dataName="Red Button"
                        >
                            <Circle
                                fill="#ee2a51"
                                cx="230.69"
                                cy="54.76"
                                r="10.04"
                            />
                            <Path
                                fill="#991b2a"
                                d="M231.17,63.55a10,10,0,0,1-9.6-12.95,10,10,0,1,0,18.73,7.07A10,10,0,0,1,231.17,63.55Z"
                            />
                            <Path
                                fill="#e081b5"
                                d="M230.69,44.72A10,10,0,0,0,223,48.28,9.93,9.93,0,0,1,229.36,46,10,10,0,0,1,237,62.54a10,10,0,0,0-6.33-17.82Z"
                            />
                        </Group>
                        <Group
                            id="Start_Button"
                            dataName="Start Button"
                        >
                            <Path
                                fill="#323341"
                                d="M136.3,70.32a5,5,0,0,1-6.24-7.73l9.49-7.67a5,5,0,1,1,6.25,7.72Z"
                            />
                            <Path
                                fill="#565461"
                                d="M135.19,69.27a3.48,3.48,0,1,1-4.37-5.42l9.65-7.77a3.49,3.49,0,1,1,4.37,5.43Z"
                            />
                            <Path
                                fill="#817e89"
                                d="M145.87,57.46a3.47,3.47,0,0,0-5.4-1.38l-9.65,7.77a3.51,3.51,0,0,0-.07,5.37h0a3.48,3.48,0,0,1,1-4l9.64-7.77a3.5,3.5,0,0,1,4.44.06Z"
                            />
                        </Group>
                        <Group
                            id="Arrow_Keys"
                            dataName="Arrow Keys"
                        >
                            <Path
                                fill="#565461"
                                d="M74.05,46.29H60.94V33.09a3.37,3.37,0,0,0-3.35-3.36H46.91a3.37,3.37,0,0,0-3.35,3.36v13.2h-13a3.37,3.37,0,0,0-3.36,3.35V60.32a3.37,3.37,0,0,0,3.36,3.35h13V76.61A3.37,3.37,0,0,0,46.91,80H57.59a3.37,3.37,0,0,0,3.35-3.36V63.67H74.05a3.37,3.37,0,0,0,3.36-3.35V49.64A3.37,3.37,0,0,0,74.05,46.29Z"
                            />
                            <Path
                                fill="#323341"
                                d="M43.84,78H57a1.68,1.68,0,0,0,1.68-1.68V61.24H73.37a1.68,1.68,0,0,0,1.68-1.68V46.45a3.38,3.38,0,0,1,2.36,3.21V60.33a3.37,3.37,0,0,1-3.36,3.36H61v13.2a3.37,3.37,0,0,1-3.35,3.36H47A3.36,3.36,0,0,1,43.84,78Z"
                            />
                            <Path
                                fill="#323341"
                                d="M58.61,48.49V30h0a3.38,3.38,0,0,1,2.33,3.2V48.49Z"
                            />
                            <Path
                                fill="#323341"
                                d="M27.27,61.22a3.37,3.37,0,0,0,3.23,2.45H45.85V61.22Z"
                            />
                            <Path
                                fill="#817e89"
                                d="M77.29,48.73a3.37,3.37,0,0,0-3.24-2.45h-12v2.45Z"
                            />
                            <Path
                                fill="#817e89"
                                d="M60.74,31.94H47.57a1.68,1.68,0,0,0-1.68,1.68v15.1H31.21a1.69,1.69,0,0,0-1.68,1.68V63.51a3.37,3.37,0,0,1-2.36-3.21V49.63a3.37,3.37,0,0,1,3.36-3.35h13V33.08a3.37,3.37,0,0,1,3.35-3.36H57.59a3.36,3.36,0,0,1,3.15,2.22Z"
                            />
                            <Path
                                fill="#817e89"
                                d="M45.89,64.63V79.8h0a3.38,3.38,0,0,1-2.33-3.2v-12Z"
                            />
                            <Circle
                                fill="#323341"
                                cx="52.2"
                                cy="54.86"
                                r="5.67"
                            />
                            <Circle
                                fill="#565461"
                                cx="52.2"
                                cy="54.86"
                                r="1.69"
                            />
                            <Path
                                stroke="#323341"
                                strokeWidth="1.5px"
                                strokeMiterlimit="10"
                                fill="none"
                                d="M64.66,57.22c0,1.65,1.17,2.31,2.59,1.48l4-2.37c1.42-.84,1.39-2.15-.08-2.9l-3.86-2a1.67,1.67,0,0,0-2.66,1.62Z"
                            />
                            <Path
                                stroke="#323341"
                                strokeWidth="1.5px"
                                strokeMiterlimit="10"
                                fill="none"
                                d="M55.85,67.31c.92,0,1.3.66.83,1.45L53,75c-.47.79-1.2.77-1.63-.05l-3.21-6.2A.94.94,0,0,1,49,67.3Z"
                            />
                            <Path
                                stroke="#323341"
                                strokeWidth="1.5px"
                                strokeMiterlimit="10"
                                fill="none"
                                d="M48.92,42.71c-.92,0-1.29-.65-.82-1.45L51.81,35c.47-.79,1.2-.77,1.62.05l3.22,6.2a.94.94,0,0,1-.91,1.49Z"
                            />
                            <Path
                                stroke="#323341"
                                strokeWidth="1.5px"
                                strokeMiterlimit="10"
                                fill="none"
                                d="M39.9,58.54c0,.93-.66,1.3-1.45.83l-6.28-3.71c-.79-.47-.77-1.2,0-1.63l6.2-3.21a.94.94,0,0,1,1.49.91Z"
                            />
                        </Group>
                        <Circle
                            id="Arrow_Key_Ring"
                            dataName="Arrow Key Ring"
                            strokeMiterLimit="10"
                            strokeWidth="2px"
                            fill="none"
                            stroke="#817e89"
                            cx="52.44"
                            cy="54.89"
                            r="34.66"
                        />
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={lightbulbRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 91.24 132.32"
                    customCursorActive={customCursorActive}
                    width={LIGHTBULB_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.lightbulb].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.lightbulb].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.lightbulb].rotate}
                    delay={1 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.lightbulb}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.lightbulb,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: lightbulbRef,
                            name: INTERACTABLE_OBJECT.lightbulb,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: lightbulbRef,
                            name: INTERACTABLE_OBJECT.lightbulb,
                        }, e),
                    })}
                >
                    <InteractableObjectShadowGroup
                        id="Lightbulb_Shadow"
                        dataName="Lightbulb Shadow"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.lightbulb}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    >
                        <Path
                            fill="#0d1d38"
                            d="M36.61,123.67v4.94a3.72,3.72,0,0,0,3.71,3.71H58.73a3.72,3.72,0,0,0,3.71-3.71v-2.43h0v-2.51H36.61Z"
                        />
                        <Path
                            fill="#0d1d38"
                            d="M85.17,23.83c0-.08-.1-.15-.14-.23-.22-.34-.42-.68-.64-1C72.47,4.65,53.49,3.81,50.06,3.81H49c-3.5,0-22.76.77-34.47,18.77C1.57,42.44,11.43,63,11.43,63c.36.75.91,1.71,1.45,2.62,1.6,3,4.36,8.17,7.26,14.17a81,81,0,0,1,6.21,18.68h.32A1.85,1.85,0,0,0,25,100.32v3.4H23.3v4.83H25v3.1H23.3v4.83H25v5.33a1.85,1.85,0,0,0,1.85,1.85H72.24a1.86,1.86,0,0,0,1.86-1.85v-5.33h1.66v-4.83H74.1v-3.1h1.66v-4.83H74.1v-3.4a1.85,1.85,0,0,0-1.74-1.84h.25s2.33-10.9,6-18.68S87.74,63,87.74,63,97.11,43.22,85.17,23.83Z"
                        />
                    </InteractableObjectShadowGroup>
                    <Group
                        id="Lightbulb_Base"
                        dataName="Lightbulb Base"
                    >
                        <Rect
                            fill="#fff"
                            x="17.12"
                            y="94.66"
                            width="49.15"
                            height="25.2"
                            rx="1.86"
                        />
                        <Rect
                            fill="#565461"
                            x="15.47"
                            y="99.91"
                            width="52.46"
                            height="4.83"
                        />
                        <Rect
                            fill="#565461"
                            x="15.47"
                            y="107.84"
                            width="52.46"
                            height="4.83"
                        />
                        <Rect
                            fill="#817e89"
                            x="22.59"
                            y="99.91"
                            width="6.69"
                            height="4.83"
                        />
                        <Rect
                            fill="#817e89"
                            x="22.59"
                            y="107.84"
                            width="6.69"
                            height="4.83"
                        />
                        <Path
                            fill="#fff"
                            d="M54.61,124.8a3.72,3.72,0,0,1-3.71,3.71H32.49a3.72,3.72,0,0,1-3.71-3.71v-4.94H54.61Z"
                        />
                        <Rect
                            fill="#323341"
                            x="28.78"
                            y="119.86"
                            width="25.84"
                            height="2.51"
                        />
                        <Rect
                            fill="#565461"
                            x="35.1"
                            y="119.87"
                            width="4.18"
                            height="2.51"
                        />
                        <Path
                            fill="#fcee26"
                            d="M18.52,94.67A81.18,81.18,0,0,0,12.31,76c-4.52-9.34-8.7-16.78-8.7-16.78S-6.25,38.63,6.67,18.78,41.69,0,41.69,0,63.41-1,76.56,18.78s3.35,40.43,3.35,40.43-5.5,9-9.15,16.78-6,18.68-6,18.68Z"
                        />
                        <Path
                            fill="#f7f1ae"
                            d="M6.75,64.58S-3.11,44,9.8,24.15s35-18.77,35-18.77,21.26-1,34.44,18.16a41.88,41.88,0,0,0-2.72-4.76C63.4-1,41.68,0,41.68,0s-22.11-1.09-35,18.77S3.6,59.21,3.6,59.21C4.55,61.16,6.75,64.58,6.75,64.58Z"
                        />
                        <Path
                            fill="#f7f1ae"
                            d="M79.7,24.15c-.14-.21-.29-.4-.43-.61"
                        />
                        <Path
                            fill="none"
                            stroke="#817e89"
                            strokeMiterLimit="10"
                            strokeWidth="4px"
                            d="M29.08,94.78a87.11,87.11,0,0,0-2.27-18.87c-1.76-8.47-4.6-16.63-6-19.12a13.29,13.29,0,0,1-1.54-8.43c.46-3.13,3.46-5,5.58-5.38a7,7,0,0,1,5.72,1.17c1.77,1.25,3.34,3.33,3.32,5.59s-.89,5-3.32,5-3-2.88-2.94-5a7.17,7.17,0,0,1,2.94-5.59,8.2,8.2,0,0,1,5.64-1.75,12.37,12.37,0,0,1,5.41,1.67s3.23,2.52,3.26,5.24-1.14,5-3.2,5-3.19-2.47-3.14-5a6.21,6.21,0,0,1,3.08-5.24,10.85,10.85,0,0,1,5.52-1.67,8.35,8.35,0,0,1,5.46,1.75,8.06,8.06,0,0,1,3.11,5.76c0,2.34-1,4.81-3.11,4.85s-3.14-2.58-3.13-4.85a7.32,7.32,0,0,1,3.13-5.76,8.25,8.25,0,0,1,6-1.17,6.75,6.75,0,0,1,5.51,5.38c.68,3.51-.88,6.35-1.72,8.43a94.17,94.17,0,0,0-5.79,19.12c-1.83,9.78-2.3,14.7-2.32,18.8"
                        />
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={moleculeRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 145.98 73.83"
                    customCursorActive={customCursorActive}
                    width={MOLECULE_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.molecule].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.molecule].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.molecule].rotate}
                    delay={2 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.molecule}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.molecule,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: moleculeRef,
                            name: INTERACTABLE_OBJECT.molecule,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: moleculeRef,
                            name: INTERACTABLE_OBJECT.molecule,
                        }, e),
                    })}
                >
                    <InteractableObjectShadow
                        id="Molecule_Model_Shadow"
                        dataName="Molecule Model Shadow"
                        fill="#0d1d38"
                        d="M142.39,67.57l.17-.21.58-.8v0c.19-.28.37-.57.54-.86l.14-.24c.14-.25.27-.5.4-.75,0,0,.05-.08.07-.13h0a16.55,16.55,0,0,0-23.87-21.19,12.49,12.49,0,0,1,3-1.86l-1.16-.16L116.64,43,100.1,32.34l.4.2L100.12,30l-.21-1.3v0s0-.14,0-.34a22.08,22.08,0,1,0-43.63-4.84c0,.07,0,.14,0,.21l-.49.33-2,6.47L34.24,40.62l-7-2.08-.93.52s.17.12.46.37A16.56,16.56,0,0,0,4.61,61.68l.12.24a16.08,16.08,0,0,0,1.11,1.82L6,64c.21.29.42.57.65.84h0c.22.27.46.53.7.79l.18.19c.25.25.5.5.76.74h0c.27.24.54.46.81.68l.21.17c.28.21.56.42.86.62h0q.43.3.9.57l.23.13c.31.17.62.34.94.5h0l1,.43.25.1c.34.13.67.25,1,.36h0c.35.1.7.2,1,.28l.27.06q.54.12,1.08.21h0c.36.06.73.09,1.1.13l.28,0c.38,0,.75,0,1.13,0h0c.38,0,.75,0,1.13,0l.28,0c.37,0,.74-.07,1.11-.13h0q.54-.09,1.08-.21l.27-.06c.35-.08.7-.18,1.05-.28h0q.53-.17,1-.36l.25-.1,1-.43h0c.32-.16.63-.33.94-.5l.23-.13q.46-.27.9-.57h0c.29-.2.57-.41.85-.62l.21-.17c.28-.22.55-.44.81-.68h0c.26-.24.51-.49.76-.74l.18-.19c.24-.26.48-.52.7-.79h0c.23-.27.44-.55.65-.84l.15-.21a16.08,16.08,0,0,0,1.11-1.82l.12-.24a16.58,16.58,0,0,0,1.68-5.83A9,9,0,0,1,36,57l.05,0v0l.83-.77v0l.38-.35,1.69-6L58.39,39.62l7.23,2.07.13,0,.11.07.26.18.59.38.24.15.84.48.08,0,.78.4.28.13c.2.1.41.19.63.28l.28.13.77.3.15.06.92.31.27.08.69.2.32.08.72.17.26,0c.32.07.65.13,1,.18l.21,0,.8.1.33,0,.72,0,.32,0q.51,0,1,0h0c.47,0,.93,0,1.38-.05l.4,0,1-.1.37-.05c.45-.07.89-.14,1.32-.23l.15,0,1.15-.28.38-.11,1-.31.32-.1c.41-.15.81-.31,1.21-.48l.22-.1c.33-.14.65-.29,1-.45l.35-.18.9-.5,0,0a14.7,14.7,0,0,0,1.54-.5l.4-.16,3.37-1.14,16.5,10.63.53,5.52h0l.74,1.53a14.54,14.54,0,0,1,.68-4.64,17,17,0,0,0,.16,7.56h0a.43.43,0,0,1,0,.05c.06.23.12.45.19.68l.08.23.16.49a2.77,2.77,0,0,1,.1.26l.18.45c0,.09.07.17.11.26s.13.3.2.45l.11.24c.09.18.18.36.28.54l.06.12.37.65a1,1,0,0,1,.11.17l.29.45.16.23.27.39.17.22.3.38.17.21.35.4.14.16c.16.18.33.36.51.53a.3.3,0,0,0,.08.08l.45.43.19.17c.12.11.24.22.37.32l.22.18.37.29.22.17.41.29.2.14.55.35.08,0,.64.37.2.1a4.16,4.16,0,0,0,.47.24l.25.12.43.2.27.11.45.18.25.09q.27.11.54.18l.18.07.73.2.17,0,.58.14.27,0,.49.09.29,0,.49.06c.1,0,.2,0,.3,0l.53,0h.25l.8,0c.36,0,.72,0,1.08,0l.29,0c.3,0,.59-.06.89-.1l.19,0c.34-.05.68-.11,1-.19l.25-.06.81-.2.25-.07c.33-.1.65-.21,1-.33l.17-.07.8-.32.25-.12c.31-.14.61-.29.91-.45h0q.44-.24.87-.51l.23-.15c.24-.15.47-.31.7-.48l.15-.11c.27-.2.53-.4.79-.62l.19-.16.6-.54.18-.18q.36-.35.69-.72l.12-.13C142,68,142.21,67.8,142.39,67.57Z"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.molecule}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    />
                    <Group
                        id="Molecule_Model_Base"
                        dataName="Molecule Model Base"
                    >
                        <Circle
                            fill="#ee2a51"
                            cx="75.47"
                            cy="22.08"
                            r="22.08"
                        />
                        <Path
                            fill="#991b2a"
                            d="M83.73,31s9.49,6.18,3,10a22.1,22.1,0,0,1-23.94-.88c-5.71-4,0-9.41,0-9.41a5,5,0,0,1,0-1.72,1.81,1.81,0,0,1,.56-.91s.49,1.52.65,1.52.16-.72.66-.86,1.85.43,2.57.55,1.85-.24,2.88-.1a28.13,28.13,0,0,1,2.82,1,7.29,7.29,0,0,0,3-.05c.52-.16.72-.95,1-1s.43.34,1.11.31,1.38-.59,1.81-.46a3.77,3.77,0,0,1,1.31.86c.36.34.52.84.86,1A9.07,9.07,0,0,0,83.73,31Z"
                        />
                        <Path
                            fill="#ea5c78"
                            d="M85.47,7.8a5.71,5.71,0,0,1,4.9,4.41c.44,1.93.39,6.88-.28,7.33s-2.09.16-3.47.82S85.52,23.89,84.8,24,80.62,20.14,80,18.16a11.21,11.21,0,0,1,.16-7A4.84,4.84,0,0,1,85.47,7.8Z"
                        />
                        <Path
                            fill="#ea5c78"
                            d="M59,14.91c.38-2.32,3.3-5.62,3.3-5.62s2.1-2.1,3.86-1.71a4.33,4.33,0,0,1,3.2,3.69,23.29,23.29,0,0,1,0,7.77c-.5,2-2.48,5.23-3.75,5.67a3.62,3.62,0,0,1-3.31-.11,6.47,6.47,0,0,1-2.64-4C59.18,18.6,58.63,17.22,59,14.91Z"
                        />
                        <Circle
                            fill="#e3e3e5"
                            cx="126.57"
                            cy="55.84"
                            r="16.56"
                        />
                        <Path
                            fill="#fff"
                            d="M114.73,48.37c.48-1.26,2.21-2.93,3-2.87s.89.59,1.23,1.29,2.3,2,2.59,2.87a8.52,8.52,0,0,1,.07,4.51,4.06,4.06,0,0,1-1.91,2.19c-.74.5-1.2,1.2-1.91,1.16s-1.47-.86-2.26-2.26a9.49,9.49,0,0,1-1.29-3.82A5.93,5.93,0,0,1,114.73,48.37Z"
                        />
                        <Path
                            fill="#fff"
                            d="M132.46,44.23c.63-.44,1.73-.09,2.6.54a11,11,0,0,1,3,4,13.41,13.41,0,0,1,.27,6.8C137.8,57.2,137,58,136,58.11s-3-1.65-4-3a8.11,8.11,0,0,1-1.61-4.47A9,9,0,0,1,131,47.1,5.39,5.39,0,0,1,132.46,44.23Z"
                        />
                        <Path
                            fill="#817e89"
                            d="M136.16,67.2a25.19,25.19,0,0,0,5.28-4.07A16.56,16.56,0,0,1,110.53,60a18.61,18.61,0,0,0,5.34,6.7c3.39,2.37,6.35,3.1,10.56,3.19A16,16,0,0,0,136.16,67.2Z"
                        />
                        <Circle
                            fill="#e3e3e5"
                            cx="16.56"
                            cy="52.83"
                            r="16.56"
                        />
                        <Path
                            fill="#817e89"
                            d="M26.26,63.54a25.74,25.74,0,0,0,5.56-4.26,16.57,16.57,0,0,1-30.52,0,15.71,15.71,0,0,0,4.76,4.19,22.08,22.08,0,0,0,10.55,2.37A19.43,19.43,0,0,0,26.26,63.54Z"
                        />
                        <Path
                            fill="#fff"
                            d="M4.35,48a5.88,5.88,0,0,1,1.12-3c.72-1,2.16-2.17,3.26-2a3.89,3.89,0,0,1,2.43,2,11.33,11.33,0,0,1,1.18,3.55,5.4,5.4,0,0,1-.71,3.31,4.06,4.06,0,0,1-3,1.78,4,4,0,0,1-3.49-1.78A8,8,0,0,1,4.35,48Z"
                        />
                        <Path
                            fill="#fff"
                            d="M24.72,53.88c1.45-.12,2.4-2,2.93-3.42a7.07,7.07,0,0,0,.12-4.5c-.45-1.77-1.4-3.25-2.68-3.47s-2.39,1-2.92,2.86a10.94,10.94,0,0,0-.55,4.57C21.75,51.58,23.28,54,24.72,53.88Z"
                        />
                        <Path
                            fill="#b6b4ba"
                            d="M94,22.73c-.89-.1-4.83-.1-9,6.53s-2.58,11-2.15,11.5a2.8,2.8,0,0,0,2.12.74,14.32,14.32,0,0,0,3.26-.85c1-.38,3.28-1.61,3.28-1.61l17,10.94.53,5.52.74,1.53A17.31,17.31,0,0,1,113,46.87c3.7-5.32,7.47-6.78,7.47-6.78l-1.16-.16-5.57,1.63L97.21,30.88l-.16-3.62a10.7,10.7,0,0,0-.68-3.06A4,4,0,0,0,94,22.73Z"
                        />
                        <Path
                            fill="#565461"
                            d="M110.7,43.69a16.25,16.25,0,0,0-1.59,2,6.1,6.1,0,0,0-.82,2,5.72,5.72,0,0,0,.12,2.21L92,39.31l-6.2,2.09-1.62-.82a13.07,13.07,0,0,1,0-6.93,14.15,14.15,0,0,1,3-5.15l5,4.88a3.6,3.6,0,0,0-.85,1.17,4.8,4.8,0,0,0-.43,1.11l17,10.78s.3-.3,1-1S110.7,43.69,110.7,43.69Z"
                        />
                        <Polygon
                            fill="#565461"
                            points="97.64 31.11 96.47 30.52 96.68 25 97.26 28.6 97.64 31.11"
                        />
                        <Path
                            fill="#565461"
                            d="M108.94,55.52l.74,1.53a17.24,17.24,0,0,1,3.37-10.15A27.49,27.49,0,0,0,110,52.3,20.44,20.44,0,0,0,108.94,55.52Z"
                        />
                        <Group
                            opacity={0.99}
                        >
                            <Path
                                fill="#e3e3e5"
                                d="M87.65,27.65q.78-.86,1.62-1.65a11,11,0,0,1,1.82-1.48,8.22,8.22,0,0,1,2.18-1,2.92,2.92,0,0,1,1.29,0,3.55,3.55,0,0,1,1.18.52.38.38,0,0,1,.11.52.37.37,0,0,1-.52.1h0a2.65,2.65,0,0,0-.89-.44,2.2,2.2,0,0,0-1,0,7.39,7.39,0,0,0-2,.8,10.32,10.32,0,0,0-1.8,1.33c-.58.5-1.13,1-1.67,1.55a.17.17,0,0,1-.24,0A.17.17,0,0,1,87.65,27.65Z"
                            />
                        </Group>
                        <Path
                            fill="#e3e3e5"
                            d="M116.93,41.37c-.8.81-1.59,1.63-2.36,2.46s-1.52,1.67-2.19,2.56a15.81,15.81,0,0,0-1.73,2.85,30.41,30.41,0,0,0-1.3,3.11h0a.2.2,0,0,1-.25.12.19.19,0,0,1-.12-.22,16,16,0,0,1,1-3.3,13.92,13.92,0,0,1,1.76-3,27.54,27.54,0,0,1,5-4.72.1.1,0,0,1,.14,0A.1.1,0,0,1,116.93,41.37Z"
                        />
                        <Path
                            fill="#b6b4ba"
                            d="M23.46,37.63s3,2,6.39,8,3.26,9.86,3.26,9.86l1.25-1.18,1.7-6L55.54,38.19l7.23,2.07,1.11-.21s1.68-4-2.08-11.31-7.09-7.34-7.09-7.34l-1.8,1.22-2,6.47L31.38,39.19l-7-2.08Z"
                        />
                        <Path
                            fill="#565461"
                            d="M28,42.56s1.27,1.91,1.94,3.08c3.41,6,3.27,9.86,3.27,9.86l.83-.77A29.83,29.83,0,0,0,31,47C29.09,44,28,42.56,28,42.56Z"
                        />
                        <Path
                            fill="#565461"
                            d="M35,43.32l19.73-9.9a1.8,1.8,0,0,0-.27-.72,1.75,1.75,0,0,0-.61-.61l6-1.59a10.68,10.68,0,0,1,2.17,4.32,17.73,17.73,0,0,1,.69,5.44l-7.23-2.07L36.06,48.32a5.37,5.37,0,0,0,.19-1.9A18.1,18.1,0,0,0,35,43.32Z"
                        />
                        <Path
                            fill="#e3e3e5"
                            d="M54.05,22a2.13,2.13,0,0,1,.69.23c.19.1.37.2.54.31a5.62,5.62,0,0,1,1,.76,16.68,16.68,0,0,1,1.46,1.85c.89,1.26,1.72,2.56,2.53,3.87a.17.17,0,0,1-.06.25.18.18,0,0,1-.25-.05c-.86-1.27-1.74-2.53-2.67-3.74a17.7,17.7,0,0,0-1.47-1.72,5,5,0,0,0-1.75-1l-.1,0a.37.37,0,0,1,.12-.73Z"
                        />
                        <Path
                            fill="#e3e3e5"
                            d="M26.77,38.22a31.52,31.52,0,0,1,2.52,3.12,36,36,0,0,1,2.19,3.37A45.67,45.67,0,0,1,34.73,52a.22.22,0,0,1-.14.28.22.22,0,0,1-.28-.13c-.47-1.23-1-2.46-1.53-3.67S31.65,46.12,31,45A37.85,37.85,0,0,0,29,41.57c-.75-1.09-1.52-2.17-2.34-3.21a.1.1,0,0,1,0-.14A.11.11,0,0,1,26.77,38.22Z"
                        />
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={abacusRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 262.79 260.27"
                    customCursorActive={customCursorActive}
                    width={ABACUS_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.abacus].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.abacus].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.abacus].rotate}
                    delay={3 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.abacus}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.abacus,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: abacusRef,
                            name: INTERACTABLE_OBJECT.abacus,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: abacusRef,
                            name: INTERACTABLE_OBJECT.abacus,
                        }, e),
                    })}
                >
                    <InteractableObjectShadowGroup
                        id="Abacus_Shadow"
                        dataName="Abacus Shadow"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.abacus}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    >
                        <Path
                            fill="#0d1d38"
                            d="M3.67,5.78v252.9a1.59,1.59,0,0,0,1.59,1.59h7.39V4.19H5.26A1.59,1.59,0,0,0,3.67,5.78Z"
                        />
                        <Path
                            fill="#0d1d38"
                            d="M261.2,4.19H246.41a1.59,1.59,0,0,0-1.59,1.59v9.73h-5.57V10.43a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18A6.92,6.92,0,0,0,221,7.25,6.78,6.78,0,0,0,215,10.43a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18v5.08H88.85V10.43a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.94,6.94,0,0,0-6.08-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18v5.08h-6.5V5.78a1.59,1.59,0,0,0-1.59-1.59h-7.4V260.27h7.4a1.6,1.6,0,0,0,1.59-1.59V240h6.5v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,7,7,0,0,0,6.08-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V240H108.9v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,7,7,0,0,0,6.08-3.18V240h57.5v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V240h0v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V240h5.57v18.65a1.6,1.6,0,0,0,1.59,1.59H261.2a1.59,1.59,0,0,0,1.59-1.59V5.78A1.59,1.59,0,0,0,261.2,4.19ZM244.82,237.36h-5.57v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19v5.08h0v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19v5.08h-57.5v-5.08a7,7,0,0,0-6.08-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19v5.08H76.71v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a7,7,0,0,0-6.08-3.19,6.8,6.8,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19v5.08h-6.5v-21.8h6.5v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,7,7,0,0,0,6.08-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18v-5.08h55.52v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18v-5.08h0v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18v-5.08h34l-.08,5a6.77,6.77,0,0,0,6,3.28,6.94,6.94,0,0,0,6.12-3.08l.09-5.18h5.69Zm0-24.48h-5.65l.08-5a6.94,6.94,0,0,0-6-3.28,6.78,6.78,0,0,0-6.12,3.09l-.08,5.17h-34.1V207.8a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18v5.08H88.85V207.8a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.94,6.94,0,0,0-6.08-3.18,6.8,6.8,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18v5.08h-6.5v-22h6.5v5.09a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19h0a6.76,6.76,0,0,0,6.07,3.18,7,7,0,0,0,6.08-3.19v-5.08H81.63v5.09a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19v-5.08h0v5.09a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19v-5.08h72.64v5.09a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,221,199.1a6.94,6.94,0,0,0,6.07-3.19h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.19v-5.08h5.57Zm0-24.72h-5.57v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18A6.92,6.92,0,0,0,221,179.9a6.76,6.76,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18v5.08H105.91v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18v5.08H64.57v-5.08a6.94,6.94,0,0,0-6.08-3.18,6.76,6.76,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18v5.08h-6.5v-22h6.5v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.06-3.16,6.74,6.74,0,0,0,6,3.16,7,7,0,0,0,6.07-3.19v-5.08h61.8v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.06-3.16,6.74,6.74,0,0,0,6.05,3.16,7,7,0,0,0,6.07-3.19v-5.08h.14v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h14.14v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6-3.16,6.78,6.78,0,0,0,6.06,3.16,7,7,0,0,0,6.07-3.19v-5.08h.14v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h13.44v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h.14v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h5.57Zm0-24.72h-5.57v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18v5.08H227v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18v5.08H201.39v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18v5.08h-.14v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.8,6.8,0,0,0-6.06,3.15,7,7,0,0,0-6-3.15,6.76,6.76,0,0,0-6.07,3.18v5.08H150.72v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.07,3.18v5.08h-.14v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6.05,3.15,7,7,0,0,0-6.06-3.15,6.76,6.76,0,0,0-6.07,3.18v5.08H52.39v-5.08a6.92,6.92,0,0,0-6.07-3.18,6.76,6.76,0,0,0-6,3.15,7,7,0,0,0-6.06-3.15,6.76,6.76,0,0,0-6.07,3.18v5.08h-6.5V141.39H87v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h0v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19,6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19,6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19,6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h30.83v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h0v5.08a6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19,6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19,6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19,6.76,6.76,0,0,0,6.07,3.19,7,7,0,0,0,6.07-3.19v-5.08h5.57Zm0-24.72h-5.57v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19v5.08H147.72v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19,6.94,6.94,0,0,0-6.07-3.19A6.78,6.78,0,0,0,87,133.64v5.08H21.64v-22h6.5v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,7,7,0,0,0,6.08-3.18v-5.08h0v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18v-5.08H118.5v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18v-5.08h0v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18v-5.08H215v5.08a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.74,6.74,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18v-5.08h5.57Zm0-24.72h-5.57v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19V114H167.06v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19V114H76.71v-5.08a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a7,7,0,0,0-6.08-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19h0a6.94,6.94,0,0,0-6.07-3.19,6.78,6.78,0,0,0-6.07,3.19V114h-6.5V92h6.5V97a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,40.28,97h0a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,52.42,97h0a6.74,6.74,0,0,0,6.07,3.18A7,7,0,0,0,64.57,97V92H142.1V97a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,154.24,97h0a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,166.38,97h0a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,178.52,97V92h0V97a6.77,6.77,0,0,0,6.08,3.18A7,7,0,0,0,190.67,97V92h12.16V97a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,215,97h0a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,227.11,97h0a6.74,6.74,0,0,0,6.07,3.18A6.94,6.94,0,0,0,239.25,97V92h5.57Zm0-24.72h-5.57V84.2A6.94,6.94,0,0,0,233.18,81a6.78,6.78,0,0,0-6.07,3.19A6.94,6.94,0,0,0,221,81,6.78,6.78,0,0,0,215,84.2,6.94,6.94,0,0,0,208.9,81a6.78,6.78,0,0,0-6.07,3.19v5.08H190.67V84.2A7,7,0,0,0,184.6,81a6.81,6.81,0,0,0-6.08,3.19A6.94,6.94,0,0,0,172.45,81a6.78,6.78,0,0,0-6.07,3.19A6.94,6.94,0,0,0,160.31,81a6.78,6.78,0,0,0-6.07,3.19A6.94,6.94,0,0,0,148.17,81a6.78,6.78,0,0,0-6.07,3.19v5.08H64.57V84.2A7,7,0,0,0,58.49,81a6.78,6.78,0,0,0-6.07,3.19A6.94,6.94,0,0,0,46.35,81a6.78,6.78,0,0,0-6.07,3.19A6.94,6.94,0,0,0,34.21,81a6.78,6.78,0,0,0-6.07,3.19v5.08h-6.5V67.48h6.5v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V67.48h56.26v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18A6.94,6.94,0,0,0,133,72.56V67.48h82v5.08A6.76,6.76,0,0,0,221,75.74a6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V67.48h5.57Zm0-24.48h-5.57V59.72a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18h0A6.92,6.92,0,0,0,221,56.54,6.78,6.78,0,0,0,215,59.72V64.8H133V59.72a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18h0a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18V64.8H52.42V59.72a6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18,6.92,6.92,0,0,0-6.07-3.18,6.78,6.78,0,0,0-6.07,3.18V64.8h-6.5v-22H55.93v5.08A6.76,6.76,0,0,0,62,51a7,7,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,74.14,51a7,7,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,86.28,51a7,7,0,0,0,6.07-3.19V42.75h28.12v5.08A6.76,6.76,0,0,0,126.54,51a7,7,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,138.68,51a7,7,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,150.82,51a7,7,0,0,0,6.08-3.19V42.75h21.65v5.08A6.76,6.76,0,0,0,184.62,51a7,7,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,196.76,51a7,7,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,208.9,51,7,7,0,0,0,215,47.83h0A6.76,6.76,0,0,0,221,51a7,7,0,0,0,6.07-3.19h0A6.76,6.76,0,0,0,233.18,51a7,7,0,0,0,6.07-3.19V42.75h5.57Zm0-24.72h-5.57V35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,227.11,35,6.92,6.92,0,0,0,221,31.82,6.76,6.76,0,0,0,215,35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,202.83,35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,190.69,35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,178.55,35v5.08H156.9V35a6.94,6.94,0,0,0-6.08-3.18A6.78,6.78,0,0,0,144.75,35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,132.61,35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,120.47,35v5.08H92.35V35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,80.21,35a6.92,6.92,0,0,0-6.07-3.18A6.76,6.76,0,0,0,68.07,35,6.92,6.92,0,0,0,62,31.82,6.76,6.76,0,0,0,55.93,35v5.08H21.64V18.19h6.5v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,7,7,0,0,0,6.08-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V18.19h89.7v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V18.19h0v5.08a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18A6.94,6.94,0,0,0,215,23.27h0A6.76,6.76,0,0,0,221,26.45a6.94,6.94,0,0,0,6.07-3.18h0a6.76,6.76,0,0,0,6.07,3.18,6.94,6.94,0,0,0,6.07-3.18V18.19h5.57Z"
                        />
                    </InteractableObjectShadowGroup>
                    <Group
                        id="Abacus_Base"
                        dataName="Abacus Base"
                    >
                        <Group
                            id="Left_Post"
                            dataName="Left Post"
                        >
                            <Path
                                fill="#8c6037"
                                d="M18,254.49a1.59,1.59,0,0,1-1.59,1.59H9V0h7.39A1.59,1.59,0,0,1,18,1.59Z"
                            />
                            <Path
                                fill="#b07b57"
                                d="M9,256.08H1.59A1.59,1.59,0,0,1,0,254.49V1.59A1.6,1.6,0,0,1,1.59,0H9Z"
                            />
                        </Group>
                        <Group
                            id="Right_Post"
                            dataName="Right Post"
                        >
                            <Path
                                fill="#8c6037"
                                d="M259.13,254.49a1.6,1.6,0,0,1-1.6,1.59h-7.39V0h7.39a1.6,1.6,0,0,1,1.6,1.59Z"
                            />
                            <Path
                                fill="#b07b57"
                                d="M250.14,256.08h-7.39a1.6,1.6,0,0,1-1.6-1.59V1.59A1.6,1.6,0,0,1,242.75,0h7.39Z"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_1"
                            dataName="Bead Rod 1"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="11.33"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="12.66"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_2"
                            dataName="Bead Rod 2"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="35.9"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="37.23"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_3"
                            dataName="Bead Rod 3"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="60.62"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="61.95"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_4"
                            dataName="Bead Rod 4"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="85.09"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="86.43"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_5"
                            dataName="Bead Rod 5"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="109.81"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="111.15"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_6"
                            dataName="Bead Rod 6"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="134.53"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="135.87"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_7"
                            dataName="Bead Rod 7"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="159.26"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="160.59"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_8"
                            dataName="Bead Rod 8"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="183.98"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="185.31"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_9"
                            dataName="Bead Rod 9"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="208.7"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="210.03"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Bead_Rod_10"
                            dataName="Bead Rod 10"
                        >
                            <Rect
                                fill="#b9bbca"
                                x="17.93"
                                y="233.17"
                                width="223.27"
                                height="1.34"
                            />
                            <Rect
                                fill="#92928c"
                                x="17.93"
                                y="234.51"
                                width="223.27"
                                height="1.34"
                            />
                        </Group>
                        <Group
                            id="Blue_Beads"
                            dataName="Blue Beads"
                        >
                            <Group
                                id="Blue_Bead_1"
                                dataName="Blue Bead 1"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M24.48,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M36.32,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,36.32,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_2"
                                dataName="Blue Bead 2"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M36.62,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M48.46,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,48.46,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_3"
                                dataName="Blue Bead 3"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M48.76,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M60.6,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19A6.12,6.12,0,0,1,49,216.16a13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,60.6,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_4"
                                dataName="Blue Bead 4"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M60.9,203.62A6.78,6.78,0,0,1,67,200.43,6.94,6.94,0,0,1,73,203.62v12.83A7,7,0,0,1,67,219.64a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M72.75,209.23v6.85a6.3,6.3,0,0,1-5.82,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.58,11.58,0,0,0,72.75,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_5"
                                dataName="Blue Bead 5"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M73,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19A6.76,6.76,0,0,1,73,216.45Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M84.89,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19,6.14,6.14,0,0,1-5.82-3.11,13.5,13.5,0,0,0,6.83-1.6A11.58,11.58,0,0,0,84.89,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_6"
                                dataName="Blue Bead 6"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M140.71,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M152.55,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,152.55,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_7"
                                dataName="Blue Bead 7"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M152.85,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M164.69,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,164.69,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_8"
                                dataName="Blue Bead 8"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M165,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M176.83,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,176.83,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_9"
                                dataName="Blue Bead 9"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M177.13,203.62a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M189,209.23v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,189,209.23Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_10"
                                dataName="Blue Bead 10"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M223.45,203.52a6.76,6.76,0,0,1,6.12-3.08,6.92,6.92,0,0,1,6,3.28l-.21,12.83a7,7,0,0,1-6.12,3.09,6.76,6.76,0,0,1-6-3.29Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M235.2,209.32l-.11,6.86a6.28,6.28,0,0,1-5.86,3.09,6.12,6.12,0,0,1-5.76-3.2,13.44,13.44,0,0,0,6.85-1.49A11.58,11.58,0,0,0,235.2,209.32Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_11"
                                dataName="Blue Bead 11"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M24.48,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M36.32,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,36.32,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_12"
                                dataName="Blue Bead 12"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M36.62,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M48.46,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,48.46,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_13"
                                dataName="Blue Bead 13"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M48.76,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M60.6,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2A6.13,6.13,0,0,1,49,240.63a13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,60.6,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_14"
                                dataName="Blue Bead 14"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M60.9,228.09A6.76,6.76,0,0,1,67,224.91,6.92,6.92,0,0,1,73,228.09v12.83A7,7,0,0,1,67,244.11a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M72.75,233.7v6.85a6.32,6.32,0,0,1-5.82,3.2,6.13,6.13,0,0,1-5.81-3.12A13.4,13.4,0,0,0,68,239,11.58,11.58,0,0,0,72.75,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_15"
                                dataName="Blue Bead 15"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M105.24,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M117.08,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,117.08,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_16"
                                dataName="Blue Bead 16"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M117.38,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M129.22,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,129.22,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_17"
                                dataName="Blue Bead 17"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M187,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M198.87,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.58,11.58,0,0,0,198.87,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_18"
                                dataName="Blue Bead 18"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M199.16,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M211,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.58,11.58,0,0,0,211,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_19"
                                dataName="Blue Bead 19"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M211.3,228.09a6.76,6.76,0,0,1,6.07-3.18,6.94,6.94,0,0,1,6.08,3.18v12.83a7,7,0,0,1-6.08,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M223.15,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.51,11.51,0,0,0,223.15,233.7Z"
                                />
                            </Group>
                            <Group
                                id="Blue_Bead_20"
                                dataName="Blue Bead 20"
                            >
                                <Path
                                    fill="#29bfec"
                                    d="M223.45,228.09a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#207d8c"
                                    d="M235.29,233.7v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,233.7Z"
                                />
                            </Group>
                        </Group>
                        <Group
                            id="Green_Beads"
                            dataName="Green Beads"
                        >
                            <Group
                                id="Green_Bead_1"
                                dataName="Green Bead 1"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M24.48,154.18A6.78,6.78,0,0,1,30.55,151a6.91,6.91,0,0,1,6.07,3.19V167a6.94,6.94,0,0,1-6.07,3.18A6.76,6.76,0,0,1,24.48,167Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M36.32,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,36.32,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_2"
                                dataName="Green Bead 2"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M36.59,154.18A6.78,6.78,0,0,1,42.66,151a6.91,6.91,0,0,1,6.07,3.19V167a6.94,6.94,0,0,1-6.07,3.18A6.76,6.76,0,0,1,36.59,167Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M48.43,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,48.43,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_3"
                                dataName="Green Bead 3"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M110.53,154.18A6.78,6.78,0,0,1,116.6,151a6.91,6.91,0,0,1,6.07,3.19V167a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M122.37,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,122.37,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_4"
                                dataName="Green Bead 4"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M122.63,154.18a6.81,6.81,0,0,1,6.08-3.19,6.91,6.91,0,0,1,6.07,3.19V167a7,7,0,0,1-6.07,3.18,6.79,6.79,0,0,1-6.08-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M134.48,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,134.48,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_5"
                                dataName="Green Bead 5"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M134.92,154.18A6.78,6.78,0,0,1,141,151a6.91,6.91,0,0,1,6.07,3.19V167a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M146.76,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,146.76,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_6"
                                dataName="Green Bead 6"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M161.19,154.18a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.08,3.19V167a7,7,0,0,1-6.08,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M173,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,173,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_7"
                                dataName="Green Bead 7"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M173.3,154.18a6.78,6.78,0,0,1,6.07-3.19,6.91,6.91,0,0,1,6.07,3.19V167a6.94,6.94,0,0,1-6.07,3.18A6.76,6.76,0,0,1,173.3,167Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M185.15,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,185.15,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_8"
                                dataName="Green Bead 8"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M185.58,154.18a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.08,3.19V167a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M197.43,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,197.43,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_9"
                                dataName="Green Bead 9"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M211.16,154.18a6.78,6.78,0,0,1,6.07-3.19,6.91,6.91,0,0,1,6.07,3.19V167a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M223,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,223,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_10"
                                dataName="Green Bead 10"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M223.45,154.18a6.78,6.78,0,0,1,6.07-3.19,6.91,6.91,0,0,1,6.07,3.19V167a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M235.29,159.79v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,159.79Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_11"
                                dataName="Green Bead 11"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M24.48,178.9a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M36.32,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,36.32,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_12"
                                dataName="Green Bead 12"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M36.62,178.9a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M48.46,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,48.46,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_13"
                                dataName="Green Bead 13"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M48.76,178.9a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M60.6,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19A6.12,6.12,0,0,1,49,191.44a13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,60.6,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_14"
                                dataName="Green Bead 14"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M78,178.9A6.78,6.78,0,0,1,84,175.71a6.94,6.94,0,0,1,6.07,3.19v12.83A6.94,6.94,0,0,1,84,194.91,6.74,6.74,0,0,1,78,191.73Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M89.81,184.51v6.85A6.3,6.3,0,0,1,84,194.55a6.12,6.12,0,0,1-5.81-3.11,13.5,13.5,0,0,0,6.83-1.6A11.58,11.58,0,0,0,89.81,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_15"
                                dataName="Green Bead 15"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M90.1,178.9a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M102,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.14,6.14,0,0,1-5.82-3.11,13.5,13.5,0,0,0,6.83-1.6A11.58,11.58,0,0,0,102,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_16"
                                dataName="Green Bead 16"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M174.88,178.9a6.78,6.78,0,0,1,6.07-3.19A6.94,6.94,0,0,1,187,178.9v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M186.73,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,186.73,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_17"
                                dataName="Green Bead 17"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M187,178.9a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M198.87,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,198.87,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_18"
                                dataName="Green Bead 18"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M199.16,178.9a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M211,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,211,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_19"
                                dataName="Green Bead 19"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M211.3,178.9a6.78,6.78,0,0,1,6.07-3.19,7,7,0,0,1,6.08,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M223.15,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,223.15,184.51Z"
                                />
                            </Group>
                            <Group
                                id="Green_Bead_20"
                                dataName="Green Bead 20"
                            >
                                <Path
                                    fill="#3cb54a"
                                    d="M223.45,178.9a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19v12.83a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#0a8a45"
                                    d="M235.29,184.51v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,184.51Z"
                                />
                            </Group>
                        </Group>
                        <Group
                            id="Red_Beads"
                            dataName="Red Beads"
                        >
                            <Group
                                id="Red_Bead_1"
                                dataName="Red Bead 1"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M24.48,104.73a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M36.32,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.58,11.58,0,0,0,36.32,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_2"
                                dataName="Red Bead 2"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M36.62,104.73a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M48.46,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.58,11.58,0,0,0,48.46,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_3"
                                dataName="Red Bead 3"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M48.76,104.73a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M60.6,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19A6.13,6.13,0,0,1,49,117.28a13.52,13.52,0,0,0,6.83-1.6A11.58,11.58,0,0,0,60.6,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_4"
                                dataName="Red Bead 4"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M60.9,104.73A6.76,6.76,0,0,1,67,101.55,6.92,6.92,0,0,1,73,104.73v12.83A7,7,0,0,1,67,120.75a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M72.75,110.34v6.86a6.32,6.32,0,0,1-5.82,3.19,6.13,6.13,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.63,11.63,0,0,0,72.75,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_5"
                                dataName="Red Bead 5"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M114.83,104.73a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M126.68,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.63,11.63,0,0,0,126.68,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_6"
                                dataName="Red Bead 6"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M127,104.73a6.79,6.79,0,0,1,6.08-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.79,6.79,0,0,1-6.08-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M138.82,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.56,11.56,0,0,0,138.82,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_7"
                                dataName="Red Bead 7"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M139.12,104.73a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M151,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.56,11.56,0,0,0,151,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_8"
                                dataName="Red Bead 8"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M151.26,104.73a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M163.1,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.56,11.56,0,0,0,163.1,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_9"
                                dataName="Red Bead 9"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M211.3,104.73a6.76,6.76,0,0,1,6.07-3.18,6.94,6.94,0,0,1,6.08,3.18v12.83a7,7,0,0,1-6.08,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M223.15,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.56,11.56,0,0,0,223.15,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_10"
                                dataName="Red Bead 10"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M223.45,104.73a6.76,6.76,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.83a7,7,0,0,1-6.07,3.19,6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M235.29,110.34v6.86a6.29,6.29,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.56,11.56,0,0,0,235.29,110.34Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_11"
                                dataName="Red Bead 11"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M83.35,129.45a6.81,6.81,0,0,1,6.08-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.79,6.79,0,0,1-6.08-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M95.2,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19A6.13,6.13,0,0,1,83.58,142a13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,95.2,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_12"
                                dataName="Red Bead 12"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M95.5,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M107.34,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19A6.13,6.13,0,0,1,95.72,142a13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,107.34,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_13"
                                dataName="Red Bead 13"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M107.64,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M119.48,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,119.48,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_14"
                                dataName="Red Bead 14"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M119.78,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M131.62,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19A6.13,6.13,0,0,1,120,142a13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,131.62,135.06Z"
                                />
                            </Group>
                            <Group
                                d="Red_Bead_15"
                                dataName="Red Bead 15"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M131.92,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M143.76,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,143.76,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_16"
                                dataName="Red Bead 16"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M174.88,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M186.73,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19A6.16,6.16,0,0,1,175.1,142a13.5,13.5,0,0,0,6.83-1.6A11.58,11.58,0,0,0,186.73,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_17"
                                dataName="Red Bead 17"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M187,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M198.87,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,198.87,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_18"
                                dataName="Red Bead 18"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M199.16,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M211,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,211,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_19"
                                dataName="Red Bead 19"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M211.3,129.45a6.78,6.78,0,0,1,6.07-3.18,6.94,6.94,0,0,1,6.08,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M223.15,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,223.15,135.06Z"
                                />
                            </Group>
                            <Group
                                id="Red_Bead_20"
                                dataName="Red Bead 20"
                            >
                                <Path
                                    fill="#ee2a51"
                                    d="M223.45,129.45a6.78,6.78,0,0,1,6.07-3.18,6.92,6.92,0,0,1,6.07,3.18v12.84a6.94,6.94,0,0,1-6.07,3.18,6.76,6.76,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#991b2a"
                                    d="M235.29,135.06v6.86a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,135.06Z"
                                />
                            </Group>
                        </Group>
                        <Group
                            id="Orange_Beads"
                            dataName="Orange Beads"
                        >
                            <Group
                                id="Orange_Bead_10"
                                dataName="Orange Bead 10"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M138.44,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,150.58,80V92.84A7,7,0,0,1,144.51,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M150.28,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.51,11.51,0,0,0,150.28,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_11"
                                dataName="Orange Bead 11"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M150.58,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,162.72,80V92.84A7,7,0,0,1,156.65,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M162.42,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,162.42,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_12"
                                dataName="Orange Bead 12"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M162.72,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,174.86,80V92.84A7,7,0,0,1,168.79,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M174.56,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,174.56,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_13"
                                dataName="Orange Bead 13"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M174.86,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,187,80V92.84A7,7,0,0,1,180.93,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M186.7,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.4,13.4,0,0,0,6.83-1.6A11.53,11.53,0,0,0,186.7,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_14"
                                dataName="Orange Bead 14"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M199.16,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,211.3,80V92.84A7,7,0,0,1,205.23,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M211,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.58,11.58,0,0,0,211,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_15"
                                dataName="Orange Bead 15"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M211.3,80a6.76,6.76,0,0,1,6.07-3.18A6.94,6.94,0,0,1,223.45,80V92.84A7,7,0,0,1,217.37,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M223.15,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.58,11.58,0,0,0,223.15,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_16"
                                dataName="Orange Bead 16"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M223.45,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,235.59,80V92.84A7,7,0,0,1,229.52,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M235.29,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12,13.35,13.35,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_7"
                                dataName="Orange Bead 7"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M24.48,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,36.62,80V92.84A7,7,0,0,1,30.55,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M36.32,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12A13.4,13.4,0,0,0,31.53,91,11.53,11.53,0,0,0,36.32,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_8"
                                dataName="Orange Bead 8"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M36.62,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,48.76,80V92.84A7,7,0,0,1,42.69,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M48.46,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2,6.13,6.13,0,0,1-5.81-3.12A13.4,13.4,0,0,0,43.67,91,11.53,11.53,0,0,0,48.46,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_9"
                                dataName="Orange Bead 9"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M48.76,80a6.76,6.76,0,0,1,6.07-3.18A6.92,6.92,0,0,1,60.9,80V92.84A7,7,0,0,1,54.83,96a6.76,6.76,0,0,1-6.07-3.19Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M60.6,85.62v6.85a6.29,6.29,0,0,1-5.81,3.2A6.13,6.13,0,0,1,49,92.55,13.4,13.4,0,0,0,55.81,91,11.53,11.53,0,0,0,60.6,85.62Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_1"
                                dataName="Orange Bead 1"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M24.48,55.54a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V68.37a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M36.32,61.15V68a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,36.32,61.15Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_2"
                                dataName="Orange Bead 2"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M36.62,55.54a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V68.37a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M48.46,61.15V68a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,48.46,61.15Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_3"
                                dataName="Orange Bead 3"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M105,55.54a6.81,6.81,0,0,1,6.08-3.19,6.94,6.94,0,0,1,6.07,3.19V68.37a6.94,6.94,0,0,1-6.07,3.18A6.77,6.77,0,0,1,105,68.37Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M116.86,61.15V68a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,116.86,61.15Z"
                                />
                            </Group>
                            <Group
                                d="Orange_Bead_4"
                                dataName="Orange Bead 4"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M117.16,55.54a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V68.37a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M129,61.15V68a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,129,61.15Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_5"
                                dataName="Orange Bead 5"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M211.3,55.54a6.78,6.78,0,0,1,6.07-3.19,7,7,0,0,1,6.08,3.19V68.37a7,7,0,0,1-6.08,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M223.15,61.15V68a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,223.15,61.15Z"
                                />
                            </Group>
                            <Group
                                id="Orange_Bead_6"
                                dataName="Orange Bead 6"
                            >
                                <Path
                                    fill="#f47421"
                                    d="M223.45,55.54a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V68.37a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#cc3c27"
                                    d="M235.29,61.15V68a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,61.15Z"
                                />
                            </Group>
                        </Group>
                        <Group
                            id="Yellow_Beads"
                            dataName="Yellow Beads"
                        >
                            <Group
                                id="Yellow_Bead_1"
                                dataName="Yellow Bead 1"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M24.48,6.25a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V19.08a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M36.32,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,36.32,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_2"
                                dataName="Yellow Bead 2"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M36.62,6.25a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V19.08a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M48.46,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,48.46,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_3"
                                dataName="Yellow Bead 3"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M48.76,6.25a6.78,6.78,0,0,1,6.07-3.19A6.94,6.94,0,0,1,60.9,6.25V19.08a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M60.6,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19A6.12,6.12,0,0,1,49,18.79a13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,60.6,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_4"
                                dataName="Yellow Bead 4"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M60.9,6.25A6.78,6.78,0,0,1,67,3.06,6.94,6.94,0,0,1,73,6.25V19.08A6.94,6.94,0,0,1,67,22.26a6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M72.75,11.86v6.85a6.3,6.3,0,0,1-5.82,3.19,6.12,6.12,0,0,1-5.81-3.11A13.52,13.52,0,0,0,68,17.19,11.58,11.58,0,0,0,72.75,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_5"
                                dataName="Yellow Bead 5"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M73,6.25a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V19.08a6.94,6.94,0,0,1-6.07,3.18A6.74,6.74,0,0,1,73,19.08Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M84.89,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.14,6.14,0,0,1-5.82-3.11,13.5,13.5,0,0,0,6.83-1.6A11.58,11.58,0,0,0,84.89,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_6"
                                dataName="Yellow Bead 6"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M174.88,6.25A6.78,6.78,0,0,1,181,3.06,6.94,6.94,0,0,1,187,6.25V19.08A6.94,6.94,0,0,1,181,22.26a6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M186.73,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,186.73,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_7"
                                dataName="Yellow Bead 7"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M187,6.25a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V19.08a6.94,6.94,0,0,1-6.07,3.18A6.74,6.74,0,0,1,187,19.08Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M198.87,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,198.87,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_8"
                                dataName="Yellow Bead 8"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M199.16,6.25a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V19.08a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M211,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,211,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_9"
                                dataName="Yellow Bead 9"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M211.3,6.25a6.78,6.78,0,0,1,6.07-3.19,7,7,0,0,1,6.08,3.19V19.08a7,7,0,0,1-6.08,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M223.15,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,223.15,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_10"
                                dataName="Yellow Bead 10"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M223.45,6.25a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V19.08a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M235.29,11.86v6.85a6.28,6.28,0,0,1-5.81,3.19,6.12,6.12,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,11.86Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_11"
                                dataName="Yellow Bead 11"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M52.26,30.82a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M64.11,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,64.11,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_12"
                                dataName="Yellow Bead 12"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M64.4,30.82a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M76.25,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,76.25,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_13"
                                dataName="Yellow Bead 13"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M76.54,30.82a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M88.39,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,88.39,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_14"
                                dataName="Yellow Bead 14"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M116.81,30.82a6.78,6.78,0,0,1,6.07-3.19A6.94,6.94,0,0,1,129,30.82V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M128.65,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19A6.13,6.13,0,0,1,117,43.36a13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,128.65,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_15"
                                dataName="Yellow Bead 15"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M129,30.82A6.78,6.78,0,0,1,135,27.63a6.94,6.94,0,0,1,6.07,3.19V43.65A6.94,6.94,0,0,1,135,46.83,6.74,6.74,0,0,1,129,43.65Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M140.79,36.43v6.85A6.28,6.28,0,0,1,135,46.47a6.13,6.13,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,140.79,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_16"
                                dataName="Yellow Bead 16"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M141.09,30.82a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M152.93,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.52,13.52,0,0,0,6.83-1.6A11.53,11.53,0,0,0,152.93,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_17"
                                dataName="Yellow Bead 17"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M174.88,30.82A6.78,6.78,0,0,1,181,27.63,6.94,6.94,0,0,1,187,30.82V43.65A6.94,6.94,0,0,1,181,46.83a6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M186.73,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,186.73,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_18"
                                dataName="Yellow Bead 18"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M187,30.82a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18A6.74,6.74,0,0,1,187,43.65Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M198.87,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,198.87,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_19"
                                dataName="Yellow Bead 19"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M199.16,30.82a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M211,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,211,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_20"
                                dataName="Yellow Bead 20"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M211.3,30.82a6.78,6.78,0,0,1,6.07-3.19,7,7,0,0,1,6.08,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M223.15,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.58,11.58,0,0,0,223.15,36.43Z"
                                />
                            </Group>
                            <Group
                                id="Yellow_Bead_21"
                                dataName="Yellow Bead 21"
                            >
                                <Path
                                    fill="#fcee26"
                                    d="M223.45,30.82a6.78,6.78,0,0,1,6.07-3.19,6.94,6.94,0,0,1,6.07,3.19V43.65a6.94,6.94,0,0,1-6.07,3.18,6.74,6.74,0,0,1-6.07-3.18Z"
                                />
                                <Path
                                    fill="#c9862c"
                                    d="M235.29,36.43v6.85a6.28,6.28,0,0,1-5.81,3.19,6.13,6.13,0,0,1-5.81-3.11,13.48,13.48,0,0,0,6.82-1.6A11.51,11.51,0,0,0,235.29,36.43Z"
                                />
                            </Group>
                        </Group>
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={magnetRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 150 108.14"
                    customCursorActive={customCursorActive}
                    width={MAGNET_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.magnet].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.magnet].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.magnet].rotate}
                    delay={4 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.magnet}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.magnet,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: magnetRef,
                            name: INTERACTABLE_OBJECT.magnet,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: magnetRef,
                            name: INTERACTABLE_OBJECT.magnet,
                        }, e),
                    })}
                >
                    <InteractableObjectShadow
                        id="Magnet_Shadow"
                        dataName="Magnet Shadow"
                        fill="#0d1d38"
                        d="M70.58,108.14S20.15,83.61,14.23,80.75C4.77,76.17-1.9,66,.49,55.2S10.31,37.72,20.61,32A88.56,88.56,0,0,1,60.48,21.69c15.52-.36,32.6,2.57,41.93,8.07C111.5,35.11,150,62.23,150,62.23l-15.1,9S101.05,48.41,92.52,42.62s-17.36-8.2-28.64-8.25-23.61,4-30.81,8.87S22.47,53.65,22.6,58.17s1.34,10,10.78,14.8S86,99.27,86,99.27Z"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.magnet}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    />
                    <Group
                        id="Magnet_Base"
                        dataName="Magnet Base"
                    >
                        <Path
                            fill="#f9fdfe"
                            d="M70.62,86.48S20.2,62,14.28,59.09C4.81,54.51-1.86,44.29.53,33.54s9.82-17.49,20.13-23.17A88.4,88.4,0,0,1,60.53,0C76.05-.33,93.12,2.6,102.46,8.09,111.55,13.45,150,40.57,150,40.57l-15.1,9S101.09,26.74,92.57,21,75.21,12.76,63.93,12.7s-23.61,4-30.82,8.88S22.52,32,22.64,36.51,24,46.51,33.42,51.3,86,77.61,86,77.61Z"
                        />
                        <Polygon
                            fill="#ee2a51"
                            points="55.25 79.01 71.28 70.28 86.03 77.61 70.62 86.48 55.25 79.01"
                        />
                        <Polygon
                            fill="#ea5c78"
                            points="70.61 101.17 70.62 86.48 86.02 77.61 86.02 92.3 70.61 101.17"
                        />
                        <Polygon
                            fill="#ee2a51"
                            points="122.09 40.97 137.6 31.87 150 40.56 134.9 49.59 122.09 40.97"
                        />
                        <Polygon
                            fill="#ea5c78"
                            points="149.99 40.56 134.9 49.59 134.89 64.29 149.99 55.26 149.99 40.56"
                        />
                        <Path
                            fill="#6a98d0"
                            d="M70.58,101.13S20.16,76.6,14.24,73.74C4.78,69.16,1.34,61.44.12,48.14c-.15-1.66,0-8.35,0-8.79C1.17,50,8.37,56.2,14.25,59.05S70.62,86.48,70.62,86.48Z"
                        />
                        <Path
                            fill="#6a98d0"
                            d="M134.86,64.25S101.05,41.4,92.53,35.61s-17.36-8.2-28.64-8.25-23.61,4-30.82,8.87a28.91,28.91,0,0,0-8.25,8,14.25,14.25,0,0,1-2.2-7.78c-.13-4.52,3.26-10,10.47-14.94s19.53-8.92,30.81-8.87S84,15.12,92.54,20.91,134.9,49.59,134.9,49.59Z"
                        />
                        <Polygon
                            fill="#991b2a"
                            points="70.63 86.47 70.62 101.17 55.26 93.65 55.26 79 70.63 86.47"
                        />
                        <Polygon
                            fill="#991b2a"
                            points="122.13 40.95 122.1 55.61 134.89 64.29 134.9 49.59 122.13 40.95"
                        />
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={puzzleBlueRef}
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    viewBox="0 0 57.38 100"
                    width={PUZZLE_BLUE_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleBlue].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleBlue].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleBlue].rotate}
                    delay={5 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleBlue}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.puzzleBlue,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: puzzleBlueRef,
                            name: INTERACTABLE_OBJECT.puzzleBlue,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: puzzleBlueRef,
                            name: INTERACTABLE_OBJECT.puzzleBlue,
                        }, e),
                    })}
                >
                    <InteractableObjectShadow
                        id="Blue_Puzzle_Piece_Shadow"
                        dataName="Blue Puzzle Piece Shadow"
                        fill="#0d1d38"
                        d="M2.51,45.24a2.51,2.51,0,0,0,.79,1.83c.55.59,1.1.69,2.27.35a20.69,20.69,0,0,0,4.64-2.36,8.1,8.1,0,0,1,4.11-1.31,11.93,11.93,0,0,1,4.12.7,5.84,5.84,0,0,1,3.14,2.85,17.77,17.77,0,0,1,1.18,6.16,9.62,9.62,0,0,1-2,5.81,9.45,9.45,0,0,1-5,2.85,9.37,9.37,0,0,1-5.86-.87,31.09,31.09,0,0,1-4.11-2.89,2.25,2.25,0,0,0-2-.35,2.11,2.11,0,0,0-1.35,1.31A41.82,41.82,0,0,0,1.2,69c-.07,4.31-.1,11,.44,11.55s7-.45,9.27-.44,8.73.26,9.54.44a3.34,3.34,0,0,1,1.93.92A2.58,2.58,0,0,1,23,83.67c-.21.84-2.42,3.55-3,5.32a11.67,11.67,0,0,0,.2,6.4,9.18,9.18,0,0,0,4,4c1.64.77,5.89.73,7.61.06a9.21,9.21,0,0,0,4.73-4,8.65,8.65,0,0,0,.35-6.65c-.77-2.4-2.56-4.18-2.76-4.84a3,3,0,0,1-.09-2,4.39,4.39,0,0,1,2.38-1.68,72.88,72.88,0,0,1,10-.65c3,0,9.54,1.44,10.42.9s.42-7.22.35-11.09-.47-9.13-.62-9.87a2.9,2.9,0,0,0-1.65-1.92,3.17,3.17,0,0,0-2.29.32,35.57,35.57,0,0,1-5.46,2.33,6.85,6.85,0,0,1-6.42-.93A10.41,10.41,0,0,1,37.6,53.9c-.43-2-.59-5.14.35-7.26s3.27-3.72,6.51-3.8,5.35,2,6.44,2.82a5.71,5.71,0,0,0,3.67,1.4,2.86,2.86,0,0,0,2.28-2.27c.43-1.4.34-3.52.29-8.34S56.8,26,56.42,25.62s-5.5.34-8.37.74a70.69,70.69,0,0,1-7.77.63,10.71,10.71,0,0,1-4.73-1.49,4.26,4.26,0,0,1-1.24-3.18,8.75,8.75,0,0,1,.58-3.07c.43-1.09,2.6-4.15,3.17-5.57a10.23,10.23,0,0,0,.52-5.09,9.25,9.25,0,0,0-2-4.86A9,9,0,0,0,30.69.52c-2.49-.29-5.57-.17-8,1.75A9.16,9.16,0,0,0,18.92,10c0,3.5,3.11,8.15,3.63,9.12a4.29,4.29,0,0,1,.52,3,4.92,4.92,0,0,1-1.71,2.44,5.43,5.43,0,0,1-2.6.93,62.92,62.92,0,0,1-8,.47c-4.24-.06-7.84-1.21-8.47-.65S2,33.61,2.06,35.72,2.42,44.34,2.51,45.24Z"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleBlue}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    />
                    <Group
                        id="Blue_Puzzle_Piece_Base"
                        dataName="Blue Puzzle Piece Base"
                    >
                        <Path
                            fill="#20aeda"
                            d="M1.35,44.83a2.5,2.5,0,0,0,.79,1.84c.55.59,1.1.69,2.27.35a20.91,20.91,0,0,0,4.64-2.36,8,8,0,0,1,4.11-1.31,11.6,11.6,0,0,1,4.11.7,5.79,5.79,0,0,1,3.15,2.85,17.76,17.76,0,0,1,1.17,6.15,9.56,9.56,0,0,1-2,5.82,9.49,9.49,0,0,1-5,2.85,9.44,9.44,0,0,1-5.86-.87A31.09,31.09,0,0,1,4.59,58a2.22,2.22,0,0,0-2-.35,2.07,2.07,0,0,0-1.35,1.31A41.24,41.24,0,0,0,0,68.55C0,72.86-.06,79.6.47,80.1s7-.46,9.28-.44,8.73.25,9.54.44a3.31,3.31,0,0,1,1.93.92,2.58,2.58,0,0,1,.65,2.25c-.21.83-2.42,3.55-3,5.32A11.73,11.73,0,0,0,19.08,95a9.23,9.23,0,0,0,4,4c1.64.77,5.89.73,7.61.06a9.26,9.26,0,0,0,4.73-4,8.7,8.7,0,0,0,.35-6.65C35,86,33.18,84.23,33,83.57a3,3,0,0,1-.09-2,4.33,4.33,0,0,1,2.38-1.67,72.76,72.76,0,0,1,10-.65c3,0,9.54,1.44,10.42.89S56.11,73,56,69.09s-.47-9.12-.62-9.86a3,3,0,0,0-1.65-1.93,3.27,3.27,0,0,0-2.29.32A34.56,34.56,0,0,1,46,60,6.81,6.81,0,0,1,39.6,59a10.41,10.41,0,0,1-3.16-5.53c-.43-2-.6-5.15.35-7.27s3.27-3.71,6.51-3.79,5.35,2,6.44,2.82a5.71,5.71,0,0,0,3.67,1.4,2.87,2.87,0,0,0,2.28-2.28C56.12,43,56,40.86,56,36s-.33-10.47-.71-10.83-5.51.35-8.37.75a70.69,70.69,0,0,1-7.77.63,10.71,10.71,0,0,1-4.73-1.49,4.26,4.26,0,0,1-1.24-3.18,8.9,8.9,0,0,1,.57-3.07c.44-1.1,2.61-4.15,3.18-5.57a10.25,10.25,0,0,0,.52-5.1,9.33,9.33,0,0,0-2-4.85A9,9,0,0,0,29.52.12C27-.17,24,0,21.54,1.87a9.16,9.16,0,0,0-3.78,7.77c0,3.5,3.11,8.14,3.63,9.12a4.26,4.26,0,0,1,.52,3,5,5,0,0,1-1.71,2.45,5.43,5.43,0,0,1-2.6.93,65,65,0,0,1-8,.47c-4.24-.06-7.84-1.21-8.47-.65S.87,33.2.9,35.32,1.26,43.94,1.35,44.83Z"
                        />
                        <Path
                            fill="#29bfec"
                            d="M22.49,3.5c1.72-1.2,6.3-2.45,9.81,0s3.29,8,2.45,10-2.6,3.68-3.39,5.53-1.41,4.17-.77,5.91c.94,2.53,3.24,3.27,5.52,3.41s5.09.09,6.94,0a74,74,0,0,0,7.54-1,3.35,3.35,0,0,1,2.26.29,4.57,4.57,0,0,1,.93,2c0,.23,0,4.2.1,6.76a20.85,20.85,0,0,1-.5,5.73,1,1,0,0,1-1.23.57,15.39,15.39,0,0,0-1.61-.66c-.78-.21-4.94-2.26-8.24-1.84s-5.27,1.29-7,3.39-2,5.31-1.84,7.66a21.3,21.3,0,0,0,1.22,6.5,9.4,9.4,0,0,0,4.4,4.56c2,1,4.9.51,6.52.19a32.78,32.78,0,0,0,5.28-2A2.76,2.76,0,0,1,53,60.24a3.65,3.65,0,0,1,1.1,2.19c.06.65-.36,4.9-.29,7.13s.12,6,0,6.72a1.73,1.73,0,0,1-1,1.44c-.56.29-1.81-.06-2.6-.19a81.78,81.78,0,0,0-8.53-.39c-2.83,0-6,.36-7.84,1.21s-2.69,1.56-3,2.57a5.12,5.12,0,0,0,0,2.9,29.91,29.91,0,0,0,2.4,4.51A7.48,7.48,0,0,1,33.74,94a6.21,6.21,0,0,1-3.63,3.24,15,15,0,0,1-5.18.32A5.59,5.59,0,0,1,21.34,95,7.32,7.32,0,0,1,20.21,91a8,8,0,0,1,1.13-3.9c.62-1.11,1.81-2.17,2.28-3.36a3.49,3.49,0,0,0-.09-3,3.33,3.33,0,0,0-1.76-2,16.32,16.32,0,0,0-4.68-1.12c-1.48-.12-5.13-.22-7.31-.2s-5.8.27-6.25.3a2.76,2.76,0,0,1-1.72-.36C1.39,77,1.38,75.75,1.37,75s-.14-4.35-.15-5.82a51.75,51.75,0,0,1,.37-5.56c.12-.59.58-3.47,1.64-3.63s2.1,1.13,2.55,1.55a13.06,13.06,0,0,0,2.79,2,13.05,13.05,0,0,0,5,.94,11,11,0,0,0,4.75-1.59,16.42,16.42,0,0,0,4.13-4.39,24.11,24.11,0,0,0,1.4-7.21,12.77,12.77,0,0,0-2.18-6.56,11.55,11.55,0,0,0-6.43-3.11A17.25,17.25,0,0,0,9,42.52,29.85,29.85,0,0,0,5.46,45c-.56.41-.88.49-1.36.3s-.59-.37-.74-1.07a49.19,49.19,0,0,1-.63-8A66,66,0,0,1,3.22,28a.79.79,0,0,1,.72-.7,3.18,3.18,0,0,1,1.23.19,59.24,59.24,0,0,0,8.1.58c3,0,7.38-1,9.38-3.42s1.8-4,1.32-5.63a16.7,16.7,0,0,0-2.37-4.57,11,11,0,0,1-1.8-4.21C19.46,8.59,20.78,4.69,22.49,3.5Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M3.42,29.36c-.16,2.1-.23,4.23-.19,6.33a24.49,24.49,0,0,0,.2,3.11c.14,1,.31,2.07.51,3.1.1.51.2,1,.32,1.52l.09.34a.61.61,0,0,1,.05.11.35.35,0,0,0,.15.13s0,0,0,0h0a2.78,2.78,0,0,0,.38-.22c.17-.11.36-.26.57-.42a7.85,7.85,0,0,1,.75-.48,11.14,11.14,0,0,1,3.23-1.21,18.42,18.42,0,0,1,3.29-.24.36.36,0,0,1,.08.71h0a24.54,24.54,0,0,0-3,.84,9.75,9.75,0,0,0-2.49,1.54c-.19.15-.37.31-.53.46a8.49,8.49,0,0,1-.68.56,3.09,3.09,0,0,1-1,.51,2.07,2.07,0,0,1-1.57-.19,2.46,2.46,0,0,1-.93-1,2.3,2.3,0,0,1-.23-.64c0-.16-.05-.3-.07-.44-.06-.56-.11-1.09-.15-1.63C2.07,41.07,2,40,2,38.91a27,27,0,0,1,.1-3.25c.19-2.14.44-4.25.72-6.37a.32.32,0,0,1,.64.07Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M4.74,26.87c1.42.18,2.87.34,4.3.44a28.76,28.76,0,0,0,4.25.06,23.51,23.51,0,0,0,4.19-.6,21.55,21.55,0,0,0,4-1.39.5.5,0,0,1,.66.25.49.49,0,0,1-.19.63,16.55,16.55,0,0,1-4.12,1.83,21,21,0,0,1-4.44.75A24.86,24.86,0,0,1,8.9,28.7a30.81,30.81,0,0,1-4.36-.85.5.5,0,0,1,.19-1Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M19.51,11.09a10.29,10.29,0,0,1-.21-4A8,8,0,0,1,21.1,3.2,7.49,7.49,0,0,1,24.79.8,9.15,9.15,0,0,1,27,.46a11.63,11.63,0,0,1,2.12.1,9,9,0,0,1,4,1.65,10.12,10.12,0,0,1,1.6,1.47,6.94,6.94,0,0,1,1.16,2,9.67,9.67,0,0,1,.6,4.16,8.47,8.47,0,0,1-1.22,3.92.55.55,0,0,1-.76.18.56.56,0,0,1-.23-.65v0a8.52,8.52,0,0,0,.4-3.43,8.24,8.24,0,0,0-.88-3.23,4.4,4.4,0,0,0-.88-1.23,7.83,7.83,0,0,0-1.27-1,7.43,7.43,0,0,0-3-1.11A10,10,0,0,0,27,3.09a7.63,7.63,0,0,0-1.6.12,5.25,5.25,0,0,0-2.73,1.43A6.49,6.49,0,0,0,21,7.43a10.62,10.62,0,0,0-.36,3.51.55.55,0,0,1-1.09.15Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M32.57,17.9A8.79,8.79,0,0,0,30.94,23a3.33,3.33,0,0,0,.81,2.22,6.59,6.59,0,0,0,1.94,1.41,9,9,0,0,0,2.43.84,16.72,16.72,0,0,0,2.68.36,46.14,46.14,0,0,0,5.62-.4l1.45-.12a12.58,12.58,0,0,0,1.33-.2c.9-.17,1.78-.41,2.77-.66l.41-.08.27,0h.27c.24,0,.51,0,.79.05a3.37,3.37,0,0,1,1.78.69,4,4,0,0,1,1.1,3.3c0,1.94,0,3.86,0,5.8s-.23,3.87-.36,5.8a.49.49,0,0,1-.53.47.52.52,0,0,1-.47-.5v0c0-1.92.06-3.85,0-5.75L53,30.43l0-.71a2.61,2.61,0,0,0-.09-.57,1.28,1.28,0,0,0-.44-.74,1.68,1.68,0,0,0-.9-.28H50.8l-.09,0-.3.08c-.87.25-1.84.57-2.82.78a12.68,12.68,0,0,1-1.52.25l-1.44.15a45.75,45.75,0,0,1-5.88.52,19.23,19.23,0,0,1-3.06-.35,10.79,10.79,0,0,1-3-1,6.1,6.1,0,0,1-2.42-2.31A5.93,5.93,0,0,1,29.62,23a11.5,11.5,0,0,1,2.13-5.65.5.5,0,1,1,.83.56h0Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M35.73,43.92a11.14,11.14,0,0,0-1.6,5.91,21.19,21.19,0,0,0,1,6.2,8.15,8.15,0,0,0,3.61,4.37,9.35,9.35,0,0,0,2.47,1.24,6.4,6.4,0,0,0,2.72,0,26.7,26.7,0,0,0,5.86-2.2c.26-.12.49-.23.83-.36a4,4,0,0,1,1.08-.25,3.64,3.64,0,0,1,1.18.06,3,3,0,0,1,.65.23,2.06,2.06,0,0,1,.67.57,4.61,4.61,0,0,1,.78,2,4.78,4.78,0,0,1,.09,1l-.05.86c-.14,2.21-.32,4.36-.38,6.53s-.17,4.38-.3,6.56a.5.5,0,0,1-.53.48.51.51,0,0,1-.47-.47c-.12-2.19-.2-4.38-.29-6.57s0-4.43,0-6.6v-.78a3.46,3.46,0,0,0-.09-.61,2.61,2.61,0,0,0-.43-1s0,0,0,0l-.12,0a1.77,1.77,0,0,0-.46,0,1.94,1.94,0,0,0-.51.14l-.66.31a28.41,28.41,0,0,1-6.34,2.57,8.94,8.94,0,0,1-3.8.11,7.06,7.06,0,0,1-1.85-.7,12.08,12.08,0,0,1-1.53-.94,10.8,10.8,0,0,1-2.57-2.61,10.2,10.2,0,0,1-1.46-3.36,25.49,25.49,0,0,1-.64-6.78,10.52,10.52,0,0,1,.69-3.41,13.85,13.85,0,0,1,1.61-3,.5.5,0,0,1,.69-.12.52.52,0,0,1,.15.66Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M31.12,82.07a9.71,9.71,0,0,0,.54,2.17A4.85,4.85,0,0,0,32.77,86a10.09,10.09,0,0,1,1.47,2.14,9.65,9.65,0,0,1,.82,2.52,6.47,6.47,0,0,1,0,2.77,6.31,6.31,0,0,1-1.2,2.5,5,5,0,0,1-2.3,1.6,7.42,7.42,0,0,1-2.59.4.5.5,0,0,1-.09-1h0a5.49,5.49,0,0,0,3.52-2A5,5,0,0,0,33.09,91a10.56,10.56,0,0,0-.65-2,10,10,0,0,0-1.14-1.88A10.57,10.57,0,0,1,30.61,86a6.49,6.49,0,0,1-.42-1.32,8.29,8.29,0,0,1-.07-2.58.51.51,0,0,1,.57-.43.52.52,0,0,1,.43.42Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M21.47,87.9A7.37,7.37,0,0,0,21.1,90a10.5,10.5,0,0,0,.27,2.1,20.3,20.3,0,0,0,.53,2.07,5.07,5.07,0,0,0,.42,1,2.41,2.41,0,0,0,.64.85.5.5,0,1,1-.58.82.23.23,0,0,1-.07-.07,15.8,15.8,0,0,1-1.73-1.94,5.84,5.84,0,0,1-1-2.39,6.57,6.57,0,0,1,0-2.65,6.21,6.21,0,0,1,1-2.41.51.51,0,0,1,.71-.09.49.49,0,0,1,.18.55Z"
                        />
                        <Path
                            fill="#50c9f0"
                            d="M4.31,59.63a46.68,46.68,0,0,0,4.42,3,8.8,8.8,0,0,0,4.83.65,12.93,12.93,0,0,0,4.92-1.37,10.19,10.19,0,0,0,2-1.55,7.48,7.48,0,0,0,1.51-2l0,0a.5.5,0,0,1,.93.36,7.64,7.64,0,0,1-1.47,2.58,10,10,0,0,1-2.17,2A12.59,12.59,0,0,1,13.81,65a9.41,9.41,0,0,1-5.87-1,22,22,0,0,1-4.28-3.69.5.5,0,0,1,0-.71.49.49,0,0,1,.63,0Z"
                        />
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={puzzleGreenRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 57.38 100"
                    customCursorActive={customCursorActive}
                    width={PUZZLE_GREEN_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleGreen].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleGreen].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleGreen].rotate}
                    delay={6 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleGreen}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.puzzleGreen,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: puzzleGreenRef,
                            name: INTERACTABLE_OBJECT.puzzleGreen,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: puzzleGreenRef,
                            name: INTERACTABLE_OBJECT.puzzleGreen,
                        }, e),
                    })}
                >
                    <InteractableObjectShadow
                        id="Green_Puzzle_Piece_Shadow"
                        dataName="Green Puzzle Piece Shadow"
                        fill="#0d1d38"
                        d="M2.51,45.24a2.51,2.51,0,0,0,.79,1.83c.55.59,1.1.69,2.27.35a20.69,20.69,0,0,0,4.64-2.36,8.1,8.1,0,0,1,4.11-1.31,11.93,11.93,0,0,1,4.12.7,5.84,5.84,0,0,1,3.14,2.85,17.77,17.77,0,0,1,1.18,6.16,9.62,9.62,0,0,1-2,5.81,9.45,9.45,0,0,1-5,2.85,9.37,9.37,0,0,1-5.86-.87,31.09,31.09,0,0,1-4.11-2.89,2.25,2.25,0,0,0-2-.35,2.11,2.11,0,0,0-1.35,1.31A41.82,41.82,0,0,0,1.2,69c-.07,4.31-.1,11,.44,11.55s7-.45,9.27-.44,8.73.26,9.54.44a3.34,3.34,0,0,1,1.93.92A2.58,2.58,0,0,1,23,83.67c-.21.84-2.42,3.55-3,5.32a11.67,11.67,0,0,0,.2,6.4,9.18,9.18,0,0,0,4,4c1.64.77,5.89.73,7.61.06a9.21,9.21,0,0,0,4.73-4,8.65,8.65,0,0,0,.35-6.65c-.77-2.4-2.56-4.18-2.76-4.84a3,3,0,0,1-.09-2,4.39,4.39,0,0,1,2.38-1.68,72.88,72.88,0,0,1,10-.65c3,0,9.54,1.44,10.42.9s.42-7.22.35-11.09-.47-9.13-.62-9.87a2.9,2.9,0,0,0-1.65-1.92,3.17,3.17,0,0,0-2.29.32,35.57,35.57,0,0,1-5.46,2.33,6.85,6.85,0,0,1-6.42-.93A10.41,10.41,0,0,1,37.6,53.9c-.43-2-.59-5.14.35-7.26s3.27-3.72,6.51-3.8,5.35,2,6.44,2.82a5.71,5.71,0,0,0,3.67,1.4,2.86,2.86,0,0,0,2.28-2.27c.43-1.4.34-3.52.29-8.34S56.8,26,56.42,25.62s-5.5.34-8.37.74a70.69,70.69,0,0,1-7.77.63,10.71,10.71,0,0,1-4.73-1.49,4.26,4.26,0,0,1-1.24-3.18,8.75,8.75,0,0,1,.58-3.07c.43-1.09,2.6-4.15,3.17-5.57a10.23,10.23,0,0,0,.52-5.09,9.25,9.25,0,0,0-2-4.86A9,9,0,0,0,30.69.52c-2.49-.29-5.57-.17-8,1.75A9.16,9.16,0,0,0,18.92,10c0,3.5,3.11,8.15,3.63,9.12a4.29,4.29,0,0,1,.52,3,4.92,4.92,0,0,1-1.71,2.44,5.43,5.43,0,0,1-2.6.93,62.92,62.92,0,0,1-8,.47c-4.24-.06-7.84-1.21-8.47-.65S2,33.61,2.06,35.72,2.42,44.34,2.51,45.24Z"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleGreen}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    />
                    <Group
                        id="Green_Puzzle_Piece_Base"
                        dataName="Green Puzzle Piece Base"
                    >
                        <Path
                            fill="#53a546"
                            d="M1.35,44.83a2.5,2.5,0,0,0,.79,1.84c.55.59,1.1.69,2.27.35a20.91,20.91,0,0,0,4.64-2.36,8,8,0,0,1,4.11-1.31,11.6,11.6,0,0,1,4.11.7,5.79,5.79,0,0,1,3.15,2.85,17.76,17.76,0,0,1,1.17,6.15,9.56,9.56,0,0,1-2,5.82,9.49,9.49,0,0,1-5,2.85,9.44,9.44,0,0,1-5.86-.87A31.09,31.09,0,0,1,4.59,58a2.22,2.22,0,0,0-2-.35,2.07,2.07,0,0,0-1.35,1.31A41.24,41.24,0,0,0,0,68.55C0,72.86-.06,79.6.47,80.1s7-.46,9.28-.44,8.73.25,9.54.44a3.31,3.31,0,0,1,1.93.92,2.58,2.58,0,0,1,.65,2.25c-.21.83-2.42,3.55-3,5.32A11.73,11.73,0,0,0,19.08,95a9.23,9.23,0,0,0,4,4c1.64.77,5.89.73,7.61.06a9.26,9.26,0,0,0,4.73-4,8.7,8.7,0,0,0,.35-6.65C35,86,33.18,84.23,33,83.57a3,3,0,0,1-.09-2,4.33,4.33,0,0,1,2.38-1.67,72.76,72.76,0,0,1,10-.65c3,0,9.54,1.44,10.42.89S56.11,73,56,69.09s-.47-9.12-.62-9.86a3,3,0,0,0-1.65-1.93,3.27,3.27,0,0,0-2.29.32A34.56,34.56,0,0,1,46,60,6.81,6.81,0,0,1,39.6,59a10.41,10.41,0,0,1-3.16-5.53c-.43-2-.6-5.15.35-7.27s3.27-3.71,6.51-3.79,5.35,2,6.44,2.82a5.71,5.71,0,0,0,3.67,1.4,2.87,2.87,0,0,0,2.28-2.28C56.12,43,56,40.86,56,36s-.33-10.47-.71-10.83-5.51.35-8.37.75a70.69,70.69,0,0,1-7.77.63,10.71,10.71,0,0,1-4.73-1.49,4.26,4.26,0,0,1-1.24-3.18,8.9,8.9,0,0,1,.57-3.07c.44-1.1,2.61-4.15,3.18-5.57a10.25,10.25,0,0,0,.52-5.1,9.33,9.33,0,0,0-2-4.85A9,9,0,0,0,29.52.12C27-.17,24,0,21.54,1.87a9.16,9.16,0,0,0-3.78,7.77c0,3.5,3.11,8.14,3.63,9.12a4.26,4.26,0,0,1,.52,3,5,5,0,0,1-1.71,2.45,5.43,5.43,0,0,1-2.6.93,65,65,0,0,1-8,.47c-4.24-.06-7.84-1.21-8.47-.65S.87,33.2.9,35.32,1.26,43.94,1.35,44.83Z"
                        />
                        <Path
                            fill="#66bc46"
                            d="M22.49,3.5c1.72-1.2,6.3-2.45,9.81,0s3.29,8,2.45,10-2.6,3.68-3.39,5.53-1.41,4.17-.77,5.91c.94,2.53,3.24,3.27,5.52,3.41s5.09.09,6.94,0a74,74,0,0,0,7.54-1,3.35,3.35,0,0,1,2.26.29,4.57,4.57,0,0,1,.93,2c0,.23,0,4.2.1,6.76a20.85,20.85,0,0,1-.5,5.73,1,1,0,0,1-1.23.57,15.39,15.39,0,0,0-1.61-.66c-.78-.21-4.94-2.26-8.24-1.84s-5.27,1.29-7,3.39-2,5.31-1.84,7.66a21.3,21.3,0,0,0,1.22,6.5,9.4,9.4,0,0,0,4.4,4.56c2,1,4.9.51,6.52.19a32.78,32.78,0,0,0,5.28-2A2.76,2.76,0,0,1,53,60.24a3.65,3.65,0,0,1,1.1,2.19c.06.65-.36,4.9-.29,7.13s.12,6,0,6.72a1.73,1.73,0,0,1-1,1.44c-.56.29-1.81-.06-2.6-.19a81.78,81.78,0,0,0-8.53-.39c-2.83,0-6,.36-7.84,1.21s-2.69,1.56-3,2.57a5.12,5.12,0,0,0,0,2.9,29.91,29.91,0,0,0,2.4,4.51A7.48,7.48,0,0,1,33.74,94a6.21,6.21,0,0,1-3.63,3.24,15,15,0,0,1-5.18.32A5.59,5.59,0,0,1,21.34,95,7.32,7.32,0,0,1,20.21,91a8,8,0,0,1,1.13-3.9c.62-1.11,1.81-2.17,2.28-3.36a3.49,3.49,0,0,0-.09-3,3.33,3.33,0,0,0-1.76-2,16.32,16.32,0,0,0-4.68-1.12c-1.48-.12-5.13-.22-7.31-.2s-5.8.27-6.25.3a2.76,2.76,0,0,1-1.72-.36C1.39,77,1.38,75.75,1.37,75s-.14-4.35-.15-5.82a51.75,51.75,0,0,1,.37-5.56c.12-.59.58-3.47,1.64-3.63s2.1,1.13,2.55,1.55a13.06,13.06,0,0,0,2.79,2,13.05,13.05,0,0,0,5,.94,11,11,0,0,0,4.75-1.59,16.42,16.42,0,0,0,4.13-4.39,24.11,24.11,0,0,0,1.4-7.21,12.77,12.77,0,0,0-2.18-6.56,11.55,11.55,0,0,0-6.43-3.11A17.25,17.25,0,0,0,9,42.52,29.85,29.85,0,0,0,5.46,45c-.56.41-.88.49-1.36.3s-.59-.37-.74-1.07a49.19,49.19,0,0,1-.63-8A66,66,0,0,1,3.22,28a.79.79,0,0,1,.72-.7,3.18,3.18,0,0,1,1.23.19,59.24,59.24,0,0,0,8.1.58c3,0,7.38-1,9.38-3.42s1.8-4,1.32-5.63a16.7,16.7,0,0,0-2.37-4.57,11,11,0,0,1-1.8-4.21C19.46,8.59,20.78,4.69,22.49,3.5Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M3.42,29.36c-.16,2.1-.23,4.23-.19,6.33a24.49,24.49,0,0,0,.2,3.11c.14,1,.31,2.07.51,3.1.1.51.2,1,.32,1.52l.09.34a.61.61,0,0,1,.05.11.35.35,0,0,0,.15.13s0,0,0,0h0a2.78,2.78,0,0,0,.38-.22c.17-.11.36-.26.57-.42a7.85,7.85,0,0,1,.75-.48,11.14,11.14,0,0,1,3.23-1.21,18.42,18.42,0,0,1,3.29-.24.36.36,0,0,1,.08.71h0a24.54,24.54,0,0,0-3,.84,9.75,9.75,0,0,0-2.49,1.54c-.19.15-.37.31-.53.46a8.49,8.49,0,0,1-.68.56,3.09,3.09,0,0,1-1,.51,2.07,2.07,0,0,1-1.57-.19,2.46,2.46,0,0,1-.93-1,2.3,2.3,0,0,1-.23-.64c0-.16-.05-.3-.07-.44-.06-.56-.11-1.09-.15-1.63C2.07,41.07,2,40,2,38.91a27,27,0,0,1,.1-3.25c.19-2.14.44-4.25.72-6.37a.32.32,0,0,1,.64.07Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M4.74,26.87c1.42.18,2.87.34,4.3.44a28.76,28.76,0,0,0,4.25.06,23.51,23.51,0,0,0,4.19-.6,21.55,21.55,0,0,0,4-1.39.5.5,0,0,1,.66.25.49.49,0,0,1-.19.63,16.55,16.55,0,0,1-4.12,1.83,21,21,0,0,1-4.44.75A24.86,24.86,0,0,1,8.9,28.7a30.81,30.81,0,0,1-4.36-.85.5.5,0,0,1,.19-1Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M19.51,11.09a10.29,10.29,0,0,1-.21-4A8,8,0,0,1,21.1,3.2,7.49,7.49,0,0,1,24.79.8,9.15,9.15,0,0,1,27,.46a11.63,11.63,0,0,1,2.12.1,9,9,0,0,1,4,1.65,10.12,10.12,0,0,1,1.6,1.47,6.94,6.94,0,0,1,1.16,2,9.67,9.67,0,0,1,.6,4.16,8.47,8.47,0,0,1-1.22,3.92.55.55,0,0,1-.76.18.56.56,0,0,1-.23-.65v0a8.52,8.52,0,0,0,.4-3.43,8.24,8.24,0,0,0-.88-3.23,4.4,4.4,0,0,0-.88-1.23,7.83,7.83,0,0,0-1.27-1,7.43,7.43,0,0,0-3-1.11A10,10,0,0,0,27,3.09a7.63,7.63,0,0,0-1.6.12,5.25,5.25,0,0,0-2.73,1.43A6.49,6.49,0,0,0,21,7.43a10.62,10.62,0,0,0-.36,3.51.55.55,0,0,1-1.09.15Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M32.57,17.9A8.79,8.79,0,0,0,30.94,23a3.33,3.33,0,0,0,.81,2.22,6.59,6.59,0,0,0,1.94,1.41,9,9,0,0,0,2.43.84,16.72,16.72,0,0,0,2.68.36,46.14,46.14,0,0,0,5.62-.4l1.45-.12a12.58,12.58,0,0,0,1.33-.2c.9-.17,1.78-.41,2.77-.66l.41-.08.27,0h.27c.24,0,.51,0,.79.05a3.37,3.37,0,0,1,1.78.69,4,4,0,0,1,1.1,3.3c0,1.94,0,3.86,0,5.8s-.23,3.87-.36,5.8a.49.49,0,0,1-.53.47.52.52,0,0,1-.47-.5v0c0-1.92.06-3.85,0-5.75L53,30.43l0-.71a2.61,2.61,0,0,0-.09-.57,1.28,1.28,0,0,0-.44-.74,1.68,1.68,0,0,0-.9-.28H50.8l-.09,0-.3.08c-.87.25-1.84.57-2.82.78a12.68,12.68,0,0,1-1.52.25l-1.44.15a45.75,45.75,0,0,1-5.88.52,19.23,19.23,0,0,1-3.06-.35,10.79,10.79,0,0,1-3-1,6.1,6.1,0,0,1-2.42-2.31A5.93,5.93,0,0,1,29.62,23a11.5,11.5,0,0,1,2.13-5.65.5.5,0,1,1,.83.56h0Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M35.73,43.92a11.14,11.14,0,0,0-1.6,5.91,21.19,21.19,0,0,0,1,6.2,8.15,8.15,0,0,0,3.61,4.37,9.35,9.35,0,0,0,2.47,1.24,6.4,6.4,0,0,0,2.72,0,26.7,26.7,0,0,0,5.86-2.2c.26-.12.49-.23.83-.36a4,4,0,0,1,1.08-.25,3.64,3.64,0,0,1,1.18.06,3,3,0,0,1,.65.23,2.06,2.06,0,0,1,.67.57,4.61,4.61,0,0,1,.78,2,4.78,4.78,0,0,1,.09,1l-.05.86c-.14,2.21-.32,4.36-.38,6.53s-.17,4.38-.3,6.56a.5.5,0,0,1-.53.48.51.51,0,0,1-.47-.47c-.12-2.19-.2-4.38-.29-6.57s0-4.43,0-6.6v-.78a3.46,3.46,0,0,0-.09-.61,2.61,2.61,0,0,0-.43-1s0,0,0,0l-.12,0a1.77,1.77,0,0,0-.46,0,1.94,1.94,0,0,0-.51.14l-.66.31a28.41,28.41,0,0,1-6.34,2.57,8.94,8.94,0,0,1-3.8.11,7.06,7.06,0,0,1-1.85-.7,12.08,12.08,0,0,1-1.53-.94,10.8,10.8,0,0,1-2.57-2.61,10.2,10.2,0,0,1-1.46-3.36,25.49,25.49,0,0,1-.64-6.78,10.52,10.52,0,0,1,.69-3.41,13.85,13.85,0,0,1,1.61-3,.5.5,0,0,1,.69-.12.52.52,0,0,1,.15.66Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M31.12,82.07a9.71,9.71,0,0,0,.54,2.17A4.85,4.85,0,0,0,32.77,86a10.09,10.09,0,0,1,1.47,2.14,9.65,9.65,0,0,1,.82,2.52,6.47,6.47,0,0,1,0,2.77,6.31,6.31,0,0,1-1.2,2.5,5,5,0,0,1-2.3,1.6,7.42,7.42,0,0,1-2.59.4.5.5,0,0,1-.09-1h0a5.49,5.49,0,0,0,3.52-2A5,5,0,0,0,33.09,91a10.56,10.56,0,0,0-.65-2,10,10,0,0,0-1.14-1.88A10.57,10.57,0,0,1,30.61,86a6.49,6.49,0,0,1-.42-1.32,8.29,8.29,0,0,1-.07-2.58.5.5,0,0,1,.57-.43.52.52,0,0,1,.43.42Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M21.47,87.9A7.37,7.37,0,0,0,21.1,90a10.5,10.5,0,0,0,.27,2.1,20.3,20.3,0,0,0,.53,2.07,5.07,5.07,0,0,0,.42,1,2.41,2.41,0,0,0,.64.85.5.5,0,1,1-.58.82.23.23,0,0,1-.07-.07,15.8,15.8,0,0,1-1.73-1.94,5.84,5.84,0,0,1-1-2.39,6.57,6.57,0,0,1,0-2.65,6.21,6.21,0,0,1,1-2.41.51.51,0,0,1,.71-.09.49.49,0,0,1,.18.55Z"
                        />
                        <Path
                            fill="#83c458"
                            d="M4.31,59.63a46.68,46.68,0,0,0,4.42,3,8.8,8.8,0,0,0,4.83.65,12.93,12.93,0,0,0,4.92-1.37,10.19,10.19,0,0,0,2-1.55,7.48,7.48,0,0,0,1.51-2l0,0a.5.5,0,0,1,.93.36,7.64,7.64,0,0,1-1.47,2.58,10,10,0,0,1-2.17,2A12.59,12.59,0,0,1,13.81,65a9.41,9.41,0,0,1-5.87-1,22,22,0,0,1-4.28-3.69.5.5,0,0,1,0-.71.49.49,0,0,1,.63,0Z"
                        />
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={puzzleOrangeRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 100 57.38"
                    customCursorActive={customCursorActive}
                    width={PUZZLE_ORANGE_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleOrange].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleOrange].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleOrange].rotate}
                    delay={7 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleOrange}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.puzzleOrange,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: puzzleOrangeRef,
                            name: INTERACTABLE_OBJECT.puzzleOrange,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: puzzleOrangeRef,
                            name: INTERACTABLE_OBJECT.puzzleOrange,
                        }, e),
                    })}
                >
                    <InteractableObjectShadow
                        id="Orange_Puzzle_Piece_Shadow"
                        dataName="Orange Puzzle Piece Shadow"
                        fill="#0d1d38"
                        d="M45.24,54.87a2.47,2.47,0,0,0,1.83-.79c.59-.55.69-1.1.35-2.27a20.91,20.91,0,0,0-2.36-4.64,8.07,8.07,0,0,1-1.31-4.11,11.83,11.83,0,0,1,.7-4.11A5.83,5.83,0,0,1,47.3,35.8a17.77,17.77,0,0,1,6.16-1.17,9.55,9.55,0,0,1,5.81,2,9.49,9.49,0,0,1,2.85,5,9.37,9.37,0,0,1-.87,5.86,31.09,31.09,0,0,1-2.89,4.11,2.25,2.25,0,0,0-.35,2A2.11,2.11,0,0,0,59.32,55,41.24,41.24,0,0,0,69,56.18c4.31.07,11,.1,11.55-.43s-.45-7-.44-9.28.26-8.73.44-9.54A3.37,3.37,0,0,1,81.42,35a2.58,2.58,0,0,1,2.25-.65c.84.21,3.55,2.42,5.32,3a11.77,11.77,0,0,0,6.4-.19,9.21,9.21,0,0,0,4-4c.77-1.64.73-5.89.06-7.61a9.21,9.21,0,0,0-4-4.73,8.7,8.7,0,0,0-6.65-.35c-2.4.77-4.18,2.56-4.84,2.76a3,3,0,0,1-2,.09A4.39,4.39,0,0,1,80.34,21a72.76,72.76,0,0,1-.65-10c0-3,1.44-9.54.9-10.42S73.37.11,69.5.18,60.37.65,59.63.8a3,3,0,0,0-1.92,1.65A3.17,3.17,0,0,0,58,4.74a35.57,35.57,0,0,1,2.33,5.46,6.84,6.84,0,0,1-.93,6.42,10.41,10.41,0,0,1-5.53,3.16c-2,.43-5.14.59-7.26-.35s-3.72-3.27-3.8-6.51,2-5.35,2.82-6.44a5.71,5.71,0,0,0,1.4-3.67A2.87,2.87,0,0,0,44.79.53C43.39.1,41.27.19,36.45.25S26,.58,25.62,1s.34,5.51.74,8.37A70.69,70.69,0,0,1,27,17.1a10.71,10.71,0,0,1-1.49,4.73,4.26,4.26,0,0,1-3.18,1.24,9,9,0,0,1-3.07-.57c-1.09-.44-4.15-2.61-5.57-3.18a10.23,10.23,0,0,0-5.09-.52,9.33,9.33,0,0,0-4.86,2A9,9,0,0,0,.52,26.7c-.29,2.48-.17,5.56,1.75,8A9.16,9.16,0,0,0,10,38.46c3.5,0,8.15-3.11,9.12-3.63a4.29,4.29,0,0,1,3-.52A4.92,4.92,0,0,1,24.58,36a5.43,5.43,0,0,1,.93,2.6,62.92,62.92,0,0,1,.47,8c-.06,4.24-1.21,7.84-.65,8.47s8.28.27,10.39.24S44.34,55,45.24,54.87Z"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleOrange}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    />
                    <Group
                        id="Orange_Puzzle_Piece_Base"
                        dataName="Orange Puzzle Piece Base"
                    >
                        <Path
                            fill="#e45125"
                            d="M44.83,56a2.46,2.46,0,0,0,1.84-.79c.59-.55.69-1.1.35-2.27a20.91,20.91,0,0,0-2.36-4.64,8,8,0,0,1-1.31-4.11,11.6,11.6,0,0,1,.7-4.11A5.79,5.79,0,0,1,46.9,37a17.76,17.76,0,0,1,6.15-1.17,9.62,9.62,0,0,1,5.82,2,9,9,0,0,1,2,10.91A31.09,31.09,0,0,1,58,52.79a2.23,2.23,0,0,0-.35,2,2.07,2.07,0,0,0,1.31,1.35,40.68,40.68,0,0,0,9.63,1.22c4.31.07,11,.1,11.55-.43s-.46-7-.44-9.28.25-8.73.44-9.54A3.31,3.31,0,0,1,81,36.16a2.57,2.57,0,0,1,2.25-.64c.83.2,3.55,2.42,5.32,3A11.73,11.73,0,0,0,95,38.3a9.18,9.18,0,0,0,4-4c.77-1.64.73-5.89.06-7.61a9.3,9.3,0,0,0-4-4.73,8.75,8.75,0,0,0-6.65-.35c-2.4.77-4.18,2.57-4.84,2.76a3,3,0,0,1-2,.09,4.34,4.34,0,0,1-1.67-2.37,73,73,0,0,1-.65-10c0-3,1.44-9.55.89-10.43S73,1.27,69.09,1.35,60,1.81,59.23,2A2.93,2.93,0,0,0,57.3,3.61a3.27,3.27,0,0,0,.32,2.29A35,35,0,0,1,60,11.36,6.81,6.81,0,0,1,59,17.78a10.41,10.41,0,0,1-5.53,3.16c-2,.43-5.15.6-7.27-.35s-3.71-3.27-3.79-6.5,2-5.36,2.82-6.45A5.69,5.69,0,0,0,46.66,4a2.87,2.87,0,0,0-2.28-2.28C43,1.26,40.86,1.35,36,1.41s-10.47.33-10.83.72.35,5.5.75,8.36a70.69,70.69,0,0,1,.63,7.77A10.71,10.71,0,0,1,25.1,23a4.22,4.22,0,0,1-3.18,1.24,8.9,8.9,0,0,1-3.07-.57c-1.1-.44-4.15-2.61-5.57-3.18A10.37,10.37,0,0,0,8.18,20a9.27,9.27,0,0,0-4.85,2A9,9,0,0,0,.12,27.86c-.29,2.48-.17,5.57,1.75,8a9.18,9.18,0,0,0,7.77,3.78c3.5,0,8.14-3.12,9.12-3.64a4.32,4.32,0,0,1,3-.52,4.9,4.9,0,0,1,2.45,1.72,5.35,5.35,0,0,1,.93,2.59,65,65,0,0,1,.47,8c-.06,4.24-1.21,7.84-.65,8.47s8.27.27,10.39.24S43.94,56.12,44.83,56Z"
                        />
                        <Path
                            fill="#f47421"
                            d="M3.5,34.89c-1.2-1.72-2.45-6.3,0-9.8s8-3.3,10-2.46S17.16,25.23,19,26s4.17,1.41,5.91.77c2.53-.94,3.27-3.24,3.41-5.51s.09-5.1,0-7a74,74,0,0,0-1-7.54,3.35,3.35,0,0,1,.29-2.26,4.45,4.45,0,0,1,2-.92c.23,0,4.2,0,6.76-.11a21.73,21.73,0,0,1,5.73.5,1,1,0,0,1,.57,1.23,15.39,15.39,0,0,0-.66,1.61c-.21.78-2.26,4.94-1.84,8.25s1.29,5.26,3.39,7,5.31,2,7.66,1.84a21,21,0,0,0,6.5-1.22,9.4,9.4,0,0,0,4.56-4.4c1-2,.51-4.9.19-6.52a32.78,32.78,0,0,0-2-5.28,2.76,2.76,0,0,1-.27-2.05,3.65,3.65,0,0,1,2.19-1.1c.65-.05,4.9.36,7.13.29s6-.12,6.72,0a1.75,1.75,0,0,1,1.44,1c.29.57-.06,1.81-.19,2.6a81.78,81.78,0,0,0-.39,8.53c0,2.83.36,6,1.21,7.84s1.56,2.69,2.57,3a5.12,5.12,0,0,0,2.9,0,29.91,29.91,0,0,0,4.51-2.4A7.48,7.48,0,0,1,94,23.65a6.15,6.15,0,0,1,3.24,3.63,14.89,14.89,0,0,1,.32,5.17A5.59,5.59,0,0,1,95,36,7.32,7.32,0,0,1,91,37.17,8,8,0,0,1,87.08,36c-1.11-.62-2.17-1.81-3.36-2.28a3.53,3.53,0,0,0-3,.09,3.33,3.33,0,0,0-2,1.76,16.4,16.4,0,0,0-1.12,4.68c-.12,1.48-.22,5.13-.2,7.31s.27,5.81.3,6.25a2.76,2.76,0,0,1-.36,1.72C77,56,75.75,56,75,56s-4.35.14-5.82.15a51.75,51.75,0,0,1-5.56-.37c-.59-.12-3.47-.58-3.63-1.63s1.13-2.1,1.55-2.56a13.06,13.06,0,0,0,2-2.79,13.05,13.05,0,0,0,.94-5A10.9,10.9,0,0,0,62.88,39a16.3,16.3,0,0,0-4.39-4.14,24.11,24.11,0,0,0-7.21-1.4,12.86,12.86,0,0,0-6.56,2.18,11.55,11.55,0,0,0-3.11,6.43,17.22,17.22,0,0,0,.91,6.25A30.89,30.89,0,0,0,45,51.93c.41.56.49.87.3,1.36s-.37.59-1.07.73a49.19,49.19,0,0,1-8,.63A63.79,63.79,0,0,1,28,54.16a.79.79,0,0,1-.7-.72,3.18,3.18,0,0,1,.19-1.23,59.1,59.1,0,0,0,.58-8.1c0-3-1-7.38-3.42-9.38s-4-1.8-5.63-1.32a17,17,0,0,0-4.57,2.37,11,11,0,0,1-4.21,1.8C8.59,37.92,4.69,36.61,3.5,34.89Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M29.36,54c2.1.17,4.23.23,6.33.2A26.48,26.48,0,0,0,38.8,54c1-.14,2.07-.31,3.1-.51.51-.1,1-.2,1.52-.32l.34-.09.11-.05a.35.35,0,0,0,.13-.15h0v0a2.33,2.33,0,0,0-.22-.37c-.11-.18-.26-.37-.42-.58a7.85,7.85,0,0,1-.48-.75,11.14,11.14,0,0,1-1.21-3.23,18.38,18.38,0,0,1-.24-3.28.36.36,0,0,1,.71-.08h0a25.23,25.23,0,0,0,.84,3A9.87,9.87,0,0,0,44.51,50c.15.19.31.37.46.53a8.49,8.49,0,0,1,.56.68,3.19,3.19,0,0,1,.51,1,2.07,2.07,0,0,1-.19,1.57,2.49,2.49,0,0,1-1,.94,2.67,2.67,0,0,1-.64.22l-.44.07-1.63.15c-1.07.09-2.14.16-3.23.19a25,25,0,0,1-3.25-.1c-2.14-.19-4.25-.43-6.37-.72a.31.31,0,0,1-.28-.36A.32.32,0,0,1,29.36,54Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M26.87,52.64c.18-1.42.34-2.87.44-4.29a28.91,28.91,0,0,0,.06-4.26,23.51,23.51,0,0,0-.6-4.19,21.55,21.55,0,0,0-1.39-4,.5.5,0,0,1,.25-.66.49.49,0,0,1,.63.19,16.55,16.55,0,0,1,1.83,4.12A21.13,21.13,0,0,1,28.84,44a24.78,24.78,0,0,1-.14,4.48,30.94,30.94,0,0,1-.85,4.37.5.5,0,0,1-1-.2Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M11.09,37.87a10.29,10.29,0,0,1-4,.21,8,8,0,0,1-3.85-1.8A7.49,7.49,0,0,1,.8,32.59a9,9,0,0,1-.34-2.16,11.63,11.63,0,0,1,.1-2.12,9.09,9.09,0,0,1,1.65-4,10.12,10.12,0,0,1,1.47-1.6,6.94,6.94,0,0,1,2-1.16,9.82,9.82,0,0,1,4.16-.6,8.47,8.47,0,0,1,3.92,1.22.55.55,0,0,1,.18.76.56.56,0,0,1-.65.23h0a8.7,8.7,0,0,0-3.43-.4,8.42,8.42,0,0,0-3.23.88,4.4,4.4,0,0,0-1.23.88,8.29,8.29,0,0,0-1,1.27,7.43,7.43,0,0,0-1.11,3,10.05,10.05,0,0,0-.13,1.66A7.58,7.58,0,0,0,3.21,32a5.32,5.32,0,0,0,1.43,2.74,6.56,6.56,0,0,0,2.79,1.7,10.62,10.62,0,0,0,3.51.36.55.55,0,0,1,.15,1.09Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M17.9,24.81A8.79,8.79,0,0,0,23,26.44a3.33,3.33,0,0,0,2.22-.81,6.59,6.59,0,0,0,1.41-1.94,8.83,8.83,0,0,0,.84-2.43,16.72,16.72,0,0,0,.36-2.68,46.14,46.14,0,0,0-.4-5.62l-.12-1.45a13,13,0,0,0-.2-1.33c-.17-.9-.41-1.78-.66-2.77L26.38,7c0-.09,0-.14,0-.27V6.47c0-.25,0-.52.05-.8a3.37,3.37,0,0,1,.69-1.78,4,4,0,0,1,3.3-1.1c1.94,0,3.86,0,5.8,0s3.87.23,5.8.36a.5.5,0,0,1,.47.53.51.51,0,0,1-.5.47h0c-1.92,0-3.85-.06-5.75,0l-5.75.23-.71,0a4.85,4.85,0,0,0-.57.09,1.24,1.24,0,0,0-.74.45,1.63,1.63,0,0,0-.28.9c0,.19,0,.4,0,.64v.09h0a.3.3,0,0,1,0,.09l.08.29c.25.87.57,1.84.78,2.82a13,13,0,0,1,.25,1.52l.15,1.44a45.75,45.75,0,0,1,.52,5.88,19.23,19.23,0,0,1-.35,3.06,11,11,0,0,1-1,3,6.07,6.07,0,0,1-2.31,2.41,5.93,5.93,0,0,1-3.27.64,11.4,11.4,0,0,1-5.65-2.13.5.5,0,0,1-.14-.69.51.51,0,0,1,.7-.14h0Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M43.92,21.65a11,11,0,0,0,5.91,1.6,21.19,21.19,0,0,0,6.2-1,8.15,8.15,0,0,0,4.37-3.61,9.35,9.35,0,0,0,1.24-2.47,6.4,6.4,0,0,0,0-2.72,26.44,26.44,0,0,0-2.2-5.85c-.12-.26-.23-.5-.36-.84a4,4,0,0,1-.25-1.08,3.65,3.65,0,0,1,.06-1.18,3,3,0,0,1,.23-.65,1.91,1.91,0,0,1,.57-.66,4.64,4.64,0,0,1,2-.79,4.22,4.22,0,0,1,1-.08l.86,0c2.21.14,4.36.32,6.53.38s4.38.17,6.56.3a.5.5,0,0,1,.48.53.51.51,0,0,1-.47.47c-2.19.13-4.38.2-6.57.29s-4.43,0-6.6,0h-.78a3.46,3.46,0,0,0-.61.09,2.77,2.77,0,0,0-1,.43s0,0,0,0,0,.08,0,.13a1.69,1.69,0,0,0,0,.45,1.84,1.84,0,0,0,.14.51c.07.17.19.43.31.66a28.6,28.6,0,0,1,2.57,6.34,8.94,8.94,0,0,1,.11,3.8,6.93,6.93,0,0,1-.7,1.85,12.08,12.08,0,0,1-.94,1.53,10.62,10.62,0,0,1-2.61,2.57,10.2,10.2,0,0,1-3.36,1.46,25.08,25.08,0,0,1-6.78.64,10.26,10.26,0,0,1-3.41-.69,13.81,13.81,0,0,1-3-1.6.51.51,0,0,1-.12-.7.52.52,0,0,1,.66-.15Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M82.07,26.26a9.71,9.71,0,0,0,2.17-.54A4.82,4.82,0,0,0,86,24.62a9.76,9.76,0,0,1,2.14-1.48,9.65,9.65,0,0,1,2.52-.82,6.35,6.35,0,0,1,5.27,1.22,5,5,0,0,1,1.6,2.31,7.39,7.39,0,0,1,.4,2.59.5.5,0,0,1-1,.08v0a5.49,5.49,0,0,0-2-3.52A5,5,0,0,0,91,24.29a9.19,9.19,0,0,0-3.92,1.79,9.19,9.19,0,0,1-1.15.69,5.92,5.92,0,0,1-1.32.42,8,8,0,0,1-2.58.07.5.5,0,0,1-.43-.57.53.53,0,0,1,.42-.43Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M87.9,35.91a7.37,7.37,0,0,0,2.11.37,11.27,11.27,0,0,0,2.1-.26c.68-.16,1.38-.31,2.07-.53a5.76,5.76,0,0,0,1-.43,2.41,2.41,0,0,0,.85-.64.5.5,0,0,1,.7-.12.51.51,0,0,1,.12.71.46.46,0,0,1-.07.07,17.64,17.64,0,0,1-1.94,1.73,6.12,6.12,0,0,1-2.39,1,6.76,6.76,0,0,1-2.65,0,6.07,6.07,0,0,1-2.41-1,.51.51,0,0,1,.46-.89Z"
                        />
                        <Path
                            fill="#f28e3d"
                            d="M59.63,53.07a46.68,46.68,0,0,0,3-4.42,8.8,8.8,0,0,0,.65-4.83,12.93,12.93,0,0,0-1.37-4.92,10.19,10.19,0,0,0-1.55-2,7.48,7.48,0,0,0-2-1.51l0,0a.5.5,0,0,1,.36-.93,7.64,7.64,0,0,1,2.58,1.47,10.21,10.21,0,0,1,2,2.17A12.63,12.63,0,0,1,65,43.57a9.41,9.41,0,0,1-1,5.87,22.38,22.38,0,0,1-3.69,4.29.51.51,0,0,1-.71,0,.5.5,0,0,1,0-.64Z"
                        />
                    </Group>
                </InteractableObject>
                <InteractableObject
                    ref={puzzleRedRef}
                    className={HOVER_TARGET_CLASSNAME}
                    viewBox="0 0 100 57.38"
                    customCursorActive={customCursorActive}
                    width={PUZZLE_RED_WIDTH}
                    top={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleRed].top}
                    left={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleRed].left}
                    rotate={initialInteractableObjectPositions[INTERACTABLE_OBJECT.puzzleRed].rotate}
                    delay={8 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={objectsRevealed}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleRed}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.puzzleRed,
                            [CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: puzzleRedRef,
                            name: INTERACTABLE_OBJECT.puzzleRed,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: puzzleRedRef,
                            name: INTERACTABLE_OBJECT.puzzleRed,
                        }, e),
                    })}
                >
                    <InteractableObjectShadow
                        id="Red_Puzzle_Piece_Shadow"
                        dataName="Red Puzzle Piece Shadow"
                        fill="#0d1d38"
                        d="M45.24,54.87a2.47,2.47,0,0,0,1.83-.79c.59-.55.69-1.1.35-2.27a20.91,20.91,0,0,0-2.36-4.64,8.07,8.07,0,0,1-1.31-4.11,11.83,11.83,0,0,1,.7-4.11A5.83,5.83,0,0,1,47.3,35.8a17.77,17.77,0,0,1,6.16-1.17,9.55,9.55,0,0,1,5.81,2,9.49,9.49,0,0,1,2.85,5,9.37,9.37,0,0,1-.87,5.86,31.09,31.09,0,0,1-2.89,4.11,2.25,2.25,0,0,0-.35,2A2.11,2.11,0,0,0,59.32,55,41.24,41.24,0,0,0,69,56.18c4.31.07,11,.1,11.55-.43s-.45-7-.44-9.28.26-8.73.44-9.54A3.37,3.37,0,0,1,81.42,35a2.58,2.58,0,0,1,2.25-.65c.84.21,3.55,2.42,5.32,3a11.77,11.77,0,0,0,6.4-.19,9.21,9.21,0,0,0,4-4c.77-1.64.73-5.89.06-7.61a9.21,9.21,0,0,0-4-4.73,8.7,8.7,0,0,0-6.65-.35c-2.4.77-4.18,2.56-4.84,2.76a3,3,0,0,1-2,.09A4.39,4.39,0,0,1,80.34,21a72.76,72.76,0,0,1-.65-10c0-3,1.44-9.54.9-10.42S73.37.11,69.5.18,60.37.65,59.63.8a3,3,0,0,0-1.92,1.65A3.17,3.17,0,0,0,58,4.74a35.57,35.57,0,0,1,2.33,5.46,6.84,6.84,0,0,1-.93,6.42,10.41,10.41,0,0,1-5.53,3.16c-2,.43-5.14.59-7.26-.35s-3.72-3.27-3.8-6.51,2-5.35,2.82-6.44a5.71,5.71,0,0,0,1.4-3.67A2.87,2.87,0,0,0,44.79.53C43.39.1,41.27.19,36.45.25S26,.58,25.62,1s.34,5.51.74,8.37A70.69,70.69,0,0,1,27,17.1a10.71,10.71,0,0,1-1.49,4.73,4.26,4.26,0,0,1-3.18,1.24,9,9,0,0,1-3.07-.57c-1.09-.44-4.15-2.61-5.57-3.18a10.23,10.23,0,0,0-5.09-.52,9.33,9.33,0,0,0-4.86,2A9,9,0,0,0,.52,26.7c-.29,2.48-.17,5.56,1.75,8A9.16,9.16,0,0,0,10,38.46c3.5,0,8.15-3.11,9.12-3.63a4.29,4.29,0,0,1,3-.52A4.92,4.92,0,0,1,24.58,36a5.43,5.43,0,0,1,.93,2.6,62.92,62.92,0,0,1,.47,8c-.06,4.24-1.21,7.84-.65,8.47s8.28.27,10.39.24S44.34,55,45.24,54.87Z"
                        isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.puzzleRed}
                        grabDuration={OBJECT_GRAB_DURATION}
                        shadowScale={OBJECT_SHADOW_SCALE}
                    />
                    <Group
                        id="Red_Puzzle_Piece_Base"
                        dataName="Red Puzzle Piece Base"
                    >
                        <Path
                            fill="#e01e3c"
                            d="M44.83,56a2.46,2.46,0,0,0,1.84-.79c.59-.55.69-1.1.35-2.27a20.91,20.91,0,0,0-2.36-4.64,8,8,0,0,1-1.31-4.11,11.6,11.6,0,0,1,.7-4.11A5.79,5.79,0,0,1,46.9,37a17.76,17.76,0,0,1,6.15-1.17,9.62,9.62,0,0,1,5.82,2,9,9,0,0,1,2,10.91A31.09,31.09,0,0,1,58,52.79a2.23,2.23,0,0,0-.35,2,2.07,2.07,0,0,0,1.31,1.35,40.68,40.68,0,0,0,9.63,1.22c4.31.07,11,.1,11.55-.43s-.46-7-.44-9.28.25-8.73.44-9.54A3.31,3.31,0,0,1,81,36.16a2.57,2.57,0,0,1,2.25-.64c.83.2,3.55,2.42,5.32,3A11.73,11.73,0,0,0,95,38.3a9.18,9.18,0,0,0,4-4c.77-1.64.73-5.89.06-7.61a9.3,9.3,0,0,0-4-4.73,8.75,8.75,0,0,0-6.65-.35c-2.4.77-4.18,2.57-4.84,2.76a3,3,0,0,1-2,.09,4.34,4.34,0,0,1-1.67-2.37,73,73,0,0,1-.65-10c0-3,1.44-9.55.89-10.43S73,1.27,69.09,1.35,60,1.81,59.23,2A2.93,2.93,0,0,0,57.3,3.61a3.27,3.27,0,0,0,.32,2.29A35,35,0,0,1,60,11.36,6.81,6.81,0,0,1,59,17.78a10.41,10.41,0,0,1-5.53,3.16c-2,.43-5.15.6-7.27-.35s-3.71-3.27-3.79-6.5,2-5.36,2.82-6.45A5.69,5.69,0,0,0,46.66,4a2.87,2.87,0,0,0-2.28-2.28C43,1.26,40.86,1.35,36,1.41s-10.47.33-10.83.72.35,5.5.75,8.36a70.69,70.69,0,0,1,.63,7.77A10.71,10.71,0,0,1,25.1,23a4.22,4.22,0,0,1-3.18,1.24,8.9,8.9,0,0,1-3.07-.57c-1.1-.44-4.15-2.61-5.57-3.18A10.37,10.37,0,0,0,8.18,20a9.27,9.27,0,0,0-4.85,2A9,9,0,0,0,.12,27.86c-.29,2.48-.17,5.57,1.75,8a9.18,9.18,0,0,0,7.77,3.78c3.5,0,8.14-3.12,9.12-3.64a4.32,4.32,0,0,1,3-.52,4.9,4.9,0,0,1,2.45,1.72,5.35,5.35,0,0,1,.93,2.59,65,65,0,0,1,.47,8c-.06,4.24-1.21,7.84-.65,8.47s8.27.27,10.39.24S43.94,56.12,44.83,56Z"
                        />
                        <Path
                            fill="#ee2a51"
                            d="M3.5,34.89c-1.2-1.72-2.45-6.3,0-9.8s8-3.3,10-2.46S17.16,25.23,19,26s4.17,1.41,5.91.77c2.53-.94,3.27-3.24,3.41-5.51s.09-5.1,0-7a74,74,0,0,0-1-7.54,3.35,3.35,0,0,1,.29-2.26,4.45,4.45,0,0,1,2-.92c.23,0,4.2,0,6.76-.11a21.73,21.73,0,0,1,5.73.5,1,1,0,0,1,.57,1.23,15.39,15.39,0,0,0-.66,1.61c-.21.78-2.26,4.94-1.84,8.25s1.29,5.26,3.39,7,5.31,2,7.66,1.84a21,21,0,0,0,6.5-1.22,9.4,9.4,0,0,0,4.56-4.4c1-2,.51-4.9.19-6.52a32.78,32.78,0,0,0-2-5.28,2.76,2.76,0,0,1-.27-2.05,3.65,3.65,0,0,1,2.19-1.1c.65-.05,4.9.36,7.13.29s6-.12,6.72,0a1.75,1.75,0,0,1,1.44,1c.29.57-.06,1.81-.19,2.6a81.78,81.78,0,0,0-.39,8.53c0,2.83.36,6,1.21,7.84s1.56,2.69,2.57,3a5.12,5.12,0,0,0,2.9,0,29.91,29.91,0,0,0,4.51-2.4A7.48,7.48,0,0,1,94,23.65a6.15,6.15,0,0,1,3.24,3.63,14.89,14.89,0,0,1,.32,5.17A5.59,5.59,0,0,1,95,36,7.32,7.32,0,0,1,91,37.17,8,8,0,0,1,87.08,36c-1.11-.62-2.17-1.81-3.36-2.28a3.53,3.53,0,0,0-3,.09,3.33,3.33,0,0,0-2,1.76,16.4,16.4,0,0,0-1.12,4.68c-.12,1.48-.22,5.13-.2,7.31s.27,5.81.3,6.25a2.76,2.76,0,0,1-.36,1.72C77,56,75.75,56,75,56s-4.35.14-5.82.15a51.75,51.75,0,0,1-5.56-.37c-.59-.12-3.47-.58-3.63-1.63s1.13-2.1,1.55-2.56a13.06,13.06,0,0,0,2-2.79,13.05,13.05,0,0,0,.94-5A10.9,10.9,0,0,0,62.88,39a16.3,16.3,0,0,0-4.39-4.14,24.11,24.11,0,0,0-7.21-1.4,12.86,12.86,0,0,0-6.56,2.18,11.55,11.55,0,0,0-3.11,6.43,17.22,17.22,0,0,0,.91,6.25A30.89,30.89,0,0,0,45,51.93c.41.56.49.87.3,1.36s-.37.59-1.07.73a49.19,49.19,0,0,1-8,.63A63.79,63.79,0,0,1,28,54.16a.79.79,0,0,1-.7-.72,3.18,3.18,0,0,1,.19-1.23,59.1,59.1,0,0,0,.58-8.1c0-3-1-7.38-3.42-9.38s-4-1.8-5.63-1.32a17,17,0,0,0-4.57,2.37,11,11,0,0,1-4.21,1.8C8.59,37.92,4.69,36.61,3.5,34.89Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M29.36,54c2.1.17,4.23.23,6.33.2A26.48,26.48,0,0,0,38.8,54c1-.14,2.07-.31,3.1-.51.51-.1,1-.2,1.52-.32l.34-.09.11-.05a.35.35,0,0,0,.13-.15h0v0a2.33,2.33,0,0,0-.22-.37c-.11-.18-.26-.37-.42-.58a7.85,7.85,0,0,1-.48-.75,11.14,11.14,0,0,1-1.21-3.23,18.38,18.38,0,0,1-.24-3.28.36.36,0,0,1,.71-.08h0a25.23,25.23,0,0,0,.84,3A9.87,9.87,0,0,0,44.51,50c.15.19.31.37.46.53a8.49,8.49,0,0,1,.56.68,3.19,3.19,0,0,1,.51,1,2.07,2.07,0,0,1-.19,1.57,2.49,2.49,0,0,1-1,.94,2.67,2.67,0,0,1-.64.22l-.44.07-1.63.15c-1.07.09-2.14.16-3.23.19a25,25,0,0,1-3.25-.1c-2.14-.19-4.25-.43-6.37-.72a.31.31,0,0,1-.28-.36A.32.32,0,0,1,29.36,54Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M26.87,52.64c.18-1.42.34-2.87.44-4.29a28.91,28.91,0,0,0,.06-4.26,23.51,23.51,0,0,0-.6-4.19,21.55,21.55,0,0,0-1.39-4,.5.5,0,0,1,.25-.66.49.49,0,0,1,.63.19,16.55,16.55,0,0,1,1.83,4.12A21.13,21.13,0,0,1,28.84,44a24.78,24.78,0,0,1-.14,4.48,30.94,30.94,0,0,1-.85,4.37.5.5,0,0,1-1-.2Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M11.09,37.87a10.29,10.29,0,0,1-4,.21,8,8,0,0,1-3.85-1.8A7.49,7.49,0,0,1,.8,32.59a9,9,0,0,1-.34-2.16,11.63,11.63,0,0,1,.1-2.12,9.09,9.09,0,0,1,1.65-4,10.12,10.12,0,0,1,1.47-1.6,6.94,6.94,0,0,1,2-1.16,9.82,9.82,0,0,1,4.16-.6,8.47,8.47,0,0,1,3.92,1.22.55.55,0,0,1,.18.76.56.56,0,0,1-.65.23h0a8.7,8.7,0,0,0-3.43-.4,8.42,8.42,0,0,0-3.23.88,4.4,4.4,0,0,0-1.23.88,8.29,8.29,0,0,0-1,1.27,7.43,7.43,0,0,0-1.11,3,10.05,10.05,0,0,0-.13,1.66A7.58,7.58,0,0,0,3.21,32a5.32,5.32,0,0,0,1.43,2.74,6.56,6.56,0,0,0,2.79,1.7,10.62,10.62,0,0,0,3.51.36.55.55,0,0,1,.15,1.09Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M17.9,24.81A8.79,8.79,0,0,0,23,26.44a3.33,3.33,0,0,0,2.22-.81,6.59,6.59,0,0,0,1.41-1.94,8.83,8.83,0,0,0,.84-2.43,16.72,16.72,0,0,0,.36-2.68,46.14,46.14,0,0,0-.4-5.62l-.12-1.45a13,13,0,0,0-.2-1.33c-.17-.9-.41-1.78-.66-2.77L26.38,7c0-.09,0-.14,0-.27V6.47c0-.25,0-.52.05-.8a3.37,3.37,0,0,1,.69-1.78,4,4,0,0,1,3.3-1.1c1.94,0,3.86,0,5.8,0s3.87.23,5.8.36a.5.5,0,0,1,.47.53.51.51,0,0,1-.5.47h0c-1.92,0-3.85-.06-5.75,0l-5.75.23-.71,0a4.85,4.85,0,0,0-.57.09,1.24,1.24,0,0,0-.74.45,1.63,1.63,0,0,0-.28.9c0,.19,0,.4,0,.64v.09h0a.3.3,0,0,1,0,.09l.08.29c.25.87.57,1.84.78,2.82a13,13,0,0,1,.25,1.52l.15,1.44a45.75,45.75,0,0,1,.52,5.88,19.1,19.1,0,0,1-.35,3.06,11,11,0,0,1-1,3,6.07,6.07,0,0,1-2.31,2.41,5.93,5.93,0,0,1-3.27.64,11.4,11.4,0,0,1-5.65-2.13.5.5,0,0,1-.14-.69.51.51,0,0,1,.7-.14h0Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M43.92,21.65a11,11,0,0,0,5.91,1.6,21.19,21.19,0,0,0,6.2-1,8.15,8.15,0,0,0,4.37-3.61,9.35,9.35,0,0,0,1.24-2.47,6.4,6.4,0,0,0,0-2.72,26.44,26.44,0,0,0-2.2-5.85c-.12-.26-.23-.5-.36-.84a4,4,0,0,1-.25-1.08,3.65,3.65,0,0,1,.06-1.18,3,3,0,0,1,.23-.65,1.91,1.91,0,0,1,.57-.66,4.64,4.64,0,0,1,2-.79,4.22,4.22,0,0,1,1-.08l.86,0c2.21.14,4.36.32,6.53.38s4.38.17,6.56.3a.5.5,0,0,1,.48.53.51.51,0,0,1-.47.47c-2.19.13-4.38.2-6.57.29s-4.43,0-6.6,0h-.78a3.46,3.46,0,0,0-.61.09,2.77,2.77,0,0,0-1,.43s0,0,0,0,0,.08,0,.13a1.69,1.69,0,0,0,0,.45,1.84,1.84,0,0,0,.14.51c.07.17.19.43.31.66a28.6,28.6,0,0,1,2.57,6.34,8.94,8.94,0,0,1,.11,3.8,6.93,6.93,0,0,1-.7,1.85,12.08,12.08,0,0,1-.94,1.53,10.62,10.62,0,0,1-2.61,2.57,10.2,10.2,0,0,1-3.36,1.46,25.08,25.08,0,0,1-6.78.64,10.26,10.26,0,0,1-3.41-.69,13.81,13.81,0,0,1-3-1.6.51.51,0,0,1-.12-.7.52.52,0,0,1,.66-.15Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M82.07,26.26a9.71,9.71,0,0,0,2.17-.54A4.82,4.82,0,0,0,86,24.62a9.76,9.76,0,0,1,2.14-1.48,9.65,9.65,0,0,1,2.52-.82,6.35,6.35,0,0,1,5.27,1.22,5,5,0,0,1,1.6,2.31,7.39,7.39,0,0,1,.4,2.59.5.5,0,0,1-1,.08v0a5.49,5.49,0,0,0-2-3.52A5,5,0,0,0,91,24.29a9.19,9.19,0,0,0-3.92,1.79,9.19,9.19,0,0,1-1.15.69,5.92,5.92,0,0,1-1.32.42,8,8,0,0,1-2.58.07.5.5,0,0,1-.43-.57.53.53,0,0,1,.42-.43Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M87.9,35.91a7.37,7.37,0,0,0,2.11.37,11.27,11.27,0,0,0,2.1-.26c.68-.16,1.38-.31,2.07-.53a5.76,5.76,0,0,0,1-.43,2.41,2.41,0,0,0,.85-.64.5.5,0,0,1,.7-.12.51.51,0,0,1,.12.71.46.46,0,0,1-.07.07,17.64,17.64,0,0,1-1.94,1.73,6.12,6.12,0,0,1-2.39,1,6.76,6.76,0,0,1-2.65,0,6.07,6.07,0,0,1-2.41-1,.51.51,0,0,1,.46-.89Z"
                        />
                        <Path
                            fill="#ee4078"
                            d="M59.63,53.07a46.68,46.68,0,0,0,3-4.42,8.8,8.8,0,0,0,.65-4.83,12.93,12.93,0,0,0-1.37-4.92,10.19,10.19,0,0,0-1.55-2,7.48,7.48,0,0,0-2-1.51l0,0a.5.5,0,0,1,.36-.93,7.64,7.64,0,0,1,2.58,1.47,10.21,10.21,0,0,1,2,2.17A12.63,12.63,0,0,1,65,43.57a9.41,9.41,0,0,1-1,5.87,22.38,22.38,0,0,1-3.69,4.29.51.51,0,0,1-.71,0,.5.5,0,0,1,0-.64Z"
                        />
                    </Group>
                </InteractableObject>
                <CuneiformTabletContainer
                    ref={cuneiformTabletRef}
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    top={initialCuneiformTabletPosition.top}
                    left={initialCuneiformTabletPosition.left}
                    rotate={initialCuneiformTabletPosition.rotate}
                    width={CUNEIFORM_TABLET_WIDTH}
                    delay={0 * OBJECT_DELAY_OFFSET_DURATION}
                    revealDuration={OBJECT_REVEAL_DURATION}
                    grabDuration={OBJECT_GRAB_DURATION}
                    isVisible={cuneiformTabletRevealed && !temporarilyHideCuneiformTablet}
                    isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.cuneiformTablet}
                    grabScale={OBJECT_GRAB_SCALE}
                    cursorZIndex={CURSOR_Z_INDEX}
                    dollyTowardObject={dollyingTowardCuneiformTablet}
                    dollyScale={DOLLY_SCALE}
                    dollyDuration={CUNEIFORM_DOLLY_DURATION}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            INTERACTABLE_OBJECT.cuneiformTablet,
                            // We don't want any hover effects when counting down
                            [CURSOR_SIGN.click, CURSOR_SIGN.grab],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                        onTouchEnd: (e: React.TouchEvent) => onObjectMouseUp(e),
                        onMouseUp: (e: React.MouseEvent) => onObjectMouseUp(e),
                    })}
                    onClick={() => onCuneiformTabletClick()}
                    {...getPressEventHandlers({
                        onTouchStart: (e: React.TouchEvent) => onObjectMouseDown({
                            ref: cuneiformTabletRef,
                            name: INTERACTABLE_OBJECT.cuneiformTablet,
                        }, e),
                        onMouseDown: (e: React.MouseEvent) => onObjectMouseDown({
                            ref: cuneiformTabletRef,
                            name: INTERACTABLE_OBJECT.cuneiformTablet,
                        }, e),
                    })}
                >
                    <CuneiformTablet
                        viewBox="0 0 265 209"
                    >
                        <InteractableObjectShadow
                            id="Cuneiform_Tablet_Shadow"
                            dataName="Cuneiform Tablet Shadow"
                            fill="#0F1E38"
                            d="M264.9,131c-0.1-1-0.3-2-0.5-3c-0.9-4-0.7-7.7-0.7-11.8c0-1.6,0.2-4.1-0.7-5.4
                            c-0.6-1,0.2-2.5,0.5-3.4c0.5-1.3,0.3-2.5,0.3-3.9c0-3.5-0.8-6.8-0.7-10.3c0.1-1.8,0.2-3.5,0.4-5.3c0.2-2.3,0.4-4.6,0.4-6.9
                            c0-1.2,0-2.4,0-3.6c0-0.9,0.2-2-0.3-2.7c0-0.1-0.1-0.1-0.1-0.2s0-0.2,0.1-0.3c0.6-1.2,0.3-2.8,0.3-4c0-1.5,0.3-2.9,0.1-4.5
                            c-0.3-1.6-0.9-3.1-0.9-4.7c0-1.2,0.3-2.3,0.4-3.5c0.5-3.3,0.4-6.7,0.3-10c0-2.4,0.5-5.5-0.5-7.7c-0.1-0.2-0.2-0.5-0.2-0.7
                            c0-0.1,0-0.2,0.1-0.3c0.4-1.2,0.4-2.1,0.4-3.3c-0.1-2.7-0.7-5.3-0.7-8.1c0-3.2,0.3-6.7-0.6-9.8c-0.7-2.6-2.1-5.1-4.3-6.7
                            c-2.5-1.8-5.8-2.3-8.9-2.7c-41.9-5-84.1-3-126.2-1.2c-20.6,0.9-41.2,1.7-61.8,1.7c-11.6,0-34.2-2.3-44.2,4.5
                            C11.4,17,9.2,24.1,8.8,30.8c-0.1,1.4-0.1,2.8-0.2,4.2c-0.1,1-0.4,2-0.3,3.1c0.1,0.9,0.8,1.6,0.6,2.4c-0.1,0.7-0.9,1.3-1.2,2
                            C7,44.8,7.3,47.7,7.4,50c0.1,3.2,0.2,6.5,0.1,9.7c-0.1,2.8-0.8,5.4-0.6,8.3c0.2,1.8,0.9,4.2,0.1,6c-0.9,2.2-1,4.6-1,7
                            c0.1,8.3,0.7,16.4-0.5,24.6c-0.1,0.4-0.1,0.9,0,1.4c0.1,0.4,0.3,0.7,0.4,1c0.6,1.9-0.3,3.7-0.6,5.6c-0.3,2.1-0.3,4.3-0.1,6.5
                            c0.3,5,0.9,9.9,1,14.9c0,2.1-0.4,4.5,0.3,6.5c0.6,1.9-0.9,3.3-0.5,5.1c0.3,1.3,0.2,2.4,0.5,3.6c0.3,2.1,0.3,4.3,0.4,6.4
                            c0,4.3-0.3,8.7,0,13c0.1,0.9,2.8,8,3.1,7.6c-0.4,0.5-0.8,1-1.3,1.4c-0.1,0.1-0.3,0.2-0.3,0.4c0,0.1,0,0.3,0,0.4
                            c0.4,3.8,0.6,7.6,1.2,11.4c0.4,3.8,1,7.8,3.7,10.6c9.7,9.9,30.5,7.4,42.6,7.4c25.2,0,50.4-2.2,75.7-2c8.1,0.1,16.2-0.1,24.3-0.4
                            c6.8-0.3,13.1,0.3,19.9,0.4c9.5,0.1,19.5-0.6,29.1-1c9.5-0.4,19.2,0.8,28.8,0.3c6.4-0.3,12.1-0.2,18.5-1.6c2.8-0.6,5.7-1.4,7.7-3.5
                            c2-2.2,2.5-5.5,2.8-8.5c0.2-1.7,0.3-3.4,0.5-5c0-2.2,0-4.3,0-6.5c0-2,0.2-4.2-0.9-5.9c0.1,0.1,0.8-0.9,0.9-1.1
                            c0.5-1.6-0.2-3.6-0.2-5.2c0-2.1,0.3-4.3,0.4-6.4c0.1-4.1,0-8.4,0.1-12.4c0-2.2,0.2-4.3-0.5-6.4c0-0.1-0.1-0.2-0.1-0.3
                            s0.1-0.3,0.1-0.4C264.5,139.2,265.3,134.6,264.9,131z"
                            isGrabbed={objectInitialClickPoint.name === INTERACTABLE_OBJECT.cuneiformTablet}
                            grabDuration={OBJECT_GRAB_DURATION}
                            shadowScale={OBJECT_SHADOW_SCALE}
                        />
                        <Group
                            id="Cuneiform_Tablet_Base"
                            dataName="Cuneiform_Tablet_Base"
                        >
                            <Path
                                fill="#DFC381"
                                d="M258.6,82.9c0.2-2.3,0.4-4.6,0.4-6.9c0-1.2,0-2.4,0-3.6c0-0.9,0.2-2-0.3-2.7c0-0.1-0.1-0.1-0.1-0.2
                                s0-0.2,0.1-0.3c0.6-1.2,0.3-2.8,0.3-4c0-1.5,0.3-2.9,0.1-4.5c-0.3-1.6-0.9-3.1-0.9-4.7c0-1.2,0.3-2.3,0.4-3.5
                                c0.5-3.3,0.4-6.7,0.3-10c0-2.4,0.5-5.5-0.5-7.7c-0.1-0.2-0.2-0.5-0.2-0.7c0-0.1,0-0.2,0.1-0.3c0.4-1.2,0.4-2.1,0.4-3.3
                                c-0.1-2.7-0.7-5.3-0.7-8.1c0-3.2,0.3-6.7-0.6-9.8c-0.7-2.6-2.1-5.1-4.3-6.7c-2.5-1.8-5.8-2.3-8.9-2.7c-42-5-84.2-3-126.3-1.2
                                C97.3,2.9,76.7,3.7,56.1,3.7c-11.6,0-34.2-2.3-44.2,4.5C6.4,12,4.2,19.1,3.8,25.8c-0.1,1.4-0.1,2.8-0.2,4.2c-0.1,1-0.4,2-0.3,3.1
                                c0.1,0.9,0.8,1.6,0.6,2.4c-0.1,0.7-0.9,1.3-1.2,2C2,39.8,2.3,42.7,2.4,45c0.1,3.2,0.2,6.5,0.1,9.7c-0.1,2.8-0.8,5.4-0.6,8.3
                                c0.2,1.8,0.9,4.2,0.1,6c-0.9,2.2-1,4.6-1,7c0.1,8.3,0.7,16.4-0.5,24.6c-0.1,0.4-0.1,0.9,0,1.4c0.1,0.4,0.3,0.7,0.4,1
                                c0.6,1.9-0.3,3.7-0.6,5.6c-0.3,2.1-0.3,4.3-0.1,6.5c0.3,5,0.9,9.9,1,14.9c0,2.1-0.4,4.5,0.3,6.5c0.6,1.9-0.9,3.3-0.5,5.1
                                c0.3,1.3,0.2,2.4,0.5,3.6c0.3,2.1,0.3,4.3,0.4,6.4c0,4.3-0.3,8.7,0,13c0.1,0.9,2.8,8,3.1,7.6c-0.4,0.5-0.8,1-1.3,1.4
                                c-0.1,0.1-0.3,0.2-0.3,0.4c0,0.1,0,0.3,0,0.4c0.4,3.8,0.6,7.6,1.2,11.4c0.4,3.8,1,7.8,3.7,10.6c9.7,9.9,30.5,7.4,42.6,7.4
                                c25.2,0,50.4-2.2,75.7-2c8.1,0.1,16.2-0.1,24.3-0.4c6.8-0.3,13.1,0.3,19.9,0.4c9.5,0.1,19.5-0.6,29.1-1c9.5-0.4,19.2,0.8,28.8,0.3
                                c6.4-0.3,12.1-0.2,18.5-1.6c2.8-0.6,5.7-1.4,7.7-3.5c2-2.2,2.5-5.5,2.8-8.5c0.2-1.7,0.3-3.4,0.5-5c0-2.2,0-4.3,0-6.5
                                c0-2,0.2-4.2-0.9-5.9c0.1,0.1,0.8-0.9,0.9-1.1c0.5-1.6-0.2-3.6-0.2-5.2c0-2.1,0.3-4.3,0.4-6.4c0.1-4.1,0-8.4,0.1-12.4
                                c0-2.2,0.2-4.3-0.5-6.4c0-0.1-0.1-0.2-0.1-0.3s0.1-0.3,0.1-0.4c1.6-3.3,2.4-7.9,2-11.6c-0.1-1-0.3-2-0.5-3
                                c-0.9-4-0.7-7.7-0.7-11.8c0-1.6,0.2-4.1-0.7-5.4c-0.6-1,0.2-2.5,0.5-3.4c0.5-1.3,0.3-2.5,0.3-3.9c0-3.5-0.8-6.8-0.7-10.3
                                C258.3,86.4,258.4,84.6,258.6,82.9z"
                            />
                            <Path
                                fill="#6A5028"
                                fillOpacity="0.3"
                                d="M259.9,126c-0.1-1-0.3-2-0.5-3c-0.9-4-0.7-7.7-0.7-11.8c0-1.6,0.2-4.1-0.7-5.4c-0.6-1,0.2-2.5,0.5-3.4
                                c0.5-1.3,0.3-2.5,0.3-3.9c0-3.5-0.8-6.8-0.7-10.3c0.1-1.8,0.2-3.5,0.4-5.3c0.2-2.3,0.4-4.6,0.4-6.9c0-1.2,0-2.4,0-3.6
                                c0-0.9,0.2-2-0.3-2.7c0-0.1-0.1-0.1-0.1-0.2s0-0.2,0.1-0.3c0.6-1.2,0.3-2.8,0.3-4c0-1.5,0.3-2.9,0.1-4.5c-0.3-1.6-0.9-3.1-0.9-4.7
                                c0-1.2,0.3-2.3,0.4-3.5c0.5-3.3,0.4-6.7,0.3-10c0-2.4,0.5-5.5-0.5-7.7c-0.1-0.2-0.2-0.5-0.2-0.7c0-0.1,0-0.2,0.1-0.3
                                c0.4-1.2,0.4-2.1,0.4-3.3c-0.1-2.7-0.7-5.3-0.7-8.1c0-3.2,0.3-6.7-0.6-9.8c-0.7-2.6-2.1-5.1-4.3-6.7c-2.5-1.8-5.8-2.3-8.9-2.7
                                c-41.9-5-84.1-3-126.2-1.2C97.3,2.9,76.7,3.7,56.1,3.7c-11.6,0-34.2-2.3-44.2,4.5C6.4,12,4.2,19.1,3.8,25.8
                                c-0.1,1.4-0.1,2.8-0.2,4.2c-0.1,1-0.4,2-0.3,3.1c0.1,0.9,0.8,1.6,0.6,2.4c-0.1,0.7-0.9,1.3-1.2,2C2,39.8,2.3,42.7,2.4,45
                                c0.1,3.2,0.2,6.5,0.1,9.7c-0.1,2.8-0.8,5.4-0.6,8.3c0.2,1.8,0.9,4.2,0.1,6c-0.9,2.2-1,4.6-1,7c0.1,8.3,0.7,16.4-0.5,24.6
                                c-0.1,0.4-0.1,0.9,0,1.4c0.1,0.4,0.3,0.7,0.4,1c0.6,1.9-0.3,3.7-0.6,5.6c-0.3,2.1-0.3,4.3-0.1,6.5c0.3,5,0.9,9.9,1,14.9
                                c0,2.1-0.4,4.5,0.3,6.5c0.6,1.9-0.9,3.3-0.5,5.1c0.3,1.3,0.2,2.4,0.5,3.6c0.3,2.1,0.3,4.3,0.4,6.4c0,4.3-0.3,8.7,0,13
                                c0.1,0.9,2.8,8,3.1,7.6c-0.4,0.5-0.8,1-1.3,1.4c-0.1,0.1-0.3,0.2-0.3,0.4c0,0.1,0,0.3,0,0.4c0.4,3.8,0.6,7.6,1.2,11.4
                                c0.4,3.8,1,7.8,3.7,10.6c9.7,9.9,30.5,7.4,42.6,7.4c25.2,0,50.4-2.2,75.7-2c8.1,0.1,16.2-0.1,24.3-0.4c6.8-0.3,13.1,0.3,19.9,0.4
                                c9.5,0.1,19.5-0.6,29.1-1c9.5-0.4,19.2,0.8,28.8,0.3c6.4-0.3,12.1-0.2,18.5-1.6c2.8-0.6,5.7-1.4,7.7-3.5c2-2.2,2.5-5.5,2.8-8.5
                                c0.2-1.7,0.3-3.4,0.5-5c0-2.2,0-4.3,0-6.5c0-2,0.2-4.2-0.9-5.9c0.1,0.1,0.8-0.9,0.9-1.1c0.5-1.6-0.2-3.6-0.2-5.2
                                c0-2.1,0.3-4.3,0.4-6.4c0.1-4.1,0-8.4,0.1-12.4c0-2.2,0.2-4.3-0.5-6.4c0-0.1-0.1-0.2-0.1-0.3s0.1-0.3,0.1-0.4
                                C259.5,134.2,260.3,129.6,259.9,126z M254.1,30.1c-0.1,0.4-0.2,0.7-0.4,1.1c-0.5,0.8-1.2,1.4-1.5,2.2c-0.4,1.3,2.3,5,2.4,6.9
                                c0,7.3-0.1,14.1,0.1,21.5c0.1,2.7-0.8,5.3-3.2,6.8c5.8,6.9,0.7,12.5,1.9,20.6c0.7,4.6,1.1,11.1-2,14.9c-0.2,0.2-0.4,0.4-0.4,0.7
                                c-0.1,0.7,0.8,1,1.5,1.4c1.4,0.7,2.1,2.4,2.2,3.9c0.1,1.6-0.3,3.1-0.4,4.7c-0.2,2.8,0.3,5.5,0.4,8.3c0.2,5-1.2,10-3.8,14.3
                                c1.6-0.2,3,1.1,3.5,2.6c1.7,5.1-0.1,13.1-0.5,18.3c-0.1,0.8-1.9,11.9-3,11c5.8,4.9,3,22.5-4.3,25.3c-7.4,2.8-16.2,1.2-23.9,1.2
                                c-37.4,0-74.7,0.7-112.2,1.4c-25.1,0.5-49.8,2.8-75,1.1c-8.4-0.5-17.5,0.9-23.2-6.9c-3.8-5.2-3-9.9-3.4-15.9
                                c-0.3-4-3-7.1-3.9-10.9c-0.9-3.8,0.2-8.1,0.1-12.1C5,145,8,137.6,5.7,130.4C4,124.9,4,119.1,4,113.4c0-3,0.2-6.4,2.5-8.4
                                c-1.4-0.9-1.3-3-1.1-4.7c1.1-7.8,0.7-15.1-0.2-23c-0.4-3.2-0.3-7.2,2.5-8.8c-2.2-0.6-2-3.7-1.6-6c1-5.6-3.5-23,2.2-26.1
                                c0.3-0.2,0.7-0.3,0.9-0.6c0.3-0.4-1.1-6.3-1-7.7c0.1-2.3,0-5,0.6-7.3c1.3-4.5,2.9-8.4,7.5-10.3c3.5-1.4,7.3-1.7,11-1.8
                                c21.3-1,42.7-1.3,64-1c15.5,0.2,31-1.6,46.5-2.4c20.5-1,41-0.8,61.5-0.8c8.3,0,16.5,0.2,24.7,0.6c6.9,0.4,13.4-0.6,20.4,0.8
                                c2.4,0.5,4.8,1.2,6.6,2.8c3.6,3.1,3.7,8.5,3.5,13.3c-0.1,1.9-0.1,3.8-0.2,5.6C254.3,28.6,254.3,29.4,254.1,30.1z"
                            />
                            <Path
                                fill="#AA8C4E"
                                d="M254.8,195.8c2-2.2,2.5-5.5,2.8-8.5c0.2-1.7,0.3-3.4,0.5-5c0-2.2,0-4.3,0-6.5c0-1.7,0.1-3.6-0.5-5.2
                                c0,0-0.1,0-0.1,0.1c-1.5,1-2.1,3.2-2.1,5.2c0.5,5.4,1.2,11.3-2.2,15.8c-5.4,7.2-16.8,7.3-24.9,6.7c-9.7-0.7-19.3-1.2-29-1
                                c-10,0.3-19.9,1-29.9,1.4c-3.9,0.2-7.7,0.3-11.6,0.2c-3.1,0-5.5-1.3-8.6-2.1c-6.6-1.6-13.4-2-20.2-1.1c-2.3,0.3-4.5,0.8-6.7,1.3
                                c-3.6,0.7-7.3,2-11,1.8c-3.3-0.2-6.5-0.3-9.8-0.2c-8.1,0.3-16.2,0.3-24.2-0.9c-2.6-0.4-5.2-0.9-7.8-0.5c-4.9,0.7-8.2,2.3-13.2,1.7
                                c-3.5-0.4-6.8-1.5-10.2-2c-7.2-1.1-14.2,0.3-21.2,1.7c-4.3,0.9-9.1-0.1-13.5-0.2c-0.2,0-0.3,0-0.5,0c10.4,7.3,28.9,5.2,40,5.2
                                c25.2,0,50.4-2.2,75.7-2c8.1,0.1,16.2-0.1,24.3-0.4c6.8-0.3,13.1,0.3,19.9,0.4c9.5,0.1,19.5-0.6,29.1-1c9.5-0.4,19.2,0.8,28.8,0.3
                                c6.4-0.3,12.1-0.2,18.5-1.6C249.9,198.7,252.9,197.9,254.8,195.8z"
                            />
                            <Path
                                fill="#AA8C4E"
                                d="M258.2,34C258.2,34,258.2,33.9,258.2,34c0-0.2,0-0.3,0.1-0.4c0.4-1.2,0.4-2.1,0.4-3.3
                                c-0.1-2.7-0.7-5.3-0.7-8.1c0-3.2,0.3-6.7-0.6-9.8c-0.6-2.1-1.6-4.1-3.1-5.6c-0.1,1.1-0.5,2.2-0.8,3.3c-1.5,5.1-0.6,10.2-0.8,15.4
                                c0,0.7-0.1,1.4-0.4,2c-0.2,0.4-0.6,0.8-1,1.1c-1.4,1.1-3.5,2.4-5.2,2.8c-2.3,0.6-3.8-0.5-6.1-0.8c-5.1-0.7-10.2,0.8-15.3,1.5
                                c-4.5,0.6-8.6-0.1-13.1-0.2c-3.9-0.1-7.9-0.6-11.8-0.4c-3.2,0.2-6.4,0.5-9.6,0.8c-2.9,0.2-4.6-0.5-7.4-0.8c-5.3-0.7-10.9,1.4-16,0
                                c-2.3-0.6-4.6-1.6-7-1.8c-2.1-0.2-4.1,0.1-6.2,0.3c-6.4,0.9-12.1,1.9-18.5,0.7c-6.3-1.3-12.7-2.6-19.1-1.6
                                c-3.4,0.6-6.6,1.7-10,1.9c-6.1,0.5-12.2-1.9-18.3-1.5c-2.4,0.2-4.8,0.8-7.2,1.3c-7.4,1.5-14.8,1.2-22.3,1.3
                                c-7.3,0.2-14.7,0.2-21.9-1.1c-1.8-0.3-3.6-0.7-5.5-0.8c-1.9,0-3.8,0.3-5.7,0.7c-4,0.7-7,0.9-10.9,0.7c-1,0-10.9-0.2-11,0.9
                                c0,0.3,0,0.5,0,0.8c0.1,0.9,0.8,1.6,0.6,2.4c-0.1,0.4-0.3,0.7-0.6,1.1c1.8,0.1,3.7-0.1,5.5-0.3c4.1-0.5,7.8-0.7,11.9-0.5
                                c11.3,0.6,22.7,0.6,34.1,0.3c17.1-0.4,34.2-1.5,51.2-2.3c28.8-1.3,57.4,3,86.1,2c11.1-0.4,22.2-0.7,33.3-0.9
                                c5.7-0.1,11.3-0.1,17,0C247.8,35.1,253,33.6,258.2,34z"
                            />
                            <Path
                                fill="#AA8C4E"
                                d="M79.6,165.1c-2.9,0.8-5.8,1.6-8.8,1.8c-9.3,0.5-18.7,1-28,0c-8-0.9-16.6-4.1-24.1,0.4
                                c-0.9,0.5-1.7,1.1-2.7,1.4c-1.1,0.3-2.2,0.2-3.3,0.2c-2.8-0.1-5.8,0.2-8.9,0.8c0.6,1.4,1.1,2.6,1.3,2.4c-0.4,0.5-0.8,1-1.3,1.4
                                c-0.1,0.1-0.1,0.1-0.2,0.2c3.3,0.2,6.7,0.2,10-0.2c5.8-0.7,10.8-3.9,16.5-4c13.5-0.2,27.1-0.5,40.6,0.5c6.2,0.4,12.4,0.6,18.7,0.6
                                c1.5,0,3.1,0.4,4.6,0.3c1.3-0.1,2.4-0.3,3.7-0.2c3.6,0.2,7.1,0.2,10.7,0.2c4.6,0,9.1-0.2,13.7-0.5c2.2-0.1,4.6-0.6,6.8-0.6
                                c1.7,0,3.4,0.2,5.1,0.2c20.6,0.3,41.2,1.1,61.7,0.4c9.9-0.3,20-1.3,29.9-0.7c6.2,0.3,12.4,1,18.6,0.8c4.6-0.1,9.3-1.6,13.9-2
                                c0.5-1.6-0.2-3.5-0.2-5.2c0-2.1,0.3-4.3,0.4-6.4c0.1-4.1,0-8.4,0.1-12.4c0-0.6,0-1.1,0-1.7c-0.2,0.6-0.5,1.2-0.7,1.8
                                c-1,2.7-1.9,5.5-2.1,6.4c-0.9,3.2-0.7,6.6-0.9,10c-0.5,7.3-10.7,5.2-15.4,5.3c-11.5,0.2-23.3-1.6-34.7,0.1
                                c-3.7,0.5-7.5,1.3-11.2,0.9c-4.1-0.5-7.9-2.4-12.1-2.5c-10.9-0.2-21.8,1.8-32.7,1.5c-8.5-0.3-16.9-0.6-25.4-0.7
                                c-4.3-0.1-8.7-0.2-13-0.1c-3.6,0.1-6.4,1.1-10,0c-3.1-1-6.4-2.5-9.7-2.5C86.7,163.1,83.1,164.1,79.6,165.1z"
                            />
                            <Path
                                fill="#AA8C4E"
                                d="M259,65.1c0-1.5,0.3-2.9,0.1-4.5c-0.3-1.6-0.9-3.1-0.9-4.7c0-1.2,0.3-2.3,0.4-3.5c0.5-3.3,0.4-6.7,0.3-10
                                c0-0.6,0-1.3,0.1-2.1c-0.4,1.3-0.7,2.6-1.1,3.9c-1.2,4.6-2.2,9.3-2.1,10.1c0.4,2.8,2.5,8.8-0.2,11c-2.2,1.7-6.9-0.8-9.3-1.2
                                c-4.2-0.7-8.5-0.7-12.8-0.4c-5.7,0.4-11.6,2-17.3,2c-4.7,0-9.6-0.8-14.5-0.9c-6.8-0.1-13.4-1.1-20.1-1.7
                                c-4.8-0.4-9.6-0.8-14.3-0.1c-6.8,1.1-12.6,2.5-19.6,2.2c-7.3-0.2-14.6-0.5-21.9-0.7c-9.3-0.2-17.8,1.8-27.1,2.1
                                c-3.9,0.1-7.8,0.2-11.7-0.1c-4-0.2-7.8-1.9-11.8-2.6c-2.6-0.4-5.2-0.4-7.7-0.3c-3.5,0.1-6.9,0.1-10.3,0.8c-3.6,0.7-7.4,1.7-11,1.9
                                c-4.2,0.2-8.2-1.6-12.4-1.4c-5.7,0.3-11.3,2.9-17.1,2.4c-1.2-0.1-2.4-0.3-3.6-0.5c-3.5-0.5-7.2-0.5-10.7,0c0,0.7-0.1,1.4-0.4,2
                                c-0.1,0.2-0.2,0.4-0.2,0.6c2.4,0.9,5.1,0.6,7.9,0.3c4.1-0.5,7.7-0.7,11.9-0.5c11.3,0.6,22.7,0.6,34.1,0.3
                                c23.3-0.6,46.5,0.3,69.8,0.4c11.1,0.1,22.1-0.1,33-1.1c11.3-1,23.1,0.8,34.5,0.4c9.9-0.3,20-1.3,29.9-0.7c6.2,0.3,12.4,1,18.6,0.8
                                c5.7-0.1,11.4-0.1,17.1-0.1c0-0.1,0-0.1,0.1-0.2C259.2,68,259,66.4,259,65.1z"
                            />
                            <Path
                                fill="#AA8C4E"
                                d="M204.9,131.9c-1.2,0.1-2.4,0.3-3.6,0.6c-5.5,1.4-11.5,1.6-17.3,1.2c-6.1-0.4-11.5-2.6-17.8-1.3
                                c-1.1,0.2-2.2,0.5-3.2,0.7c-13.1,2.3-26.9-0.2-40-0.2c-4.9,0-9.9,0-14.8,0.6c-6.3,0.8-13.5,2.8-19.7,0.3c-0.8-0.3-1.6-0.7-2.4-0.9
                                c-2-0.5-4,0-5.9,0.5c-5.9,1.5-10.8,1.2-16.9,0.5c-2.2-0.2-4.4-0.1-6.6-0.6c-4.2-0.9-7.1-2.1-11.5-1.5c-3.7,0.6-7.2,2.1-11,2.2
                                c-3.5,0.1-6.9-0.1-10.3,0.1c-7.2,0.5-15.1-0.4-22.6,0.1c0,0.8,0.1,1.5,0.4,2.2c0.3,0.8,0.1,1.5-0.1,2.3c1.3,0.1,2.5,0.1,3.8,0
                                c5.2-0.5,9.8-1.9,15.3-1.6c11.3,0.6,22.7,0.6,34.1,0.3c23.3-0.6,46.5,0.3,69.8,0.4c11.1,0.1,22.1-0.1,33-1.1
                                c14.1-1.3,28.2,0.2,42.3,1c13.6,0.8,27.2-0.1,40.8-0.4c5.9-0.1,11.8,0,17.8-0.1c1.4-3.3,2.2-7.6,1.8-11c-0.1-1-0.3-2-0.5-3
                                c-0.9-4-0.7-7.7-0.7-11.8c0-0.6,0-1.4,0-2.1c-1.1,0.7-2,1.6-2.6,2.7c-0.2,0.3-0.4,0.7-0.5,1.1c-0.5,1.4-0.4,3-0.2,4.5
                                c0.3,2.9,0.8,5.8,1.6,8.7c0.2,0.8,0.5,1.8,0,2.5c-0.2,0.4-0.6,0.6-0.9,0.9c-3.1,2.2-5.2,3.9-9.1,4.3c-7.1,0.7-14.2,0.6-21.3,0.6
                                C218.5,134.2,211.9,131.4,204.9,131.9z"
                            />
                            <Path
                                fill="#AA8C4E"
                                d="M44,99.3c-4.8,0.6-9.7-0.4-14.5-1.1c-5.1-0.7-9.9,0.1-15,0.7c-1.1,0.1-14.1,2.2-14.1,1.9c0,0.4-0.1,0.7,0,1.1
                                s0.3,0.7,0.4,1s0.2,0.6,0.2,0.9c8.3-0.1,16.4,0.8,24.7,1c10,0.2,20.3,0,30.3-0.3c23.3-0.6,46.5,0.3,69.8,0.4
                                c11.1,0.1,22.1-0.1,33-1.1c11.3-1,23.1,0.8,34.5,0.4c16.2-0.5,32.4,0.5,48.5,0.1c5.3-0.1,10.6-0.1,16-0.1c0.1-0.7,0.5-1.5,0.7-2
                                c0.5-1.3,0.3-2.5,0.3-3.9c0-3.5-0.8-6.8-0.7-10.3c0.1-1.8,0.2-3.5,0.4-5.3c0.2-2.3,0.4-4.6,0.4-6.9c0-1.2,0-2.4,0-3.6
                                c0-0.3,0-0.6,0-0.9c-0.7,0.5-1.4,1.2-1.8,2c-1,1.7-1.5,3.4-1.7,5.3c-0.3,2.6-0.2,5.2-0.4,7.8c-0.5,6.7,1.3,12.7-6.4,13.6
                                c-8,0.9-16.1-0.3-24.2,0.3c-15.6,1.3-31.3,1-47,0.6c-14.3-0.4-28-3.1-42.4-2.2c-7.2,0.5-13.9,3-21.2,2.8c-6.6-0.2-12.8-1.8-19.4-1
                                c-6.5,0.8-13.2-0.2-19.7-1c-5.3-0.6-10.5-1.3-15.7-2.2C54,96.6,49.2,98.7,44,99.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M40.2,10c-0.5,0-0.9,0-1.4,0c-4.5,0.1-8.8,0.4-13.2,1.2c-4.9,0.8-9.7,2.2-12.1,7c-1.4,2.8-1.3,6.1-2.4,9
                                c-0.9,2.3-2.6,4.6-4.1,1.3c-0.6-1.4-0.1-4.5,0.1-6c0.6-4,2.3-8.1,5.5-10.7C17.9,7.6,23.4,6.7,30,6.5c13.7-0.4,27.1-0.2,40.8-0.2
                                c17.4,0.3,34.6,2.1,52,0.5c7.1-0.7,14.2-1.7,21.4-2.3c13.7-1,27.4,0,41.1-0.4c14.2-0.4,28.4-0.7,42.6,0.5c5,0.4,10.1,0.2,15,1.2
                                c2.4,0.5,4.8,1.3,6.9,2.6c1,0.6,4.9,3.8,1.6,4.2c-1.5,0.2-3.7-1.2-5-1.9c-4.3-2-9.9-1.1-14.5-1.1c-3.6,0-7.2-0.1-10.8-0.7
                                c-6.8-1.2-13.5-4.5-20.3-3.5c-1.9,0.3-3.8,0.9-5.7,1.3c-5.6,1.2-11.2,0.2-16.8,0.4c-5.9,0.2-11.9,0.2-17.8,0.2
                                c-6.5,0-12.7-1-19.2,0.1c-9.8,1.6-18.6,6.3-28.9,4.4c-2.8-0.5-5.5-1.4-8.2-1.8c-5.1-0.6-10.8-0.3-15.8,0c-3.5,0.2-7,1-10.5,1.2
                                c-4.1,0.2-8.2,0.3-12.3,0c-4.6-0.3-9.2,0.6-13.9-0.1c-1.7-0.3-3.3-0.6-5-0.8C44.6,10,42.4,10,40.2,10z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M148.6,71.5c8.4,0.7,16.8,1.2,25.2,1.5c4.5,0.1,9.3-0.8,13.8-1.1c3.4-0.2,7.1-0.8,10.5-0.5
                                c3.2,0.3,6.3,1.4,9.4,2.1c4.3,0.9,8.8,0.9,13.1,0.3c4.8-0.6,8.7-2.4,13.6-2c3,0.2,6-0.1,9-0.2c3.9-0.2,7.9-0.6,11.8-1
                                c1.3-0.1,2.6-0.2,3.9-0.2c-0.1-0.2-0.1-0.5-0.3-0.7c0-0.1-0.1-0.1-0.1-0.2s0-0.2,0.1-0.3c0-0.1,0-0.1,0.1-0.2
                                c-3.1-0.3-6.2-0.5-9.3-0.6c-5.5-0.2-10.8,0.2-16.3,0.2s-11-0.1-16.5-0.2c-8.6-0.1-17.2-0.2-25.7-0.3c-6.3-0.1-12.7,0-19,0.1
                                c-15.6,0.3-31.2,0.9-46.7,0.3c-4.4-0.2-8.9-0.2-13.4-0.1c-6.8,0.1-13.6,0.2-20.4,0.4c-7.3,0.2-14.8-0.1-22.1-0.1
                                c-9.1,0-18.2-0.1-27.2-0.1c-3.4,0-6.7-0.1-10.1-0.1c-6.5-0.1-13.1-0.3-19.7-0.3c-1.9,0-4,0.1-5.5,1.4c-2.2,2-2.1,6.8-2.3,9.6
                                c-0.3,4.1-0.2,8.2-0.2,12.4c0,2-0.3,4.4,0.1,6.4c0.2,1,1.2,3.1,2.2,1.6c0.3-0.5,0.4-1,0.5-1.6c0.8-5.2,1-10.3,1.1-15.7
                                c0-1.7,0.3-3.8,1.5-5.1c1.4-1.5,3.5-1.2,5.3-1.5c2.4-0.4,4.3-2,6.5-3c7.4-3.4,15.8,0.1,23.6-0.6c1.6-0.1,3.1-0.8,4.7-1
                                c2.3-0.2,4.5,1.2,6.8,1.9c2.7,0.8,5.5,1.3,8.4,1.4c7,0.4,13.5-1.7,20.4-2.3c6.7-0.6,13.4-1.3,20.1-1c7.5,0.4,15.1,1.8,22.7,1.2
                                c1.8-0.1,3.6-0.6,5.3-1c1.8-0.5,3.3-0.3,5.1-0.4C142.2,71,145.4,71.2,148.6,71.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M258.8,68.8c-11.5-1.1-23.3-1.2-35-1.2c-7.9,0-15.8-0.3-23.8-0.4c-5.6,0-11.3,0.6-16.6-0.7
                                c-2.7-0.7-5.6-0.1-8.4,0.2c-3.2,0.3-6.4,0-9.6,0.1c-2.6,0.1-4.9,0.8-7.4,1.1c-6.1,0.6-13,0.1-19.1-0.1
                                c-14.7-0.4-29.5-0.1-44.2,0.1C79.3,68,63.6,68,48.2,68.3s-30.5-1.2-46-0.5c0,0.2-0.1,0.4-0.1,0.5c16.5,1.2,30.4,0.7,47.3,1.1
                                c18.4,0.5,39.2-0.2,57.7-0.3c82.7-0.7,103.7-0.4,151.5,0.3c0-0.1,0-0.1,0.1-0.2C258.7,69.1,258.8,68.9,258.8,68.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M257.6,169.4c-7.7-0.1-15.3,0.3-22.9,0.3c-4.5,0-9.1,0.1-13.6,0.1c-9.1,0.1-18.2,0.2-27.3,0.3
                                c-7.3,0.1-14.8,0.5-22.1,0.2c-6.8-0.3-13.6-0.4-20.4-0.5c-4.5-0.1-9-0.1-13.4,0.2c-15.6,0.8-31.2,0.2-46.8-0.1
                                c-6.3-0.1-12.7-0.3-19-0.1c-13.4,0.4-26.8,0.6-40.2,0.9c-5.2,0.1-10.3,0.3-15.5,0.5c-3.6,0.1-9-0.7-8.2,4.6
                                c0.7,4.9,2.1,9.6,3.4,14.3c0.4,1.5,1.4,3.2,3.2,2.8c0.1-2.8-1.7-6-2.6-8.7c-1.1-3.2-0.7-6.6,2.2-8.6c4-2.7,7.3-0.9,11.8-0.9
                                c2.7,0,5.4-0.6,8.2-0.6c3.2,0,6.4,1,9.7,1.1c4.1,0.2,8.8-1,12.7-2c4.1-1.1,8.3-0.8,12.5,0c2,0.4,4,0.9,6,0.7
                                c2.6-0.3,4.8-1.5,7.2-2c2.7-0.6,5.8,1.3,8.4,1.7c1.3,0.2,2.7,0.2,4,0.1c6.2-0.2,12.3-0.5,18.5-0.8c6.4-0.2,12.2,1.3,18.4,2
                                c5.3,0.6,10.6,0.2,15.8-0.5c2.5-0.3,5.2-1.2,7.8-1.2c2.1,0,4.4,0.5,6.6,0.6c5.4,0.2,10.8,0.3,16.1,0.2c3,0,5.9,1,8.9,0.5
                                c1.7-0.3,3.3-1.3,5-1.3c1.5,0,3.1,0.6,4.6,0.8c4.5,0.6,9.1,0.8,13.7,0.8c2.6,0,5.3-0.2,7.9-0.6c2.5-0.3,5.1-1.3,7.6-1.2
                                c3,0.1,5.8,1.1,8.8,1.2c3.8,0.1,7.2-1.6,10.9-1.9c1.1-0.1,12.2-1.6,12.3-1.5c-0.1-0.4-0.3-0.8-0.5-1.1
                                C257.2,169.9,257.4,169.7,257.6,169.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M215,169.5c-7.3-0.1-14.7-0.2-22-0.3c-5.8,0-11.7-1.1-17.1-0.2c-2.4,0.4-5.1,0-7.5,0
                                c-14.8-0.2-29.5-0.4-44.3,0.1c-20.3,0.7-39.8,0-60.2,0.3c-7.9,0.1-16.8-0.1-24.7-0.1c-3,0-6,0.1-9,0.1c-2.6,0-4.5-0.8-7-1
                                c-1.8-0.1-2.9,1-4.4,1.4c-2,0.5-4.6,0.2-6.7,0.4c-2.4,0.2-5,0.7-7.5,1.1c0.3,0.6,0.5,0.9,0.6,0.8c0,0,0,0-0.1,0.1
                                c47.5-1,68.5-1.9,151-1.4c18.5,0.1,39.3,0.8,57.8,0.2c15.5-0.6,28.7-0.1,43.4-1.1l0,0c0.1,0.1,0.8-0.9,0.9-1.1c0-0.1,0-0.1,0-0.2
                                C243.7,168.1,229.4,169.8,215,169.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M137,36.8c2.4,0,4.9-0.2,7.3-0.2c2.2,0,4.5-0.2,6.6,0c1.9,0.2,3.7,0.9,5.6,1.2c3.1,0.5,6.2,0.9,9.3,1.2
                                c6.3,0.6,12.7,0.7,19.1,0.4c5.4-0.2,10.7-0.9,15.9-2.1c2.3-0.5,4.5-1.4,6.8-1.4c2.2,0,4.4,0,6.6,0c8,0,16,1,24,1.2
                                c6.9,0.2,13.5-1.3,20.3-2.1c0-0.1-0.1-0.2-0.1-0.3c-0.1-0.2-0.2-0.5-0.2-0.7c0-0.1,0-0.2,0.1-0.3c0-0.2,0.1-0.3,0.1-0.5
                                c-6.4,0.2-12.9,0.5-19.3,0.6c-5.4,0.1-10.7,0.1-16.1,0.2c-10.7,0.1-21.5,0.2-32.2,0.3c-8.6,0.1-17.5,0.5-26.1,0.3
                                c-8-0.2-16-0.2-24.1-0.3c-5.3,0-10.6,0-15.8,0.2c-18.4,0.7-36.9,0.3-55.3,0.1c-7.5-0.1-15-0.2-22.5,0c-7.2,0.2-14.4,0.3-21.6,0.4
                                c-3.7,0-7.4,0-11.1,0.1c-2.2,0-6.8-1.1-8.1,1.2c-0.6,1-0.6,2.2-0.7,3.3c-0.4,7.9-0.4,15.7,0,23.6c0,0.8,0.2,1.8,0.9,2.2
                                c2.1-2.4,2.4-6,2.1-9.2c-0.3-4-1.2-8.4,0.3-12.2c1.2-3.1,3.7-4.9,7-5.4c1.4-0.2,3.2-0.6,4.6-0.4c2.3,0.2,4.3,1.3,6.7,1.4
                                c4.6,0.1,9-1.8,13.6-2.4c4.6-0.6,9.6-0.2,14.1,1.1c6,1.7,13.3,1.5,19.4,0.5c3.5-0.6,7-1.5,10.5-1.7c3.4-0.2,7,0.7,10.4,0.8
                                c7.2,0.3,14.3-0.7,21.4-1.5c4.5-0.5,9.4-0.9,13.8-0.1C132.4,36.8,134.7,36.8,137,36.8z"
                            />
                            <Path
                                fill="#463116"
                                d="M258.5,32.6c-14.3,0.1-28.3,1.1-42.7,1c-18.2-0.2-36.7-0.1-55.1-0.2c-17.4-0.1-34.9-0.2-52.3,0.3
                                c-24,0.7-47.1,0.2-71.1,0.5c-9.4,0.1-19.8,0-29.2,0.1c-1.5,0-2.9,0-4.4,0c0.2,0.4,0.3,0.8,0.2,1.2v0.1C39.8,35,72,34.7,146.2,34.7
                                c21.8,0,46.4,0.5,68.2-0.2c15.7-0.4,29.2-0.3,43.8-0.8c0-0.1,0-0.2,0.1-0.3C258.4,33.2,258.4,32.9,258.5,32.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M7.3,120.1c-0.1-2.9-1-7.3,0.6-9.9c3.1-5.2,14.3-3.5,19-2.9c5.4,0.6,10.8,0.6,16.2-0.2
                                c4.9-0.7,9.6-0.2,14.6-0.5c5.5-0.3,10.4,2.2,15.8,2.3c2,0,4-0.4,6-0.8c12.8-2.8,26.5,0.3,39.6,0.3c1.8,0.1,3.6,0.1,5.3-0.3
                                c1.7-0.4,3.3-0.5,5.1-0.7c4.4-0.4,8.8-0.8,13.2-1c3.6-0.2,7.3-0.9,10.8,0.3c1.2,0.4,2.3,1,3.5,1.3c1,0.2,2,0.2,3,0.2
                                c3.3,0,6.4-0.8,9.6-0.7c3.6,0.1,7.2,0.1,10.7,0c7.6-0.1,15.1-0.6,22.7-0.7c3-0.1,5.2-0.8,8.1,0.2c2.8,0.9,5.4,1.6,8.4,1.8
                                c1.4,0.1,2.7,0.1,4.1,0.1c4.6,0,9.1,0.6,13.7,0.9c3.5,0.2,6.9-0.4,10.2-1.2c3.2-0.9,6-1.3,9.4-1.6c0.5,0,1-0.1,1.5-0.1
                                c-0.1-0.4-0.2-0.7-0.4-1c-0.3-0.5-0.3-1.2-0.1-1.8c-5.3,0.2-10.6,0.4-16,0.4s-10.7,0-16.1,0.1c-10.7,0-21.5,0.1-32.2,0.1
                                c-8.6,0-17.5,0.3-26.1,0.1c-8-0.2-16-0.4-24.1-0.5c-5.3-0.1-10.6-0.1-15.8,0.1c-18.4,0.6-36.9,0.1-55.3-0.3
                                c-7.5-0.1-15-0.3-22.5-0.1c-7.9,0.1-15.8,0.2-23.7,0.2c-3.9,0-7.9,0.4-11.8,0.1c-2.4-0.1-6.1-1.5-8.2,0.1c-2,1.5-2.8,5.9-3.3,8.2
                                c-1.4,6.7-0.5,13.8,2.6,19.9c0.3,0.5,0.5,1,1,1.3c2.4,1.2,2.1-3.4,2-4.4C8.1,126,7.3,123.1,7.3,120.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M218.7,104c-18.2-0.3-36.7-0.4-55.1-0.6c-17.4-0.2-34.9-0.4-52.3-0.1c-14.3,0.3-29.6,0.7-43.8-0.4
                                c-9.9-0.7-20-0.9-29.9-0.4c-4.7,0.2-9.6,1.1-14.2,1.1c-4,0-8.2-0.4-12.3-0.4c-3.4,0-6.7,0-10.1,0.1c0.1,0.5,0.1,1,0.1,1.4
                                c38.4-0.4,69.9-0.5,148.1,0.1c21.8,0.2,46.4,0.8,68.2,0.3c14.5-0.3,27.2-0.1,40.5-0.5c0-0.4,0.2-0.9,0.3-1.4
                                C245,103.4,232,104.2,218.7,104z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M258.2,136.7c-5.4,0.2-10.8,0.5-16.2,0.5s-10.7,0-16.1,0.1c-10.7,0-21.5,0.1-32.2,0.1
                                c-8.6,0-17.5,0.3-26.1,0.1c-8-0.2-16-0.4-24.1-0.5c-5.3-0.1-10.6-0.1-15.8,0.1c-18.4,0.6-36.9,0.1-55.3-0.3
                                c-7.5-0.1-15-0.3-22.5-0.2c-7.4,0.1-14.8,0.2-22.2,0.3c-3.7,0.1-7.4,0.1-11.1,0.2c-3.7,0.1-7.8-0.2-11.1,1.9
                                c-0.5,0.3-0.9,0.7-1.2,1.2c-0.2,0.4-0.2,0.8-0.3,1.2c-0.2,2-0.3,3.9-0.5,5.9c-0.2,2.5-0.4,5.1-0.2,7.6c0.2,2.3,1.1,4.4,1.4,6.6
                                c0.3,2.2,1,4.3,1.6,6.5c0.4,0,0.8,0,1.2,0c1.5,0-0.3-7.7-0.3-8.4c-0.1-3,1.2-6.2,1.3-9.3c0.4-7.3,2.4-11,10.3-11
                                c7,0,13.9-0.4,20.9-0.1c4,0.2,8,0.3,12,0.2c3.8-0.1,7.4,1.3,11.3,1.1c1.5-0.1,2.9-0.3,4.3-0.5c3.6-0.5,6.6-0.9,10.2-0.8
                                c4.9,0.2,10-0.5,14.9-0.1c2,0.2,3.9,0,5.8,0c1.7,0,2.9,0.7,4.5,1.2c4,1.4,8.3,1.6,12.4,1c2.7-0.4,5.3-1.3,8-1.7
                                c3.4-0.5,6.9-0.4,10.3-0.2c4.8,0.2,10.3,1.2,15.1,0.6c3.2-0.4,6.2-0.7,9.3-0.1c3.8,0.7,7.8,0.9,11.6,1.4c4.2,0.5,8.3,1,12.5,1.1
                                c7.9,0.3,15.9-0.7,23.8-1.2c4-0.2,8.3-0.8,12.3,0.1c3.3,0.7,6.9,0.8,10.2,0.1c2.2-0.5,3.5-0.8,5.8-0.4c3.2,0.6,7.4,1.1,10.7,0.6
                                c4.1-0.7,7.3-1.7,11.5-1.7c0.6,0,1.2,0,1.9,0c-0.1-0.6-0.2-1.2-0.4-1.8c0-0.1-0.1-0.2-0.1-0.3s0.1-0.3,0.1-0.4
                                C258,137.3,258.1,137,258.2,136.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M258.4,136.3c-13.2,0.1-26.3,0.9-39.7,0.7c-35.9-0.6-71.5-1.4-107.4-0.6c-11.9,0.2-23.7,0.2-35.6,0.1
                                c-6,0-12-0.1-18-0.1c-4,0-7.7-0.8-11.8-0.8c-4.4,0.1-8.8,0.6-13.2,0.6c-7.3,0.1-14.5,0-21.8,0c-3.2,0-6.4,0-9.6,0v0.1
                                c0.2,0.5,0.2,0.9,0.1,1.3c38.1-0.4,69.7-0.5,147.5,0.1c21.6,0.2,43.3,1.1,64.9,0.8c9.7-0.1,19.2-0.7,29-0.8c5,0,9.9-0.1,14.9-0.2
                                c0,0,0-0.1,0.1-0.1C258.1,137.1,258.2,136.7,258.4,136.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M116.9,98.2c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.2,3.7-0.2,5.5
                                c-0.1,1.6,0,3.4-0.5,4.9C117.7,95.9,117.2,97,116.9,98.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M130.2,92.9c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.4-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9
                                c-0.5,0.6-0.7,1.2-1.2,1.8c-0.5,0.7-1.2,1.2-1.9,1.7c1.2,0,2.2-0.4,3.3-0.6c1.2-0.2,2.4-0.6,3.6-1c1.4-0.6,2.9-1.5,4.3-1.9
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.9c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C130.8,92.9,130.5,92.9,130.2,92.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M119.7,87.7c-0.1-1.1-0.4-2.1-0.7-3.2c2,1.1,3.5,2.8,5.1,4.4c1.3,1.3,2.6,2.6,4.5,2.8
                                c2.1,0.1,4.3,0.4,6.4,0.6c1.1,0.1,2.2,0.3,3.3,0.5c1,0.2,2.1,0.6,3.1,0.6c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2
                                c-0.5,0-0.7-0.1-0.8-0.7C119.9,91.1,119.9,89.4,119.7,87.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M119.3,94.5c-0.1,0.6-0.1,1.2-0.1,1.7c0-0.2,1.2-0.9,1.3-1c2.7-1.8,6-1.7,9.2-1.7c3,0,6,0,9,0.1
                                c1,0,2,0,3-0.2c-3.8-0.3-7.6-0.5-11.4-0.7c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.3-0.6-0.6-0.9-1
                                c-0.9-1.2-1.7-2.5-2.3-3.9c0.1,1.6,0.3,3.3,0.1,4.9C119.5,92.7,119.4,93.6,119.3,94.5z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M104.2,27.8c0.2,0,0.4-0.1,0.5-0.2s0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.6,1.1-1c0.2-0.2,0.4-0.4,0.5-0.7
                                c0.2-0.3,0.2-0.7,0.3-1.1c0.2-1.5,0.3-3.1,0.1-4.6c-0.2-2.4-1.3-4.2-2-6.4c-0.1-0.1-0.2,0.1-0.2,0.2c-0.2,1.8,0.2,3.7,0.2,5.5
                                c0.1,1.6,0.3,3.3-0.1,4.9C104.8,25.4,104.4,26.6,104.2,27.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M117,21.3c-0.6,0-1.1,0.1-1.7,0.1c-1,0-1.9-0.2-2.9-0.1c-1.1,0.1-2,0-3,0.6c-0.9,0.5-1.6,1.3-2.2,2.1
                                c-0.5,0.6-0.6,1.3-1,1.9c-0.4,0.7-1.1,1.3-1.7,1.8c1.1-0.1,2.1-0.6,3.2-0.9c1.2-0.3,2.4-0.8,3.5-1.3c1.4-0.7,2.7-1.7,4.2-2.2
                                c1.6-0.6,3.4-0.8,5.1-0.9c0.8,0,1.7-0.1,2.5-0.2c1.7-0.2,4.1-0.4,5.5-1.4c-1.5,0.2-3,0.1-4.4,0.2c-2,0.1-4,0.1-6,0.2
                                C117.6,21.3,117.3,21.3,117,21.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M106.1,17.1c-0.2-1.1-0.6-2.1-0.9-3.1c2.1,0.9,3.7,2.5,5.4,4c1.4,1.1,2.9,2.4,4.7,2.4c2.1,0,4.3,0,6.4,0.1
                                c1.1,0,2.2,0.1,3.3,0.2c1,0.1,2.2,0.4,3.2,0.3c-3.5,0.3-7,0.5-10.5,0.8c-3.3,0.2-6.6,0.8-9.9,1.1c-0.5,0-0.7-0.1-0.9-0.6
                                C106.6,20.4,106.4,18.7,106.1,17.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M106.4,23.9c0,0.6,0,1.2,0,1.7c0-0.2,1.1-1,1.2-1.1c2.6-2.1,5.8-2.3,9-2.5c3-0.2,6-0.5,9-0.7
                                c1-0.1,2-0.2,2.9-0.5c-3.8,0-7.6,0.1-11.4,0.3c-2.1,0.1-4.4,0.6-6.4-0.1c-0.4-0.1-0.7-0.2-1.1-0.4c-0.4-0.2-0.7-0.5-1-0.9
                                c-1-1.1-1.9-2.3-2.7-3.6c0.2,1.6,0.6,3.2,0.5,4.9C106.4,22,106.4,22.9,106.4,23.9z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M117.8,192.7c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9
                                c0.2-0.2,0.4-0.4,0.6-0.6c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2
                                c-0.4,1.8-0.2,3.7-0.2,5.5c-0.1,1.6,0,3.4-0.5,4.9C118.6,190.4,118.1,191.5,117.8,192.7z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M131.1,187.4c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.4-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9
                                c-0.5,0.6-0.7,1.2-1.2,1.8c-0.5,0.7-1.2,1.2-1.9,1.7c1.2,0,2.2-0.4,3.3-0.6c1.2-0.2,2.4-0.6,3.6-1c1.4-0.6,2.9-1.5,4.3-1.9
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.9c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C131.7,187.4,131.4,187.4,131.1,187.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M120.6,182.2c-0.1-1.1-0.4-2.1-0.7-3.2c2,1.1,3.5,2.8,5.1,4.4c1.3,1.3,2.6,2.6,4.5,2.8
                                c2.1,0.1,4.3,0.4,6.4,0.6c1.1,0.1,2.2,0.3,3.3,0.5c1,0.2,2.1,0.6,3.1,0.6c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2
                                c-0.5,0-0.7-0.1-0.8-0.7C120.8,185.6,120.8,183.9,120.6,182.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M120.2,189c-0.1,0.6-0.1,1.2-0.1,1.7c0-0.2,1.2-0.9,1.3-1c2.7-1.8,6-1.7,9.2-1.7c3,0,6,0,9,0.1c1,0,2,0,3-0.2
                                c-3.8-0.3-7.6-0.5-11.4-0.7c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.3-0.6-0.6-0.9-1c-0.9-1.2-1.7-2.5-2.3-3.9
                                c0.1,1.6,0.3,3.3,0.1,4.9C120.4,187.1,120.3,188.1,120.2,189z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M78.3,62.5c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.2,3.7-0.2,5.5
                                c-0.1,1.6,0,3.4-0.5,4.9C79,60.2,78.5,61.3,78.3,62.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M91.5,57.2c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.4-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9
                                c-0.5,0.6-0.7,1.2-1.2,1.8c-0.5,0.7-1.2,1.2-1.9,1.7c1.2,0,2.2-0.4,3.3-0.6c1.2-0.2,2.4-0.6,3.6-1c1.4-0.6,2.9-1.5,4.3-1.9
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.9c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C92.2,57.2,91.9,57.2,91.5,57.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M81.1,52c-0.1-1.1-0.4-2.1-0.7-3.2c2,1.1,3.5,2.8,5.1,4.4c1.3,1.3,2.6,2.6,4.5,2.8c2.1,0.1,4.3,0.4,6.4,0.6
                                c1.1,0.1,2.2,0.3,3.3,0.5c1,0.2,2.1,0.6,3.1,0.6c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2c-0.5,0-0.7-0.1-0.8-0.7
                                C81.3,55.4,81.2,53.7,81.1,52z"
                            />
                            <Path
                                fill="#463116"
                                d="M80.7,58.8c-0.1,0.6-0.1,1.2-0.1,1.7c0-0.2,1.2-0.9,1.3-1c2.7-1.8,6-1.7,9.2-1.7c3,0,6,0,9,0.1c1,0,2,0,3-0.2
                                c-3.8-0.3-7.6-0.5-11.4-0.7c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.3-0.6-0.6-0.9-1c-0.9-1.2-1.7-2.5-2.3-3.9
                                c0.1,1.6,0.3,3.3,0.1,4.9C80.9,56.9,80.8,57.9,80.7,58.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M103,40.5c-1.4,0.3-2.8,0.7-4.3,0.9s-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8C98.9,45,101.7,43.5,103,40.5
                                C103.1,40.5,103,40.5,103,40.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M103.5,39.9c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C103.6,39.8,103.6,39.9,103.5,39.9z"
                            />
                            <Path
                                fill="#463116"
                                d="M93.9,48.8c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C96,46.9,95.2,48.1,93.9,48.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M167.4,74.6c-1.4,0.3-2.8,0.7-4.3,0.9c-1.5,0.2-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8c1.9-2.5,4.7-4,6-6.9
                                C167.5,74.6,167.5,74.6,167.4,74.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M168,74c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8c-1.6-0.5-3.1-1.5-4.4-2.5
                                c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6c1.4-0.3,2.8-0.7,4.1-1.3
                                C168,73.9,168,74,168,74z"
                            />
                            <Path
                                fill="#463116"
                                d="M158.4,82.9c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C160.4,81.1,159.6,82.2,158.4,82.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M99.8,74.6c-1.4,0.3-2.8,0.7-4.3,0.9c-1.5,0.2-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8c1.9-2.5,4.7-4,6-6.9
                                C99.9,74.6,99.8,74.6,99.8,74.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M100.3,74c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C100.4,73.9,100.4,74,100.3,74z"
                            />
                            <Path
                                fill="#463116"
                                d="M90.7,82.9C90.5,83,90.4,83,90.3,83c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C92.8,81.1,92,82.2,90.7,82.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M28.8,139.9c-1.2,0.3-2.4,0.7-3.7,0.9c-1.3,0.2-2.6,0.2-3.9,0.3c-1.6,0-3.4,0.2-4.9-0.3
                                c-0.2,0-0.3-0.1-0.5-0.2c-0.8-0.3-1.6-0.5-2.4-0.7c0,0.8,0.7,1.9,1.1,2.5c0.5,0.7,1.1,1.4,1.7,2c0.3,0.4,0.7,0.8,1,1.2
                                c1.8,2.4,2.8,5.1,2.9,8.3c0.1,4,0.4,8.1,1,12.1c0.2-3.9,0.4-7.8,0.6-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,0.9-3.4,1.8-4.8
                                c1.6-2.5,4-4,5.2-6.9C28.9,139.9,28.8,139.9,28.8,139.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M29.3,139.3c-0.6,1-1.6,1.9-2.5,2.5s-1.9,1.2-2.9,1.7c-1.5,0.8-3.3,1.4-5,0.8c-1.4-0.5-2.6-1.5-3.8-2.5
                                c-0.7-0.6-1.3-1.4-1.8-2.2c0.8,0.3,1.6,0.5,2.4,0.7c2,0.5,4.1,0.9,6.1,0.7c1.4-0.1,2.7-0.3,4-0.6c1.2-0.3,2.4-0.7,3.5-1.3
                                C29.3,139.2,29.3,139.3,29.3,139.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M21,148.2c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.6-1-1.4-1.5-2c-0.6-0.7-1.2-1.4-1.9-2.1c-0.9-0.9-1.8-1.9-2.7-2.8
                                c2,1.1,4.3,3.1,6.6,2.7c1.1-0.2,2.2-0.7,3.3-1.2c0.7-0.4,1.4-0.6,2.2-1c-0.3,0.3-0.6,0.6-0.9,0.9c-0.5,0.5-0.9,1-1.4,1.6
                                c-0.3,0.3-0.6,0.7-0.8,1C22.8,146.4,22.1,147.5,21,148.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M46.2,140.5c-1.2,0.3-2.4,0.7-3.7,0.9s-2.6,0.2-3.9,0.3c-1.6,0-3.4,0.2-4.9-0.3c-0.2,0-0.3-0.1-0.5-0.2
                                c-0.8-0.3-1.6-0.5-2.4-0.7c0,0.8,0.7,1.9,1.1,2.5c0.5,0.7,1.1,1.4,1.7,2c0.3,0.4,0.7,0.8,1,1.2c1.8,2.4,2.8,5.1,2.9,8.3
                                c0.1,4,0.4,8.1,1,12.1c0.2-3.9,0.4-7.8,0.6-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,0.9-3.4,1.8-4.8c1.6-2.5,4-4,5.2-6.9
                                C46.2,140.5,46.2,140.5,46.2,140.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M46.6,139.9c-0.6,1-1.6,1.9-2.5,2.5s-1.9,1.2-2.9,1.7c-1.5,0.8-3.3,1.4-5,0.8c-1.4-0.5-2.6-1.5-3.8-2.5
                                c-0.7-0.6-1.3-1.4-1.8-2.2c0.8,0.3,1.6,0.5,2.4,0.7c2,0.5,4.1,0.9,6.1,0.7c1.4-0.1,2.7-0.3,4-0.6c1.2-0.3,2.4-0.7,3.5-1.3
                                C46.7,139.8,46.7,139.8,46.6,139.9z"
                            />
                            <Path
                                fill="#463116"
                                d="M38.4,148.7c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.6-1-1.4-1.5-2c-0.6-0.7-1.2-1.4-1.9-2.1
                                c-0.9-0.9-1.8-1.9-2.7-2.8c2,1.1,4.3,3.1,6.6,2.7c1.1-0.2,2.2-0.7,3.3-1.2c0.7-0.4,1.4-0.6,2.2-1c-0.3,0.3-0.6,0.6-0.9,0.9
                                c-0.5,0.5-0.9,1-1.4,1.6c-0.3,0.3-0.6,0.7-0.8,1C40.2,146.9,39.4,148,38.4,148.7z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M221.4,173.7c-1.1,0.3-2.1,0.6-3.2,0.8c-1.1,0.2-2.2,0.2-3.3,0.2c-1.4,0-2.9,0.1-4.2-0.2
                                c-0.2,0-0.3-0.1-0.4-0.1c-0.7-0.2-1.3-0.4-2-0.6c0,0.7,0.6,1.6,0.9,2.1c0.4,0.6,0.9,1.2,1.4,1.8c0.3,0.3,0.6,0.7,0.8,1
                                c1.5,2.1,2.4,4.4,2.5,7.1c0.1,3.5,0.4,6.9,0.8,10.3c0.2-3.3,0.4-6.7,0.6-10c0-0.8,0.1-1.6,0.2-2.3c0.3-1.4,0.7-2.9,1.5-4.1
                                C218.5,177.5,220.5,176.1,221.4,173.7C221.5,173.7,221.5,173.7,221.4,173.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M221.8,173.2c-0.5,0.9-1.4,1.6-2.2,2.1c-0.8,0.6-1.6,1-2.5,1.5c-1.3,0.7-2.8,1.2-4.3,0.7
                                c-1.2-0.5-2.3-1.3-3.2-2.1c-0.6-0.5-1.1-1.2-1.6-1.9c0.7,0.2,1.4,0.5,2.1,0.6c1.7,0.4,3.5,0.7,5.3,0.6c1.2-0.1,2.3-0.3,3.4-0.5
                                C219.8,174,220.8,173.6,221.8,173.2C221.9,173.1,221.8,173.1,221.8,173.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M214.8,180.8c-0.1,0.1-0.2,0.1-0.3,0.1c-0.5-0.5-0.9-1.2-1.3-1.7c-0.5-0.6-1.1-1.2-1.6-1.8
                                c-0.7-0.8-1.5-1.6-2.3-2.4c1.7,0.9,3.7,2.7,5.7,2.3c1-0.2,1.9-0.6,2.8-1c0.6-0.3,1.2-0.6,1.9-0.9c-0.3,0.3-0.5,0.5-0.8,0.8
                                c-0.4,0.4-0.8,0.9-1.2,1.3c-0.2,0.3-0.5,0.6-0.7,0.9C216.3,179.2,215.7,180.1,214.8,180.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M154.2,141.3c-1.4,0.3-2.8,0.7-4.3,0.9s-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8c1.9-2.5,4.7-4,6-6.9
                                C154.2,141.2,154.2,141.3,154.2,141.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M154.7,140.7c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C154.8,140.6,154.7,140.6,154.7,140.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M145.1,149.5c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1
                                c-1-0.9-2-1.9-3.1-2.8c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9
                                c-0.5,0.5-1.1,1-1.6,1.6c-0.3,0.3-0.7,0.7-1,1C147.2,147.7,146.3,148.8,145.1,149.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M194.4,141.5c-1.4,0.3-2.8,0.7-4.3,0.9s-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8c1.9-2.5,4.7-4,6-6.9
                                C194.5,141.4,194.4,141.5,194.4,141.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M194.9,140.9c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C195,140.7,194.9,140.8,194.9,140.9z"
                            />
                            <Path
                                fill="#463116"
                                d="M185.3,149.7c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1
                                c-1-0.9-2-1.9-3.1-2.8c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9
                                c-0.5,0.5-1.1,1-1.6,1.6c-0.3,0.3-0.7,0.7-1,1C187.4,147.9,186.5,149,185.3,149.7z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M252.4,73.8c-1.4,0.3-2.8,0.7-4.3,0.9c-1.5,0.2-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8c1.9-2.5,4.7-4,6-6.9
                                C252.5,73.8,252.4,73.8,252.4,73.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M252.9,73.2c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C253,73.1,253,73.2,252.9,73.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M243.3,82.1c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C245.4,80.3,244.6,81.4,243.3,82.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M48,72.8c-1.4,0.3-2.8,0.7-4.3,0.9c-1.5,0.2-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8c1.9-2.5,4.7-4,6-6.9
                                C48.1,72.8,48.1,72.8,48,72.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M48.5,72.2c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C48.6,72.1,48.6,72.2,48.5,72.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M38.9,81.1c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C41,79.2,40.2,80.4,38.9,81.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M103.5,108.2c-1.4,0.3-2.8,0.7-4.3,0.9c-1.5,0.2-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3
                                c-0.2,0-0.4-0.1-0.6-0.2c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2
                                c2.1,2.4,3.3,5.1,3.4,8.3c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8
                                c1.9-2.5,4.7-4,6-6.9C103.6,108.2,103.5,108.2,103.5,108.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M104,107.6c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C104.1,107.5,104.1,107.6,104,107.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M94.4,116.5c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C96.5,114.7,95.7,115.8,94.4,116.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M169.6,107.8c-1.4,0.3-2.8,0.7-4.3,0.9c-1.5,0.2-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3
                                c-0.2,0-0.4-0.1-0.6-0.2c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2
                                c2.1,2.4,3.3,5.1,3.4,8.3c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8
                                c1.9-2.5,4.7-4,6-6.9C169.7,107.8,169.6,107.8,169.6,107.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M170.1,107.2c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C170.2,107.1,170.2,107.2,170.1,107.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M160.5,116.1c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1
                                c-1-0.9-2-1.9-3.1-2.8c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9
                                c-0.5,0.5-1.1,1-1.6,1.6c-0.3,0.3-0.7,0.7-1,1C162.6,114.3,161.8,115.4,160.5,116.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M197.2,8c-1.4,0.3-2.7,0.7-4,0.8C191.8,9,190.4,9,189,9.1c-1.8,0-3.7,0.1-5.4-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.7-0.5-2.6-0.7c-0.1,0.8,0.7,1.8,1.2,2.3c0.6,0.7,1.2,1.3,1.8,1.9c0.4,0.4,0.7,0.7,1.1,1.1c2,2.3,3.1,4.8,3.2,7.9
                                c0.1,3.8,0.5,7.6,1.1,11.4c0.2-3.7,0.5-7.3,0.7-11c0.1-0.9,0.1-1.7,0.3-2.5c0.3-1.6,1-3.2,1.9-4.5c1.8-2.3,4.4-3.8,5.7-6.5
                                C197.3,8,197.2,8,197.2,8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M197.7,7.4c-0.7,0.9-1.8,1.8-2.8,2.4s-2.1,1.1-3.2,1.6c-1.7,0.8-3.6,1.4-5.5,0.7c-1.5-0.5-2.9-1.4-4.2-2.4
                                c-0.8-0.6-1.4-1.3-2-2.1c0.9,0.3,1.7,0.5,2.6,0.7c2.2,0.4,4.4,0.8,6.7,0.7c1.5-0.1,3-0.3,4.4-0.6c1.3-0.3,2.6-0.7,3.9-1.2
                                C197.8,7.3,197.7,7.3,197.7,7.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M188.6,15.7c-0.2,0.1-0.3,0.1-0.3,0.1c-0.7-0.5-1.1-1.3-1.7-1.9c-0.7-0.7-1.4-1.3-2.1-2
                                c-1-0.9-1.9-1.8-2.9-2.6c2.2,1,4.7,2.9,7.3,2.6c1.3-0.2,2.5-0.6,3.6-1.1c0.8-0.3,1.6-0.6,2.4-1c-0.3,0.3-0.7,0.6-1,0.9
                                c-0.5,0.5-1,1-1.5,1.5c-0.3,0.3-0.6,0.6-0.9,1C190.6,14,189.8,15.1,188.6,15.7z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M178.6,8c-1.4,0.3-2.7,0.7-4,0.8c-1.4,0.2-2.8,0.2-4.2,0.3c-1.8,0-3.7,0.1-5.4-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.7-0.5-2.6-0.7c-0.1,0.8,0.7,1.8,1.2,2.3c0.6,0.7,1.2,1.3,1.8,1.9c0.4,0.4,0.7,0.7,1.1,1.1c2,2.3,3.1,4.8,3.2,7.9
                                c0.1,3.8,0.5,7.6,1.1,11.4c0.2-3.7,0.5-7.3,0.7-11c0.1-0.9,0.1-1.7,0.3-2.5c0.3-1.6,1-3.2,1.9-4.5c1.8-2.3,4.4-3.8,5.7-6.5
                                C178.7,8,178.7,8,178.6,8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M179.1,7.4c-0.7,0.9-1.8,1.8-2.8,2.4s-2.1,1.1-3.2,1.6c-1.7,0.8-3.6,1.4-5.5,0.7c-1.5-0.5-2.9-1.4-4.2-2.4
                                c-0.8-0.6-1.4-1.3-2-2.1c0.9,0.3,1.7,0.5,2.6,0.7c2.2,0.4,4.4,0.8,6.7,0.7c1.5-0.1,3-0.3,4.4-0.6c1.3-0.3,2.6-0.7,3.9-1.2
                                C179.2,7.3,179.2,7.4,179.1,7.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M170.1,15.8c-0.2,0.1-0.3,0.1-0.3,0.1c-0.7-0.5-1.1-1.3-1.7-1.9c-0.7-0.7-1.4-1.3-2.1-2
                                c-1-0.9-1.9-1.8-2.9-2.6c2.2,1,4.7,2.9,7.3,2.6c1.3-0.2,2.5-0.6,3.6-1.1c0.8-0.3,1.6-0.6,2.4-1c-0.3,0.3-0.7,0.6-1,0.9
                                c-0.5,0.5-1,1-1.5,1.5c-0.3,0.3-0.6,0.6-0.9,1C172.1,14.1,171.3,15.1,170.1,15.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M148.9,107.7c-1.4,0.3-2.8,0.7-4.3,0.9c-1.5,0.2-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3
                                c-0.2,0-0.4-0.1-0.6-0.2c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2
                                c1.6,1.8,2.9,3.8,4.5,5.6c0.9-2.4,2.6-4.1,4.4-5.9C145.1,112.3,148.4,107.8,148.9,107.7C148.9,107.7,148.9,107.7,148.9,107.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M149.4,107.1c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C149.5,107,149.4,107,149.4,107.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M139.8,115.9c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1
                                c-1-0.9-2-1.9-3.1-2.8c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9
                                c-0.5,0.5-1.1,1-1.6,1.6c-0.3,0.3-0.7,0.7-1,1C141.9,114.1,141,115.2,139.8,115.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M143.2,122c-1.7,0.2-3.4,0.2-5.2-0.2c-1-0.2-2.1-0.4-3.1-0.7c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c1.1,1.2,2.4,2.5,3.3,3.9
                                c0.9,1.5,0.6,3.2,1.1,4.8c-0.1-0.4,0.3-1.5,0.3-2c0.1-0.7,0.2-1.5,0.5-2.2c0.5-1.4,1.7-2.5,2.8-3.4c1.2-1.1,2.3-2.2,3.4-3.4
                                c0.9-1,2.2-2.4,2.5-3.8c-0.5,0.2-1,0.4-1.5,0.6c-0.9,0.4-1.9,0.7-2.8,1C144.8,121.8,144,121.9,143.2,122z"
                            />
                            <Path
                                fill="#97773C"
                                d="M150,119.7c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C150.1,119.6,150,119.7,150,119.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M140.4,128.6c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1
                                c-1-0.9-2-1.9-3.1-2.8c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9
                                c-0.5,0.5-1.1,1-1.6,1.6c-0.3,0.3-0.7,0.7-1,1C142.5,126.7,141.6,127.9,140.4,128.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M101.1,11c-1.1,0.2-2.1,0.5-3.2,0.7c-1.1,0.1-2.3,0.2-3.4,0.2c-1.4,0-2.9,0.1-4.3-0.2c-0.2,0-0.3-0.1-0.5-0.1
                                c-0.7-0.2-1.4-0.4-2.1-0.5c0,0.6,0.6,1.4,1,1.9c0.4,0.6,1,1,1.5,1.5c0.3,0.3,0.6,0.6,0.8,0.9c1.2,1.4,2.2,2.9,3.4,4.2
                                c0.7-1.8,2-3.1,3.3-4.4c0.6-0.6,3.1-4,3.6-4.1C101.1,11,101.1,11,101.1,11z"
                            />
                            <Path
                                fill="#97773C"
                                d="M101.5,10.5c-0.5,0.8-1.4,1.4-2.2,1.9c-0.8,0.5-1.7,0.9-2.5,1.3c-1.3,0.6-2.9,1.1-4.4,0.6
                                c-1.2-0.4-2.3-1.1-3.3-1.9c-0.6-0.5-1.1-1-1.6-1.7c0.7,0.2,1.4,0.4,2.1,0.6c1.8,0.4,3.6,0.7,5.4,0.6c1.2-0.1,2.4-0.2,3.5-0.5
                                C99.6,11.2,100.6,10.9,101.5,10.5C101.5,10.5,101.5,10.5,101.5,10.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M94.2,17.2c-0.1,0.1-0.2,0.1-0.3,0.1c-0.5-0.4-0.9-1-1.4-1.5s-1.1-1.1-1.6-1.6c-0.8-0.7-1.5-1.4-2.3-2.1
                                c1.8,0.8,3.8,2.4,5.8,2.1c1-0.2,2-0.5,2.9-0.9c0.6-0.3,1.3-0.5,1.9-0.8c-0.3,0.3-0.5,0.4-0.8,0.7c-0.4,0.4-0.8,0.8-1.2,1.2
                                c-0.2,0.3-0.5,0.5-0.7,0.8C95.8,15.8,95.2,16.7,94.2,17.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M96.8,21.8c-1.3,0.2-2.6,0.1-3.9-0.1c-0.8-0.1-1.6-0.3-2.3-0.5c-0.2,0-0.3-0.1-0.5-0.1
                                c-0.7-0.2-1.4-0.4-2.1-0.5c0,0.6,0.6,1.4,1,1.9c0.4,0.6,1,1,1.5,1.5c0.3,0.3,0.6,0.6,0.8,0.9c0.8,0.9,1.8,1.9,2.5,3
                                s0.4,2.4,0.8,3.6c-0.1-0.3,0.2-1.1,0.3-1.5c0.1-0.6,0.2-1.1,0.4-1.6c0.4-1,1.3-1.9,2.1-2.6c0.9-0.8,1.8-1.7,2.6-2.6
                                c0.7-0.8,1.6-1.8,1.9-2.9c-0.4,0.2-0.8,0.3-1.1,0.5c-0.7,0.3-1.4,0.6-2.1,0.8C98,21.6,97.4,21.8,96.8,21.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M101.9,20.1c-0.5,0.8-1.4,1.4-2.2,1.9c-0.8,0.5-1.7,0.9-2.5,1.3c-1.3,0.6-2.9,1.1-4.4,0.6
                                c-1.2-0.4-2.3-1.1-3.3-1.9c-0.6-0.5-1.1-1-1.6-1.7c0.7,0.2,1.4,0.4,2.1,0.6c1.8,0.4,3.6,0.7,5.4,0.6c1.2-0.1,2.4-0.2,3.5-0.5
                                c1.1-0.2,2.1-0.5,3.1-1C102,20,102,20,101.9,20.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M94.7,26.8c-0.1,0.1-0.2,0.1-0.3,0.1c-0.5-0.4-0.9-1-1.4-1.5s-1.1-1.1-1.6-1.6c-0.8-0.7-1.5-1.4-2.3-2.1
                                c1.8,0.8,3.8,2.4,5.8,2.1c1-0.2,2-0.5,2.9-0.9c0.6-0.3,1.3-0.5,1.9-0.8c-0.3,0.3-0.5,0.4-0.8,0.7c-0.4,0.4-0.8,0.8-1.2,1.2
                                c-0.2,0.3-0.5,0.5-0.7,0.8C96.2,25.4,95.6,26.2,94.7,26.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M247.2,140.9c-1.2,0.3-2.3,0.7-3.5,0.8c-1.2,0.2-2.5,0.2-3.7,0.3c-1.5,0-3.2,0.1-4.7-0.3
                                c-0.2,0-0.3-0.1-0.5-0.2c-0.7-0.3-1.5-0.5-2.3-0.7c0,0.8,0.7,1.8,1.1,2.3c0.5,0.7,1.1,1.3,1.6,1.9c0.3,0.4,0.6,0.7,0.9,1.1
                                c1.3,1.7,2.4,3.6,3.7,5.3c0.8-2.2,2.2-3.9,3.6-5.5c0.6-0.7,3.4-5,3.9-5.2C247.3,140.9,247.2,140.9,247.2,140.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M247.6,140.3c-0.6,0.9-1.6,1.8-2.4,2.4c-0.9,0.6-1.8,1.1-2.8,1.6c-1.5,0.8-3.2,1.4-4.8,0.7
                                c-1.3-0.5-2.5-1.4-3.7-2.4c-0.7-0.6-1.2-1.3-1.8-2.1c0.8,0.3,1.5,0.5,2.3,0.7c2,0.4,3.9,0.8,5.9,0.7c1.3-0.1,2.6-0.3,3.9-0.6
                                c1.2-0.3,2.3-0.7,3.4-1.2C247.7,140.2,247.7,140.3,247.6,140.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M239.7,148.6c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.3-1.5-1.9c-0.6-0.7-1.2-1.3-1.8-2
                                c-0.8-0.9-1.7-1.8-2.6-2.6c2,1,4.2,2.9,6.4,2.6c1.1-0.2,2.2-0.6,3.2-1.1c0.7-0.3,1.4-0.6,2.1-1c-0.3,0.3-0.6,0.6-0.9,0.9
                                c-0.4,0.5-0.9,1-1.3,1.5c-0.3,0.3-0.5,0.6-0.8,0.9C241.4,146.9,240.7,148,239.7,148.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M242.5,154.4c-1.4,0.2-2.8,0.2-4.3-0.2c-0.9-0.2-1.7-0.4-2.6-0.6c-0.2,0-0.3-0.1-0.5-0.2
                                c-0.7-0.3-1.5-0.5-2.3-0.7c0,0.8,0.7,1.8,1.1,2.3c0.5,0.7,1.1,1.3,1.6,1.9c0.3,0.4,0.6,0.7,0.9,1.1c0.9,1.2,2,2.4,2.7,3.7
                                c0.7,1.4,0.5,3,0.9,4.5c-0.1-0.4,0.2-1.4,0.3-1.9c0.1-0.7,0.2-1.4,0.4-2.1c0.4-1.3,1.4-2.3,2.3-3.2c1-1,1.9-2.1,2.8-3.2
                                c0.7-1,1.8-2.3,2.1-3.6c-0.4,0.2-0.8,0.4-1.3,0.6c-0.8,0.4-1.5,0.7-2.3,1C243.8,154.2,243.2,154.3,242.5,154.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M248.1,152.2c-0.6,0.9-1.6,1.8-2.4,2.4c-0.9,0.6-1.8,1.1-2.8,1.6c-1.5,0.8-3.2,1.4-4.8,0.7
                                c-1.3-0.5-2.5-1.4-3.7-2.4c-0.7-0.6-1.2-1.3-1.8-2.1c0.8,0.3,1.5,0.5,2.3,0.7c2,0.4,3.9,0.8,5.9,0.7c1.3-0.1,2.6-0.3,3.9-0.6
                                c1.2-0.3,2.3-0.7,3.4-1.2C248.2,152.1,248.2,152.2,248.1,152.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M240.2,160.6c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.3-1.5-1.9c-0.6-0.7-1.2-1.3-1.8-2
                                c-0.8-0.9-1.7-1.8-2.6-2.6c2,1,4.2,2.9,6.4,2.6c1.1-0.2,2.2-0.6,3.2-1.1c0.7-0.3,1.4-0.6,2.1-1c-0.3,0.3-0.6,0.6-0.9,0.9
                                c-0.4,0.5-0.9,1-1.3,1.5c-0.3,0.3-0.5,0.6-0.8,0.9C241.9,158.9,241.2,159.9,240.2,160.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M65.9,107.6c-1.2,0.3-2.3,0.7-3.5,0.8c-1.2,0.2-2.5,0.2-3.7,0.3c-1.5,0-3.2,0.1-4.7-0.3
                                c-0.2,0-0.3-0.1-0.5-0.2c-0.7-0.3-1.5-0.5-2.3-0.7c0,0.8,0.7,1.8,1.1,2.3c0.5,0.7,1.1,1.3,1.6,1.9c0.3,0.4,0.6,0.7,0.9,1.1
                                c1.3,1.7,2.4,3.6,3.7,5.3c0.8-2.2,2.2-3.9,3.6-5.5c0.6-0.7,3.4-5,3.9-5.2C66,107.6,66,107.6,65.9,107.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M66.4,107c-0.6,0.9-1.6,1.8-2.4,2.4c-0.9,0.6-1.8,1.1-2.8,1.6c-1.5,0.8-3.2,1.4-4.8,0.7
                                c-1.3-0.5-2.5-1.4-3.7-2.4c-0.7-0.6-1.2-1.3-1.8-2.1c0.8,0.3,1.5,0.5,2.3,0.7c2,0.4,3.9,0.8,5.9,0.7c1.3-0.1,2.6-0.3,3.9-0.6
                                c1.2-0.3,2.3-0.7,3.4-1.2C66.5,106.9,66.4,107,66.4,107z"
                            />
                            <Path
                                fill="#463116"
                                d="M58.4,115.4c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.3-1.5-1.9c-0.6-0.7-1.2-1.3-1.8-2
                                c-0.8-0.9-1.7-1.8-2.6-2.6c2,1,4.2,2.9,6.4,2.6c1.1-0.2,2.2-0.6,3.2-1.1c0.7-0.3,1.4-0.6,2.1-1c-0.3,0.3-0.6,0.6-0.9,0.9
                                c-0.4,0.5-0.9,1-1.3,1.5c-0.3,0.3-0.5,0.6-0.8,0.9C60.2,113.6,59.5,114.7,58.4,115.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M61.3,121.1c-1.4,0.2-2.8,0.2-4.3-0.2c-0.9-0.2-1.7-0.4-2.6-0.6c-0.2,0-0.3-0.1-0.5-0.2
                                c-0.7-0.3-1.5-0.5-2.3-0.7c0,0.8,0.7,1.8,1.1,2.3c0.5,0.7,1.1,1.3,1.6,1.9c0.3,0.4,0.6,0.7,0.9,1.1c0.9,1.2,2,2.4,2.7,3.7
                                c0.7,1.4,0.5,3,0.9,4.5c-0.1-0.4,0.2-1.4,0.3-1.9c0.1-0.7,0.2-1.4,0.4-2.1c0.4-1.3,1.4-2.3,2.3-3.2c1-1,1.9-2.1,2.8-3.2
                                c0.7-1,1.8-2.3,2.1-3.6c-0.4,0.2-0.8,0.4-1.3,0.6c-0.8,0.4-1.5,0.7-2.3,1C62.5,120.9,61.9,121,61.3,121.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M66.9,118.9c-0.6,0.9-1.6,1.8-2.4,2.4c-0.9,0.6-1.8,1.1-2.8,1.6c-1.5,0.8-3.2,1.4-4.8,0.7
                                c-1.3-0.5-2.5-1.4-3.7-2.4c-0.7-0.6-1.2-1.3-1.8-2.1c0.8,0.3,1.5,0.5,2.3,0.7c2,0.4,3.9,0.8,5.9,0.7c1.3-0.1,2.6-0.3,3.9-0.6
                                c1.2-0.3,2.3-0.7,3.4-1.2C66.9,118.8,66.9,118.9,66.9,118.9z"
                            />
                            <Path
                                fill="#463116"
                                d="M58.9,127.3c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.3-1.5-1.9c-0.6-0.7-1.2-1.3-1.8-2
                                c-0.8-0.9-1.7-1.8-2.6-2.6c2,1,4.2,2.9,6.4,2.6c1.1-0.2,2.2-0.6,3.2-1.1c0.7-0.3,1.4-0.6,2.1-1c-0.3,0.3-0.6,0.6-0.9,0.9
                                c-0.4,0.5-0.9,1-1.3,1.5c-0.3,0.3-0.5,0.6-0.8,0.9C60.7,125.6,60,126.6,58.9,127.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M206.1,173.9c-1.1,0.3-2.1,0.6-3.2,0.8c-1.1,0.2-2.2,0.2-3.3,0.2c-1.4,0-2.9,0.1-4.2-0.2
                                c-0.2,0-0.3-0.1-0.4-0.1c-0.7-0.2-1.3-0.4-2-0.6c0,0.7,0.6,1.6,0.9,2.1c0.4,0.6,0.9,1.2,1.4,1.7c0.3,0.3,0.6,0.7,0.8,1
                                c1.2,1.5,2.1,3.2,3.3,4.7c0.7-2,1.9-3.4,3.2-4.9C203.3,177.8,205.7,174,206.1,173.9C206.2,173.9,206.1,173.9,206.1,173.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M206.5,173.4c-0.5,0.8-1.4,1.6-2.2,2.1s-1.6,1-2.5,1.4c-1.3,0.7-2.8,1.2-4.3,0.7c-1.2-0.5-2.3-1.2-3.2-2.1
                                c-0.6-0.5-1.1-1.2-1.6-1.8c0.7,0.2,1.4,0.5,2.1,0.6c1.7,0.4,3.5,0.7,5.3,0.6c1.2-0.1,2.3-0.3,3.4-0.5c1-0.2,2-0.6,3-1.1
                                C206.5,173.3,206.5,173.3,206.5,173.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M199.4,180.8c-0.1,0.1-0.2,0.1-0.3,0.1c-0.5-0.5-0.9-1.1-1.3-1.7c-0.5-0.6-1.1-1.2-1.6-1.8
                                c-0.7-0.8-1.5-1.6-2.3-2.3c1.7,0.9,3.7,2.6,5.7,2.3c1-0.2,1.9-0.6,2.8-1c0.6-0.3,1.2-0.5,1.9-0.9c-0.3,0.3-0.5,0.5-0.8,0.8
                                c-0.4,0.4-0.8,0.9-1.2,1.3c-0.2,0.3-0.5,0.6-0.7,0.8C200.9,179.3,200.3,180.2,199.4,180.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M201.9,185.9c-1.3,0.2-2.5,0.1-3.8-0.1c-0.8-0.2-1.5-0.4-2.3-0.6c-0.2,0-0.3-0.1-0.4-0.1
                                c-0.7-0.2-1.3-0.4-2-0.6c0,0.7,0.6,1.6,0.9,2.1c0.4,0.6,0.9,1.2,1.4,1.7c0.3,0.3,0.6,0.7,0.8,1c0.8,1,1.8,2.1,2.4,3.3
                                c0.6,1.2,0.4,2.7,0.8,4c-0.1-0.3,0.2-1.3,0.3-1.7c0.1-0.6,0.2-1.2,0.4-1.8c0.4-1.1,1.3-2.1,2.1-2.9c0.9-0.9,1.7-1.9,2.5-2.9
                                c0.7-0.9,1.6-2,1.8-3.2c-0.4,0.2-0.7,0.3-1.1,0.5c-0.7,0.3-1.4,0.6-2.1,0.9C203.1,185.7,202.5,185.8,201.9,185.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M206.9,184c-0.5,0.8-1.4,1.6-2.2,2.1s-1.6,1-2.5,1.4c-1.3,0.7-2.8,1.2-4.3,0.7c-1.2-0.5-2.3-1.2-3.2-2.1
                                c-0.6-0.5-1.1-1.2-1.6-1.8c0.7,0.2,1.4,0.5,2.1,0.6c1.7,0.4,3.5,0.7,5.3,0.6c1.2-0.1,2.3-0.3,3.4-0.5c1-0.2,2-0.6,3-1.1
                                C207,183.9,207,183.9,206.9,184z"
                            />
                            <Path
                                fill="#463116"
                                d="M199.9,191.4c-0.1,0.1-0.2,0.1-0.3,0.1c-0.5-0.5-0.9-1.1-1.3-1.7c-0.5-0.6-1.1-1.2-1.6-1.8
                                c-0.7-0.8-1.5-1.6-2.3-2.3c1.7,0.9,3.7,2.6,5.7,2.3c1-0.2,1.9-0.6,2.8-1c0.6-0.3,1.2-0.5,1.9-0.9c-0.3,0.3-0.5,0.5-0.8,0.8
                                c-0.4,0.4-0.8,0.9-1.2,1.3c-0.2,0.3-0.5,0.6-0.7,0.8C201.4,189.9,200.8,190.8,199.9,191.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M132.8,75.4c-1.7,0.2-3.4,0.2-5.2-0.2c-1-0.2-2.1-0.4-3.1-0.7c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c1.1,1.2,2.4,2.5,3.3,3.9
                                c0.9,1.5,0.6,3.2,1.1,4.8c-0.1-0.4,0.3-1.5,0.3-2c0.1-0.7,0.2-1.5,0.5-2.2c0.5-1.4,1.7-2.5,2.8-3.4c1.2-1.1,2.3-2.2,3.4-3.4
                                c0.9-1,2.2-2.4,2.5-3.8c-0.5,0.2-1,0.4-1.5,0.6c-0.9,0.4-1.9,0.7-2.8,1C134.3,75.1,133.5,75.3,132.8,75.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M139.6,73.1c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C139.6,73,139.6,73,139.6,73.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M130,81.9c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C132,80.1,131.2,81.2,130,81.9z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M117.3,110.3c-0.1,1.9-0.4,3.7-0.7,5.6c-0.1,0.9-0.3,1.9-0.3,2.9c0,0.7-0.1,1.9,0.5,2.4
                                c0.5-0.4,0.8-0.9,1.2-1.3c0.4-0.4,0.6-0.9,0.9-1.3c0.3-0.7,0.4-1.5,0.5-2.2c0.1-1.8-0.1-3.6-0.6-5.3c-0.3-1-0.9-1.9-1.2-2.9
                                C117.4,108.8,117.4,109.5,117.3,110.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M128.2,116.2c0.1-0.2-0.1-0.4-0.3-0.4c-1.4-0.4-2.7-0.7-4.1-0.7c-0.4,0-0.8-0.1-1.2,0
                                c-0.4,0.1-0.7,0.3-1.1,0.5c-0.7,0.4-1.2,0.9-1.8,1.5c-0.7,0.7-1.4,1.6-1.9,2.5c-0.2,0.3-0.4,0.6-0.6,0.9c-0.1,0.2-0.4,0.5-0.4,0.7
                                c0.3,0.1,0.7,0,0.8,0c0.3,0,0.6-0.1,0.9-0.2c0.6-0.2,1-0.5,1.3-1c0.8-1.1,1.7-2.2,3-2.7C124.6,116.6,126.3,116.5,128.2,116.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M117.9,110.4c-0.1-0.8-0.2-1.6-0.3-2.3c1.4,1.4,1.5,5.1,3.5,5.6c0.7,0.2,1.4,0.2,2.2,0.3
                                c0.9,0.1,1.7,0.4,2.6,0.7c0.9,0.3,1.9,0.7,2.4,1.4c-1.8-0.2-3.5-0.2-5.3-0.1c-0.9,0-1.9,0.2-2.9,0.2c-0.3,0-0.8,0.1-1-0.1
                                s-0.2-0.8-0.3-1.1C118.5,113.4,118.1,111.9,117.9,110.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M119.3,114.3c-0.3-0.5-0.5-1.1-0.6-1.7c-0.1-0.3-0.2-0.6-0.3-0.9c-0.1-0.3-0.2-0.5-0.4-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0.1,0.8c0,0.3,0,0.6,0.1,0.9c0.1,0.6,0.1,1.1,0.1,1.7c0.1,1,0.1,1.9,0.1,2.9c0,0.2,0,0.5,0,0.7s0.1,0.4,0.2,0.4
                                s0.2-0.1,0.3-0.2c0.5-0.5,1.1-0.9,1.7-1.2c0.7-0.3,1.3-0.3,2-0.5c1.4-0.3,2.8-0.4,4.2-0.5c-0.4-0.2-0.8-0.3-1.2-0.3
                                c-0.4-0.1-0.7-0.1-1.1-0.2c-0.9-0.1-1.8,0-2.7-0.1C120.8,115.3,119.9,115.3,119.3,114.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M69.1,79.6c-0.1,2.3-0.5,4.5-0.8,6.8c-0.1,1.1-0.4,2.3-0.4,3.5c0,0.9-0.1,2.3,0.6,3c0.6-0.5,1-1.1,1.4-1.6
                                s0.7-1.1,1.1-1.6c0.4-0.8,0.5-1.8,0.6-2.7c0.1-2.2-0.1-4.4-0.7-6.5c-0.4-1.3-1.1-2.3-1.5-3.6C69.1,77.7,69.1,78.6,69.1,79.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M82.3,86.8c0.1-0.2-0.2-0.4-0.4-0.5c-1.7-0.5-3.3-0.8-5-0.9c-0.5,0-1-0.1-1.5,0.1s-0.9,0.4-1.3,0.6
                                c-0.8,0.5-1.5,1.1-2.2,1.8c-0.9,0.9-1.7,1.9-2.3,3c-0.2,0.3-0.5,0.7-0.7,1c-0.1,0.2-0.5,0.6-0.5,0.8c0.4,0.1,0.9,0,1,0
                                c0.4,0,0.7-0.1,1-0.3c0.7-0.3,1.2-0.7,1.6-1.3c0.9-1.3,2-2.7,3.6-3.3C77.9,87.3,80,87.1,82.3,86.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M69.8,79.7c-0.1-1-0.2-1.9-0.4-2.9c1.7,1.8,1.9,6.2,4.3,6.8c0.9,0.2,1.8,0.2,2.6,0.4c1.1,0.2,2.1,0.5,3.1,0.9
                                c1.1,0.4,2.3,0.8,3,1.7c-2.2-0.3-4.3-0.3-6.5-0.1c-1.1,0.1-2.4,0.2-3.5,0.2c-0.4,0-1,0.1-1.3-0.2c-0.3-0.3-0.3-1-0.4-1.4
                                C70.5,83.4,70,81.5,69.8,79.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M71.5,84.5c-0.4-0.6-0.6-1.3-0.7-2c-0.1-0.4-0.2-0.8-0.4-1.1c-0.1-0.3-0.2-0.6-0.4-0.9c0,0,0,0-0.1,0
                                c0,0.3,0,0.6,0.1,1c0,0.4,0,0.7,0.1,1.1c0.1,0.7,0.1,1.4,0.2,2.1c0.1,1.2,0.1,2.3,0.1,3.5c0,0.3,0,0.6,0,0.8
                                c0,0.2,0.1,0.5,0.3,0.5c0.1,0,0.3-0.1,0.4-0.2c0.6-0.6,1.3-1.1,2.1-1.5c0.8-0.4,1.5-0.4,2.4-0.6c1.7-0.4,3.4-0.5,5.2-0.7
                                c-0.5-0.2-1-0.4-1.5-0.4c-0.4-0.1-0.9-0.2-1.3-0.2c-1.1-0.1-2.2-0.1-3.3-0.1C73.3,85.7,72.2,85.7,71.5,84.5z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M140.7,78.5c-0.1,1.9-0.4,3.7-0.7,5.6c-0.1,0.9-0.3,1.9-0.3,2.9c0,0.7-0.1,1.9,0.5,2.4
                                c0.5-0.4,0.8-0.9,1.2-1.3s0.6-0.9,0.9-1.3c0.3-0.7,0.4-1.5,0.5-2.2c0.1-1.8-0.1-3.6-0.6-5.3c-0.3-1-0.9-1.9-1.2-2.9
                                C140.8,77,140.8,77.7,140.7,78.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M151.6,84.4c0.1-0.2-0.1-0.4-0.3-0.4c-1.4-0.4-2.7-0.7-4.1-0.7c-0.4,0-0.8-0.1-1.2,0s-0.7,0.3-1.1,0.5
                                c-0.7,0.4-1.2,0.9-1.8,1.5c-0.7,0.7-1.4,1.6-1.9,2.5c-0.2,0.3-0.4,0.6-0.6,0.9c-0.1,0.2-0.4,0.5-0.4,0.7c0.3,0.1,0.7,0,0.8,0
                                c0.3,0,0.6-0.1,0.9-0.2c0.6-0.2,1-0.5,1.3-1c0.8-1.1,1.7-2.2,3-2.7C148,84.8,149.7,84.7,151.6,84.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M141.3,78.6c-0.1-0.8-0.2-1.6-0.3-2.3c1.4,1.4,1.5,5.1,3.5,5.6c0.7,0.2,1.4,0.2,2.2,0.3
                                c0.9,0.1,1.7,0.4,2.6,0.7c0.9,0.3,1.9,0.7,2.4,1.4c-1.8-0.2-3.5-0.2-5.3-0.1c-0.9,0-1.9,0.2-2.9,0.2c-0.3,0-0.8,0.1-1-0.1
                                s-0.2-0.8-0.3-1.1C141.9,81.6,141.5,80.1,141.3,78.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M142.7,82.5c-0.3-0.5-0.5-1.1-0.6-1.7c-0.1-0.3-0.2-0.6-0.3-0.9s-0.2-0.5-0.4-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0.1,0.8c0,0.3,0,0.6,0.1,0.9c0.1,0.6,0.1,1.1,0.1,1.7c0.1,1,0.1,1.9,0.1,2.9c0,0.2,0,0.5,0,0.7s0.1,0.4,0.2,0.4
                                c0.1,0,0.2-0.1,0.3-0.2c0.5-0.5,1.1-0.9,1.7-1.2c0.7-0.3,1.3-0.3,2-0.5c1.4-0.3,2.8-0.4,4.2-0.5c-0.4-0.2-0.8-0.3-1.2-0.3
                                c-0.4-0.1-0.7-0.1-1.1-0.2c-0.9-0.1-1.8,0-2.7-0.1C144.2,83.5,143.3,83.5,142.7,82.5z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M131.4,30.3c0.2,0.1,0.3,0,0.5-0.1c0.1,0,0.2-0.1,0.3-0.2c0.4-0.2,0.7-0.5,1-0.7c0.2-0.2,0.4-0.3,0.5-0.5
                                c0.2-0.3,0.3-0.6,0.3-0.9c0.3-1.3,0.5-2.6,0.5-3.9c0-2-0.8-3.7-1.2-5.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.3,1.5-0.1,3.1-0.2,4.7
                                c0,1.3,0,2.9-0.4,4.1C132,28.2,131.6,29.2,131.4,30.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M142.7,25.7c-0.5,0-1,0-1.4-0.1c-0.8-0.1-1.6-0.3-2.5-0.3c-1,0-1.7-0.2-2.6,0.3c-0.8,0.4-1.5,1-2,1.7
                                c-0.4,0.5-0.6,1-1,1.6s-1,1-1.6,1.4c1,0,1.8-0.4,2.8-0.5c1.1-0.1,2.1-0.5,3-0.9c1.2-0.5,2.4-1.2,3.7-1.6c1.4-0.4,2.9-0.4,4.4-0.4
                                c0.7,0,1.4,0.1,2.1,0.1c1.4,0,3.5,0,4.8-0.8c-1.3,0.1-2.5-0.1-3.8-0.2c-1.7-0.1-3.4-0.2-5.1-0.3C143.2,25.7,142.9,25.7,142.7,25.7
                                z"
                            />
                            <Path
                                fill="#97773C"
                                d="M133.8,21.3c-0.1-1-0.3-1.8-0.6-2.7c1.7,0.9,2.9,2.4,4.3,3.8c1.1,1.1,2.3,2.2,3.8,2.4
                                c1.8,0.1,3.7,0.3,5.5,0.6c0.9,0.1,1.9,0.2,2.8,0.4c0.8,0.1,1.8,0.5,2.7,0.5c-3,0-6-0.1-9-0.1c-2.8,0-5.6,0.2-8.5,0.2
                                c-0.5,0-0.6-0.1-0.7-0.6C133.9,24.2,133.9,22.7,133.8,21.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M133.4,27.1c0,0.5-0.1,1-0.1,1.5c0-0.1,1-0.7,1.1-0.8c2.3-1.6,5.1-1.5,7.8-1.5c2.6,0,5.1,0,7.7,0.1
                                c0.8,0,1.7,0,2.5-0.2c-3.2-0.2-6.5-0.4-9.7-0.6c-1.8-0.1-3.8,0.2-5.4-0.5c-0.3-0.1-0.6-0.3-0.9-0.5c-0.3-0.2-0.5-0.5-0.8-0.8
                                c-0.8-1-1.5-2.1-2-3.3c0.1,1.4,0.3,2.8,0.1,4.2C133.6,25.5,133.5,26.3,133.4,27.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M144.9,10.8c-1.5,0.2-2.9,0.1-4.4-0.1c-0.9-0.2-1.8-0.4-2.6-0.6c-0.2,0-0.3-0.1-0.5-0.1
                                c-0.8-0.2-1.6-0.4-2.3-0.6c0,0.7,0.7,1.6,1.1,2.1c0.5,0.6,1.1,1.2,1.7,1.7c0.3,0.3,0.7,0.7,1,1c0.9,1.1,2,2.1,2.8,3.3
                                s0.5,2.7,0.9,4.1c-0.1-0.3,0.2-1.3,0.3-1.7c0.1-0.6,0.2-1.3,0.4-1.9c0.4-1.2,1.5-2.1,2.4-2.9c1-0.9,2-1.9,2.9-2.9
                                c0.8-0.9,1.9-2.1,2.1-3.2c-0.4,0.2-0.9,0.3-1.3,0.5c-0.8,0.3-1.6,0.6-2.4,0.9C146.2,10.6,145.5,10.7,144.9,10.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M150.7,8.8c-0.6,0.8-1.6,1.6-2.5,2.1c-0.9,0.5-1.9,1-2.8,1.5c-1.5,0.7-3.3,1.2-4.9,0.7
                                c-1.4-0.5-2.6-1.3-3.8-2.1c-0.7-0.5-1.3-1.2-1.8-1.9c0.8,0.2,1.6,0.5,2.4,0.6c2,0.4,4,0.7,6.1,0.6c1.3-0.1,2.7-0.3,4-0.5
                                c1.2-0.2,2.4-0.6,3.5-1.1C150.7,8.7,150.7,8.8,150.7,8.8z"
                            />
                            <Path
                                fill="#463116"
                                d="M142.5,16.3c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.2-1.5-1.7c-0.6-0.6-1.2-1.2-1.9-1.8
                                c-0.9-0.8-1.7-1.6-2.6-2.4c2,0.9,4.3,2.7,6.6,2.3c1.1-0.2,2.2-0.6,3.3-1c0.7-0.3,1.4-0.6,2.1-0.9c-0.3,0.3-0.6,0.5-0.9,0.8
                                c-0.5,0.4-0.9,0.9-1.3,1.3c-0.3,0.3-0.6,0.6-0.8,0.9C144.2,14.8,143.5,15.7,142.5,16.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M151.6,13.4c-0.1,1.6-0.4,3.2-0.6,4.8c-0.1,0.8-0.3,1.6-0.3,2.4c0,0.6-0.1,1.6,0.4,2.1c0.4-0.3,0.7-0.8,1-1.1
                                c0.3-0.3,0.5-0.8,0.8-1.1c0.3-0.6,0.4-1.3,0.4-1.9c0.1-1.5-0.1-3.1-0.5-4.5c-0.3-0.9-0.8-1.6-1.1-2.5
                                C151.7,12.1,151.7,12.7,151.6,13.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M160.9,18.5c0.1-0.1-0.1-0.3-0.3-0.4c-1.2-0.4-2.3-0.6-3.5-0.6c-0.4,0-0.7-0.1-1,0s-0.6,0.3-0.9,0.4
                                c-0.6,0.3-1,0.8-1.5,1.3c-0.6,0.6-1.2,1.4-1.6,2.1c-0.2,0.2-0.3,0.5-0.5,0.7c-0.1,0.1-0.4,0.4-0.4,0.6c0.3,0,0.6,0,0.7,0
                                c0.3,0,0.5-0.1,0.7-0.2c0.5-0.2,0.8-0.5,1.1-0.9c0.7-0.9,1.4-1.9,2.5-2.3C157.8,18.8,159.3,18.7,160.9,18.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M152.2,13.5c-0.1-0.7-0.1-1.3-0.3-2c1.2,1.2,1.3,4.3,3,4.8c0.6,0.1,1.2,0.2,1.8,0.3c0.8,0.1,1.5,0.4,2.2,0.6
                                c0.8,0.3,1.6,0.6,2.1,1.2c-1.5-0.2-3-0.2-4.5-0.1c-0.8,0-1.7,0.1-2.4,0.2c-0.3,0-0.7,0.1-0.9-0.1s-0.2-0.7-0.2-1
                                C152.6,16.1,152.3,14.8,152.2,13.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M153.3,16.8c-0.3-0.4-0.4-0.9-0.5-1.4c-0.1-0.3-0.1-0.5-0.3-0.8c-0.1-0.2-0.2-0.4-0.3-0.6l0,0
                                c0,0.2,0,0.5,0,0.7c0,0.3,0,0.5,0.1,0.8c0.1,0.5,0.1,1,0.1,1.4c0,0.8,0.1,1.6,0.1,2.4c0,0.2,0,0.4,0,0.6s0,0.4,0.2,0.4
                                c0.1,0,0.2-0.1,0.3-0.1c0.4-0.4,0.9-0.8,1.5-1c0.6-0.3,1.1-0.3,1.7-0.4c1.2-0.3,2.4-0.4,3.6-0.5c-0.3-0.1-0.7-0.2-1-0.3
                                c-0.3,0-0.6-0.1-0.9-0.1c-0.8-0.1-1.5,0-2.3,0C154.6,17.7,153.8,17.7,153.3,16.8z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M61.3,62.1c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.2,3.7-0.2,5.5
                                c-0.1,1.6,0,3.4-0.5,4.9C62.1,59.8,61.6,60.9,61.3,62.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M74.6,57.1c-1.5-0.4-3-0.8-4.6-0.8c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9c-0.5,0.6-0.7,1.2-1.2,1.8
                                c-0.5,0.7-1.2,1.2-1.9,1.7c2.5,0,4.8-1.3,7-2.4c1.1-0.6,2-0.8,3.2-1c1.4-0.2,2.7-0.5,4.1-0.8c0.1,0,0.3-0.2,0.2-0.3l-0.1-0.1
                                C75.5,57.4,75,57.3,74.6,57.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M64.1,51.6c-0.1-0.8-0.2-1.6-0.3-2.3c0.1,0.3,0.7,1,0.9,1.3c0.3,0.6,0.8,1.1,1.2,1.6c0.8,0.9,1.7,1.7,2.7,2.3
                                c1.1,0.6,2.3,0.9,3.4,1.3c0.9,0.3,1.9,0.7,2.4,1.4c-3.1-0.4-6,0.1-9.1,0.1c-0.5,0-0.7-0.1-0.8-0.7C64.3,55,64.3,53.3,64.1,51.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M63.7,58.4c-0.1,0.6-0.2,1.3-0.3,1.9c0.5,0.1,0.9-0.2,1.3-0.5c0.6-0.5,1.1-1,1.9-1.4c2.6-1.3,5.8-0.9,8.7-1
                                c-1-0.4-2.1-0.5-3.2-0.6c-1.3-0.1-2.6-0.2-4-0.4c-1-0.1-2,0.2-2.6-0.9c-0.3-0.7-0.6-1.3-0.8-2c-0.2-0.7-0.3-1.4-0.6-2
                                c0,1.4,0.2,2.7,0,4.1C64,56.5,63.8,57.5,63.7,58.4z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M39,193.1c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.2,3.7-0.2,5.5
                                c-0.1,1.6,0,3.4-0.5,4.9C39.8,190.8,39.3,191.9,39,193.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M52.3,188.2c-1.5-0.4-3-0.8-4.6-0.8c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9c-0.5,0.6-0.7,1.2-1.2,1.8
                                c-0.5,0.7-1.2,1.2-1.9,1.7c2.5,0,4.8-1.3,7-2.4c1.1-0.6,2-0.8,3.2-1c1.4-0.2,2.7-0.5,4.1-0.8c0.1,0,0.3-0.2,0.2-0.3l-0.1-0.1
                                C53.2,188.4,52.7,188.3,52.3,188.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M41.9,182.6c-0.1-0.8-0.2-1.6-0.3-2.3c0.1,0.3,0.7,1,0.9,1.3c0.3,0.6,0.8,1.1,1.2,1.6
                                c0.8,0.9,1.7,1.7,2.7,2.3c1.1,0.6,2.3,0.9,3.4,1.3c0.9,0.3,1.9,0.7,2.4,1.4c-3.1-0.4-6,0.1-9.1,0.1c-0.5,0-0.7-0.1-0.8-0.7
                                C42,186,42,184.3,41.9,182.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M41.4,189.4c-0.1,0.6-0.2,1.3-0.3,1.9c0.5,0.1,0.9-0.2,1.3-0.5c0.6-0.5,1.1-1,1.9-1.4c2.6-1.3,5.8-0.9,8.7-1
                                c-1-0.4-2.1-0.5-3.2-0.6c-1.3-0.1-2.6-0.2-4-0.4c-1-0.1-2,0.2-2.6-0.9c-0.3-0.7-0.6-1.3-0.8-2s-0.3-1.4-0.6-2c0,1.4,0.2,2.7,0,4.1
                                C41.7,187.6,41.5,188.5,41.4,189.4z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M18.4,28c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.2,3.7-0.2,5.5
                                c-0.1,1.6,0,3.4-0.5,4.9C19.2,25.6,18.7,26.8,18.4,28z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M31.7,23c-1.5-0.4-3-0.8-4.6-0.8c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9c-0.5,0.6-0.7,1.2-1.2,1.8
                                c-0.5,0.7-1.2,1.2-1.9,1.7c2.5,0,4.8-1.3,7-2.4c1.1-0.6,2-0.8,3.2-1c1.4-0.2,2.7-0.5,4.1-0.8c0.1,0,0.3-0.2,0.2-0.3L33,23.3
                                C32.6,23.3,32.1,23.1,31.7,23z"
                            />
                            <Path
                                fill="#97773C"
                                d="M21.2,17.5c-0.1-0.8-0.2-1.6-0.3-2.3c0.1,0.3,0.7,1,0.9,1.3c0.3,0.6,0.8,1.1,1.2,1.6c0.8,0.9,1.7,1.7,2.7,2.3
                                c1.1,0.6,2.3,0.9,3.4,1.3c0.9,0.3,1.9,0.7,2.4,1.4c-3.1-0.4-6,0.1-9.1,0.1c-0.5,0-0.7-0.1-0.8-0.7C21.4,20.8,21.4,19.1,21.2,17.5z
                                "
                            />
                            <Path
                                fill="#463116"
                                d="M20.8,24.2c-0.1,0.6-0.2,1.3-0.3,1.9c0.5,0.1,0.9-0.2,1.3-0.5c0.6-0.5,1.1-1,1.9-1.4c2.6-1.3,5.8-0.9,8.7-1
                                c-1-0.4-2.1-0.5-3.2-0.6c-1.3-0.1-2.6-0.2-4-0.4c-1-0.1-2,0.2-2.6-0.9c-0.3-0.7-0.6-1.3-0.8-2c-0.2-0.7-0.3-1.4-0.6-2
                                c0,1.4,0.2,2.7,0,4.1C21,22.4,20.9,23.3,20.8,24.2z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M72.6,122.3c-0.1,1.8-0.4,3.5-0.6,5.2c-0.1,0.9-0.3,1.8-0.3,2.7c0,0.7-0.1,1.7,0.4,2.3
                                c0.4-0.4,0.7-0.8,1.1-1.2c0.3-0.4,0.5-0.9,0.8-1.2c0.3-0.6,0.4-1.4,0.5-2.1c0.1-1.7-0.1-3.4-0.5-5c-0.3-1-0.9-1.8-1.2-2.8
                                C72.6,120.9,72.6,121.6,72.6,122.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M82.8,127.9c0.1-0.2-0.1-0.3-0.3-0.4c-1.3-0.4-2.5-0.7-3.8-0.7c-0.4,0-0.8-0.1-1.1,0c-0.4,0.1-0.7,0.3-1,0.5
                                c-0.6,0.4-1.2,0.9-1.7,1.4c-0.7,0.7-1.3,1.5-1.8,2.3c-0.2,0.3-0.3,0.5-0.5,0.8c-0.1,0.1-0.4,0.4-0.4,0.6c0.3,0.1,0.7,0,0.8,0
                                c0.3,0,0.5-0.1,0.8-0.2c0.5-0.2,0.9-0.5,1.2-1c0.7-1,1.6-2.1,2.8-2.5C79.4,128.2,81,128.1,82.8,127.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M73.1,122.4c-0.1-0.7-0.2-1.5-0.3-2.2c1.3,1.4,1.4,4.8,3.3,5.2c0.7,0.2,1.4,0.2,2,0.3
                                c0.8,0.1,1.6,0.4,2.4,0.7c0.8,0.3,1.7,0.6,2.3,1.3c-1.7-0.2-3.3-0.2-5-0.1c-0.9,0-1.8,0.2-2.7,0.2c-0.3,0-0.8,0.1-1-0.1
                                s-0.2-0.8-0.3-1C73.6,125.2,73.3,123.8,73.1,122.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M74.4,126.1c-0.3-0.5-0.4-1-0.5-1.6c-0.1-0.3-0.2-0.6-0.3-0.9c-0.1-0.2-0.2-0.5-0.3-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0,0.8s0,0.6,0.1,0.9c0.1,0.5,0.1,1.1,0.1,1.6c0,0.9,0.1,1.8,0.1,2.7c0,0.2,0,0.4,0,0.6c0,0.2,0.1,0.4,0.2,0.4
                                s0.2-0.1,0.3-0.1c0.5-0.5,1-0.8,1.6-1.1s1.2-0.3,1.8-0.5c1.3-0.3,2.6-0.4,4-0.5c-0.4-0.2-0.7-0.3-1.1-0.3c-0.3,0-0.7-0.1-1-0.2
                                c-0.8-0.1-1.7,0-2.6-0.1C75.8,127,75,127,74.4,126.1z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M71.1,119.1c0.2,0.1,0.4,0,0.5-0.1s0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.1-0.8c0.2-0.2,0.4-0.4,0.5-0.6
                                c0.2-0.3,0.3-0.7,0.4-1c0.3-1.4,0.5-2.8,0.5-4.2c0-2.2-0.8-4-1.3-6.1c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.7-0.2,3.4-0.2,5.1
                                c0,1.5,0,3.1-0.5,4.5C71.8,116.9,71.3,117.9,71.1,119.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M83.3,114.5c-1.4-0.4-2.8-0.8-4.2-0.8c-1,0-1.8-0.2-2.8,0.3c-0.9,0.4-1.6,1.1-2.2,1.8
                                c-0.5,0.5-0.6,1.1-1.1,1.7s-1.1,1.1-1.7,1.5c2.3,0,4.4-1.2,6.4-2.2c1.1-0.5,1.8-0.8,3-0.9c1.3-0.2,2.5-0.4,3.7-0.8
                                c0.1,0,0.3-0.1,0.2-0.2l-0.1-0.1C84.1,114.7,83.7,114.6,83.3,114.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M73.7,109.4c-0.1-0.7-0.2-1.4-0.3-2.2c0.1,0.3,0.6,0.9,0.8,1.2c0.3,0.5,0.7,1,1.1,1.5
                                c0.7,0.8,1.6,1.6,2.5,2.2c1,0.6,2.1,0.9,3.2,1.2c0.8,0.3,1.7,0.6,2.2,1.3c-2.8-0.3-5.6,0.1-8.4,0.1c-0.5,0-0.7-0.1-0.8-0.6
                                C73.8,112.5,73.8,110.9,73.7,109.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M73.3,115.6c-0.1,0.6-0.2,1.2-0.2,1.8c0.4,0.1,0.9-0.2,1.2-0.5c0.6-0.5,1.1-0.9,1.7-1.3
                                c2.4-1.2,5.4-0.9,8-0.9c-0.9-0.4-1.9-0.5-2.9-0.6c-1.2-0.1-2.4-0.2-3.7-0.3c-0.9-0.1-1.9,0.2-2.4-0.8c-0.3-0.6-0.6-1.2-0.7-1.9
                                c-0.1-0.6-0.3-1.3-0.6-1.9c0,1.3,0.2,2.5,0,3.8C73.5,113.9,73.4,114.8,73.3,115.6z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M225.7,88.5c-0.1,1.8-0.4,3.5-0.6,5.2c-0.1,0.9-0.3,1.8-0.3,2.7c0,0.7-0.1,1.7,0.4,2.3
                                c0.4-0.4,0.7-0.8,1.1-1.2c0.3-0.4,0.5-0.9,0.8-1.2c0.3-0.6,0.4-1.4,0.5-2.1c0.1-1.7-0.1-3.4-0.5-5c-0.3-1-0.9-1.8-1.2-2.8
                                C225.7,87.1,225.7,87.8,225.7,88.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M235.9,94.1c0.1-0.2-0.1-0.3-0.3-0.4c-1.3-0.4-2.5-0.7-3.8-0.7c-0.4,0-0.8-0.1-1.1,0c-0.4,0.1-0.7,0.3-1,0.5
                                c-0.6,0.4-1.2,0.9-1.7,1.4c-0.7,0.7-1.3,1.5-1.8,2.3c-0.2,0.3-0.3,0.5-0.5,0.8c-0.1,0.1-0.4,0.4-0.4,0.6c0.3,0.1,0.7,0,0.8,0
                                c0.3,0,0.5-0.1,0.8-0.2c0.5-0.2,0.9-0.5,1.2-1c0.7-1,1.6-2.1,2.8-2.5C232.5,94.5,234.1,94.4,235.9,94.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M226.3,88.6c-0.1-0.7-0.2-1.5-0.3-2.2c1.3,1.4,1.4,4.8,3.3,5.2c0.7,0.2,1.4,0.2,2,0.3
                                c0.8,0.1,1.6,0.4,2.4,0.7c0.8,0.3,1.7,0.6,2.3,1.3c-1.7-0.2-3.3-0.2-5-0.1c-0.9,0-1.8,0.2-2.7,0.2c-0.3,0-0.8,0.1-1-0.1
                                s-0.2-0.8-0.3-1C226.8,91.5,226.4,90.1,226.3,88.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M227.5,92.3c-0.3-0.5-0.4-1-0.5-1.6c-0.1-0.3-0.2-0.6-0.3-0.9c-0.1-0.2-0.2-0.5-0.3-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0,0.8s0,0.6,0.1,0.9c0.1,0.5,0.1,1.1,0.1,1.6c0,0.9,0.1,1.8,0.1,2.7c0,0.2,0,0.4,0,0.6s0.1,0.4,0.2,0.4
                                c0.1,0,0.2-0.1,0.3-0.1c0.5-0.5,1-0.8,1.6-1.1c0.6-0.3,1.2-0.3,1.8-0.5c1.3-0.3,2.6-0.4,4-0.5c-0.4-0.2-0.7-0.3-1.1-0.3
                                c-0.3,0-0.7-0.1-1-0.2c-0.8-0.1-1.7,0-2.6-0.1C229,93.3,228.1,93.3,227.5,92.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M224.2,85.3c0.2,0.1,0.4,0,0.5-0.1s0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.1-0.8c0.2-0.2,0.4-0.4,0.5-0.6
                                c0.2-0.3,0.3-0.7,0.4-1c0.3-1.4,0.5-2.8,0.5-4.2c0-2.2-0.8-4-1.3-6.1c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.7-0.2,3.4-0.2,5.1
                                c0,1.5,0,3.1-0.5,4.5C224.9,83.1,224.4,84.2,224.2,85.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M236.4,80.7c-1.4-0.4-2.8-0.8-4.2-0.8c-1,0-1.8-0.2-2.8,0.3c-0.9,0.4-1.6,1.1-2.2,1.8
                                c-0.5,0.5-0.6,1.1-1.1,1.7s-1.1,1.1-1.7,1.5c2.3,0,4.4-1.2,6.4-2.2c1.1-0.5,1.8-0.8,3-0.9c1.3-0.2,2.5-0.4,3.7-0.8
                                c0.1,0,0.3-0.1,0.2-0.2l-0.1-0.1C237.2,81,236.8,80.8,236.4,80.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M226.8,75.6c-0.1-0.7-0.2-1.4-0.3-2.2c0.1,0.3,0.6,0.9,0.8,1.2c0.3,0.5,0.7,1,1.1,1.5
                                c0.7,0.8,1.6,1.6,2.5,2.2c1,0.6,2.1,0.9,3.2,1.2c0.8,0.3,1.7,0.6,2.2,1.3c-2.8-0.3-5.6,0.1-8.4,0.1c-0.5,0-0.7-0.1-0.8-0.6
                                C226.9,78.7,226.9,77.2,226.8,75.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M226.4,81.9c-0.1,0.6-0.2,1.2-0.2,1.8c0.4,0.1,0.9-0.2,1.2-0.5c0.6-0.5,1.1-0.9,1.7-1.3
                                c2.4-1.2,5.4-0.9,8-0.9c-0.9-0.4-1.9-0.5-2.9-0.6c-1.2-0.1-2.4-0.2-3.7-0.3c-0.9-0.1-1.9,0.2-2.4-0.8c-0.3-0.6-0.6-1.2-0.7-1.9
                                c-0.1-0.6-0.3-1.3-0.6-1.9c0,1.3,0.2,2.5,0,3.8C226.6,80.2,226.5,81,226.4,81.9z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M100.9,155.5c-0.1,1.8-0.4,3.5-0.6,5.2c-0.1,0.9-0.3,1.8-0.3,2.7c0,0.7-0.1,1.7,0.4,2.3
                                c0.4-0.4,0.7-0.8,1.1-1.2c0.3-0.4,0.5-0.9,0.8-1.2c0.3-0.6,0.4-1.4,0.5-2.1c0.1-1.7-0.1-3.4-0.5-5c-0.3-1-0.9-1.8-1.2-2.8
                                C101,154.1,101,154.8,100.9,155.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M111.2,161.1c0.1-0.2-0.1-0.3-0.3-0.4c-1.3-0.4-2.5-0.7-3.8-0.7c-0.4,0-0.8-0.1-1.1,0c-0.4,0.1-0.7,0.3-1,0.5
                                c-0.6,0.4-1.2,0.9-1.7,1.4c-0.7,0.7-1.3,1.5-1.8,2.3c-0.2,0.3-0.3,0.5-0.5,0.8c-0.1,0.1-0.4,0.4-0.4,0.6c0.3,0.1,0.7,0,0.8,0
                                c0.3,0,0.5-0.1,0.8-0.2c0.5-0.2,0.9-0.5,1.2-1c0.7-1,1.6-2.1,2.8-2.5C107.8,161.4,109.4,161.4,111.2,161.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M101.5,155.6c-0.1-0.7-0.2-1.5-0.3-2.2c1.3,1.4,1.4,4.8,3.3,5.2c0.7,0.2,1.4,0.2,2,0.3
                                c0.8,0.1,1.6,0.4,2.4,0.7s1.7,0.6,2.3,1.3c-1.7-0.2-3.3-0.2-5-0.1c-0.9,0-1.8,0.2-2.7,0.2c-0.3,0-0.8,0.1-1-0.1
                                c-0.2-0.2-0.2-0.8-0.3-1C102,158.5,101.7,157,101.5,155.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M102.8,159.3c-0.3-0.5-0.4-1-0.5-1.6c-0.1-0.3-0.2-0.6-0.3-0.9c-0.1-0.2-0.2-0.5-0.3-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0,0.8s0,0.6,0.1,0.9c0.1,0.5,0.1,1.1,0.1,1.6c0,0.9,0.1,1.8,0.1,2.7c0,0.2,0,0.4,0,0.6c0,0.2,0.1,0.4,0.2,0.4
                                s0.2-0.1,0.3-0.1c0.5-0.5,1-0.8,1.6-1.1s1.2-0.3,1.8-0.5c1.3-0.3,2.6-0.4,4-0.5c-0.4-0.2-0.7-0.3-1.1-0.3c-0.3,0-0.7-0.1-1-0.2
                                c-0.8-0.1-1.7,0-2.6-0.1C104.2,160.3,103.4,160.2,102.8,159.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M99.5,152.3c0.2,0.1,0.4,0,0.5-0.1s0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.1-0.8c0.2-0.2,0.4-0.4,0.5-0.6
                                c0.2-0.3,0.3-0.7,0.4-1c0.3-1.4,0.5-2.8,0.5-4.2c0-2.2-0.8-4-1.3-6.1c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.7-0.2,3.4-0.2,5.1
                                c0,1.5,0,3.1-0.5,4.5C100.2,150.1,99.7,151.2,99.5,152.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M111.7,147.7c-1.4-0.4-2.8-0.8-4.2-0.8c-1,0-1.8-0.2-2.8,0.3c-0.9,0.4-1.6,1.1-2.2,1.8
                                c-0.5,0.5-0.6,1.1-1.1,1.7c-0.5,0.6-1.1,1.1-1.7,1.5c2.3,0,4.4-1.2,6.4-2.2c1.1-0.5,1.8-0.8,3-0.9c1.3-0.2,2.5-0.4,3.7-0.8
                                c0.1,0,0.3-0.1,0.2-0.2l-0.1-0.1C112.5,148,112.1,147.8,111.7,147.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M102.1,142.6c-0.1-0.7-0.2-1.4-0.3-2.2c0.1,0.3,0.6,0.9,0.8,1.2c0.3,0.5,0.7,1,1.1,1.5
                                c0.7,0.8,1.6,1.6,2.5,2.2c1,0.6,2.1,0.9,3.2,1.2c0.8,0.3,1.7,0.6,2.2,1.3c-2.8-0.3-5.6,0.1-8.4,0.1c-0.5,0-0.7-0.1-0.8-0.6
                                C102.2,145.7,102.2,144.2,102.1,142.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M101.6,148.9c-0.1,0.6-0.2,1.2-0.2,1.8c0.4,0.1,0.9-0.2,1.2-0.5c0.6-0.5,1.1-0.9,1.7-1.3
                                c2.4-1.2,5.4-0.9,8-0.9c-0.9-0.4-1.9-0.5-2.9-0.6c-1.2-0.1-2.4-0.2-3.7-0.3c-0.9-0.1-1.9,0.2-2.4-0.8c-0.3-0.6-0.6-1.2-0.7-1.9
                                c-0.1-0.6-0.3-1.3-0.6-1.9c0,1.3,0.2,2.5,0,3.8C101.9,147.1,101.8,148,101.6,148.9z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M47.3,53.3c-0.1,1.8-0.4,3.5-0.6,5.2c-0.1,0.9-0.3,1.8-0.3,2.7c0,0.7-0.1,1.7,0.4,2.3
                                c0.4-0.4,0.7-0.8,1.1-1.2c0.3-0.4,0.5-0.9,0.8-1.2c0.3-0.6,0.4-1.4,0.5-2.1c0.1-1.7-0.1-3.4-0.5-5c-0.3-1-0.9-1.8-1.2-2.8
                                C47.4,51.9,47.4,52.6,47.3,53.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M57.6,58.9c0.1-0.2-0.1-0.3-0.3-0.4c-1.3-0.4-2.5-0.7-3.8-0.7c-0.4,0-0.8-0.1-1.1,0c-0.4,0.1-0.7,0.3-1,0.5
                                c-0.6,0.4-1.2,0.9-1.7,1.4c-0.7,0.7-1.3,1.5-1.8,2.3c-0.2,0.3-0.3,0.5-0.5,0.8c-0.1,0.1-0.4,0.4-0.4,0.6c0.3,0.1,0.7,0,0.8,0
                                c0.3,0,0.5-0.1,0.8-0.2c0.5-0.2,0.9-0.5,1.2-1c0.7-1,1.6-2.1,2.8-2.5C54.2,59.2,55.8,59.1,57.6,58.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M47.9,53.4c-0.1-0.7-0.2-1.5-0.3-2.2c1.3,1.4,1.4,4.8,3.3,5.2c0.7,0.2,1.4,0.2,2,0.3c0.8,0.1,1.6,0.4,2.4,0.7
                                c0.8,0.3,1.7,0.6,2.3,1.3c-1.7-0.2-3.3-0.2-5-0.1c-0.9,0-1.8,0.2-2.7,0.2c-0.3,0-0.8,0.1-1-0.1c-0.2-0.2-0.2-0.8-0.3-1
                                C48.4,56.3,48.1,54.8,47.9,53.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M49.2,57.1c-0.3-0.5-0.4-1-0.5-1.6c-0.1-0.3-0.2-0.6-0.3-0.9c-0.1-0.2-0.2-0.5-0.3-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0,0.8s0,0.6,0.1,0.9c0.1,0.5,0.1,1.1,0.1,1.6c0,0.9,0.1,1.8,0.1,2.7c0,0.2,0,0.4,0,0.6c0,0.2,0.1,0.4,0.2,0.4
                                c0.1,0,0.2-0.1,0.3-0.1c0.5-0.5,1-0.8,1.6-1.1s1.2-0.3,1.8-0.5c1.3-0.3,2.6-0.4,4-0.5c-0.4-0.2-0.7-0.3-1.1-0.3
                                c-0.3,0-0.7-0.1-1-0.2c-0.8-0.1-1.7,0-2.6-0.1C50.6,58.1,49.8,58,49.2,57.1z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M45.8,50.1c0.2,0.1,0.4,0,0.5-0.1s0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.1-0.8c0.2-0.2,0.4-0.4,0.5-0.6
                                c0.2-0.3,0.3-0.7,0.4-1c0.3-1.4,0.5-2.8,0.5-4.2c0-2.2-0.8-4-1.3-6.1c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.7-0.2,3.4-0.2,5.1
                                c0,1.5,0,3.1-0.5,4.5C46.6,47.9,46.1,49,45.8,50.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M58.1,45.5c-1.4-0.4-2.8-0.8-4.2-0.8c-1,0-1.8-0.2-2.8,0.3c-0.9,0.4-1.6,1.1-2.2,1.8
                                c-0.5,0.5-0.6,1.1-1.1,1.7c-0.5,0.6-1.1,1.1-1.7,1.5c2.3,0,4.4-1.2,6.4-2.2c1.1-0.5,1.8-0.8,3-0.9c1.3-0.2,2.5-0.4,3.7-0.8
                                c0.1,0,0.3-0.1,0.2-0.2l-0.1-0.1C58.9,45.7,58.5,45.6,58.1,45.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M48.4,40.4c-0.1-0.7-0.2-1.4-0.3-2.2c0.1,0.3,0.6,0.9,0.8,1.2c0.3,0.5,0.7,1,1.1,1.5c0.7,0.8,1.6,1.6,2.5,2.2
                                c1,0.6,2.1,0.9,3.2,1.2c0.8,0.3,1.7,0.6,2.2,1.3c-2.8-0.3-5.6,0.1-8.4,0.1c-0.5,0-0.7-0.1-0.8-0.6C48.6,43.5,48.6,41.9,48.4,40.4z
                                "
                            />
                            <Path
                                fill="#463116"
                                d="M48,46.7c-0.1,0.6-0.2,1.2-0.2,1.8c0.4,0.1,0.9-0.2,1.2-0.5c0.6-0.5,1.1-0.9,1.7-1.3c2.4-1.2,5.4-0.9,8-0.9
                                c-0.9-0.4-1.9-0.5-2.9-0.6c-1.2-0.1-2.4-0.2-3.7-0.3c-0.9-0.1-1.9,0.2-2.4-0.8c-0.3-0.6-0.6-1.2-0.7-1.9c-0.1-0.6-0.3-1.3-0.6-1.9
                                c0,1.3,0.2,2.5,0,3.8C48.3,44.9,48.2,45.8,48,46.7z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M164.3,90.3c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C163.4,90.5,163.9,90.4,164.3,90.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M174.7,84.7c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C174.6,88.1,174.6,86.4,174.7,84.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M175.2,91.5c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4
                                c-2.6-1.3-5.8-0.9-8.7-1c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2s0.3-1.4,0.6-2
                                c0,1.4-0.2,2.7,0,4.1C174.9,89.6,175.1,90.6,175.2,91.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M229.3,21.1c1.8-0.5,3.7-1,5.6-1c1.4,0,2.4-0.2,3.7,0.4c1.2,0.5,2.1,1.4,3,2.4c1.3,1.4,1.5,3.2,1.9,5
                                c-0.6-0.2-1-0.7-1.4-1.1c-1.5-1.3-3.6-1.6-5.3-2.5c-1.4-0.7-2.5-1-4-1.2c-1.7-0.2-3.4-0.6-5-1c-0.2,0-0.4-0.2-0.3-0.3
                                c0,0,0.1-0.1,0.2-0.1C228.1,21.4,228.7,21.3,229.3,21.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M242.1,14.3c0.1-1,0.2-1.9,0.4-2.9c-0.1,0.4-0.8,1.3-1.1,1.7c-0.4,0.7-0.9,1.3-1.5,2c-1,1.1-2.1,2.1-3.4,2.9
                                c-1.3,0.7-2.8,1.2-4.2,1.6c-1.1,0.4-2.3,0.8-3,1.8c3.8-0.5,7.4,0.1,11.2,0.1c0.7,0,0.9-0.2,1-0.9
                                C241.9,18.4,241.9,16.3,242.1,14.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M242.7,22.6c0.1,0.8,0.2,1.6,0.3,2.4c-0.6,0.1-1.1-0.2-1.6-0.6c-0.8-0.6-1.4-1.2-2.3-1.7
                                c-3.2-1.6-7.2-1.2-10.7-1.3c1.2-0.5,2.6-0.7,3.9-0.8c1.6-0.1,3.3-0.3,4.9-0.4c1.3-0.1,2.5,0.3,3.2-1.1c0.4-0.8,0.8-1.6,1-2.5
                                c0.2-0.8,0.3-1.7,0.8-2.5c0,1.8-0.2,3.3,0,5.1C242.3,20.3,242.5,21.5,242.7,22.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M195.1,46c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C194.2,46.3,194.7,46.2,195.1,46z"
                            />
                            <Path
                                fill="#97773C"
                                d="M205.5,40.5c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C205.4,43.9,205.4,42.2,205.5,40.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M206,47.3c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2s0.3-1.4,0.6-2c0,1.4-0.2,2.7,0,4.1
                                C205.7,45.4,205.9,46.4,206,47.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M195.5,59.9c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C194.6,60.1,195,60,195.5,59.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M205.9,54.3c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C205.8,57.7,205.8,56,205.9,54.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M206.4,61.1c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4
                                c-2.6-1.3-5.8-0.9-8.7-1c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2s0.3-1.4,0.6-2
                                c0,1.4-0.2,2.7,0,4.1C206.1,59.3,206.2,60.2,206.4,61.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M88.3,140.3c-1.4,0.3-2.8,0.7-4.3,0.9s-3,0.2-4.5,0.3c-1.9,0-3.9,0.2-5.7-0.3c-0.2,0-0.4-0.1-0.6-0.2
                                c-0.9-0.3-1.8-0.5-2.7-0.7c-0.1,0.8,0.8,1.9,1.3,2.5c0.6,0.7,1.3,1.4,1.9,2c0.4,0.4,0.8,0.8,1.1,1.2c2.1,2.4,3.3,5.1,3.4,8.3
                                c0.1,4,0.5,8.1,1.1,12.1c0.2-3.9,0.5-7.8,0.7-11.6c0.1-0.9,0.1-1.8,0.3-2.7c0.3-1.7,1-3.4,2-4.8c1.9-2.5,4.7-4,6-6.9
                                C88.3,140.3,88.3,140.3,88.3,140.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M88.8,139.7c-0.7,1-1.9,1.9-2.9,2.5c-1.1,0.6-2.2,1.2-3.3,1.7c-1.8,0.8-3.9,1.4-5.8,0.8
                                c-1.6-0.5-3.1-1.5-4.4-2.5c-0.8-0.6-1.5-1.4-2.1-2.2c0.9,0.3,1.8,0.5,2.8,0.7c2.4,0.5,4.7,0.9,7.1,0.7c1.6-0.1,3.1-0.3,4.7-0.6
                                c1.4-0.3,2.8-0.7,4.1-1.3C88.9,139.6,88.8,139.7,88.8,139.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M79.2,148.6c-0.2,0.1-0.3,0.1-0.4,0.1c-0.7-0.6-1.2-1.4-1.8-2c-0.7-0.7-1.4-1.4-2.2-2.1c-1-0.9-2-1.9-3.1-2.8
                                c2.4,1.1,5,3.1,7.7,2.7c1.3-0.2,2.6-0.7,3.8-1.2c0.8-0.4,1.7-0.6,2.5-1c-0.4,0.3-0.7,0.6-1,0.9c-0.5,0.5-1.1,1-1.6,1.6
                                c-0.3,0.3-0.7,0.7-1,1C81.3,146.8,80.4,147.9,79.2,148.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M85.2,156c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C84.2,156.2,84.7,156.1,85.2,156z"
                            />
                            <Path
                                fill="#97773C"
                                d="M95.6,150.4c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C95.4,153.8,95.4,152.1,95.6,150.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M96,157.2c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2c0.2-0.7,0.3-1.4,0.6-2
                                c0,1.4-0.2,2.7,0,4.1C95.8,155.3,95.9,156.3,96,157.2z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M8.5,94.6c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.2,3.7-0.2,5.5
                                c-0.1,1.6,0,3.4-0.5,4.9C9.3,92.3,8.8,93.4,8.5,94.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M21.8,89.7c-1.5-0.4-3-0.8-4.6-0.8c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9c-0.5,0.6-0.7,1.2-1.2,1.8
                                c-0.5,0.7-1.2,1.2-1.9,1.7c2.5,0,4.8-1.3,7-2.4c1.1-0.6,2-0.8,3.2-1c1.4-0.2,2.7-0.5,4.1-0.8c0.1,0,0.3-0.2,0.2-0.3L23.1,90
                                C22.7,89.9,22.2,89.8,21.8,89.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M11.4,84.1c-0.1-0.8-0.2-1.6-0.3-2.3c0.1,0.3,0.7,1,0.9,1.3c0.3,0.6,0.8,1.1,1.2,1.6c0.8,0.9,1.7,1.7,2.7,2.3
                                c1.1,0.6,2.3,0.9,3.4,1.3c0.9,0.3,1.9,0.7,2.4,1.4c-3.1-0.4-6,0.1-9.1,0.1c-0.5,0-0.7-0.1-0.8-0.7C11.5,87.5,11.5,85.8,11.4,84.1z
                                "
                            />
                            <Path
                                fill="#463116"
                                d="M10.9,90.9c-0.1,0.6-0.2,1.3-0.3,1.9c0.5,0.1,0.9-0.2,1.3-0.5c0.6-0.5,1.1-1,1.9-1.4c2.6-1.3,5.8-0.9,8.7-1
                                c-1-0.4-2.1-0.5-3.2-0.6c-1.3-0.1-2.6-0.2-4-0.4c-1-0.1-2,0.2-2.6-0.9c-0.3-0.7-0.6-1.3-0.8-2s-0.3-1.4-0.6-2c0,1.4,0.2,2.7,0,4.1
                                C11.2,89,11,90,10.9,90.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M24.3,89.7c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C23.4,89.9,23.8,89.8,24.3,89.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M34.7,84.1c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C34.5,87.5,34.6,85.8,34.7,84.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M35.2,90.9c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2s0.3-1.4,0.6-2c0,1.4-0.2,2.7,0,4.1
                                C34.9,89,35,90,35.2,90.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M31.2,59.6c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C30.3,59.9,30.8,59.7,31.2,59.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M41.6,54.1c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C41.5,57.5,41.5,55.8,41.6,54.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M42.1,60.9c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2s0.3-1.4,0.6-2c0,1.4-0.2,2.7,0,4.1
                                C41.8,59,42,59.9,42.1,60.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M31.5,46c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C30.6,46.3,31,46.2,31.5,46z"
                            />
                            <Path
                                fill="#97773C"
                                d="M41.9,40.5c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C41.7,43.9,41.8,42.2,41.9,40.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M42.4,47.3c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2s0.3-1.4,0.6-2c0,1.4-0.2,2.7,0,4.1
                                C42.1,45.4,42.2,46.3,42.4,47.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M158.7,187.4c-0.1,1.7-0.4,3.3-0.6,4.9c-0.1,0.8-0.3,1.7-0.3,2.5c0,0.6-0.1,1.6,0.4,2.2
                                c0.4-0.3,0.7-0.8,1.1-1.2c0.3-0.3,0.5-0.8,0.8-1.1c0.3-0.6,0.4-1.3,0.4-2c0.1-1.6-0.1-3.2-0.5-4.7c-0.3-0.9-0.8-1.7-1.1-2.6
                                C158.8,186.1,158.8,186.7,158.7,187.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M168.4,192.7c0.1-0.2-0.1-0.3-0.3-0.4c-1.2-0.4-2.4-0.6-3.6-0.6c-0.4,0-0.7-0.1-1.1,0c-0.3,0.1-0.7,0.3-1,0.5
                                c-0.6,0.4-1.1,0.8-1.6,1.3c-0.6,0.6-1.2,1.4-1.7,2.2c-0.2,0.3-0.3,0.5-0.5,0.8c-0.1,0.1-0.4,0.4-0.4,0.6c0.3,0.1,0.6,0,0.7,0
                                c0.3,0,0.5-0.1,0.8-0.2c0.5-0.2,0.9-0.5,1.2-0.9c0.7-1,1.5-2,2.6-2.4C165.2,193,166.7,192.9,168.4,192.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M159.3,187.5c-0.1-0.7-0.2-1.4-0.3-2.1c1.2,1.3,1.3,4.5,3.1,4.9c0.6,0.2,1.3,0.2,1.9,0.3
                                c0.8,0.1,1.5,0.4,2.3,0.6c0.8,0.3,1.7,0.6,2.1,1.3c-1.6-0.2-3.1-0.2-4.7-0.1c-0.8,0-1.7,0.1-2.5,0.2c-0.3,0-0.7,0.1-0.9-0.1
                                c-0.2-0.2-0.2-0.7-0.3-1C159.8,190.2,159.4,188.9,159.3,187.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M160.5,191c-0.3-0.4-0.4-1-0.5-1.5c-0.1-0.3-0.1-0.6-0.3-0.8c-0.1-0.2-0.2-0.5-0.3-0.7c0,0,0,0-0.1,0
                                c0,0.2,0,0.5,0,0.7c0,0.3,0,0.5,0.1,0.8c0.1,0.5,0.1,1,0.1,1.5c0,0.8,0.1,1.7,0.1,2.5c0,0.2,0,0.4,0,0.6c0,0.2,0.1,0.4,0.2,0.4
                                c0.1,0,0.2-0.1,0.3-0.1c0.4-0.4,1-0.8,1.5-1.1c0.6-0.3,1.1-0.3,1.7-0.4c1.3-0.3,2.5-0.4,3.8-0.5c-0.3-0.2-0.7-0.3-1.1-0.3
                                c-0.3,0-0.6-0.1-0.9-0.2c-0.8-0.1-1.6,0-2.4,0C161.8,191.9,161,191.9,160.5,191z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M157.3,184.4c0.2,0.1,0.3,0,0.5-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.2,0.7-0.5,1-0.8c0.2-0.2,0.4-0.3,0.5-0.6
                                c0.2-0.3,0.3-0.6,0.3-0.9c0.3-1.3,0.5-2.6,0.5-4c0-2.1-0.8-3.7-1.2-5.7c-0.1-0.1-0.2,0.1-0.2,0.2c-0.3,1.6-0.1,3.2-0.2,4.8
                                c0,1.4,0,2.9-0.5,4.2C158,182.3,157.6,183.3,157.3,184.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M168.9,180.1c-1.3-0.4-2.6-0.7-4-0.7c-1,0-1.7-0.2-2.6,0.3c-0.8,0.4-1.5,1-2.1,1.7c-0.5,0.5-0.6,1.1-1,1.6
                                c-0.4,0.6-1,1-1.6,1.4c2.2,0,4.2-1.1,6.1-2.1c1-0.5,1.7-0.7,2.8-0.9c1.2-0.2,2.4-0.4,3.5-0.7c0.1,0,0.3-0.1,0.2-0.2
                                c0,0-0.1,0-0.1-0.1C169.6,180.3,169.3,180.2,168.9,180.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M159.8,175.2c-0.1-0.7-0.1-1.4-0.3-2c0.1,0.3,0.6,0.9,0.8,1.2c0.3,0.5,0.7,1,1,1.4c0.7,0.8,1.5,1.5,2.4,2
                                s2,0.8,3,1.2c0.8,0.3,1.6,0.6,2.1,1.2c-2.7-0.3-5.2,0.1-7.9,0.1c-0.5,0-0.6-0.1-0.7-0.6C159.9,178.2,159.9,176.7,159.8,175.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M159.4,181.1c-0.1,0.6-0.2,1.1-0.2,1.7c0.4,0.1,0.8-0.2,1.1-0.4c0.6-0.5,1-0.9,1.6-1.2
                                c2.3-1.1,5.1-0.8,7.6-0.9c-0.9-0.4-1.8-0.5-2.8-0.6c-1.2-0.1-2.3-0.2-3.5-0.3c-0.9-0.1-1.8,0.2-2.2-0.7c-0.3-0.6-0.5-1.1-0.7-1.8
                                c-0.1-0.6-0.2-1.2-0.6-1.7c0,1.2,0.2,2.3,0,3.6C159.6,179.5,159.5,180.3,159.4,181.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M144.3,192.9c1.4-0.4,2.8-0.8,4.3-0.8c1.1,0,1.8-0.2,2.8,0.3c0.9,0.4,1.6,1.1,2.3,1.8c1,1.1,1.1,2.5,1.5,3.8
                                c-0.4-0.2-0.8-0.5-1.1-0.8c-1.1-1-2.8-1.3-4.1-1.9c-1.1-0.5-1.9-0.8-3-1c-1.3-0.2-2.6-0.5-3.8-0.8c-0.1,0-0.3-0.1-0.2-0.3l0.1-0.1
                                C143.4,193.1,143.9,193,144.3,192.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M154.1,187.6c0.1-0.7,0.2-1.5,0.3-2.2c-0.1,0.3-0.6,1-0.8,1.3c-0.3,0.5-0.7,1-1.1,1.5
                                c-0.8,0.8-1.6,1.6-2.6,2.2c-1,0.6-2.2,0.9-3.2,1.3c-0.9,0.3-1.8,0.6-2.3,1.3c2.9-0.4,5.7,0.1,8.6,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C154,190.8,154,189.2,154.1,187.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M154.6,194.1c0.1,0.6,0.2,1.2,0.2,1.8c-0.4,0.1-0.9-0.2-1.2-0.5c-0.6-0.5-1.1-1-1.8-1.3
                                c-2.5-1.2-5.5-0.9-8.2-1c0.9-0.4,2-0.5,3-0.6c1.3-0.1,2.5-0.2,3.8-0.3c1-0.1,1.9,0.2,2.4-0.8c0.3-0.6,0.6-1.2,0.8-1.9
                                c0.1-0.6,0.3-1.3,0.6-1.9c0,1.3-0.2,2.5,0,3.9C154.3,192.3,154.4,193.2,154.6,194.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M144.6,180.1c1.4-0.4,2.8-0.8,4.3-0.8c1.1,0,1.8-0.2,2.8,0.3c0.9,0.4,1.6,1.1,2.3,1.8c1,1.1,1.1,2.5,1.5,3.8
                                c-0.4-0.2-0.8-0.5-1.1-0.8c-1.1-1-2.8-1.3-4.1-1.9c-1.1-0.5-1.9-0.8-3-1c-1.3-0.2-2.6-0.5-3.8-0.8c-0.1,0-0.3-0.1-0.2-0.3l0.1-0.1
                                C143.7,180.3,144.1,180.2,144.6,180.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M154.4,174.8c0.1-0.7,0.2-1.5,0.3-2.2c-0.1,0.3-0.6,1-0.8,1.3c-0.3,0.5-0.7,1-1.1,1.5
                                c-0.8,0.8-1.6,1.6-2.6,2.2c-1,0.6-2.2,0.9-3.2,1.3c-0.9,0.3-1.8,0.6-2.3,1.3c2.9-0.4,5.7,0.1,8.6,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C154.2,178,154.3,176.4,154.4,174.8z"
                            />
                            <Path
                                fill="#463116"
                                d="M154.8,181.2c0.1,0.6,0.2,1.2,0.2,1.8c-0.4,0.1-0.9-0.2-1.2-0.5c-0.6-0.5-1.1-1-1.8-1.3
                                c-2.5-1.2-5.5-0.9-8.2-1c0.9-0.4,2-0.5,3-0.6c1.3-0.1,2.5-0.2,3.8-0.3c1-0.1,1.9,0.2,2.4-0.8c0.3-0.6,0.6-1.2,0.8-1.9
                                c0.1-0.6,0.3-1.3,0.6-1.9c0,1.3-0.2,2.5,0,3.9C154.6,179.5,154.7,180.4,154.8,181.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M102.2,92.7c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C101.3,93,101.8,92.9,102.2,92.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M112.6,87.2c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C112.5,90.6,112.5,88.9,112.6,87.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M113.1,94c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2c0.2-0.7,0.3-1.4,0.6-2
                                c0,1.4-0.2,2.7,0,4.1C112.8,92.1,113,93,113.1,94z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M102.5,79.1c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C101.6,79.4,102,79.3,102.5,79.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M112.9,73.6c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C112.7,77,112.8,75.3,112.9,73.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M113.4,80.4c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4
                                c-2.6-1.3-5.8-0.9-8.7-1c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2
                                c0.2-0.7,0.3-1.4,0.6-2c0,1.4-0.2,2.7,0,4.1C113.1,78.5,113.2,79.4,113.4,80.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M12.3,127.8c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C11.4,128,11.8,127.9,12.3,127.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M22.7,122.2c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C22.5,125.6,22.6,123.9,22.7,122.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M23.2,129c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2c0.2-0.7,0.3-1.4,0.6-2
                                c0,1.4-0.2,2.7,0,4.1C22.9,127.2,23,128.1,23.2,129z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M12.6,114.2c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C11.7,114.4,12.1,114.3,12.6,114.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M23,108.6c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C22.8,112,22.9,110.3,23,108.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M23.4,115.4c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4
                                c-2.6-1.3-5.8-0.9-8.7-1c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2
                                c0.2-0.7,0.3-1.4,0.6-2c0,1.4-0.2,2.7,0,4.1C23.2,113.6,23.3,114.5,23.4,115.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M70.7,193.4c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C69.8,193.6,70.3,193.5,70.7,193.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M81.2,187.8c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C81,191.2,81,189.5,81.2,187.8z"
                            />
                            <Path
                                fill="#463116"
                                d="M81.6,194.6c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4
                                c-2.6-1.3-5.8-0.9-8.7-1c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2
                                c0.2-0.7,0.3-1.4,0.6-2c0,1.4-0.2,2.7,0,4.1C81.3,192.7,81.5,193.7,81.6,194.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M71,179.8c1.5-0.4,3-0.8,4.6-0.8c1.1,0,2-0.2,3,0.3c0.9,0.4,1.7,1.2,2.4,1.9c1,1.2,1.2,2.6,1.5,4.1
                                c-0.5-0.2-0.8-0.6-1.2-0.9c-1.2-1-2.9-1.3-4.3-2c-1.1-0.6-2-0.8-3.2-1c-1.4-0.2-2.7-0.5-4.1-0.8c-0.1,0-0.3-0.2-0.2-0.3l0.1-0.1
                                C70.1,180,70.5,179.9,71,179.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M81.4,174.2c0.1-0.8,0.2-1.6,0.3-2.3c-0.1,0.3-0.7,1-0.9,1.3c-0.3,0.6-0.8,1.1-1.2,1.6
                                c-0.8,0.9-1.7,1.7-2.7,2.3c-1.1,0.6-2.3,0.9-3.4,1.3c-0.9,0.3-1.9,0.7-2.4,1.4c3.1-0.4,6,0.1,9.1,0.1c0.5,0,0.7-0.1,0.8-0.7
                                C81.3,177.6,81.3,175.9,81.4,174.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M81.9,181c0.1,0.6,0.2,1.3,0.3,1.9c-0.5,0.1-0.9-0.2-1.3-0.5c-0.6-0.5-1.1-1-1.9-1.4c-2.6-1.3-5.8-0.9-8.7-1
                                c1-0.4,2.1-0.5,3.2-0.6c1.3-0.1,2.6-0.2,4-0.4c1-0.1,2,0.2,2.6-0.9c0.3-0.7,0.6-1.3,0.8-2c0.2-0.7,0.3-1.4,0.6-2
                                c0,1.4-0.2,2.7,0,4.1C81.6,179.2,81.7,180.1,81.9,181z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M102.5,131.6c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9
                                c0.2-0.2,0.4-0.4,0.6-0.6c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2
                                c-0.4,1.8-0.2,3.7-0.2,5.5c-0.1,1.6,0,3.4-0.5,4.9C103.2,129.3,102.7,130.4,102.5,131.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M115.7,126.3c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.4-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9
                                c-0.5,0.6-0.7,1.2-1.2,1.8c-0.5,0.7-1.2,1.2-1.9,1.7c1.2,0,2.2-0.4,3.3-0.6c1.2-0.2,2.4-0.6,3.6-1c1.4-0.6,2.9-1.5,4.3-1.9
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.9c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C116.4,126.3,116.1,126.3,115.7,126.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M105.3,121.1c-0.1-1.1-0.4-2.1-0.7-3.2c2,1.1,3.5,2.8,5.1,4.4c1.3,1.3,2.6,2.6,4.5,2.8
                                c2.1,0.1,4.3,0.4,6.4,0.6c1.1,0.1,2.2,0.3,3.3,0.5c1,0.2,2.1,0.6,3.1,0.6c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2
                                c-0.5,0-0.7-0.1-0.8-0.7C105.5,124.5,105.4,122.8,105.3,121.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M104.9,127.9c-0.1,0.6-0.1,1.2-0.1,1.7c0-0.2,1.2-0.9,1.3-1c2.7-1.8,6-1.7,9.2-1.7c3,0,6,0,9,0.1
                                c1,0,2,0,3-0.2c-3.8-0.3-7.6-0.5-11.4-0.7c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.3-0.6-0.6-0.9-1
                                c-0.9-1.2-1.7-2.5-2.3-3.9c0.1,1.6,0.3,3.3,0.1,4.9C105.1,126,105,127,104.9,127.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M53.4,86.6c0.6,0,1.1,0,1.7-0.1c1-0.1,1.9-0.4,2.9-0.4c1.1,0,2-0.2,3,0.3c1.1,0.5,2.2,1.7,2.9,2.6
                                c0.6,0.7,1,1.7,0.9,2.6c0,0.1,0,0.2-0.1,0.2c-0.1,0.1-0.2,0.1-0.3,0c-1.6-0.5-3.3-1-4.8-1.6c-1.4-0.6-2.9-1.5-4.3-1.9
                                c-1.6-0.4-3.5-0.5-5.1-0.4c-0.8,0-1.7,0.1-2.5,0.1c-1.7,0-4.1,0-5.6-0.9c1.5,0.1,3-0.1,4.4-0.2c2-0.1,4-0.2,6-0.3
                                C52.8,86.6,53.1,86.6,53.4,86.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M63.9,81.5c0.1-1.1,0.4-2.1,0.7-3.2c-2,1.1-3.5,2.8-5.1,4.4c-1.3,1.3-2.6,2.6-4.5,2.8
                                c-2.1,0.1-4.3,0.4-6.4,0.6c-1.1,0.1-2.2,0.3-3.3,0.5c-1,0.2-2.1,0.6-3.1,0.6c3.5,0,7-0.1,10.5-0.1c3.3,0,6.6,0.2,9.9,0.2
                                c0.5,0,0.7-0.1,0.8-0.7C63.7,84.8,63.7,83.1,63.9,81.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M64.3,88.3c0.1,0.6,0.1,1.2,0.1,1.7c0-0.2-1.2-0.9-1.3-1c-2.7-1.8-6-1.7-9.2-1.7c-3,0-6,0-9,0.1
                                c-1,0-2,0-3-0.2c3.8-0.3,7.6-0.5,11.4-0.7c2.1-0.1,4.4,0.2,6.4-0.6c0.4-0.1,0.7-0.3,1-0.5c0.4-0.3,0.6-0.6,0.9-1
                                c0.9-1.2,1.7-2.5,2.3-3.9c-0.1,1.6-0.3,3.3-0.1,4.9C64.1,86.4,64.2,87.3,64.3,88.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M201.2,86.2c0,0.1,0,0.1,0,0.2c0-0.2,0.9-0.6,1.1-0.7c0.4-0.3,0.7-0.6,0.8-1.1c0.3-1.4,0.6-2.9,0.6-4.4
                                c0-2.1-0.8-3.8-1.3-5.8c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.3,3.7-0.2,5.5C202,82.1,201.6,84.2,201.2,86.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M212.2,82.1c-0.5,0-1,0-1.5-0.1c-0.9-0.1-1.7-0.3-2.6-0.3c-1,0-1.7-0.2-2.7,0.3c-0.8,0.4-1.5,1-2.1,1.7
                                c-0.5,0.5-0.6,1.1-1,1.6c-0.3,0.4-1,0.7-1.1,1.1c1.2,0,1.9-0.8,2.8-1.4c0.7-0.5,1.6-0.9,2.4-1.2c2-0.8,4.2-0.9,6.3-0.8
                                c3.2,0.1,6.8,0.8,9.9-0.3c0-0.1-0.1-0.1-0.2-0.1c-1.3,0-2.6-0.1-4-0.2c-1.8-0.1-3.5-0.2-5.3-0.3C212.8,82.1,212.5,82.1,212.2,82.1
                                z"
                            />
                            <Path
                                fill="#97773C"
                                d="M217.9,82.5c-1.7,0-3.4,0-5.1-0.1c-2.9,0-5.9,0.2-8.8,0.2c-0.5,0-0.6-0.1-0.7-0.6c-0.2-1.5-0.2-3-0.4-4.5
                                c-0.1-0.7-0.8-2.5-0.5-3.1c-0.2,0.4,1,1.8,1.2,2.1c1.4,1.8,2.9,3.3,5.1,4c1.5,0.5,3.3,0.8,4.9,0.9c1.9,0.1,3.9,0.4,5.8,0.6
                                c1.1,0.1,2.2,0.2,3.2,0.6C221,82.8,219.5,82.5,217.9,82.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M202.6,83.5c0,0.5-0.1,1-0.1,1.5c0-0.1,1-0.8,1.2-0.9c2.4-1.6,5.3-1.5,8.1-1.5c2.7,0,5.3,0,8,0.1
                                c0.9,0,1.8,0,2.6-0.2c-3.4-0.3-6.7-0.5-10.1-0.6c-1.8-0.1-3.9,0.2-5.6-0.6c-0.3-0.1-0.6-0.3-0.9-0.5c-0.3-0.2-0.6-0.5-0.8-0.8
                                c-0.8-1.1-1.5-2.2-2.1-3.4c0.1,1.5,0.3,2.9,0.1,4.4C202.8,81.8,202.7,82.7,202.6,83.5z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M200.4,99.2c0.2,0.1,0.3,0,0.5-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.2,0.7-0.5,1.1-0.8c0.2-0.2,0.4-0.3,0.5-0.6
                                c0.2-0.3,0.3-0.6,0.3-1c0.3-1.3,0.5-2.7,0.5-4.1c0-2.1-0.8-3.8-1.3-5.8c-0.1-0.1-0.2,0.1-0.2,0.2c-0.3,1.6-0.1,3.2-0.2,4.9
                                c0,1.4,0,3-0.5,4.3C201.1,97.2,200.6,98.2,200.4,99.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M212.1,94.5c-0.5,0-1,0-1.5-0.1c-0.9-0.1-1.7-0.3-2.6-0.3c-1,0-1.7-0.2-2.7,0.3c-0.8,0.4-1.5,1-2.1,1.7
                                c-0.5,0.5-0.6,1.1-1,1.6c-0.4,0.6-1.1,1-1.6,1.5c1,0,1.9-0.4,2.9-0.5c1.1-0.1,2.1-0.5,3.2-0.9c1.3-0.5,2.5-1.3,3.8-1.7
                                c1.4-0.4,3.1-0.5,4.5-0.4c0.7,0,1.5,0.1,2.2,0.1c1.5,0,3.6,0,5-0.8c-1.3,0.1-2.6-0.1-3.9-0.2c-1.8-0.1-3.5-0.2-5.3-0.3
                                C212.7,94.5,212.4,94.5,212.1,94.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M202.9,89.9c-0.1-1-0.4-1.9-0.6-2.8c1.8,1,3.1,2.5,4.5,3.9c1.1,1.1,2.3,2.3,4,2.4c1.9,0.1,3.8,0.4,5.7,0.6
                                c1,0.1,1.9,0.3,2.9,0.4c0.9,0.1,1.9,0.5,2.8,0.5c-3.1,0-6.2-0.1-9.3-0.1c-2.9,0-5.9,0.2-8.8,0.2c-0.5,0-0.6-0.1-0.7-0.6
                                C203,92.9,203,91.4,202.9,89.9z"
                            />
                            <Path
                                fill="#463116"
                                d="M202.5,96c0,0.5-0.1,1-0.1,1.5c0-0.1,1-0.8,1.2-0.9c2.4-1.6,5.3-1.5,8.1-1.5c2.7,0,5.3,0,8,0.1
                                c0.9,0,1.8,0,2.6-0.2c-3.4-0.3-6.7-0.5-10.1-0.6c-1.8-0.1-3.9,0.2-5.6-0.6c-0.3-0.1-0.6-0.3-0.9-0.5c-0.3-0.2-0.6-0.5-0.8-0.8
                                c-0.8-1.1-1.5-2.2-2.1-3.4c0.1,1.5,0.3,2.9,0.1,4.4C202.7,94.3,202.6,95.1,202.5,96z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M157,151.8c0,0.1,0,0.1,0,0.2c0-0.2,0.9-0.6,1.1-0.7c0.4-0.3,0.7-0.6,0.8-1.1c0.3-1.4,0.6-2.9,0.6-4.4
                                c0-2.1-0.8-3.8-1.3-5.8c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.3,3.7-0.2,5.5C157.8,147.8,157.4,149.8,157,151.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M168,147.7c-0.5,0-1,0-1.5-0.1c-0.9-0.1-1.7-0.3-2.6-0.3c-1,0-1.7-0.2-2.7,0.3c-0.8,0.4-1.5,1-2.1,1.7
                                c-0.5,0.5-0.6,1.1-1,1.6c-0.3,0.4-1,0.7-1.1,1.1c1.2,0,1.9-0.8,2.8-1.4c0.7-0.5,1.6-0.9,2.4-1.2c2-0.8,4.2-0.9,6.3-0.8
                                c3.2,0.1,6.8,0.8,9.9-0.3c0-0.1-0.1-0.1-0.2-0.1c-1.3,0-2.6-0.1-4-0.2c-1.8-0.1-3.5-0.2-5.3-0.3
                                C168.5,147.7,168.3,147.7,168,147.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M173.7,148.1c-1.7,0-3.4,0-5.1-0.1c-2.9,0-5.9,0.2-8.8,0.2c-0.5,0-0.6-0.1-0.7-0.6c-0.2-1.5-0.2-3-0.4-4.5
                                c-0.1-0.7-0.8-2.5-0.5-3.1c-0.2,0.4,1,1.8,1.2,2.1c1.4,1.8,2.9,3.3,5.1,4c1.5,0.5,3.3,0.8,4.9,0.9c1.9,0.1,3.9,0.4,5.8,0.6
                                c1.1,0.1,2.2,0.2,3.2,0.6C176.8,148.5,175.3,148.1,173.7,148.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M158.4,149.1c0,0.5-0.1,1-0.1,1.5c0-0.1,1-0.8,1.2-0.9c2.4-1.6,5.3-1.5,8.1-1.5c2.7,0,5.3,0,8,0.1
                                c0.9,0,1.8,0,2.6-0.2c-3.4-0.3-6.7-0.5-10.1-0.6c-1.8-0.1-3.9,0.2-5.6-0.6c-0.3-0.1-0.6-0.3-0.9-0.5c-0.3-0.2-0.6-0.5-0.8-0.8
                                c-0.8-1.1-1.5-2.2-2.1-3.4c0.1,1.5,0.3,2.9,0.1,4.4C158.5,147.5,158.5,148.3,158.4,149.1z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M156.2,164.9c0.2,0.1,0.3,0,0.5-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.2,0.7-0.5,1.1-0.8
                                c0.2-0.2,0.4-0.3,0.5-0.6c0.2-0.3,0.3-0.6,0.3-1c0.3-1.3,0.5-2.7,0.5-4.1c0-2.1-0.8-3.8-1.3-5.8c-0.1-0.1-0.2,0.1-0.2,0.2
                                c-0.3,1.6-0.1,3.2-0.2,4.9c0,1.4,0,3-0.5,4.3C156.9,162.8,156.4,163.8,156.2,164.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M167.9,160.2c-0.5,0-1,0-1.5-0.1c-0.9-0.1-1.7-0.3-2.6-0.3c-1,0-1.7-0.2-2.7,0.3c-0.8,0.4-1.5,1-2.1,1.7
                                c-0.5,0.5-0.6,1.1-1,1.6c-0.4,0.6-1.1,1-1.6,1.5c1,0,1.9-0.4,2.9-0.5c1.1-0.1,2.1-0.5,3.2-0.9c1.3-0.5,2.5-1.3,3.8-1.7
                                c1.4-0.4,3.1-0.5,4.5-0.4c0.7,0,1.5,0.1,2.2,0.1c1.5,0,3.6,0,5-0.8c-1.3,0.1-2.6-0.1-3.9-0.2c-1.8-0.1-3.5-0.2-5.3-0.3
                                C168.5,160.2,168.2,160.2,167.9,160.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M158.7,155.6c-0.1-1-0.4-1.9-0.6-2.8c1.8,1,3.1,2.5,4.5,3.9c1.1,1.1,2.3,2.3,4,2.4c1.9,0.1,3.8,0.4,5.7,0.6
                                c1,0.1,1.9,0.3,2.9,0.4c0.9,0.1,1.9,0.5,2.8,0.5c-3.1,0-6.2-0.1-9.3-0.1c-2.9,0-5.9,0.2-8.8,0.2c-0.5,0-0.6-0.1-0.7-0.6
                                C158.8,158.6,158.8,157.1,158.7,155.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M158.3,161.6c0,0.5-0.1,1-0.1,1.5c0-0.1,1-0.8,1.2-0.9c2.4-1.6,5.3-1.5,8.1-1.5c2.7,0,5.3,0,8,0.1
                                c0.9,0,1.8,0,2.6-0.2c-3.4-0.3-6.7-0.5-10.1-0.6c-1.8-0.1-3.9,0.2-5.6-0.6c-0.3-0.1-0.6-0.3-0.9-0.5c-0.3-0.2-0.6-0.5-0.8-0.8
                                c-0.8-1.1-1.5-2.2-2.1-3.4c0.1,1.5,0.3,2.9,0.1,4.4C158.5,159.9,158.4,160.8,158.3,161.6z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M199,32.4c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                c0.2-0.2,0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C199.8,31,199.3,31.7,199,32.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M212.3,29.3c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C212.9,29.3,212.6,29.3,212.3,29.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M201.8,26.4c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6
                                c2.1,0.1,4.3,0.2,6.4,0.4c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1
                                c-0.5,0-0.7-0.1-0.8-0.4C202,28.3,202,27.3,201.8,26.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M201.4,30.2c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,3-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C201.6,29.2,201.5,29.7,201.4,30.2z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M199.3,24.7c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                c0.2-0.2,0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C200,23.4,199.5,24,199.3,24.7z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M212.5,21.7c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C213.2,21.7,212.8,21.7,212.5,21.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M202.1,18.7c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6
                                c2.1,0.1,4.3,0.2,6.4,0.4c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1
                                c-0.5,0-0.7-0.1-0.8-0.4C202.3,20.6,202.2,19.7,202.1,18.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M201.7,22.6c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,2.9-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C201.9,21.5,201.8,22.1,201.7,22.6z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M199.5,17.8c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.2-0.8c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.6,0.4-1c0.4-1.3,0.6-2.7,0.5-4.1c0-2.1-0.9-3.8-1.4-5.9c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.6-0.2,3.3-0.2,4.9
                                c-0.1,1.4,0,3-0.5,4.3C200.3,15.7,199.8,16.7,199.5,17.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M212.8,13c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.3-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1-2.4,1.7
                                c-0.5,0.5-0.7,1.1-1.2,1.6c-0.5,0.6-1.2,1-1.9,1.5c1.2,0,2.2-0.4,3.3-0.5c1.2-0.1,2.4-0.5,3.6-0.9c1.4-0.5,2.9-1.3,4.3-1.7
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.8c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C213.4,13,213.1,13,212.8,13z"
                            />
                            <Path
                                fill="#97773C"
                                d="M202.3,8.4c-0.1-1-0.4-1.9-0.7-2.9c2,1,3.5,2.5,5.1,3.9c1.3,1.1,2.6,2.4,4.5,2.5c2.1,0.1,4.3,0.4,6.4,0.6
                                c1.1,0.1,2.2,0.3,3.3,0.4c1,0.1,2.1,0.5,3.1,0.5c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2c-0.5,0-0.7-0.1-0.8-0.6
                                C202.5,11.4,202.5,9.9,202.3,8.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M201.9,14.5c-0.1,0.5-0.1,1-0.1,1.6c0-0.1,1.2-0.8,1.3-0.9c2.7-1.6,6-1.6,9.2-1.5c3,0,6,0,9,0.1
                                c1,0,2,0,3-0.2c-3.8-0.3-7.6-0.5-11.4-0.6c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.2-0.6-0.5-0.9-0.9
                                c-0.9-1.1-1.7-2.2-2.3-3.5c0.1,1.5,0.3,2.9,0.1,4.4C202.1,12.8,202,13.6,201.9,14.5z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M223.6,195.8c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.4c0.2-0.1,0.4-0.2,0.6-0.3
                                c0.2-0.2,0.3-0.4,0.4-0.6c0.4-0.8,0.6-1.6,0.6-2.4c0-1.2-0.9-2.2-1.4-3.4c-0.1,0-0.2,0-0.2,0.1c-0.4,0.9-0.2,1.9-0.2,2.8
                                c-0.1,0.8,0,1.7-0.5,2.5C224.4,194.5,223.9,195.1,223.6,195.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M237.1,193c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3.1,0.2c-0.9,0.2-1.7,0.6-2.4,1
                                c-0.5,0.3-0.7,0.6-1.2,0.9c-0.5,0.3-1.2,0.6-1.9,0.8c1.2,0,2.2-0.2,3.3-0.3c1.3-0.1,2.5-0.3,3.6-0.5c1.5-0.3,2.9-0.8,4.4-1
                                c1.7-0.2,3.5-0.3,5.2-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.2,0,5.7-0.5c-1.5,0-3-0.1-4.5-0.1c-2-0.1-4.1-0.1-6.1-0.2
                                C237.7,193,237.4,193,237.1,193z"
                            />
                            <Path
                                fill="#97773C"
                                d="M226.5,190.3c-0.1-0.6-0.4-1.1-0.7-1.6c2,0.6,3.5,1.4,5.2,2.3c1.3,0.6,2.7,1.4,4.6,1.4
                                c2.1,0.1,4.3,0.2,6.5,0.3c1.1,0.1,2.2,0.1,3.3,0.2c1,0.1,2.2,0.3,3.2,0.3c-3.6,0-7.1,0-10.7-0.1c-3.4,0-6.7,0.1-10.1,0.1
                                c-0.6,0-0.7-0.1-0.8-0.4C226.7,192.1,226.7,191.2,226.5,190.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M226.1,193.8c-0.1,0.3-0.1,0.6-0.1,0.9c0-0.1,1.2-0.5,1.3-0.5c2.8-0.9,6.1-0.9,9.3-0.9c3,0,6.1,0,9.1,0
                                c1,0,2,0,3-0.1c-3.8-0.2-7.7-0.3-11.6-0.3c-2.1,0-4.5,0.1-6.4-0.3c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5
                                c-0.9-0.6-1.7-1.3-2.4-2c0.1,0.8,0.4,1.7,0.1,2.5C226.3,192.9,226.2,193.4,226.1,193.8z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M223.9,188.8c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.4c0.2-0.1,0.4-0.2,0.6-0.3
                                c0.2-0.2,0.3-0.4,0.4-0.6c0.4-0.8,0.6-1.6,0.6-2.4c0-1.2-0.9-2.2-1.4-3.4c-0.1,0-0.2,0-0.2,0.1c-0.4,0.9-0.2,1.9-0.2,2.8
                                c-0.1,0.8,0,1.7-0.5,2.5C224.7,187.6,224.2,188.2,223.9,188.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M237.3,186.1c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3.1,0.2c-0.9,0.2-1.7,0.6-2.4,1
                                c-0.5,0.3-0.7,0.6-1.2,0.9c-0.5,0.3-1.2,0.6-1.9,0.8c1.2,0,2.2-0.2,3.3-0.3c1.3-0.1,2.5-0.3,3.6-0.5c1.5-0.3,2.9-0.8,4.4-1
                                c1.7-0.2,3.5-0.3,5.2-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.2,0,5.7-0.5c-1.5,0-3-0.1-4.5-0.1c-2-0.1-4.1-0.1-6.1-0.2
                                C238,186.1,237.7,186.1,237.3,186.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M226.8,183.4c-0.1-0.6-0.4-1.1-0.7-1.6c2,0.6,3.5,1.4,5.2,2.3c1.3,0.6,2.7,1.4,4.6,1.4
                                c2.1,0.1,4.3,0.2,6.5,0.3c1.1,0.1,2.2,0.1,3.3,0.2c1,0.1,2.2,0.3,3.2,0.3c-3.6,0-7.1,0-10.7-0.1c-3.4,0-6.7,0.1-10.1,0.1
                                c-0.6,0-0.7-0.1-0.8-0.4C226.9,185.2,226.9,184.3,226.8,183.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M226.4,186.9c-0.1,0.3-0.1,0.6-0.1,0.9c0-0.1,1.2-0.5,1.3-0.5c2.8-0.9,6.1-0.9,9.3-0.9c3,0,6.1,0,9.1,0
                                c1,0,2,0,3-0.1c-3.8-0.2-7.7-0.3-11.6-0.3c-2.1,0-4.5,0.1-6.4-0.3c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5
                                c-0.9-0.6-1.7-1.3-2.4-2c0.1,0.8,0.4,1.7,0.1,2.5C226.6,186,226.5,186.5,226.4,186.9z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M224.1,182.6c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.2,0.8-0.4,1.2-0.7c0.2-0.2,0.4-0.3,0.6-0.5
                                c0.2-0.3,0.3-0.6,0.4-0.9c0.4-1.2,0.6-2.5,0.6-3.7c0-1.9-0.9-3.5-1.4-5.3c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.5-0.2,3-0.2,4.4
                                c-0.1,1.3,0,2.7-0.5,3.9C224.9,180.7,224.4,181.6,224.1,182.6z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M237.6,178.3c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.3-2.9-0.3c-1.1,0-2-0.2-3.1,0.2c-0.9,0.4-1.7,0.9-2.4,1.6
                                c-0.5,0.5-0.7,1-1.2,1.5s-1.2,0.9-1.9,1.3c1.2,0,2.2-0.3,3.3-0.5c1.3-0.1,2.5-0.5,3.6-0.8c1.5-0.5,2.9-1.2,4.4-1.5
                                c1.7-0.4,3.5-0.4,5.2-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.2,0,5.7-0.7c-1.5,0.1-3-0.1-4.5-0.2c-2-0.1-4.1-0.2-6.1-0.2
                                C238.2,178.3,237.9,178.3,237.6,178.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M227,174.1c-0.1-0.9-0.4-1.7-0.7-2.6c2,0.9,3.5,2.3,5.2,3.6c1.3,1,2.7,2.1,4.6,2.2c2.1,0.1,4.3,0.3,6.5,0.5
                                c1.1,0.1,2.2,0.2,3.3,0.4c1,0.1,2.2,0.5,3.2,0.5c-3.6,0-7.1-0.1-10.7-0.1c-3.4,0-6.7,0.2-10.1,0.2c-0.6,0-0.7-0.1-0.8-0.6
                                C227.2,176.8,227.2,175.5,227,174.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M226.6,179.6c-0.1,0.5-0.1,0.9-0.1,1.4c0-0.1,1.2-0.7,1.3-0.8c2.8-1.5,6.1-1.4,9.3-1.4c3,0,6.1,0,9.1,0.1
                                c1,0,2,0,3-0.2c-3.8-0.2-7.7-0.4-11.6-0.5c-2.1-0.1-4.5,0.2-6.4-0.5c-0.4-0.1-0.7-0.2-1-0.4c-0.4-0.2-0.6-0.5-0.9-0.8
                                c-0.9-1-1.7-2-2.4-3.1c0.1,1.3,0.4,2.7,0.1,4C226.8,178.1,226.7,178.8,226.6,179.6z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M114.5,166c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                s0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C115.3,164.6,114.8,165.3,114.5,166z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M127.8,162.9c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C128.5,163,128.1,162.9,127.8,162.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M117.4,160c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6c2.1,0.1,4.3,0.2,6.4,0.4
                                c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1c-0.5,0-0.7-0.1-0.8-0.4
                                C117.5,161.9,117.5,161,117.4,160z"
                            />
                            <Path
                                fill="#463116"
                                d="M117,163.9c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,3-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C117.2,162.8,117.1,163.3,117,163.9z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M114.8,158.3c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                s0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C115.6,157,115.1,157.6,114.8,158.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M128.1,155.3c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C128.7,155.3,128.4,155.3,128.1,155.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M117.6,152.3c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6
                                c2.1,0.1,4.3,0.2,6.4,0.4c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1
                                c-0.5,0-0.7-0.1-0.8-0.4C117.8,154.3,117.8,153.3,117.6,152.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M117.2,156.2c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,3-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C117.4,155.2,117.3,155.7,117.2,156.2z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M115,151.4c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.2-0.8c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.6,0.4-1c0.4-1.3,0.6-2.7,0.5-4.1c0-2.1-0.9-3.8-1.4-5.9c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.6-0.2,3.3-0.2,4.9
                                c-0.1,1.4,0,3-0.5,4.3C115.8,149.3,115.3,150.3,115,151.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M128.3,146.6c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.3-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1-2.4,1.7
                                c-0.5,0.5-0.7,1.1-1.2,1.6c-0.5,0.6-1.2,1-1.9,1.5c1.2,0,2.2-0.4,3.3-0.5c1.2-0.1,2.4-0.5,3.6-0.9c1.4-0.5,2.9-1.3,4.3-1.7
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.8c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C128.9,146.6,128.6,146.6,128.3,146.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M117.8,142c-0.1-1-0.4-1.9-0.7-2.9c2,1,3.5,2.5,5.1,3.9c1.3,1.1,2.6,2.4,4.5,2.5c2.1,0.1,4.3,0.4,6.4,0.6
                                c1.1,0.1,2.2,0.3,3.3,0.4c1,0.1,2.1,0.5,3.1,0.5c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2c-0.5,0-0.7-0.1-0.8-0.6
                                C118,145,118,143.5,117.8,142z"
                            />
                            <Path
                                fill="#463116"
                                d="M117.5,148.1c-0.1,0.5-0.1,1-0.1,1.6c0-0.1,1.2-0.8,1.3-0.9c2.7-1.6,6-1.6,9.2-1.5c3,0,6,0,9,0.1
                                c1,0,2,0,3-0.2c-3.8-0.3-7.6-0.5-11.4-0.6c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.2-0.6-0.5-0.9-0.9
                                c-0.9-1.1-1.7-2.2-2.3-3.5c0.1,1.5,0.3,2.9,0.1,4.4C117.7,146.4,117.6,147.2,117.5,148.1z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M105.3,64c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                s0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C106.1,62.7,105.6,63.3,105.3,64z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M118.6,61c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C119.2,61,118.9,61,118.6,61z"
                            />
                            <Path
                                fill="#97773C"
                                d="M108.1,58c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6c2.1,0.1,4.3,0.2,6.4,0.4
                                c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1c-0.5,0-0.7-0.1-0.8-0.4
                                C108.3,60,108.3,59,108.1,58z"
                            />
                            <Path
                                fill="#463116"
                                d="M107.7,61.9c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,3-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C107.9,60.8,107.8,61.4,107.7,61.9z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M105.6,56.4c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                s0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C106.3,55,105.8,55.7,105.6,56.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M118.8,53.3c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C119.5,53.3,119.2,53.3,118.8,53.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M108.4,50.4c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6
                                c2.1,0.1,4.3,0.2,6.4,0.4c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1
                                c-0.5,0-0.7-0.1-0.8-0.4C108.6,52.3,108.5,51.4,108.4,50.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M108,54.3c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,3-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C108.2,53.2,108.1,53.7,108,54.3z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M105.8,49.5c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.2-0.8c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.6,0.4-1c0.4-1.3,0.6-2.7,0.5-4.1c0-2.1-0.9-3.8-1.4-5.9c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.6-0.2,3.3-0.2,4.9
                                c-0.1,1.4,0,3-0.5,4.3C106.6,47.3,106.1,48.4,105.8,49.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M119.1,44.7c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.3-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1-2.4,1.7
                                c-0.5,0.5-0.7,1.1-1.2,1.6c-0.5,0.6-1.2,1-1.9,1.5c1.2,0,2.2-0.4,3.3-0.5c1.2-0.1,2.4-0.5,3.6-0.9c1.4-0.5,2.9-1.3,4.3-1.7
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.8c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C119.7,44.7,119.4,44.7,119.1,44.7z"
                            />
                            <Path
                                fill="#97773C"
                                d="M108.6,40c-0.1-1-0.4-1.9-0.7-2.9c2,1,3.5,2.5,5.1,3.9c1.3,1.1,2.6,2.4,4.5,2.5c2.1,0.1,4.3,0.4,6.4,0.6
                                c1.1,0.1,2.2,0.3,3.3,0.4c1,0.1,2.1,0.5,3.1,0.5c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2c-0.5,0-0.7-0.1-0.8-0.6
                                C108.8,43.1,108.8,41.6,108.6,40z"
                            />
                            <Path
                                fill="#463116"
                                d="M108.2,46.1c-0.1,0.5-0.1,1-0.1,1.6c0-0.1,1.2-0.8,1.3-0.9c2.7-1.6,6-1.6,9.2-1.5c3,0,6,0,9,0.1
                                c1,0,2,0,3-0.2c-3.8-0.3-7.6-0.5-11.4-0.6c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.2-0.6-0.5-0.9-0.9
                                c-0.9-1.1-1.7-2.2-2.3-3.5c0.1,1.5,0.3,2.9,0.1,4.4C108.4,44.5,108.3,45.3,108.2,46.1z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M48.4,165.8c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                c0.2-0.2,0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C49.1,164.5,48.6,165.1,48.4,165.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M61.6,162.8c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C62.3,162.8,62,162.8,61.6,162.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M51.2,159.8c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6
                                c2.1,0.1,4.3,0.2,6.4,0.4c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1
                                c-0.5,0-0.7-0.1-0.8-0.4C51.4,161.7,51.4,160.8,51.2,159.8z"
                            />
                            <Path
                                fill="#463116"
                                d="M50.8,163.7c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,3-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C51,162.6,50.9,163.2,50.8,163.7z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M48.6,158.1c0.2,0,0.4,0,0.6-0.1c0.1,0,0.2-0.1,0.3-0.1c0.4-0.1,0.8-0.3,1.2-0.5c0.2-0.1,0.4-0.2,0.6-0.4
                                c0.2-0.2,0.3-0.4,0.4-0.6c0.4-0.9,0.6-1.7,0.5-2.6c0-1.4-0.9-2.4-1.4-3.7c-0.1,0-0.2,0-0.2,0.1c-0.4,1-0.2,2.1-0.2,3.1
                                c-0.1,0.9,0,1.9-0.5,2.8C49.4,156.8,48.9,157.5,48.6,158.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M61.9,155.1c-0.6,0-1.1,0-1.7,0c-1-0.1-1.9-0.2-2.9-0.2c-1.1,0-2-0.1-3,0.2c-0.9,0.3-1.7,0.7-2.4,1.1
                                c-0.5,0.3-0.7,0.7-1.2,1c-0.5,0.4-1.2,0.7-1.9,0.9c1.2,0,2.2-0.2,3.3-0.3c1.2-0.1,2.4-0.3,3.6-0.6c1.4-0.3,2.9-0.8,4.3-1.1
                                c1.6-0.2,3.5-0.3,5.1-0.2c0.8,0,1.7,0,2.5,0c1.7,0,4.1,0,5.6-0.5c-1.5,0-3-0.1-4.4-0.1c-2-0.1-4-0.1-6-0.2
                                C62.6,155.1,62.2,155.1,61.9,155.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M51.5,152.2c-0.1-0.6-0.4-1.2-0.7-1.8c2,0.6,3.5,1.6,5.1,2.5c1.3,0.7,2.6,1.5,4.5,1.6
                                c2.1,0.1,4.3,0.2,6.4,0.4c1.1,0.1,2.2,0.2,3.3,0.3c1,0.1,2.1,0.3,3.1,0.3c-3.5,0-7,0-10.5-0.1c-3.3,0-6.6,0.1-9.9,0.1
                                c-0.5,0-0.7-0.1-0.8-0.4C51.6,154.1,51.6,153.1,51.5,152.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M51.1,156c-0.1,0.3-0.1,0.7-0.1,1c0-0.1,1.2-0.5,1.3-0.6c2.7-1,6-1,9.2-1c3,0,6,0,9,0c1,0,2,0,3-0.1
                                c-3.8-0.2-7.6-0.3-11.4-0.4c-2.1,0-4.4,0.1-6.4-0.4c-0.4-0.1-0.7-0.2-1-0.3c-0.4-0.1-0.6-0.3-0.9-0.5c-0.9-0.7-1.7-1.4-2.3-2.2
                                c0.1,0.9,0.3,1.9,0.1,2.8C51.3,155,51.2,155.5,51.1,156z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M48.9,151.2c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.2,0.8-0.5,1.2-0.8c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.6,0.4-1c0.4-1.3,0.6-2.7,0.5-4.1c0-2.1-0.9-3.8-1.4-5.9c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.6-0.2,3.3-0.2,4.9
                                c-0.1,1.4,0,3-0.5,4.3C49.6,149.1,49.1,150.1,48.9,151.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M62.1,146.5c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.3-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1-2.4,1.7
                                c-0.5,0.5-0.7,1.1-1.2,1.6c-0.5,0.6-1.2,1-1.9,1.5c1.2,0,2.2-0.4,3.3-0.5c1.2-0.1,2.4-0.5,3.6-0.9c1.4-0.5,2.9-1.3,4.3-1.7
                                c1.6-0.4,3.5-0.5,5.1-0.4c0.8,0,1.7,0.1,2.5,0.1c1.7,0,4.1,0,5.6-0.8c-1.5,0.1-3-0.1-4.4-0.2c-2-0.1-4-0.2-6-0.3
                                C62.8,146.5,62.5,146.5,62.1,146.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M51.7,141.8c-0.1-1-0.4-1.9-0.7-2.9c2,1,3.5,2.5,5.1,3.9c1.3,1.1,2.6,2.4,4.5,2.5c2.1,0.1,4.3,0.4,6.4,0.6
                                c1.1,0.1,2.2,0.3,3.3,0.4c1,0.1,2.1,0.5,3.1,0.5c-3.5,0-7-0.1-10.5-0.1c-3.3,0-6.6,0.2-9.9,0.2c-0.5,0-0.7-0.1-0.8-0.6
                                C51.9,144.8,51.8,143.3,51.7,141.8z"
                            />
                            <Path
                                fill="#463116"
                                d="M51.3,147.9c-0.1,0.5-0.1,1-0.1,1.6c0-0.1,1.2-0.8,1.3-0.9c2.7-1.6,6-1.6,9.2-1.5c3,0,6,0,9,0.1
                                c1,0,2,0,3-0.2c-3.8-0.3-7.6-0.5-11.4-0.6c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5c-0.4-0.2-0.6-0.5-0.9-0.9
                                c-0.9-1.1-1.7-2.2-2.3-3.5c0.1,1.5,0.3,2.9,0.1,4.4C51.5,146.2,51.4,147.1,51.3,147.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M225.9,37.3c-1.2,0.3-2.4,0.6-3.6,0.8c-1.2,0.2-2.5,0.2-3.8,0.2c-1.6,0-3.3,0.1-4.8-0.2
                                c-0.2,0-0.3-0.1-0.5-0.1c-0.8-0.2-1.5-0.4-2.3-0.6c0,0.7,0.7,1.6,1.1,2.1c0.5,0.6,1.1,1.2,1.6,1.7c0.3,0.3,0.6,0.7,0.9,1
                                c1.8,2,2.8,4.4,2.9,7.1c0.1,2.7,0.2,5.4,0.4,8.1c0.2,2.6,0.3,5.2,0.7,7.7c0.6-2.5,0.4-5,0.5-7.6c0.1-3.1,0.2-6.1,0.3-9.2
                                c0.1-2,0.4-3.5,1.6-5.1C222.5,41.1,224.8,39.8,225.9,37.3C225.9,37.3,225.9,37.3,225.9,37.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M226.3,36.8c-0.6,0.8-1.6,1.6-2.5,2.1s-1.8,1-2.8,1.4c-1.5,0.7-3.2,1.2-4.8,0.7c-1.4-0.5-2.6-1.3-3.7-2.1
                                c-0.7-0.5-1.3-1.2-1.8-1.9c0.8,0.2,1.6,0.5,2.3,0.6c2,0.4,4,0.7,6,0.6c1.3-0.1,2.6-0.3,3.9-0.5c1.2-0.2,2.3-0.6,3.4-1.1
                                C226.4,36.7,226.4,36.7,226.3,36.8z"
                            />
                            <Path
                                fill="#463116"
                                d="M218.3,44.3c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.2-1.5-1.7c-0.6-0.6-1.2-1.2-1.8-1.8
                                c-0.8-0.8-1.7-1.6-2.6-2.4c2,0.9,4.2,2.7,6.4,2.3c1.1-0.2,2.2-0.6,3.2-1c0.7-0.3,1.4-0.5,2.1-0.9c-0.3,0.3-0.6,0.5-0.9,0.8
                                c-0.4,0.4-0.9,0.9-1.3,1.3c-0.3,0.3-0.5,0.6-0.8,0.9C220,42.7,219.3,43.7,218.3,44.3z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M234.3,39.2c-1.2,0.2-2.4,0.1-3.7-0.1c-0.7-0.1-1.5-0.3-2.2-0.5c-0.1,0-0.3-0.1-0.4-0.1
                                c-0.6-0.2-1.3-0.4-1.9-0.5c0,0.6,0.6,1.3,0.9,1.8c0.4,0.5,0.9,1,1.4,1.5c0.3,0.3,0.5,0.6,0.8,0.8c0.8,0.9,1.7,1.8,2.3,2.8
                                s0.4,2.3,0.8,3.4c-0.1-0.3,0.2-1.1,0.2-1.4c0.1-0.5,0.2-1.1,0.4-1.6c0.4-1,1.2-1.8,2-2.5c0.8-0.8,1.7-1.6,2.4-2.5
                                c0.6-0.7,1.5-1.7,1.8-2.7c-0.4,0.1-0.7,0.3-1.1,0.4c-0.7,0.3-1.3,0.5-2,0.7C235.4,39,234.9,39.1,234.3,39.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M239.1,37.5c-0.5,0.7-1.3,1.3-2.1,1.8s-1.6,0.8-2.4,1.2c-1.2,0.6-2.7,1-4.1,0.6c-1.1-0.4-2.2-1.1-3.1-1.8
                                c-0.6-0.5-1.1-1-1.5-1.6c0.7,0.2,1.3,0.4,2,0.5c1.7,0.3,3.3,0.6,5,0.5c1.1-0.1,2.2-0.2,3.3-0.4c1-0.2,2-0.5,2.9-0.9
                                C239.2,37.4,239.1,37.5,239.1,37.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M232.3,43.8c-0.1,0.1-0.2,0.1-0.2,0c-0.5-0.4-0.8-1-1.3-1.4c-0.5-0.5-1-1-1.5-1.5c-0.7-0.7-1.4-1.3-2.2-2
                                c1.7,0.8,3.5,2.2,5.4,1.9c0.9-0.1,1.8-0.5,2.7-0.9c0.6-0.3,1.2-0.5,1.8-0.7c-0.2,0.2-0.5,0.4-0.7,0.7c-0.4,0.4-0.7,0.7-1.1,1.1
                                c-0.2,0.2-0.5,0.5-0.7,0.7C233.8,42.5,233.2,43.3,232.3,43.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M239.8,45.5c-1.2,0.3-2.4,0.6-3.6,0.8c-1.2,0.2-2.5,0.2-3.8,0.2c-1.6,0-3.3,0.1-4.8-0.2
                                c-0.2,0-0.3-0.1-0.5-0.1c-0.8-0.2-1.5-0.4-2.3-0.6c0,0.7,0.7,1.6,1.1,2.1c0.5,0.6,1.1,1.2,1.6,1.7c0.3,0.3,0.6,0.7,0.9,1
                                c1.8,2,2.8,4.4,2.9,7.1c0.1,2.7,0.2,5.4,0.8,8.1c-0.1-0.3,0.2-1.2,0.3-1.6c0.1-0.6,0.1-1.2,0.2-1.8c0.1-1.4,0.2-2.9,0.3-4.3
                                c0-0.8,0.1-1.5,0.3-2.3c0.3-1.4,0.8-2.9,1.7-4.1c1.6-2.1,3.9-3.4,5-5.9C239.9,45.5,239.8,45.5,239.8,45.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M240.2,45c-0.6,0.8-1.6,1.6-2.5,2.1s-1.8,1-2.8,1.4c-1.5,0.7-3.2,1.2-4.8,0.7c-1.4-0.5-2.6-1.3-3.7-2.1
                                c-0.7-0.5-1.3-1.2-1.8-1.9c0.8,0.2,1.6,0.5,2.3,0.6c2,0.4,4,0.7,6,0.6c1.3-0.1,2.6-0.3,3.9-0.5c1.2-0.2,2.3-0.6,3.4-1.1
                                C240.3,44.9,240.3,45,240.2,45z"
                            />
                            <Path
                                fill="#463116"
                                d="M232.2,52.5c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.2-1.5-1.7c-0.6-0.6-1.2-1.2-1.8-1.8
                                c-0.8-0.8-1.7-1.6-2.6-2.4c2,0.9,4.2,2.7,6.4,2.3c1.1-0.2,2.2-0.6,3.2-1c0.7-0.3,1.4-0.5,2.1-0.9c-0.3,0.3-0.6,0.5-0.9,0.8
                                c-0.4,0.4-0.9,0.9-1.3,1.3c-0.3,0.3-0.5,0.6-0.8,0.9C233.9,51,233.2,51.9,232.2,52.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M68.1,173.9c-1.2,0.2-2.4,0.6-3.7,0.7c-1.3,0.1-2.6,0.2-3.8,0.2c-1.6,0-3.3,0.1-4.9-0.2
                                c-0.2,0-0.3-0.1-0.5-0.1c-0.8-0.2-1.6-0.4-2.4-0.6c0,0.6,0.7,1.5,1.1,1.9c0.5,0.6,1.1,1.1,1.7,1.6c0.3,0.3,0.7,0.6,1,0.9
                                c1.8,1.9,2.8,4,2.9,6.6c0.1,2.5,0.2,5,0.4,7.5c0.2,2.4,0.3,4.8,0.7,7.2c0.6-2.3,0.4-4.7,0.5-7.1c0.1-2.8,0.2-5.7,0.3-8.5
                                c0.1-1.8,0.4-3.2,1.7-4.7c1.6-1.9,4-3.2,5.2-5.5C68.2,173.9,68.1,173.9,68.1,173.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M68.6,173.4c-0.6,0.8-1.6,1.5-2.5,2s-1.9,0.9-2.9,1.3c-1.5,0.6-3.3,1.1-5,0.6c-1.4-0.4-2.6-1.2-3.8-2
                                c-0.7-0.5-1.3-1.1-1.8-1.7c0.8,0.2,1.6,0.4,2.4,0.6c2,0.4,4,0.7,6.1,0.6c1.3-0.1,2.7-0.3,4-0.5C66.3,174.1,67.5,173.8,68.6,173.4
                                C68.6,173.3,68.6,173.4,68.6,173.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M60.3,180.4c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.4-1-1.1-1.5-1.6c-0.6-0.6-1.2-1.1-1.9-1.7
                                c-0.9-0.7-1.7-1.5-2.6-2.2c2,0.9,4.3,2.5,6.6,2.1c1.1-0.2,2.2-0.5,3.3-1c0.7-0.3,1.4-0.5,2.2-0.8c-0.3,0.3-0.6,0.5-0.9,0.7
                                c-0.5,0.4-0.9,0.8-1.4,1.2c-0.3,0.3-0.6,0.5-0.8,0.8C62.1,178.9,61.4,179.8,60.3,180.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M190.5,74.6c-1.2,0.2-2.5,0.1-3.7-0.1c-0.8-0.1-1.5-0.3-2.2-0.5c-0.1,0-0.3-0.1-0.4-0.1
                                c-0.7-0.2-1.3-0.4-2-0.5c0,0.6,0.6,1.4,0.9,1.8c0.4,0.5,0.9,1,1.4,1.5c0.3,0.3,0.6,0.6,0.8,0.9c0.8,0.9,1.7,1.8,2.4,2.8
                                c0.6,1.1,0.4,2.3,0.8,3.5c-0.1-0.3,0.2-1.1,0.2-1.4c0.1-0.5,0.2-1.1,0.4-1.6c0.4-1,1.3-1.8,2-2.5c0.9-0.8,1.7-1.6,2.4-2.5
                                c0.6-0.8,1.6-1.7,1.8-2.7c-0.4,0.1-0.7,0.3-1.1,0.4c-0.7,0.3-1.3,0.5-2,0.7C191.6,74.4,191.1,74.6,190.5,74.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M195.4,73c-0.5,0.7-1.4,1.4-2.1,1.8c-0.8,0.5-1.6,0.9-2.4,1.2c-1.3,0.6-2.8,1-4.2,0.6
                                c-1.2-0.4-2.2-1.1-3.2-1.8c-0.6-0.5-1.1-1-1.5-1.6c0.7,0.2,1.3,0.4,2,0.5c1.7,0.3,3.4,0.6,5.2,0.5c1.1-0.1,2.3-0.2,3.4-0.4
                                c1-0.2,2-0.5,3-1C195.5,72.9,195.4,72.9,195.4,73z"
                            />
                            <Path
                                fill="#463116"
                                d="M188.5,79.4c-0.1,0.1-0.2,0.1-0.3,0c-0.5-0.4-0.8-1-1.3-1.4c-0.5-0.5-1-1-1.6-1.5c-0.7-0.7-1.5-1.4-2.2-2
                                c1.7,0.8,3.6,2.3,5.6,2c1-0.1,1.9-0.5,2.8-0.9c0.6-0.3,1.2-0.5,1.8-0.7c-0.3,0.2-0.5,0.4-0.8,0.7c-0.4,0.4-0.8,0.8-1.1,1.1
                                c-0.2,0.2-0.5,0.5-0.7,0.7C190,78,189.4,78.8,188.5,79.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M196.1,81.1c-1.2,0.3-2.4,0.6-3.7,0.8c-1.3,0.2-2.6,0.2-3.8,0.2c-1.6,0-3.3,0.1-4.9-0.2
                                c-0.2,0-0.3-0.1-0.5-0.2c-0.8-0.2-1.6-0.5-2.4-0.6c0,0.7,0.7,1.6,1.1,2.1c0.5,0.6,1.1,1.2,1.7,1.8c0.3,0.3,0.7,0.7,1,1
                                c1.8,2.1,2.8,4.4,2.9,7.1c0.1,2.7,0.2,5.5,0.8,8.2c-0.1-0.3,0.3-1.3,0.3-1.6c0.1-0.6,0.1-1.2,0.2-1.8c0.1-1.5,0.2-2.9,0.3-4.4
                                c0.1-0.8,0.1-1.6,0.3-2.3c0.3-1.4,0.9-3,1.8-4.1c1.6-2.1,4-3.5,5.2-5.9C196.2,81,196.2,81.1,196.1,81.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M196.6,80.5c-0.6,0.9-1.6,1.6-2.5,2.1c-0.9,0.6-1.9,1-2.9,1.5c-1.5,0.7-3.3,1.2-5,0.7
                                c-1.4-0.5-2.6-1.3-3.8-2.1c-0.7-0.5-1.3-1.2-1.8-1.9c0.8,0.2,1.6,0.5,2.4,0.6c2,0.4,4,0.7,6.1,0.6c1.3-0.1,2.7-0.3,4-0.5
                                c1.2-0.2,2.4-0.6,3.5-1.1C196.6,80.4,196.6,80.5,196.6,80.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M188.3,88.1c-0.1,0.1-0.2,0.1-0.3,0.1c-0.6-0.5-1-1.2-1.5-1.7c-0.6-0.6-1.2-1.2-1.9-1.8
                                c-0.9-0.8-1.7-1.6-2.6-2.4c2,0.9,4.3,2.7,6.6,2.3c1.1-0.2,2.2-0.6,3.3-1c0.7-0.3,1.4-0.6,2.2-0.9c-0.3,0.3-0.6,0.5-0.9,0.8
                                c-0.5,0.4-0.9,0.9-1.4,1.3c-0.3,0.3-0.6,0.6-0.8,0.9C190.1,86.6,189.4,87.5,188.3,88.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M91.7,184.3c0,1,0.6,2.2,0.7,3.1c0.2,1.1,0.2,2.1,0.3,3.2c0.1,2.1,0.3,4.1,0.3,6.2c0,0.4,0,0.8,0.3,1
                                c0.1-3.7,0.1-7.5,0.7-11.2c0.2-1,0.4-2.1,0.8-3c0.3-0.7,2.2-4.5,3.1-4.3c0.2,0,0.3,0.2,0.4,0.3c1.7,1.9,2.8,5,3,7.5
                                c0.3,3.1,0.2,6.2,0.3,9.3c0,0.4,0,0.9,0.2,1.2c-0.2-0.4,0.2-2.1,0.2-2.6c0.2-1.7,0.4-3.3,0.5-4.9c0.2-2.6,0.2-5.1,1.3-7.5
                                c0.6-1.4,1.5-2.6,2.5-3.7c0.1-0.1,0.2-0.2,0.3-0.2c0.1,0,0.2,0.1,0.3,0.1c3.4,2.3,4,5.9,4.4,9.7c0.3,3.2,0.6,6.4,1,9.6
                                c0.2-3.1,0.4-6.2,0.6-9.4c0.1-1.8,0.2-3.6,0.7-5.3c0.4-1.6,1.2-3.3,2.6-4.4c1.3-1.1,2.1-2.7,2.6-4.3c-2.3,1-4.4,1.8-7,1.6
                                c-1.9-0.1-3.7-0.8-5.6-0.9c-2.3-0.2-4.6,0.6-6.9,0.7c-1.5,0-3-0.2-4.5-0.1c-0.8,0.1-1.6,0.2-2.4,0.2c-0.6,0-1.1-0.2-1.6-0.3
                                c-0.9-0.3-1.8-0.5-2.6-1c-0.3-0.2-2.5-0.7-2.5-0.6c0.2,0.6,0.3,0.9,0.7,1.3c0.5,0.5,0.9,1.1,1.3,1.6
                                C89.3,179.3,90.8,181.7,91.7,184.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M93.5,178.4c-1.6,0-2.9-1.1-4.2-1.8c-0.4-0.2-0.9-0.3-1.3-0.5c-0.5-0.2-1-0.7-1.4-1s-0.8-0.7-1-1.2
                                c0.6,0,1.1,0.1,1.6,0.3c0.8,0.2,1.4,0.2,2.1,0.3c1.4,0.2,2.7,0.6,4.1,0.6c1.5,0,3-0.3,4.3-0.9c0.1,0,0.2-0.1,0.3-0.1
                                c0.1,0,0.2,0.1,0.3,0.1c1.2,0.6,2.2,1,3.6,1.1c1.6,0.1,3.4-0.2,4.8-0.9c0.2-0.1,0.3-0.1,0.5-0.1s0.4,0.1,0.5,0.1
                                c1.6,0.6,3.4,0.9,5.2,0.8c1.9-0.1,3.9-0.3,5.7-0.7c-0.3,0.8-1,1.5-1.7,1.9c-1.3,0.9-2.7,1.2-4.1,1.5c-1.5,0.3-3.2,0.2-4.5-0.7
                                c-0.4-0.3-0.8-0.5-1.2-0.8c-0.1-0.1-0.2-0.1-0.3-0.1c-0.2-0.1-0.4,0-0.6,0.1c-1,0.4-1.9,0.9-2.9,1.2c-1.3,0.4-2.9,0.3-4.1-0.3
                                c-0.4-0.2-0.9-0.5-1.3-0.6c-0.6-0.1-0.8,0.1-1.2,0.5C95.9,178,94.5,178.4,93.5,178.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M115.9,176.6c-1.1,0.3-2.2,0.5-3.4,0.6c-1.1,0.1-2.2-0.1-3.3-0.4c-0.7-0.2-1.8-0.4-2.2-0.9
                                c-0.7,0.3-1.3,0.8-2.1,1c-1.1,0.3-2.3,0.5-3.4,0.3c-1.2-0.2-2.4-0.7-3.4-1.2c-0.4-0.2-0.8,0.2-1.2,0.3c-0.5,0.3-1.1,0.5-1.7,0.7
                                c-1.1,0.3-2.3,0.4-3.4,0.2c-1.5-0.3-2.8-1.2-4.2-1.6c0,0.3,0.3,0.6,0.6,0.8c1.9,1.4,3.5,3.5,4.6,5.6c0.1,0.3,0.3,0.6,0.4,0.8
                                c0,0.1,0.4-0.7,0.5-0.8c1-1.8,2.5-3.4,4.4-4.4c0.1,0,0.1-0.1,0.2-0.1s0.1,0,0.2,0.1c1.4,0.8,2.5,2.7,3.2,4.1
                                c0.1,0.3,0.3,0.6,0.4,0.8c0,0.1,0.4-0.7,0.5-0.8c0.6-1,1.3-1.9,2.1-2.8c0.7-0.7,1.3-1.2,2.1-1.7c0.6-0.4,1.8,1.1,2.2,1.5
                                c1.2,1,2.4,2.3,3.1,3.7c0-0.1,0.7-1.1,0.8-1.2c0.6-1,1.6-1.7,2.4-2.6c0.8-1,1.6-2,2.6-2.8C117.1,176.2,116.5,176.4,115.9,176.6z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M191.9,162.7c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9
                                c0.2-0.2,0.4-0.4,0.6-0.6c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2
                                c-0.4,1.8-0.2,3.7-0.2,5.5c-0.1,1.6,0,3.4-0.5,4.9C192.6,160.4,192.1,161.5,191.9,162.7z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M205.1,157.4c-0.6,0-1.1,0-1.7-0.1c-1-0.1-1.9-0.4-2.9-0.4c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9
                                c-0.5,0.6-0.7,1.2-1.2,1.8c-0.5,0.7-1.2,1.2-1.9,1.7c0.4-0.3,1.9-0.2,2.4-0.3c0.8-0.2,1.6-0.5,2.4-0.8c1.7-0.7,3.1-1.3,4.9-1.6
                                c2.7-0.5,5.4-0.8,8.2-0.8c4.2,0,8.3,0.5,12.3-0.7c0.3-0.1,0.6-0.2,0.7-0.4c-1.7-0.3-3.5,0-5.2,0c-1.6-0.1-3.3-0.1-4.9-0.2
                                c-2.3-0.1-4.6-0.2-6.8-0.3C205.8,157.4,205.5,157.4,205.1,157.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M194.7,152.2c-0.1-1.1-0.4-2.1-0.7-3.2c2,1.1,3.5,2.8,5.1,4.4c1.3,1.3,2.6,2.6,4.5,2.8
                                c3.5,0.2,7,0.5,10.4,0.6c2.6,0.1,5.2-0.1,7.8,0.4c0.5,0.1,1,0.2,1.4,0.6c-1.6,0.1-3.2,0.3-4.9,0.3c-1.4,0-2.8,0.1-4.2-0.1
                                c-2.7-0.4-5.6-0.2-8.3-0.2c-3.3,0-6.6,0.2-9.9,0.2c-0.5,0-0.7-0.1-0.8-0.7C194.9,155.6,194.8,153.9,194.7,152.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M194.3,159c-0.1,0.6-0.1,1.2-0.1,1.7c0-0.2,1.2-0.9,1.3-1c2.7-1.8,6-1.7,9.2-1.7c2.9,0,5.8,0.2,8.7,0.3
                                s5.9,0,8.9-0.1c0.4,0,0.8-0.1,1.1-0.3c-6-0.6-12.1-0.4-18.1-0.6c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5
                                c-0.4-0.3-0.6-0.6-0.9-1c-0.9-1.2-1.7-2.5-2.3-3.9c0.1,1.6,0.3,3.3,0.1,4.9C194.5,157.1,194.4,158.1,194.3,159z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M202.6,155.1c0.6,2.2,0.5,4.7,0.6,7c0,0.5,0.1,1,0.2,1.5c0.3-0.8,0.4-1.6,0.6-2.4c0.2-1.2,0.3-2.5,0.5-3.7
                                c0.1-1,0.3-2.1,0.6-3c0.2-0.7,1.9-4.5,2.6-4.3c0.1,0,0.3,0.2,0.4,0.3c2.6,3.4,2.4,7.9,2.8,12.1c0,0.3,0.1,0.6,0.2,0.8
                                c0,0,0.3-1.7,0.3-1.9c0.1-0.9,0.2-1.9,0.3-2.8c0.2-1.8,0.4-3.7,1.1-5.3c0.5-1.4,1.3-2.6,2.1-3.7c0.1-0.1,0.2-0.2,0.3-0.2
                                s0.2,0.1,0.2,0.1c1.6,1.3,2.8,3.4,3.4,5.6c0.7,2.5,0.5,5.3,0.8,7.9c0.4-0.8,0.3-1.8,0.4-2.7c0.2-3,0.6-5.9,2-8.5
                                c1.1-2.1,2.7-3.9,3.3-6.4c-1.9,1-3.7,1.8-5.8,1.6c-1.6-0.1-3.1-0.8-4.7-0.9c-1.9-0.2-3.8,0.6-5.8,0.7c-1.3,0-2.5-0.2-3.8-0.1
                                c-0.7,0.1-1.4,0.2-2,0.2c-0.5,0-0.9-0.2-1.4-0.3c-0.8-0.3-1.5-0.5-2.2-1c-0.2-0.2-2.1-0.7-2.1-0.6c0.1,0.6,0.2,0.9,0.6,1.3
                                c0.4,0.5,0.8,1.1,1.1,1.6C200.6,150.1,201.9,152.5,202.6,155.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M204.1,149.2c-1.3,0-2.4-1.1-3.6-1.8c-0.4-0.2-0.7-0.3-1.1-0.5c-0.4-0.2-0.9-0.7-1.2-1s-0.7-0.7-0.8-1.2
                                c0.5,0,0.9,0.1,1.4,0.3c0.6,0.2,1.1,0.2,1.8,0.3c1.2,0.2,2.3,0.6,3.5,0.6s2.5-0.3,3.6-0.9c0.1,0,0.2-0.1,0.3-0.1s0.2,0.1,0.3,0.1
                                c1,0.6,1.8,1,3,1.1c1.3,0.1,2.8-0.2,4.1-0.9c0.1-0.1,0.3-0.1,0.4-0.1c0.2,0,0.3,0.1,0.4,0.1c1.4,0.6,2.9,0.9,4.3,0.8
                                c1.6-0.1,3.2-0.3,4.8-0.7c-0.3,0.8-0.8,1.5-1.4,1.9c-1.1,0.9-2.2,1.2-3.5,1.5s-2.6,0.2-3.8-0.7c-0.3-0.3-0.7-0.5-1-0.8
                                c-0.1-0.1-0.1-0.1-0.2-0.1c-0.2-0.1-0.3,0-0.5,0.1c-0.8,0.4-1.6,0.9-2.5,1.2c-1.1,0.4-2.4,0.3-3.5-0.3c-0.3-0.2-0.7-0.5-1.1-0.6
                                c-0.5-0.1-0.7,0.1-1,0.5C206.1,148.8,205,149.3,204.1,149.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M222.8,147.4c-0.9,0.3-1.9,0.5-2.9,0.6c-0.9,0.1-1.9-0.1-2.8-0.4c-0.6-0.2-1.5-0.4-1.9-0.9
                                c-0.6,0.3-1.1,0.8-1.8,1c-0.9,0.3-1.9,0.5-2.9,0.3s-2-0.7-2.9-1.2c-0.3-0.2-0.7,0.2-1,0.3c-0.5,0.3-0.9,0.5-1.4,0.7
                                c-0.9,0.3-1.9,0.4-2.9,0.2c-1.3-0.3-2.3-1.2-3.6-1.6c0,0.3,0.3,0.6,0.5,0.8c1.6,1.4,3,3.5,3.8,5.6c0.1,0.3,0.2,0.6,0.3,0.8
                                c0,0.1,0.4-0.7,0.4-0.8c0.8-1.8,2.1-3.4,3.7-4.4l0.1-0.1c0.1,0,0.1,0,0.1,0.1c1.2,0.8,2.1,2.7,2.6,4.1c0.1,0.3,0.2,0.6,0.3,0.8
                                c0,0.1,0.4-0.7,0.4-0.8c0.5-1,1.1-1.9,1.8-2.8c0.6-0.7,1.1-1.2,1.8-1.7c0.5-0.4,1.5,1.1,1.9,1.5c1,1,2,2.3,2.6,3.7
                                c0-0.1,0.6-1.1,0.7-1.2c0.5-1,1.4-1.7,2-2.6c0.7-1,1.3-2,2.2-2.8C223.8,147.1,223.3,147.3,222.8,147.4z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M49.3,29.8c0.2,0.1,0.4,0,0.6-0.1c0.1-0.1,0.2-0.1,0.3-0.2c0.4-0.3,0.8-0.5,1.2-0.9c0.2-0.2,0.4-0.4,0.6-0.6
                                c0.2-0.3,0.3-0.7,0.4-1.1c0.4-1.5,0.6-3,0.5-4.6c0-2.4-0.9-4.3-1.4-6.6c-0.1-0.1-0.2,0.1-0.2,0.2c-0.4,1.8-0.2,3.7-0.2,5.5
                                c-0.1,1.6,0,3.4-0.5,4.9C50.1,27.5,49.6,28.6,49.3,29.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M62.6,24.5c-0.6,0-1.1,0-1.7-0.1C59.9,24.3,59,24,58,24c-1.1,0-2-0.2-3,0.3c-0.9,0.4-1.7,1.2-2.4,1.9
                                c-0.5,0.6-0.7,1.2-1.2,1.8c-0.5,0.7-1.2,1.2-1.9,1.7c0.4-0.3,1.9-0.2,2.4-0.3c0.8-0.2,1.6-0.5,2.4-0.8c1.7-0.7,3.1-1.3,4.9-1.6
                                c2.7-0.5,5.4-0.8,8.2-0.8c4.2,0,8.3,0.5,12.3-0.7c0.3-0.1,0.6-0.2,0.7-0.4c-1.7-0.3-3.5,0-5.2,0c-1.6-0.1-3.3-0.1-4.9-0.2
                                c-2.3-0.1-4.6-0.2-6.8-0.3C63.2,24.5,62.9,24.5,62.6,24.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M52.1,19.3c-0.1-1.1-0.4-2.1-0.7-3.2c2,1.1,3.5,2.8,5.1,4.4c1.3,1.3,2.6,2.6,4.5,2.8c3.5,0.2,7,0.5,10.4,0.6
                                c2.6,0.1,5.2-0.1,7.8,0.4c0.5,0.1,1,0.2,1.4,0.6c-1.6,0.1-3.2,0.3-4.9,0.3c-1.4,0-2.8,0.1-4.2-0.1c-2.7-0.4-5.6-0.2-8.3-0.2
                                c-3.3,0-6.6,0.2-9.9,0.2c-0.5,0-0.7-0.1-0.8-0.7C52.3,22.7,52.3,21,52.1,19.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M51.7,26.1c-0.1,0.6-0.1,1.2-0.1,1.7c0-0.2,1.2-0.9,1.3-1c2.7-1.8,6-1.7,9.2-1.7c2.9,0,5.8,0.2,8.7,0.3
                                c2.9,0.1,5.9,0,8.9-0.1c0.4,0,0.8-0.1,1.1-0.3c-6-0.6-12.1-0.4-18.1-0.6c-2.1-0.1-4.4,0.2-6.4-0.6c-0.4-0.1-0.7-0.3-1-0.5
                                c-0.4-0.3-0.6-0.6-0.9-1c-0.9-1.2-1.7-2.5-2.3-3.9c0.1,1.6,0.3,3.3,0.1,4.9C51.9,24.2,51.8,25.2,51.7,26.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M60,22.3c0.6,2.2,0.5,4.7,0.6,7c0,0.5,0.1,1,0.2,1.5c0.3-0.8,0.4-1.6,0.6-2.4c0.2-1.2,0.3-2.5,0.5-3.7
                                c0.1-1,0.3-2.1,0.6-3c0.2-0.7,1.9-4.5,2.6-4.3c0.1,0,0.3,0.2,0.4,0.3c2.6,3.4,2.4,7.9,2.8,12.1c0,0.3,0.1,0.6,0.2,0.8
                                c0,0,0.3-1.7,0.3-1.9c0.1-0.9,0.2-1.9,0.3-2.8c0.2-1.8,0.4-3.7,1.1-5.3c0.5-1.4,1.3-2.6,2.1-3.7c0.1-0.1,0.2-0.2,0.3-0.2
                                c0.1,0,0.2,0.1,0.2,0.1c1.6,1.3,2.8,3.4,3.4,5.6c0.7,2.5,0.5,5.3,0.8,7.9c0.4-0.8,0.3-1.8,0.4-2.7c0.2-3,0.6-5.9,2-8.5
                                c1.1-2.1,2.7-3.9,3.3-6.4c-1.9,1-3.7,1.8-5.8,1.6c-1.6-0.1-3.1-0.8-4.7-0.9c-1.9-0.2-3.8,0.6-5.8,0.7c-1.3,0-2.5-0.2-3.8-0.1
                                c-0.7,0.1-1.4,0.2-2,0.2c-0.5,0-0.9-0.2-1.4-0.3c-0.8-0.3-1.5-0.5-2.2-1c-0.2-0.2-2.1-0.7-2.1-0.6c0.1,0.6,0.2,0.9,0.6,1.3
                                c0.4,0.5,0.8,1.1,1.1,1.6C58,17.3,59.3,19.6,60,22.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M61.5,16.4c-1.3,0-2.4-1.1-3.6-1.8c-0.4-0.2-0.7-0.3-1.1-0.5c-0.4-0.2-0.9-0.7-1.2-1s-0.7-0.7-0.8-1.2
                                c0.5,0,0.9,0.1,1.4,0.3c0.6,0.2,1.1,0.2,1.8,0.3c1.2,0.2,2.3,0.6,3.5,0.6s2.5-0.3,3.6-0.9c0.1,0,0.2-0.1,0.3-0.1
                                c0.1,0,0.2,0.1,0.3,0.1c1,0.6,1.8,1,3,1.1c1.3,0.1,2.8-0.2,4.1-0.9c0.1-0.1,0.3-0.1,0.4-0.1c0.2,0,0.3,0.1,0.4,0.1
                                c1.4,0.6,2.9,0.9,4.3,0.8c1.6-0.1,3.2-0.3,4.8-0.7c-0.3,0.8-0.8,1.5-1.4,1.9c-1.1,0.9-2.2,1.2-3.5,1.5c-1.3,0.3-2.6,0.2-3.8-0.7
                                c-0.3-0.3-0.7-0.5-1-0.8c-0.1-0.1-0.1-0.1-0.2-0.1c-0.2-0.1-0.3,0-0.5,0.1c-0.8,0.4-1.6,0.9-2.5,1.2c-1.1,0.4-2.4,0.3-3.5-0.3
                                c-0.3-0.2-0.7-0.5-1.1-0.6c-0.5-0.1-0.7,0.1-1,0.5C63.5,15.9,62.4,16.4,61.5,16.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M80.3,14.5c-0.9,0.3-1.9,0.5-2.9,0.6c-0.9,0.1-1.9-0.1-2.8-0.4c-0.6-0.2-1.5-0.4-1.9-0.9
                                c-0.6,0.3-1.1,0.8-1.8,1C70,15.1,69,15.3,68,15.1s-2-0.7-2.9-1.2c-0.3-0.2-0.7,0.2-1,0.3c-0.5,0.3-0.9,0.5-1.4,0.7
                                C62,15.3,61,15.4,60,15.2c-1.3-0.3-2.3-1.2-3.6-1.6c0,0.3,0.3,0.6,0.5,0.8c1.6,1.4,3,3.5,3.8,5.6c0.1,0.3,0.2,0.6,0.3,0.8
                                c0,0.1,0.4-0.7,0.4-0.8c0.8-1.8,2.1-3.4,3.7-4.4l0.1-0.1c0.1,0,0.1,0,0.1,0.1c1.2,0.8,2.1,2.7,2.6,4.1c0.1,0.3,0.2,0.6,0.3,0.8
                                c0,0.1,0.4-0.7,0.4-0.8c0.5-1,1.1-1.9,1.8-2.8c0.6-0.7,1.1-1.2,1.8-1.7c0.5-0.4,1.5,1.1,1.9,1.5c1,1,2,2.3,2.6,3.7
                                c0-0.1,0.6-1.1,0.7-1.2c0.5-1,1.4-1.7,2-2.6c0.7-1,1.3-2,2.2-2.8C81.2,14.2,80.8,14.4,80.3,14.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M176.1,52.1c0.1-0.1,0.2-0.2,0.3-0.2s0.2,0.1,0.3,0.1c2.6,1.8,3.8,4.8,5.1,7.6c-0.1-0.3,0.7-1.6,0.8-1.9
                                c0.8-1.8,2-3.5,3.2-5.1c0.6-0.7,1.1-1.4,1.6-2.2c0.2-0.4,0.4-0.8,0.6-1.3c0.1-0.2,0.3-1.2,0.5-1.3c-2.3,1-4.4,1.8-7,1.6
                                c-1.9-0.1-3.7-0.8-5.6-0.9c-2.3-0.2-4.6,0.6-6.9,0.7c-1.5,0-3-0.2-4.5-0.1c-0.8,0.1-1.6,0.2-2.4,0.2c-0.6,0-1.1-0.2-1.6-0.3
                                c-0.9-0.3-1.8-0.5-2.6-1c-0.3-0.2-2.5-0.7-2.5-0.6c0.2,0.6,0.3,0.9,0.7,1.3c0.5,0.5,0.9,1.1,1.3,1.6c1,1.3,2.2,2.3,2.9,3.8
                                c0.8,1.6,1.6,3.3,2.5,4.8c0,0.1,0.1,0.1,0.2,0.1c0.1,0,0.2-0.1,0.2-0.2l0,0c0.6-1.3,1.3-2.5,1.9-3.8c0.4-0.7,1.5-2.8,2.5-2.6
                                c0.2,0,0.3,0.2,0.4,0.3c1.8,2,2.1,4.8,3.7,6.9C173,57.2,174,54.4,176.1,52.1z"
                            />
                            <Path
                                fill="#97773C"
                                d="M163.2,51.7c-1.6,0-2.9-1.1-4.2-1.8c-0.4-0.2-0.9-0.3-1.3-0.5c-0.5-0.2-1-0.7-1.4-1s-0.8-0.7-1-1.2
                                c0.6,0,1.1,0.1,1.6,0.3c0.8,0.2,1.4,0.2,2.1,0.3c1.4,0.2,2.7,0.6,4.1,0.6c1.5,0,3-0.3,4.3-0.9c0.1,0,0.2-0.1,0.3-0.1
                                s0.2,0.1,0.3,0.1c1.2,0.6,2.2,1,3.6,1.1c1.6,0.1,3.4-0.2,4.8-0.9c0.2-0.1,0.3-0.1,0.5-0.1s0.4,0.1,0.5,0.1
                                c1.6,0.6,3.4,0.9,5.2,0.8c1.9-0.1,3.9-0.3,5.7-0.7c-0.3,0.8-1,1.5-1.7,1.9c-1.3,0.9-2.7,1.2-4.1,1.5c-1.5,0.3-3.2,0.2-4.5-0.7
                                c-0.4-0.3-0.8-0.5-1.2-0.8c-0.1-0.1-0.2-0.1-0.3-0.1c-0.2-0.1-0.4,0-0.6,0.1c-1,0.4-1.9,0.9-2.9,1.2c-1.3,0.4-2.9,0.3-4.1-0.3
                                c-0.4-0.2-0.9-0.5-1.3-0.6c-0.6-0.1-0.8,0.1-1.2,0.5C165.6,51.3,164.2,51.7,163.2,51.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M185.6,49.9c-1.1,0.3-2.2,0.5-3.4,0.6c-1.1,0.1-2.2-0.1-3.3-0.4c-0.7-0.2-1.8-0.4-2.2-0.9
                                c-0.7,0.3-1.3,0.8-2.1,1c-1.1,0.3-2.3,0.5-3.4,0.3c-1.2-0.2-2.4-0.7-3.4-1.2c-0.4-0.2-0.8,0.2-1.2,0.3c-0.5,0.3-1.1,0.5-1.7,0.7
                                c-1.1,0.3-2.3,0.4-3.4,0.2c-1.5-0.3-2.8-1.2-4.2-1.6c0,0.3,0.3,0.6,0.6,0.8c1.9,1.4,3.5,3.5,4.6,5.6c0.1,0.3,0.3,0.6,0.4,0.8
                                c0,0.1,0.4-0.7,0.5-0.8c1-1.8,2.5-3.4,4.4-4.4c0.1,0,0.1-0.1,0.2-0.1c0.1,0,0.1,0,0.2,0.1c1.4,0.8,2.5,2.7,3.2,4.1
                                c0.1,0.3,0.3,0.6,0.4,0.8c0,0.1,0.4-0.7,0.5-0.8c0.6-1,1.3-1.9,2.1-2.8c0.7-0.7,1.3-1.2,2.1-1.7c0.6-0.4,1.8,1.1,2.2,1.5
                                c1.2,1,2.4,2.3,3.1,3.7c0-0.1,0.7-1.1,0.8-1.2c0.6-1,1.6-1.7,2.4-2.6c0.8-1,1.6-2,2.6-2.8C186.8,49.5,186.2,49.7,185.6,49.9z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M195.5,112.8c0.1-0.1,0.2-0.2,0.3-0.2s0.2,0.1,0.3,0.1c2.6,1.8,3.8,4.8,5.1,7.6c-0.1-0.3,0.7-1.6,0.8-1.9
                                c0.8-1.8,2-3.5,3.2-5.1c0.6-0.7,1.1-1.4,1.6-2.2c0.2-0.4,0.4-0.8,0.6-1.3c0.1-0.2,0.3-1.2,0.5-1.3c-2.3,1-4.4,1.8-7,1.6
                                c-1.9-0.1-3.7-0.8-5.6-0.9c-2.3-0.2-4.6,0.6-6.9,0.7c-1.5,0-3-0.2-4.5-0.1c-0.8,0.1-1.6,0.2-2.4,0.2c-0.6,0-1.1-0.2-1.6-0.3
                                c-0.9-0.3-1.8-0.5-2.6-1c-0.3-0.2-2.5-0.7-2.5-0.6c0.2,0.6,0.3,0.9,0.7,1.3c0.5,0.5,0.9,1.1,1.3,1.6c1,1.3,2.2,2.3,2.9,3.8
                                c0.8,1.6,1.6,3.3,2.5,4.8c0,0.1,0.1,0.1,0.2,0.1c0.1,0,0.2-0.1,0.2-0.2l0,0c0.6-1.3,1.3-2.5,1.9-3.8c0.4-0.7,1.5-2.8,2.5-2.6
                                c0.2,0,0.3,0.2,0.4,0.3c1.8,2,2.1,4.8,3.7,6.9C192.4,117.9,193.4,115,195.5,112.8z"
                            />
                            <Path
                                fill="#97773C"
                                d="M182.5,112.3c-1.6,0-2.9-1.1-4.2-1.8c-0.4-0.2-0.9-0.3-1.3-0.5c-0.5-0.2-1-0.7-1.4-1s-0.8-0.7-1-1.2
                                c0.6,0,1.1,0.1,1.6,0.3c0.8,0.2,1.4,0.2,2.1,0.3c1.4,0.2,2.7,0.6,4.1,0.6c1.5,0,3-0.3,4.3-0.9c0.1,0,0.2-0.1,0.3-0.1
                                s0.2,0.1,0.3,0.1c1.2,0.6,2.2,1,3.6,1.1c1.6,0.1,3.4-0.2,4.8-0.9c0.2-0.1,0.3-0.1,0.5-0.1s0.4,0.1,0.5,0.1
                                c1.6,0.6,3.4,0.9,5.2,0.8c1.9-0.1,3.9-0.3,5.7-0.7c-0.3,0.8-1,1.5-1.7,1.9c-1.3,0.9-2.7,1.2-4.1,1.5c-1.5,0.3-3.2,0.2-4.5-0.7
                                c-0.4-0.3-0.8-0.5-1.2-0.8c-0.1-0.1-0.2-0.1-0.3-0.1c-0.2-0.1-0.4,0-0.6,0.1c-1,0.4-1.9,0.9-2.9,1.2c-1.3,0.4-2.9,0.3-4.1-0.3
                                c-0.4-0.2-0.9-0.5-1.3-0.6c-0.6-0.1-0.8,0.1-1.2,0.5C185,111.9,183.6,112.4,182.5,112.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M205,110.5c-1.1,0.3-2.2,0.5-3.4,0.6c-1.1,0.1-2.2-0.1-3.3-0.4c-0.7-0.2-1.8-0.4-2.2-0.9
                                c-0.7,0.3-1.3,0.8-2.1,1c-1.1,0.3-2.3,0.5-3.4,0.3c-1.2-0.2-2.4-0.7-3.4-1.2c-0.4-0.2-0.8,0.2-1.2,0.3c-0.5,0.3-1.1,0.5-1.7,0.7
                                c-1.1,0.3-2.3,0.4-3.4,0.2c-1.5-0.3-2.8-1.2-4.2-1.6c0,0.3,0.3,0.6,0.6,0.8c1.9,1.4,3.5,3.5,4.6,5.6c0.1,0.3,0.3,0.6,0.4,0.8
                                c0,0.1,0.4-0.7,0.5-0.8c1-1.8,2.5-3.4,4.4-4.4c0.1,0,0.1-0.1,0.2-0.1c0.1,0,0.1,0,0.2,0.1c1.4,0.8,2.5,2.7,3.2,4.1
                                c0.1,0.3,0.3,0.6,0.4,0.8c0,0.1,0.4-0.7,0.5-0.8c0.6-1,1.3-1.9,2.1-2.8c0.7-0.7,1.3-1.2,2.1-1.7c0.6-0.4,1.8,1.1,2.2,1.5
                                c1.2,1,2.4,2.3,3.1,3.7c0-0.1,0.7-1.1,0.8-1.2c0.6-1,1.6-1.7,2.4-2.6c0.8-1,1.6-2,2.6-2.8C206.1,110.2,205.6,110.4,205,110.5z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M199.7,125.9c0.8-1.4,1.6-2.7,2.2-4.2c-0.8,0.4-1.5,0.7-2.3,0.9c-0.7,0.2-1.4,0.2-2.2,0.2
                                c-1.5,0.1-3,0.1-4.5,0.3c-1.5,0.1-3-0.2-4.5,0c-0.8,0.1-1.6,0.2-2.4,0.2c-0.6,0-1.1-0.2-1.6-0.3c-0.9-0.3-1.8-0.5-2.6-1
                                c-0.3-0.2-2.5-0.7-2.5-0.6c0.2,0.6,0.3,0.9,0.7,1.3c0.5,0.5,0.9,1.1,1.3,1.6c1,1.3,2.2,2.3,2.9,3.8c0.8,1.6,1.6,3.3,2.5,4.8
                                c0,0.1,0.1,0.1,0.2,0.1c0.1,0,0.2-0.1,0.2-0.2l0,0c0.6-1.3,1.3-2.5,1.9-3.8c0.4-0.7,1.5-2.8,2.5-2.6c0.2,0,0.3,0.2,0.4,0.3
                                c1.8,2,2.1,4.8,3.7,6.9c-0.1-0.2,1.1-2.4,1.3-2.7c0.5-1,1-1.9,1.5-2.9C198.8,127.5,199.3,126.7,199.7,125.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M187.1,125.7c-1.6,0-2.9-1.1-4.2-1.8c-0.4-0.2-0.9-0.3-1.3-0.5c-0.5-0.2-1-0.7-1.4-1s-0.8-0.7-1-1.2
                                c0.6,0,1.1,0.1,1.6,0.3c0.8,0.2,1.4,0.2,2.1,0.3c1.4,0.2,2.7,0.6,4.1,0.6c1.5,0,3-0.3,4.3-0.9c0.1,0,0.2-0.1,0.3-0.1
                                s0.2,0.1,0.3,0.1c1.2,0.6,2.2,1,3.6,1.1c1.2,0,2.7-0.1,4-0.3c0.6-0.1,1.1-0.2,1.6-0.4c0.2-0.1,1.3-0.4,1.4-0.6
                                c-1.4,1.9-3.3,3-5.5,3.7c-1.3,0.4-2.9,0.3-4.1-0.3c-0.4-0.2-0.9-0.5-1.3-0.6c-0.6-0.1-0.8,0.1-1.2,0.5
                                C189.5,125.2,188.1,125.7,187.1,125.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M200.4,123.2c-0.8,0.4-1.6,0.9-2.5,1.1c-0.2,0.1-2.9,0.5-2.9,0.2c-1.2-0.2-2.4-0.7-3.4-1.2
                                c-0.4-0.2-0.8,0.2-1.2,0.3c-0.5,0.3-1.1,0.5-1.7,0.7c-1.1,0.3-2.3,0.4-3.4,0.2c-1.5-0.3-2.8-1.2-4.2-1.6c0,0.3,0.3,0.6,0.6,0.8
                                c1.9,1.4,3.5,3.5,4.6,5.6c0.1,0.3,0.3,0.6,0.4,0.8c0,0.1,0.4-0.7,0.5-0.8c1-1.8,2.5-3.4,4.4-4.4c0.1,0,0.1-0.1,0.2-0.1
                                c0.1,0,0.1,0,0.2,0.1c1.4,0.8,2.5,2.7,3.2,4.1c0.1,0.3,0.3,0.6,0.4,0.8c0,0.1,0.4-0.7,0.5-0.8c0.6-1.1,1.5-2,2.3-3
                                C198.9,125.2,199.5,124.1,200.4,123.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M144.2,43.9c0.2,0,0.3,0.2,0.4,0.3c2.4,2.7,2.2,6,2.4,9.3c0.1,1.9,0.2,3.7,0.2,5.6c0,1.8-0.1,3.7,0.1,5.5
                                c0,0.3,0.1,0.6,0.2,0.9c0.2-0.8,0.4-1.7,0.5-2.5s0.1-1.6,0.2-2.4c0.2-1.8,0.4-3.6,0.6-5.4c0.3-3.3,0.7-6.7,2.4-9.6
                                c0.4-0.8,0.9-1.6,1.4-2.3c0.8-1.4,1.6-2.7,2.2-4.2c-0.8,0.4-1.5,0.7-2.3,0.9c-0.7,0.2-1.4,0.2-2.2,0.2c-1.5,0.1-3,0.1-4.5,0.3
                                c-1.5,0.1-3-0.2-4.5,0c-0.8,0.1-1.6,0.2-2.4,0.2c-0.6,0-1.1-0.2-1.6-0.3c-0.9-0.3-1.8-0.5-2.6-1c-0.3-0.2-2.5-0.7-2.5-0.6
                                c0.2,0.6,0.3,0.9,0.7,1.3c0.5,0.5,0.9,1.1,1.3,1.6c2.6,3.4,4.4,6.1,4.5,10.5c0.1,2.2,0,4.4,0,6.6c0,1.1,0,2.2,0,3.3c0,1,0,2,0.3,3
                                c0.4-1,0.5-2.1,0.6-3.2c0.1-1.1,0.2-2.2,0.3-3.3c0.2-2.3,0.2-4.6,0.5-6.9C140.6,50.1,141.2,43.2,144.2,43.9z"
                            />
                            <Path
                                fill="#97773C"
                                d="M139.8,42.6c-1.6,0-2.9-1.1-4.2-1.8c-0.4-0.2-0.9-0.3-1.3-0.5c-0.5-0.2-1-0.7-1.4-1s-0.8-0.7-1-1.2
                                c0.6,0,1.1,0.1,1.6,0.3c0.8,0.2,1.4,0.2,2.1,0.3c1.4,0.2,2.7,0.6,4.1,0.6c1.5,0,3-0.3,4.3-0.9c0.1,0,0.2-0.1,0.3-0.1
                                s0.2,0.1,0.3,0.1c1.2,0.6,2.2,1,3.6,1.1c1.2,0,2.7-0.1,4-0.3c0.6-0.1,1.1-0.2,1.6-0.4c0.2-0.1,1.3-0.4,1.4-0.6
                                c-1.4,1.9-3.3,3-5.5,3.7c-1.3,0.4-2.9,0.3-4.1-0.3c-0.4-0.2-0.9-0.5-1.3-0.6c-0.6-0.1-0.8,0.1-1.2,0.5
                                C142.3,42.1,140.9,42.6,139.8,42.6z"
                            />
                            <Path
                                fill="#463116"
                                d="M153.2,40.1c-0.8,0.4-1.6,0.9-2.5,1.1c-0.2,0.1-2.9,0.5-2.9,0.2c-1.2-0.2-2.4-0.7-3.4-1.2
                                c-0.4-0.2-0.8,0.2-1.2,0.3c-0.5,0.3-1.1,0.5-1.7,0.7c-1.1,0.3-2.3,0.4-3.4,0.2c-1.5-0.3-2.8-1.2-4.2-1.6c0,0.3,0.3,0.6,0.6,0.8
                                c1.9,1.4,3.5,3.5,4.6,5.6c0.1,0.3,0.3,0.6,0.4,0.8c0,0.1,0.4-0.7,0.5-0.8c1-1.8,2.5-3.4,4.4-4.4c0.1,0,0.1-0.1,0.2-0.1
                                c0.1,0,0.1,0,0.2,0.1c1.4,0.8,2.5,2.7,3.2,4.1c0.1,0.3,0.3,0.6,0.4,0.8c0,0.1,0.4-0.7,0.5-0.8c0.6-1.1,1.5-2,2.3-3
                                C151.7,42,152.3,41,153.2,40.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M40.2,15.6c0.1,0,0.2,0.1,0.3,0.2c1.9,1.9,1.7,4.2,1.9,6.5c0.1,1.3,0.1,2.6,0.1,3.9s0,2.6,0.1,3.8
                                c0,0.2,0,0.4,0.2,0.6c0.2-0.6,0.3-1.2,0.4-1.7c0.1-0.6,0.1-1.1,0.2-1.7c0.2-1.3,0.3-2.5,0.5-3.8c0.3-2.3,0.6-4.6,1.9-6.7
                                c0.3-0.5,0.7-1.1,1.1-1.6c0.6-0.9,1.3-1.9,1.8-2.9c-0.6,0.2-1.2,0.5-1.8,0.6c-0.9,0.2-1.4,0.2-2,0.3c-1.2,0.1-2.4,0.1-3.6,0.2
                                c-1.2,0.1-2.4-0.1-3.6,0c-0.6,0-1.3,0.2-1.9,0.1c-0.4,0-0.9-0.1-1.3-0.2c-0.7-0.2-1.4-0.3-2.1-0.7c-0.2-0.1-2-0.5-2-0.4
                                c0.1,0.4,0.2,0.6,0.5,0.9c0.4,0.4,0.7,0.8,1,1.1c2.1,2.4,3.5,4.3,3.6,7.3c0,1.5,0,3.1,0,4.6c0,0.8,0,1.5,0,2.3
                                c0,0.7,0,1.4,0.2,2.1c0.3-0.7,0.4-1.5,0.5-2.2c0.1-0.8,0.2-1.5,0.3-2.3c0.1-1.6,0.2-3.2,0.4-4.8C37.3,20,37.8,15.1,40.2,15.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M36.7,14.7c-1.3,0-2.3-0.8-3.3-1.3c-0.3-0.2-0.7-0.2-1-0.4c-0.4-0.1-0.8-0.5-1.1-0.7
                                c-0.3-0.2-0.6-0.5-0.8-0.8c0.4,0,0.9,0.1,1.3,0.2c0.6,0.2,1.1,0.2,1.7,0.2c1.1,0.1,2.1,0.4,3.3,0.4s2.3-0.2,3.4-0.6
                                c0.1,0,0.2-0.1,0.3-0.1s0.2,0,0.3,0.1c0.9,0.4,1.7,0.7,2.8,0.8c1,0,2.2-0.1,3.1-0.2c0.4-0.1,0.8-0.1,1.3-0.3
                                c0.2-0.1,1.1-0.3,1.1-0.4c-1.1,1.3-2.6,2.1-4.3,2.6c-1.1,0.3-2.2,0.2-3.3-0.2c-0.3-0.1-0.7-0.3-1-0.4c-0.5-0.1-0.6,0.1-1,0.3
                                C38.6,14.4,37.6,14.7,36.7,14.7z"
                            />
                            <Path
                                fill="#463116"
                                d="M47.2,13c-0.7,0.3-1.3,0.6-2,0.8c-0.2,0-2.3,0.3-2.3,0.2c-0.9-0.1-1.9-0.5-2.7-0.9c-0.3-0.1-0.6,0.1-0.9,0.2
                                c-0.4,0.2-0.9,0.4-1.3,0.5c-0.9,0.2-1.8,0.3-2.7,0.2c-1.2-0.2-2.2-0.8-3.3-1.1c0,0.2,0.3,0.4,0.5,0.5c1.5,1,2.8,2.4,3.6,3.9
                                c0.1,0.2,0.2,0.4,0.3,0.6c0,0,0.3-0.5,0.4-0.6c0.8-1.3,2-2.4,3.5-3h0.1h0.1c1.1,0.5,1.9,1.9,2.5,2.9c0.1,0.2,0.2,0.4,0.3,0.6
                                c0,0,0.3-0.5,0.4-0.6c0.5-0.8,1.2-1.4,1.8-2.1C46,14.3,46.5,13.6,47.2,13z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M20.4,47c0.1,0,0.3,0.2,0.4,0.3c2,2.3,1.9,5.2,2.1,8.1c0.1,1.6,0.1,3.2,0.2,4.8c0,1.6,0,3.2,0.1,4.8
                                c0,0.3,0.1,0.5,0.2,0.7c0.2-0.7,0.3-1.4,0.4-2.2c0.1-0.7,0.1-1.4,0.2-2.1c0.2-1.6,0.3-3.1,0.5-4.7c0.3-2.8,0.6-5.8,2-8.3
                                c0.4-0.7,0.8-1.3,1.2-2c0.7-1.2,1.4-2.3,1.9-3.6c-0.7,0.3-1.3,0.6-2,0.8c-0.6,0.1-1.2,0.2-1.9,0.2c-1.3,0.1-2.6,0.1-3.9,0.2
                                c-1.3,0.1-2.6-0.1-3.9,0c-0.7,0-1.4,0.2-2.1,0.1c-0.5,0-1-0.2-1.4-0.3c-0.8-0.2-1.5-0.4-2.3-0.8c-0.2-0.1-2.2-0.6-2.1-0.5
                                c0.1,0.5,0.2,0.7,0.6,1.1c0.4,0.5,0.8,0.9,1.1,1.4c2.3,2.9,3.8,5.3,3.9,9.1c0,1.9,0,3.8,0,5.7c0,0.9,0,1.9,0,2.8
                                c0,0.9,0,1.7,0.3,2.6c0.4-0.8,0.4-1.8,0.5-2.7s0.2-1.9,0.3-2.8c0.1-2,0.2-4,0.5-5.9C17.3,52.4,17.9,46.4,20.4,47z"
                            />
                            <Path
                                fill="#97773C"
                                d="M16.7,45.9c-1.4,0-2.5-0.9-3.7-1.6c-0.4-0.2-0.7-0.3-1.1-0.4c-0.4-0.2-0.9-0.6-1.2-0.9
                                c-0.4-0.3-0.7-0.6-0.9-1c0.5,0,0.9,0.1,1.4,0.2c0.7,0.2,1.2,0.2,1.8,0.3c1.2,0.1,2.3,0.5,3.6,0.5s2.6-0.3,3.8-0.8
                                c0.1,0,0.2-0.1,0.3-0.1c0.1,0,0.2,0,0.3,0.1c1,0.5,1.9,0.9,3.1,0.9c1.1,0,2.4-0.1,3.4-0.3c0.5-0.1,0.9-0.2,1.4-0.4
                                c0.2-0.1,1.2-0.4,1.2-0.5c-1.2,1.6-2.9,2.6-4.7,3.2c-1.2,0.4-2.5,0.3-3.6-0.2c-0.4-0.2-0.7-0.4-1.1-0.5c-0.5-0.1-0.7,0.1-1.1,0.4
                                C18.8,45.5,17.6,45.9,16.7,45.9z"
                            />
                            <Path
                                fill="#463116"
                                d="M28.2,43.8c-0.7,0.3-1.4,0.7-2.2,0.9c-0.2,0.1-2.5,0.4-2.5,0.2c-1-0.2-2.1-0.6-3-1.1c-0.3-0.2-0.7,0.2-1,0.3
                                c-0.5,0.2-0.9,0.4-1.4,0.6c-1,0.3-2,0.4-3,0.2c-1.3-0.3-2.4-1-3.7-1.4c0,0.3,0.3,0.5,0.5,0.7c1.6,1.2,3,3,3.9,4.8
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.9-1.6,2.2-3,3.8-3.8h0.1c0.1,0,0.1,0,0.1,0c1.2,0.7,2.1,2.4,2.7,3.6
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.5-1,1.3-1.7,2-2.5C26.9,45.4,27.4,44.5,28.2,43.8z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M180.8,178.6c0.1,0,0.3,0.2,0.4,0.3c2,2.3,1.9,5.2,2.1,8.1c0.1,1.6,0.1,3.2,0.2,4.8c0,1.6,0,3.2,0.1,4.8
                                c0,0.3,0.1,0.5,0.2,0.7c0.2-0.7,0.3-1.4,0.4-2.2c0.1-0.7,0.1-1.4,0.2-2.1c0.2-1.6,0.3-3.1,0.5-4.7c0.3-2.8,0.6-5.8,2-8.3
                                c0.4-0.7,0.8-1.3,1.2-2c0.7-1.2,1.4-2.3,1.9-3.6c-0.7,0.3-1.3,0.6-2,0.8c-0.6,0.1-1.2,0.2-1.9,0.2c-1.3,0.1-2.6,0.1-3.9,0.2
                                c-1.3,0.1-2.6-0.1-3.9,0c-0.7,0-1.4,0.2-2.1,0.1c-0.5,0-1-0.2-1.4-0.3c-0.8-0.2-1.5-0.4-2.3-0.8c-0.2-0.1-2.2-0.6-2.1-0.5
                                c0.1,0.5,0.2,0.7,0.6,1.1c0.4,0.5,0.8,0.9,1.1,1.4c2.3,2.9,3.8,5.3,3.9,9.1c0,1.9,0,3.8,0,5.7c0,0.9,0,1.9,0,2.8
                                c0,0.9,0,1.7,0.3,2.6c0.4-0.8,0.4-1.8,0.5-2.7s0.2-1.9,0.3-2.8c0.1-2,0.2-4,0.5-5.9C177.7,184,178.2,178,180.8,178.6z"
                            />
                            <Path
                                fill="#97773C"
                                d="M177,177.4c-1.4,0-2.5-0.9-3.7-1.6c-0.4-0.2-0.7-0.3-1.1-0.4c-0.4-0.2-0.9-0.6-1.2-0.9
                                c-0.4-0.3-0.7-0.6-0.9-1c0.5,0,0.9,0.1,1.4,0.2c0.7,0.2,1.2,0.2,1.8,0.3c1.2,0.1,2.3,0.5,3.6,0.5s2.6-0.3,3.8-0.8
                                c0.1,0,0.2-0.1,0.3-0.1s0.2,0,0.3,0.1c1,0.5,1.9,0.9,3.1,0.9c1.1,0,2.4-0.1,3.4-0.3c0.5-0.1,0.9-0.2,1.4-0.4
                                c0.2-0.1,1.2-0.4,1.2-0.5c-1.2,1.6-2.9,2.6-4.7,3.2c-1.2,0.4-2.5,0.3-3.6-0.2c-0.4-0.2-0.7-0.4-1.1-0.5c-0.5-0.1-0.7,0.1-1.1,0.4
                                C179.1,177.1,178,177.5,177,177.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M188.6,175.3c-0.7,0.3-1.4,0.7-2.2,0.9c-0.2,0.1-2.5,0.4-2.5,0.2c-1-0.2-2.1-0.6-3-1.1
                                c-0.3-0.2-0.7,0.2-1,0.3c-0.5,0.2-0.9,0.4-1.4,0.6c-1,0.3-2,0.4-3,0.2c-1.3-0.3-2.4-1-3.7-1.4c0,0.3,0.3,0.5,0.5,0.7
                                c1.6,1.2,3,3,3.9,4.8c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.9-1.6,2.2-3,3.8-3.8h0.1c0.1,0,0.1,0,0.1,0
                                c1.2,0.7,2.1,2.4,2.7,3.6c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.5-1,1.3-1.7,2-2.5C187.2,177,187.8,176.1,188.6,175.3z
                                "
                            />
                            <Path
                                fill="#F2DF99"
                                d="M212.9,110.2c-0.1,1.9-0.4,3.7-0.7,5.6c-0.1,0.9-0.3,1.9-0.3,2.9c0,0.7-0.1,1.9,0.5,2.4
                                c0.5-0.4,0.8-0.9,1.2-1.3s0.6-0.9,0.9-1.3c0.3-0.7,0.4-1.5,0.5-2.2c0.1-1.8-0.1-3.6-0.6-5.3c-0.3-1-0.9-1.9-1.2-2.9
                                C213,108.7,213,109.4,212.9,110.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M223.8,116.2c0.1-0.2-0.1-0.4-0.3-0.4c-1.4-0.4-2.7-0.7-4.1-0.7c-0.4,0-0.8-0.1-1.2,0s-0.7,0.3-1.1,0.5
                                c-0.7,0.4-1.2,0.9-1.8,1.5c-0.7,0.7-1.4,1.6-1.9,2.5c-0.2,0.3-0.4,0.6-0.6,0.9c-0.1,0.2-0.4,0.5-0.4,0.7c0.3,0.1,0.7,0,0.8,0
                                c0.3,0,0.6-0.1,0.9-0.2c0.6-0.2,1-0.5,1.3-1c0.8-1.1,1.7-2.2,3-2.7C220.2,116.5,221.9,116.4,223.8,116.2z"
                            />
                            <Path
                                fill="#97773C"
                                d="M213.5,110.3c-0.1-0.8-0.2-1.6-0.3-2.3c1.4,1.4,1.5,5.1,3.5,5.6c0.7,0.2,1.4,0.2,2.2,0.3
                                c0.9,0.1,1.7,0.4,2.6,0.7c0.9,0.3,1.9,0.7,2.4,1.4c-1.8-0.2-3.5-0.2-5.3-0.1c-0.9,0-1.9,0.2-2.9,0.2c-0.3,0-0.8,0.1-1-0.1
                                s-0.2-0.8-0.3-1.1C214.1,113.4,213.7,111.8,213.5,110.3z"
                            />
                            <Path
                                fill="#463116"
                                d="M214.9,114.2c-0.3-0.5-0.5-1.1-0.6-1.7c-0.1-0.3-0.2-0.6-0.3-0.9s-0.2-0.5-0.4-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0.1,0.8c0,0.3,0,0.6,0.1,0.9c0.1,0.6,0.1,1.1,0.1,1.7c0.1,1,0.1,1.9,0.1,2.9c0,0.2,0,0.5,0,0.7s0.1,0.4,0.2,0.4
                                c0.1,0,0.2-0.1,0.3-0.2c0.5-0.5,1.1-0.9,1.7-1.2c0.7-0.3,1.3-0.3,2-0.5c1.4-0.3,2.8-0.4,4.2-0.5c-0.4-0.2-0.8-0.3-1.2-0.3
                                c-0.4-0.1-0.7-0.1-1.1-0.2c-0.9-0.1-1.8,0-2.7-0.1C216.4,115.3,215.5,115.2,214.9,114.2z"
                            />
                            <Path
                                fill="#F2DF99"
                                d="M221.7,121.4c-0.1,1.9-0.4,3.7-0.7,5.6c-0.1,0.9-0.3,1.9-0.3,2.9c0,0.7-0.1,1.9,0.5,2.4
                                c0.5-0.4,0.8-0.9,1.2-1.3c0.4-0.4,0.6-0.9,0.9-1.3c0.3-0.7,0.4-1.5,0.5-2.2c0.1-1.8-0.1-3.6-0.6-5.3c-0.3-1-0.9-1.9-1.2-2.9
                                C221.8,119.9,221.8,120.6,221.7,121.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M232.6,127.4c0.1-0.2-0.1-0.4-0.3-0.4c-1.4-0.4-2.7-0.7-4.1-0.7c-0.4,0-0.8-0.1-1.2,0s-0.7,0.3-1.1,0.5
                                c-0.7,0.4-1.2,0.9-1.8,1.5c-0.7,0.7-1.4,1.6-1.9,2.5c-0.2,0.3-0.4,0.6-0.6,0.9c-0.1,0.2-0.4,0.5-0.4,0.7c0.3,0.1,0.7,0,0.8,0
                                c0.3,0,0.6-0.1,0.9-0.2c0.6-0.2,1-0.5,1.3-1c0.8-1.1,1.7-2.2,3-2.7C229,127.7,230.7,127.6,232.6,127.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M222.3,121.5c-0.1-0.8-0.2-1.6-0.3-2.3c1.4,1.4,1.5,5.1,3.5,5.6c0.7,0.2,1.4,0.2,2.2,0.3
                                c0.9,0.1,1.7,0.4,2.6,0.7c0.9,0.3,1.9,0.7,2.4,1.4c-1.8-0.2-3.5-0.2-5.3-0.1c-0.9,0-1.9,0.2-2.9,0.2c-0.3,0-0.8,0.1-1-0.1
                                s-0.2-0.8-0.3-1.1C222.9,124.5,222.5,123,222.3,121.5z"
                            />
                            <Path
                                fill="#463116"
                                d="M223.7,125.4c-0.3-0.5-0.5-1.1-0.6-1.7c-0.1-0.3-0.2-0.6-0.3-0.9s-0.2-0.5-0.4-0.7c0,0,0,0-0.1,0
                                c0,0.3,0,0.5,0.1,0.8c0,0.3,0,0.6,0.1,0.9c0.1,0.6,0.1,1.1,0.1,1.7c0.1,1,0.1,1.9,0.1,2.9c0,0.2,0,0.5,0,0.7s0.1,0.4,0.2,0.4
                                c0.1,0,0.2-0.1,0.3-0.2c0.5-0.5,1.1-0.9,1.7-1.2c0.7-0.3,1.3-0.3,2-0.5c1.4-0.3,2.8-0.4,4.2-0.5c-0.4-0.2-0.8-0.3-1.2-0.3
                                c-0.4-0.1-0.7-0.1-1.1-0.2c-0.9-0.1-1.8,0-2.7-0.1C225.2,126.4,224.3,126.4,223.7,125.4z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M239.2,114.5c0.1,0,0.3,0.2,0.4,0.3c2,2.3,1.9,5.2,2.1,8.1c0.1,1.6,0.1,3.2,0.2,4.8c0,1.6,0,3.2,0.1,4.8
                                c0,0.3,0.1,0.5,0.2,0.7c0.2-0.7,0.3-1.4,0.4-2.2c0.1-0.7,0.1-1.4,0.2-2.1c0.2-1.6,0.3-3.1,0.5-4.7c0.3-2.8,0.6-5.8,2-8.3
                                c0.4-0.7,0.8-1.3,1.2-2c0.7-1.2,1.4-2.3,1.9-3.6c-0.7,0.3-1.3,0.6-2,0.8c-0.6,0.1-1.2,0.2-1.9,0.2c-1.3,0.1-2.6,0.1-3.9,0.2
                                c-1.3,0.1-2.6-0.1-3.9,0c-0.7,0-1.4,0.2-2.1,0.1c-0.5,0-1-0.2-1.4-0.3c-0.8-0.2-1.5-0.4-2.3-0.8c-0.2-0.1-2.2-0.6-2.1-0.5
                                c0.1,0.5,0.2,0.7,0.6,1.1c0.4,0.5,0.8,0.9,1.1,1.4c2.3,2.9,3.8,5.3,3.9,9.1c0,1.9,0,3.8,0,5.7c0,0.9,0,1.9,0,2.8s0,1.7,0.3,2.6
                                c0.4-0.8,0.4-1.8,0.5-2.7s0.2-1.9,0.3-2.8c0.1-2,0.2-4,0.5-5.9C236,119.9,236.6,113.9,239.2,114.5z"
                            />
                            <Path
                                fill="#97773C"
                                d="M235.4,113.4c-1.4,0-2.5-0.9-3.7-1.6c-0.4-0.2-0.7-0.3-1.1-0.4c-0.4-0.2-0.9-0.6-1.2-0.9
                                c-0.4-0.3-0.7-0.6-0.9-1c0.5,0,0.9,0.1,1.4,0.2c0.7,0.2,1.2,0.2,1.8,0.3c1.2,0.1,2.3,0.5,3.6,0.5s2.6-0.3,3.8-0.8
                                c0.1,0,0.2-0.1,0.3-0.1s0.2,0,0.3,0.1c1,0.5,1.9,0.9,3.1,0.9c1.1,0,2.4-0.1,3.4-0.3c0.5-0.1,0.9-0.2,1.4-0.4
                                c0.2-0.1,1.2-0.4,1.2-0.5c-1.2,1.6-2.9,2.6-4.7,3.2c-1.2,0.4-2.5,0.3-3.6-0.2c-0.4-0.2-0.7-0.4-1.1-0.5c-0.5-0.1-0.7,0.1-1.1,0.4
                                C237.5,113,236.3,113.4,235.4,113.4z"
                            />
                            <Path
                                fill="#463116"
                                d="M247,111.2c-0.7,0.3-1.4,0.7-2.2,0.9c-0.2,0.1-2.5,0.4-2.5,0.2c-1-0.2-2.1-0.6-3-1.1c-0.3-0.2-0.7,0.2-1,0.3
                                c-0.5,0.2-0.9,0.4-1.4,0.6c-1,0.3-2,0.4-3,0.2c-1.3-0.3-2.4-1-3.7-1.4c0,0.3,0.3,0.5,0.5,0.7c1.6,1.2,3,3,3.9,4.8
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.9-1.6,2.2-3,3.8-3.8h0.1c0.1,0,0.1,0,0.1,0c1.2,0.7,2.1,2.4,2.7,3.6
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.5-1,1.3-1.7,2-2.5C245.6,112.9,246.2,112,247,111.2z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M38.2,113.4c0.1,0,0.3,0.2,0.4,0.3c2,2.3,1.9,5.2,2.1,8.1c0.1,1.6,0.1,3.2,0.2,4.8c0,1.6,0,3.2,0.1,4.8
                                c0,0.3,0.1,0.5,0.2,0.7c0.2-0.7,0.3-1.4,0.4-2.2c0.1-0.7,0.1-1.4,0.2-2.1c0.2-1.6,0.3-3.1,0.5-4.7c0.3-2.8,0.6-5.8,2-8.3
                                c0.4-0.7,0.8-1.3,1.2-2c0.7-1.2,1.4-2.3,1.9-3.6c-0.7,0.3-1.3,0.6-2,0.8c-0.6,0.1-1.2,0.2-1.9,0.2c-1.3,0.1-2.6,0.1-3.9,0.2
                                c-1.3,0.1-2.6-0.1-3.9,0c-0.7,0-1.4,0.2-2.1,0.1c-0.5,0-1-0.2-1.4-0.3c-0.8-0.2-1.5-0.4-2.3-0.8c-0.2-0.1-2.2-0.6-2.1-0.5
                                c0.1,0.5,0.2,0.7,0.6,1.1c0.4,0.5,0.8,0.9,1.1,1.4c2.3,2.9,3.8,5.3,3.9,9.1c0,1.9,0,3.8,0,5.7c0,0.9,0,1.9,0,2.8
                                c0,0.9,0,1.7,0.3,2.6c0.4-0.8,0.4-1.8,0.5-2.7s0.2-1.9,0.3-2.8c0.1-2,0.2-4,0.5-5.9C35,118.7,35.6,112.8,38.2,113.4z"
                            />
                            <Path
                                fill="#97773C"
                                d="M34.4,112.2c-1.4,0-2.5-0.9-3.7-1.6c-0.4-0.2-0.7-0.3-1.1-0.4c-0.4-0.2-0.9-0.6-1.2-0.9
                                c-0.4-0.3-0.7-0.6-0.9-1c0.5,0,0.9,0.1,1.4,0.2c0.7,0.2,1.2,0.2,1.8,0.3c1.2,0.1,2.3,0.5,3.6,0.5s2.6-0.3,3.8-0.8
                                c0.1,0,0.2-0.1,0.3-0.1s0.2,0,0.3,0.1c1,0.5,1.9,0.9,3.1,0.9c1.1,0,2.4-0.1,3.4-0.3c0.5-0.1,0.9-0.2,1.4-0.4
                                c0.2-0.1,1.2-0.4,1.2-0.5c-1.2,1.6-2.9,2.6-4.7,3.2c-1.2,0.4-2.5,0.3-3.6-0.2c-0.4-0.2-0.7-0.4-1.1-0.5c-0.5-0.1-0.7,0.1-1.1,0.4
                                C36.5,111.8,35.3,112.2,34.4,112.2z"
                            />
                            <Path
                                fill="#463116"
                                d="M45.9,110.1c-0.7,0.3-1.4,0.7-2.2,0.9c-0.2,0.1-2.5,0.4-2.5,0.2c-1-0.2-2.1-0.6-3-1.1c-0.3-0.2-0.7,0.2-1,0.3
                                c-0.5,0.2-0.9,0.4-1.4,0.6c-1,0.3-2,0.4-3,0.2c-1.3-0.3-2.4-1-3.7-1.4c0,0.3,0.3,0.5,0.5,0.7c1.6,1.2,3,3,3.9,4.8
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.9-1.6,2.2-3,3.8-3.8h0.1c0.1,0,0.1,0,0.1,0c1.2,0.7,2.1,2.4,2.7,3.6
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.5-1,1.3-1.7,2-2.5C44.6,111.8,45.1,110.9,45.9,110.1z"
                            />
                            <Path
                                fill="#F7F3B1"
                                d="M28.4,179.3c0.1,0,0.3,0.2,0.4,0.3c2,2.3,1.9,5.2,2.1,8.1c0.1,1.6,0.1,3.2,0.2,4.8c0,1.6,0,3.2,0.1,4.8
                                c0,0.3,0.1,0.5,0.2,0.7c0.2-0.7,0.3-1.4,0.4-2.2c0.1-0.7,0.1-1.4,0.2-2.1c0.2-1.6,0.3-3.1,0.5-4.7c0.3-2.8,0.6-5.8,2-8.3
                                c0.4-0.7,0.8-1.3,1.2-2c0.7-1.2,1.4-2.3,1.9-3.6c-0.7,0.3-1.3,0.6-2,0.8c-0.6,0.1-1.2,0.2-1.9,0.2c-1.3,0.1-2.6,0.1-3.9,0.2
                                c-1.3,0.1-2.6-0.1-3.9,0c-0.7,0-1.4,0.2-2.1,0.1c-0.5,0-1-0.2-1.4-0.3c-0.8-0.2-1.5-0.4-2.3-0.8c-0.2-0.1-2.2-0.6-2.1-0.5
                                c0.1,0.5,0.2,0.7,0.6,1.1c0.4,0.5,0.8,0.9,1.1,1.4c2.3,2.9,3.8,5.3,3.9,9.1c0,1.9,0,3.8,0,5.7c0,0.9,0,1.9,0,2.8
                                c0,0.9,0,1.7,0.3,2.6c0.4-0.8,0.4-1.8,0.5-2.7s0.2-1.9,0.3-2.8c0.1-2,0.2-4,0.5-5.9C25.3,184.6,25.8,178.7,28.4,179.3z"
                            />
                            <Path
                                fill="#97773C"
                                d="M24.6,178.1c-1.4,0-2.5-0.9-3.7-1.6c-0.4-0.2-0.7-0.3-1.1-0.4c-0.4-0.2-0.9-0.6-1.2-0.9
                                c-0.4-0.3-0.7-0.6-0.9-1c0.5,0,0.9,0.1,1.4,0.2c0.7,0.2,1.2,0.2,1.8,0.3c1.2,0.1,2.3,0.5,3.6,0.5c1.3,0,2.6-0.3,3.8-0.8
                                c0.1,0,0.2-0.1,0.3-0.1c0.1,0,0.2,0,0.3,0.1c1,0.5,1.9,0.9,3.1,0.9c1.1,0,2.4-0.1,3.4-0.3c0.5-0.1,0.9-0.2,1.4-0.4
                                c0.2-0.1,1.2-0.4,1.2-0.5c-1.2,1.6-2.9,2.6-4.7,3.2c-1.2,0.4-2.5,0.3-3.6-0.2c-0.4-0.2-0.7-0.4-1.1-0.5c-0.5-0.1-0.7,0.1-1.1,0.4
                                C26.7,177.7,25.6,178.1,24.6,178.1z"
                            />
                            <Path
                                fill="#463116"
                                d="M36.2,176c-0.7,0.3-1.4,0.7-2.2,0.9c-0.2,0.1-2.5,0.4-2.5,0.2c-1-0.2-2.1-0.6-3-1.1c-0.3-0.2-0.7,0.2-1,0.3
                                c-0.5,0.2-0.9,0.4-1.4,0.6c-1,0.3-2,0.4-3,0.2c-1.3-0.3-2.4-1-3.7-1.4c0,0.3,0.3,0.5,0.5,0.7c1.6,1.2,3,3,3.9,4.8
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.9-1.6,2.2-3,3.8-3.8h0.1c0.1,0,0.1,0,0.1,0c1.2,0.7,2.1,2.4,2.7,3.6
                                c0.1,0.2,0.2,0.5,0.3,0.7c0,0.1,0.4-0.6,0.4-0.7c0.5-1,1.3-1.7,2-2.5C34.8,177.7,35.4,176.8,36.2,176z"
                            />
                            <CuneiformTabletFlicker
                                isActive={cuneiformTabletRevealed}
                                flickerDuration={CUNEIFORM_FLICKER_DURATION}
                                fill="#FFFFFF"
                                d="M260.1,126
                                c-0.1-1-0.3-2-0.5-3c-0.9-4-0.7-7.7-0.7-11.8c0-1.6,0.2-4.1-0.7-5.4c-0.6-1,0.2-2.5,0.5-3.4c0.5-1.3,0.3-2.5,0.3-3.9
                                c0-3.5-0.8-6.8-0.7-10.3c0.1-1.8,0.2-3.5,0.4-5.3c0.2-2.3,0.4-4.6,0.4-6.9c0-1.2,0-2.4,0-3.6c0-0.9,0.2-2-0.3-2.7
                                c0-0.1-0.1-0.1-0.1-0.2s0-0.2,0.1-0.3c0.6-1.2,0.3-2.8,0.3-4c0-1.5,0.3-2.9,0.1-4.5c-0.3-1.6-0.9-3.1-0.9-4.7
                                c0-1.2,0.3-2.3,0.4-3.5c0.5-3.3,0.4-6.7,0.3-10c0-2.4,0.5-5.5-0.5-7.7c-0.1-0.2-0.2-0.5-0.2-0.7c0-0.1,0-0.2,0.1-0.3
                                c0.4-1.2,0.4-2.1,0.4-3.3c-0.1-2.7-0.7-5.3-0.7-8.1c0-3.2,0.3-6.7-0.6-9.8c-0.7-2.6-2.1-5.1-4.3-6.7c-2.5-1.8-5.8-2.3-8.9-2.7
                                c-41.9-5-84.1-3-126.2-1.2C97.5,2.9,76.9,3.7,56.3,3.7c-11.6,0-34.2-2.3-44.2,4.5C6.6,12,4.4,19.1,4,25.8c-0.1,1.4-0.1,2.8-0.2,4.2
                                c-0.1,1-0.4,2-0.3,3.1c0.1,0.9,0.8,1.6,0.6,2.4c-0.1,0.7-0.9,1.3-1.2,2c-0.7,2.3-0.4,5.2-0.3,7.5c0.1,3.2,0.2,6.5,0.1,9.7
                                c-0.1,2.8-0.8,5.4-0.6,8.3c0.2,1.8,0.9,4.2,0.1,6c-0.9,2.2-1,4.6-1,7c0.1,8.3,0.7,16.4-0.5,24.6c-0.1,0.4-0.1,0.9,0,1.4
                                c0.1,0.4,0.3,0.7,0.4,1c0.6,1.9-0.3,3.7-0.6,5.6c-0.3,2.1-0.3,4.3-0.1,6.5c0.3,5,0.9,9.9,1,14.9c0,2.1-0.4,4.5,0.3,6.5
                                c0.6,1.9-0.9,3.3-0.5,5.1c0.3,1.3,0.2,2.4,0.5,3.6c0.3,2.1,0.3,4.3,0.4,6.4c0,4.3-0.3,8.7,0,13c0.1,0.9,2.8,8,3.1,7.6
                                c-0.4,0.5-0.8,1-1.3,1.4c-0.1,0.1-0.3,0.2-0.3,0.4c0,0.1,0,0.3,0,0.4c0.4,3.8,0.6,7.6,1.2,11.4c0.4,3.8,1,7.8,3.7,10.6
                                c9.7,9.9,30.5,7.4,42.6,7.4c25.2,0,50.4-2.2,75.7-2c8.1,0.1,16.2-0.1,24.3-0.4c6.8-0.3,13.1,0.3,19.9,0.4c9.5,0.1,19.5-0.6,29.1-1
                                c9.5-0.4,19.2,0.8,28.8,0.3c6.4-0.3,12.1-0.2,18.5-1.6c2.8-0.6,5.7-1.4,7.7-3.5c2-2.2,2.5-5.5,2.8-8.5c0.2-1.7,0.3-3.4,0.5-5
                                c0-2.2,0-4.3,0-6.5c0-2,0.2-4.2-0.9-5.9c0.1,0.1,0.8-0.9,0.9-1.1c0.5-1.6-0.2-3.6-0.2-5.2c0-2.1,0.3-4.3,0.4-6.4
                                c0.1-4.1,0-8.4,0.1-12.4c0-2.2,0.2-4.3-0.5-6.4c0-0.1-0.1-0.2-0.1-0.3s0.1-0.3,0.1-0.4C259.7,134.2,260.5,129.6,260.1,126z"
                            />
                        </Group>
                    </CuneiformTablet>
                    <CuneiformTabletText
                        isVisible={cuneiformTabletRevealed}
                        delay={CUNEIFORM_TABLET_REVEAL_DURATION}
                        revealDuration={CUNEIFORM_TABLET_TEXT_REVEAL_DURATION}
                    >
                        {initiateCuneiformCountdown && cuneiformCountdownDigit !== 0
                            ? cuneiformCountdownDigit
                            : `${cuneiformCountdownDigit === 0 ? CUNEIFORM_COUNTDOWN_COMPLETE_TEXT : CUNEIFORM_IDLE_TEXT}`}
                    </CuneiformTabletText>
                </CuneiformTabletContainer>
            </InteractableObjectContainer>
            <Footer
                isVisible={landingPageContentRevealed}
                isTouchDevice={detectTouchDevice(document)}
                fontMultiplier={COPYRIGHT_FONT_MULTIPLIER}
                translateDuration={DEFAULT_CSS_TRANSITION_DURATION}
                translateDelay={EXPANSION_DURATION}
            >
                <FooterButtonContainer>
                    <FooterButton
                        className={HOVER_TARGET_CLASSNAME}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onObjectCursorEnter(
                                CURSOR_TARGET.privacyPolicyButton,
                                [CURSOR_SIGN.click],
                                e,
                            ),
                            onMouseLeave: (e) => onObjectCursorLeave(e),
                        })}
                        {...getPressEventHandlers({ onMouseDown: onPrivacyPolicy })}
                    >
                        PRIVACY POLICY
                    </FooterButton>
                    <FooterButton
                        className={HOVER_TARGET_CLASSNAME}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onObjectCursorEnter(
                                CURSOR_TARGET.termsOfUseButton,
                                [CURSOR_SIGN.click],
                                e,
                            ),
                            onMouseLeave: (e) => onObjectCursorLeave(e),
                        })}
                        {...getPressEventHandlers({ onMouseDown: onTermsOfUse })}
                    >
                        TERMS OF USE
                    </FooterButton>
                </FooterButtonContainer>
                <CopyrightClause>
                    {`© ${new Date().getUTCFullYear()} VERASCOPE, INC`}
                </CopyrightClause>
                <Anchor
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    href={`mailto:${MISC_EMAIL_ADDRESS}`}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            CURSOR_TARGET.email,
                            [CURSOR_SIGN.click],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                    })}
                    {...getPressEventHandlers({ onMouseDown: onEmailButtonClick })}
                >
                    <Envelope
                        viewBox="0 0 14.27 11.1"
                        detectTouchDevice={detectTouchDevice(document)}
                        height={BODY_FONT_SIZE * COPYRIGHT_FONT_MULTIPLIER}
                    >
                        <Rect x="3.17" y="3.17" width="1.59" height="1.59" />
                        <Rect x="4.76" y="4.76" width="1.59" height="1.59" />
                        <Rect x="7.93" y="4.76" width="1.59" height="1.59" />
                        <Rect x="6.34" y="6.34" width="1.59" height="1.59" />
                        <Path d="M0,0V11.1H14.27V0ZM1.59,9.51V1.59h0V3.17H3.17V1.59H11.1V3.17H9.51V4.76H11.1V3.17h1.58V9.51Z" />
                    </Envelope>
                </Anchor>
                <Anchor
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    href="https://x.com/playverascope"
                    target="_blank"
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            CURSOR_TARGET.xTwitter,
                            [CURSOR_SIGN.click],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                    })}
                    {...getPressEventHandlers({ onMouseDown: onXTwitterButtonClick })}
                >
                    <XTwitter
                        viewBox="0 0 1200 1227"
                        height={BODY_FONT_SIZE * COPYRIGHT_FONT_MULTIPLIER}
                        detectTouchDevice={detectTouchDevice(document)}
                    >
                        <Path d="M714.2,519.3L1160.9,0h-105.9l-387.9,450.9L357.3,0H0l468.5,681.8L0,1226.4h105.9l409.6-476.2,327.2,476.2h357.3l-485.9-707.1h0ZM569.2,687.8l-47.5-67.9L144,79.7h162.6l304.8,436,47.5,67.9,396.2,566.7h-162.6l-323.3-462.4h0Z" />
                    </XTwitter>
                </Anchor>
                <Anchor
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    href="https://www.instagram.com/playverascope"
                    target="_blank"
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onObjectCursorEnter(
                            CURSOR_TARGET.instagram,
                            [CURSOR_SIGN.click],
                            e,
                        ),
                        onMouseLeave: (e) => onObjectCursorLeave(e),
                    })}
                    {...getPressEventHandlers({ onMouseDown: onInstagramButtonClick })}
                >
                    <Instagram
                        viewBox="0 0 1000.01 1000.1"
                        height={BODY_FONT_SIZE * COPYRIGHT_FONT_MULTIPLIER}
                        detectTouchDevice={detectTouchDevice(document)}
                    >
                        <Path d="M292.94,3.53c-53.2,2.5-89.5,11-121.3,23.5-32.9,12.8-60.7,30-88.4,57.8-27.7,27.8-44.8,55.7-57.5,88.6-12.3,31.8-20.6,68.2-23,121.4C.34,348.13-.16,365.23.04,501.03s.9,152.8,3.4,206.1c2.5,53.2,11,89.5,23.5,121.3,12.8,32.9,30,60.7,57.8,88.5,27.8,27.7,55.7,44.8,88.7,57.5,31.8,12.3,68.2,20.7,121.4,23,53.2,2.3,70.4,2.9,206.1,2.6,135.7-.3,152.8-.9,206.2-3.4s89.5-11,121.2-23.5c32.9-12.9,60.7-30,88.5-57.8s44.8-55.7,57.5-88.7c12.3-31.8,20.7-68.2,23-121.3,2.3-53.4,2.9-70.4,2.6-206.2s-.9-152.8-3.4-206.1-11-89.5-23.5-121.3c-12.8-32.9-30-60.7-57.8-88.4s-55.7-44.8-88.7-57.5c-31.8-12.3-68.2-20.7-121.4-23S634.84-.17,499.04.03s-152.8.8-206.1,3.4M298.84,907.43c-48.8-2.1-75.2-10.2-92.9-17-23.4-9-40-19.9-57.6-37.3s-28.4-34.1-37.5-57.4c-6.8-17.6-15.1-44.1-17.4-92.8-2.5-52.7-3-68.5-3.3-202s.2-149.3,2.5-202c2.1-48.7,10.2-75.2,17-92.8,9-23.4,19.8-40,37.3-57.6s34.1-28.4,57.4-37.5c17.6-6.9,44.1-15.1,92.8-17.4,52.7-2.5,68.5-3,202-3.3s149.3.2,202.1,2.5c48.7,2.1,75.2,10.2,92.8,17,23.4,9,40,19.8,57.6,37.3s28.4,34.1,37.5,57.4c6.9,17.6,15.1,44,17.4,92.8,2.5,52.7,3.1,68.5,3.3,202s-.2,149.3-2.5,202c-2.1,48.8-10.2,75.2-17,92.9-9,23.3-19.8,40-37.3,57.6s-34.1,28.4-57.4,37.5c-17.6,6.9-44.1,15.1-92.8,17.4-52.7,2.5-68.5,3-202,3.3s-149.3-.2-202-2.5M706.44,232.83c0,33.1,27,60,60.1,59.9s60-27,59.9-60.1c0-33.1-27-60-60.1-59.9h0c-33.1,0-59.9,27-59.9,60.1M243.34,500.53c.3,141.8,115.4,256.5,257.2,256.2s256.5-115.4,256.3-257.2-115.5-256.5-257.2-256.2-256.5,115.5-256.2,257.2M333.34,500.33c-.2-92,74.3-166.8,166.3-167s166.8,74.3,167,166.3-74.3,166.8-166.3,167-166.8-74.3-167-166.3h0" />
                    </Instagram>
                </Anchor>
            </Footer>
            <TooltipContainer
                isVisible={!landingPageContentRevealed}
                transitionDuration={DEFAULT_CSS_TRANSITION_DURATION}
                delayDuration={TOOLTIP_DELAY_DURATION}
            >
                <TooltipIcon
                    isVisible={tooltipActionIndex === TOOLTIP_POINT_INDEX && !jellyHeld}
                    transitionDuration={TOOLTIP_ICON_TRANSITION_DURATION}
                    pulseDuration={PULSE_DURATION}
                    src={ClickSign}
                />
                <TooltipIcon
                    isVisible={tooltipActionIndex === TOOLTIP_GRAB_INDEX && !jellyHeld}
                    transitionDuration={TOOLTIP_ICON_TRANSITION_DURATION}
                    pulseDuration={PULSE_DURATION}
                    src={GrabSign}
                />
                <TooltipIcon
                    isVisible={jellyHeld}
                    transitionDuration={TOOLTIP_ICON_TRANSITION_DURATION}
                    pulseDuration={PULSE_DURATION}
                    src={MoveSign}
                />
                <TooltipText>
                    {tooltipText}
                </TooltipText>
            </TooltipContainer>
        </>
    );
}

export default LandingView;
